import { socket } from "../../services/websocket";
import {
  filterLeaveTypesByGender,
  isFemaleOnlyLeaveType,
  isPaternityLeaveType,
} from "../../lib/leaveGenderFilter";
import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { createPortal } from "react-dom";
import { debounce } from "lodash";
import { List } from "react-window";
import {
  ChevronLeft,
  ChevronRight,
  Search,
  Filter,
  Lock,
  Unlock,
  X,
  Download,
  FileUp,
  Upload,
  Settings,
  Menu,
  MoreVertical,
  Check,
  Eye,
  EyeOff,
  Edit,
  RefreshCw,
  LayoutDashboard,
  ChevronDown,
} from "lucide-react";
import * as XLSX from "xlsx";
import {
  attendanceService,
  employeeService,
  branchService,
  departmentService,
  designationService,
  gradesService,
  categoryService,
  subDepartmentService,
  attendanceStructureService,
  companyService,
} from "../../services/api";

import { useAttendanceCodeConfig } from "../../context/attendanceCodeConfigContext";
import AttendancePolicyComponent from "./AttendancePolicyComponent";
import GeneralSettingsTab from "./attendance/components/structureTabs/GeneralSettingsTab";
import WeeklyOffStructureTab from "./attendance/components/structureTabs/WeeklyOffStructureTab";
import HolidayStructureTab from "./attendance/components/structureTabs/HolidayStructureTab";
import ShiftStructureTab from "./attendance/components/structureTabs/ShiftStructureTab";
import AttendanceRulesTab from "./attendance/components/structureTabs/AttendanceRulesTab";
import ConfigurationStructureTab from "./attendance/components/structureTabs/ConfigurationStructureTab";
import EmployeeAttendanceCalendarPopup from "./attendance/components/EmployeeAttendanceCalendarPopup";
import OpeningBalanceUploadModal from "./attendance/components/popups/OpeningBalanceUploadModal";
import LeaveTypeApprovalWorkflowSection from "./attendance/components/LeaveTypeApprovalWorkflowSection";
import LeaveGroupWorkflowSection from "./attendance/components/LeaveGroupWorkflowSection";
import {
  LEAVE_APPROVAL_ROLES,
  defaultLeaveGroupWorkflow,
  mapLeaveRequestApiToInsight,
  normalizeLeaveGroupWorkflow,
} from "../../lib/leaveApprovalWorkflow";
import {
  getCompanyServiceCommencementDate,
  getEmployeeDoj,
  getFirstEligibleMonthInYear,
  getLeaveBalanceYearOptions,
  resolveLeaveBalanceStartDate,
} from "../../lib/leaveBalanceDates";
import { roundLeaveDaysForDisplay } from "../../lib/leaveBalanceDisplay";

import { toast } from "react-hot-toast";
import AttendanceReports from "./AttendanceReports";
import Filters from "./Filters";
import ActionInsightsPanel from "./ActionInsightsPanel";
import AttendanceDashboardPanel from "./AttendanceDashboardPanel";
import {
  ATTENDANCE_INSIGHTS_KEY,
  LEAVE_REQUEST_ACTION,
  loadAttendanceInsights,
  saveAttendanceInsights,
  buildLeaveRequestInsight,
  syncQuickActionLeaveRequestsToInsights,
  mapActionLabelToInsightAction,
} from "./attendance/attendanceInsightStore";
import { notifyLeaveStatusChange } from "../../utils/navbarNotifications";
import { usePermissions } from "../../rbac/PermissionsContext";
import { getNameByIdFromList } from "../../lib/employeeHelpers";
import { buildMonthPolicyStatusMap } from "../../lib/attendancePolicyEngine";
import {
  ATTENDANCE_PUNCH_UPDATED_EVENT,
  readEmployeeWorklogMap,
} from "../../lib/attendancePunchStorage";
import {
  loadAttendanceGeneralSettings,
  ATTENDANCE_SCHEDULE_RECALC_EVENT,
} from "../../lib/attendanceGeneralSettings";
import { startAttendanceScheduleRunner } from "../../lib/attendanceScheduleRunner";

// Shared Excel filename helpers (module-scope so sub-components can use them)
const getCompanyCodeForAttendanceExcelsStatic = () => {
  try {
    const raw = localStorage.getItem("companyData");
    const companyData = raw ? (typeof raw === "string" ? JSON.parse(raw) : raw) : null;
    const code = String(companyData?.code || "")
      .trim()
      .replace(/[^A-Za-z0-9]/g, "_")
      .replace(/_+/g, "_")
      .replace(/^_+|_+$/g, "")
      .toUpperCase();
    return code || "COMPANY";
  } catch {
    return "COMPANY";
  }
};

const buildAttendanceExcelFilenameStatic = (baseName, month0 = null, year = null) => {
  const now = new Date();
  const mm =
    Number.isFinite(Number(month0)) && Number(month0) >= 0 && Number(month0) <= 11
      ? Number(month0) + 1
      : now.getMonth() + 1;
  const yyyy = Number.isFinite(Number(year)) && Number(year) > 0 ? Number(year) : now.getFullYear();
  const safeName = String(baseName || "Attendance_Export")
    .trim()
    .replace(/[^A-Za-z0-9]+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "");
  return `${getCompanyCodeForAttendanceExcelsStatic()}_${safeName}_${String(mm).padStart(2, "0")}_${yyyy}.xlsx`;
};

// PERFORMANCE OPTIMIZATIONS APPLIED:
// 1. ✅ Debug logs disabled (console.debug = () => {}) - prevents 246K+ log messages
// 2. ✅ Button handlers memoized with useCallback - prevents unnecessary re-renders
// 3. ✅ Expensive operations deferred with requestIdleCallback - prevents UI freeze
// 4. ✅ Search debounced at 700ms - reduces API calls during typing
// 5. ✅ Totals calculation only when needed (useMemo) - prevents expensive recalc
// 6. ✅ Table virtualized with react-window - only renders visible rows
// 7. ✅ State updates batched in requestIdleCallback - smooth UI performance

// 8. ✅ Employee map cached - O(1) lookups instead of O(n)
// 9. ✅ Leave code memoization - O(1) set lookups instead of array searches
// 10. ✅ AttendanceCell/Row/Table wrapped in React.memo - prevents unnecessary re-renders
// Result: 500+ employees load instantly, buttons responsive, smooth scrolling, low CPU usage
const ENABLE_DEBUG_LOGS = false; // Set to true ONLY when debugging
if (!ENABLE_DEBUG_LOGS) {
  console.debug = () => {};
}

import DatePicker from "../Common/DatePicker";
// Pagination removed: using infinite scroll instead
import useIntersectionObserver from "../../hooks/useIntersectionObserver";

// Determine if the given date is a weekly-off for the supplied employee/context.
// Returns "full", "first_half", "second_half" or false when not a weekly-off.
const isWeeklyOffForEmployee = (
  date,
  employeeContext = null,
  weeklyOffPolicies = [],
  employeeWeeklyOffMappings = {}
) => {
  try {
    if (!(date instanceof Date) || isNaN(date.getTime())) return false;

    const dayName = date
      .toLocaleDateString("en-US", { weekday: "long" })
      .toLowerCase();

    // Try to resolve a policy assigned to this employee
    const assigned = resolveEmployeePolicyAssignment(
      employeeContext,
      employeeWeeklyOffMappings
    );

    let matchedPolicy = null;

    // If assigned is an array of policy ids or mapping object with policies, pick the first matching policy
    if (assigned) {
      const policyIds = Array.isArray(assigned)
        ? assigned
        : assigned?.policies || [];
      matchedPolicy = weeklyOffPolicies.find((p) => policyIds.includes(p.id));
    }

    // If no employee-specific policy, pick a global policy that is effective on the date
    if (!matchedPolicy) {
      matchedPolicy = weeklyOffPolicies.find(
        (p) => isPolicyEffectiveOnDate(p, date) && p.weeklyOffs
      );
    }

    if (!matchedPolicy) return false;

    const weeklyOffConfig = matchedPolicy.weeklyOffs?.[dayName];
    if (!weeklyOffConfig || !weeklyOffConfig.enabled) return false;
    return weeklyOffConfig.type || "full";
  } catch (error) {
    console.error("Error checking weekly off for employee:", error);
    return false;
  }
};

const normalizeEmployeeWeeklyOffMapping = (data = {}) => {
  console.debug("normalizeEmployeeWeeklyOffMapping: input data", data);
  const result = Object.fromEntries(
    Object.entries(data || {}).map(([empId, mapping]) => {
      if (Array.isArray(mapping)) {
        return [
          empId,
          mapping.map((v) => Number(v)).filter((v) => !Number.isNaN(v)),
        ];
      }
      if (typeof mapping === "object" && mapping !== null) {
        // Handle backend format: { policyId: 19, allowOverride: false }
        if (mapping.policyId && !mapping.policies) {
          const normalizedPolicies = [Number(mapping.policyId)].filter(
            (v) => !Number.isNaN(v)
          );
          return [
            empId,
            {
              ...mapping,
              policies: normalizedPolicies,
              allowOverride:
                mapping.allowOverride === true ||
                mapping.allowOverride === "true",
              overridePolicy:
                mapping.overridePolicy === true ||
                mapping.overridePolicy === "true",
            },
          ];
        }

        // Handle existing format: { policies: [19], allowOverride: false }
        const normalizedPolicies = Array.isArray(mapping.policies)
          ? mapping.policies
              .map((v) => Number(v))
              .filter((v) => !Number.isNaN(v))
          : mapping.policies;
        return [
          empId,
          {
            ...mapping,
            policies: normalizedPolicies,
            allowOverride:
              mapping.allowOverride === true ||
              mapping.allowOverride === "true",
            overridePolicy:
              mapping.overridePolicy === true ||
              mapping.overridePolicy === "true",
          },
        ];
      }
      const asNum = Number(mapping);
      return [empId, Number.isNaN(asNum) ? mapping : [asNum]];
    })
  );
  console.debug("normalizeEmployeeWeeklyOffMapping: result", result);
  return result;
};

const normalizePolicyIdList = (rawPolicies) => {
  if (!rawPolicies) return [];
  if (Array.isArray(rawPolicies)) {
    return rawPolicies
      .map((value) => Number(value))
      .filter((value) => !Number.isNaN(value));
  }
  if (typeof rawPolicies === "object") {
    if (Array.isArray(rawPolicies.policies)) {
      return rawPolicies.policies
        .map((value) => Number(value))
        .filter((value) => !Number.isNaN(value));
    }
    return Object.keys(rawPolicies)
      .map((key) => Number(key))
      .filter((value) => !Number.isNaN(value));
  }
  if (typeof rawPolicies === "number" || typeof rawPolicies === "string") {
    const numeric = Number(rawPolicies);
    return Number.isNaN(numeric) ? [] : [numeric];
  }
  return [];
};

const buildEmployeeKeyCandidates = (employeeContext) => {
  const candidates = new Set();
  const addValue = (value) => {
    if (value === undefined || value === null) return;
    if (typeof value === "number") {
      if (!Number.isNaN(value)) {
        candidates.add(String(value));
      }
      return;
    }
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (!trimmed) return;
      candidates.add(trimmed);
      candidates.add(trimmed.toLowerCase());
      candidates.add(trimmed.toUpperCase());
    }
  };

  if (employeeContext && typeof employeeContext === "object") {
    addValue(employeeContext.id);
    addValue(employeeContext.employeeId);
    addValue(employeeContext.empId);
    addValue(employeeContext.empCode);
    addValue(employeeContext.code);
    addValue(employeeContext.employeeCode);
    addValue(employeeContext.user?.employeeCode);
  } else {
    addValue(employeeContext);
  }

  return Array.from(candidates).filter((value) => value !== "");
};

const resolveEmployeePolicyAssignment = (employeeContext, mappings = {}) => {
  if (!mappings || typeof mappings !== "object") return null;
  const candidates = buildEmployeeKeyCandidates(employeeContext);

  // Debug: log candidates and mappings
  console.debug("resolveEmployeePolicyAssignment: debug", {
    employeeContext,
    candidates,
    mappingsKeys: Object.keys(mappings),
    mappingsSample: Object.entries(mappings)
      .slice(0, 5)
      .map(([k, v]) => ({ key: k, value: v })),
  });

  for (const key of candidates) {
    if (Object.prototype.hasOwnProperty.call(mappings, key)) {
      console.debug("resolveEmployeePolicyAssignment: found match", {
        key,
        value: mappings[key],
      });
      return mappings[key];
    }
  }

  console.debug("resolveEmployeePolicyAssignment: no match found");
  return null;
};

const parseDateValue = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const isPolicyEffectiveOnDate = (policy, targetDate) => {
  if (!policy) return false;
  const effectiveDate = parseDateValue(policy.effectiveDate);
  if (!effectiveDate) return true;
  return effectiveDate <= targetDate;
};

/**
 * Returns the weekly off policy that applies for an employee on a given date.
 * Used when employee has no explicit mapping (e.g. new employees joining after policy creation)
 * to determine which policy's override flag applies.
 */
const getEffectiveWeeklyOffPolicyForEmployee = (
  date,
  employeeContext,
  weeklyOffPolicies = [],
  employeeWeeklyOffMappings = {}
) => {
  if (!(date instanceof Date) || isNaN(date.getTime())) return null;
  if (!Array.isArray(weeklyOffPolicies) || weeklyOffPolicies.length === 0)
    return null;

  const assigned = resolveEmployeePolicyAssignment(
    employeeContext,
    employeeWeeklyOffMappings
  );
  if (assigned) {
    const policyIds = Array.isArray(assigned)
      ? assigned
      : assigned?.policies || [];
    const policy = weeklyOffPolicies.find((p) => policyIds.includes(p.id));
    return policy || null;
  }
  return (
    weeklyOffPolicies.find(
      (p) => isPolicyEffectiveOnDate(p, date) && p.weeklyOffs
    ) || null
  );
};

/**
 * Returns whether weekly off can be overridden for an employee on a given date.
 * Handles new employees who have no mapping: uses the effective policy's override flag.
 */
const getAllowOverrideForEmployee = (
  date,
  employeeContext,
  weeklyOffPolicies = [],
  employeeWeeklyOffMappings = {},
  employeeIdForMapping
) => {
  const empId = employeeIdForMapping ?? (employeeContext && typeof employeeContext === "object"
    ? employeeContext.id || employeeContext.employeeId || employeeContext.code
    : employeeContext);
  const mapping = empId != null ? employeeWeeklyOffMappings?.[String(empId)] : null;

  // When employee has a mapping, use the POLICY's override flag (not the mapping's)
  // so that "Override Weekly Off" in policy config is the single source of truth.
  if (mapping) {
    const policyIds = (
      Array.isArray(mapping) ? mapping : mapping?.policies || []
    )
      .map((v) => Number(v))
      .filter((n) => !Number.isNaN(n));
    // Handle backend format: { policyId: 19, allowOverride: false }
    const singlePolicyId =
      typeof mapping === "object" &&
      !Array.isArray(mapping) &&
      mapping.policyId != null
        ? Number(mapping.policyId)
        : null;
    const ids = policyIds.length > 0 ? policyIds : (singlePolicyId != null && !Number.isNaN(singlePolicyId) ? [singlePolicyId] : []);
    if (ids.length > 0) {
      const policy = weeklyOffPolicies.find((p) =>
        ids.includes(typeof p.id === "string" ? Number(p.id) : p.id)
      );
      return policy != null && (policy.allowOverride === true || policy.overridePolicy === true);
    }
  }

  const effectivePolicy = getEffectiveWeeklyOffPolicyForEmployee(
    date,
    employeeContext,
    weeklyOffPolicies,
    employeeWeeklyOffMappings
  );
  return (
    effectivePolicy != null &&
    (effectivePolicy.allowOverride === true ||
      effectivePolicy.overridePolicy === true)
  );
};

// Global weekly-off check (non-employee-specific). Used as a fallback
// when an employee context is not provided.
const isWeeklyOff = (date, weeklyOffPolicies = []) => {
  try {
    if (!(date instanceof Date) || isNaN(date.getTime())) return false;
    const dayName = date
      .toLocaleDateString("en-US", { weekday: "long" })
      .toLowerCase();
    if (!Array.isArray(weeklyOffPolicies)) return false;
    for (const p of weeklyOffPolicies) {
      if (!p) continue;
      if (!isPolicyEffectiveOnDate(p, date)) continue;
      const weeklyOffConfig = p.weeklyOffs?.[dayName];
      if (weeklyOffConfig && weeklyOffConfig.enabled) return true;
    }
    return false;
  } catch (e) {
    console.error("isWeeklyOff error:", e);
    return false;
  }
};

const getPolicySortValue = (policy) => {
  if (!policy) return 0;
  const effectiveValue = parseDateValue(policy.effectiveDate);
  if (effectiveValue) return effectiveValue.getTime();
  const createdValue = parseDateValue(policy.createdAt);
  return createdValue ? createdValue.getTime() : 0;
};

const isHoliday = (
  date,
  employeeId = null,
  holidaysData = [],
  holidayPolicies = [],
  employeeHolidayPolicyMappings = {}
) => {
  // Guard against invalid dates
  if (!(date instanceof Date) || isNaN(date.getTime())) return false;
  // Use local date components so holiday (e.g. Jan 26) matches the calendar day in user's timezone.
  // toISOString() uses UTC and shifts the date (e.g. Jan 27 local in IST becomes "2026-01-26" UTC, causing wrong day to show as PH).
  const dateString =
    date.getFullYear() +
    "-" +
    String(date.getMonth() + 1).padStart(2, "0") +
    "-" +
    String(date.getDate()).padStart(2, "0");

  // Normalize holiday date to YYYY-MM-DD for comparison (API may return "2026-01-26" or "2026-01-26T00:00:00.000Z")
  const norm = (d) => (d && typeof d === "string" ? d.trim().slice(0, 10) : "");

  // Check global holidays first
  if (
    holidaysData.some(
      (holiday) => norm(holiday.date) === dateString && holiday.isGlobal
    )
  ) {
    return true;
  }

  // Check employee-specific holiday policies
  if (employeeId) {
    const employeePolicyIds = employeeHolidayPolicyMappings[employeeId] || [];
    const employeePolicies = holidayPolicies.filter((policy) =>
      employeePolicyIds.includes(policy.id)
    );

    // Check if any holiday in the assigned policies matches the date
    return employeePolicies.some((policy) =>
      policy.holidays?.some((holiday) => norm(holiday.date) === dateString)
    );
  }

  return false;
};

// Helper that returns both status and whether it's locked (cannot be overridden)
const getDefaultStatusWithLock = (
  date,
  employeeContext = null,
  weeklyOffPolicies = [],
  employeeWeeklyOffMappings = {},
  holidaysData = [],
  holidayPolicies = [],
  employeeHolidayMappings = {}
) => {
  // Guard against invalid dates
  if (!(date instanceof Date) || isNaN(date.getTime())) {
    return { status: "-", locked: true };
  }

  // Check if date is outside employee's join/relieved date range
  // This check should happen FIRST before any other policies
  if (employeeContext && typeof employeeContext === "object") {
    const joinDate = employeeContext.joinDate || employeeContext.joiningDate;
    const relievedDate =
      employeeContext.relievedDate ||
      employeeContext.exitDate ||
      employeeContext.resignationDate ||
      employeeContext.relievingDate;

    // If join date is set and date is before join date, return "-"
    if (joinDate) {
      // Parse join date using numeric comparison to avoid timezone issues
      const joinParts = joinDate.match(/(\d{4})-(\d{2})-(\d{2})/);
      if (joinParts) {
        const joinNum = parseInt(joinParts[1] + joinParts[2] + joinParts[3]);
        const dateNum = parseInt(
          date.getFullYear().toString() +
            String(date.getMonth() + 1).padStart(2, "0") +
            String(date.getDate()).padStart(2, "0")
        );

        if (dateNum < joinNum) {
          return { status: "-", locked: true };
        }
      }
    }

    // If relieved date is set and date is after relieved date, return "-"
    // This returns immediately before checking any policies
    if (relievedDate) {
      const rd = new Date(relievedDate);
      if (date > rd) {
        return { status: "-", locked: true };
      }
    }
  }

  // Resolve employee identifier for holiday checks
  const holidayEmployeeKey =
    employeeContext && typeof employeeContext === "object"
      ? employeeContext.id || employeeContext.employeeId || employeeContext.code
      : employeeContext;

  // Public Holiday: always locked
  if (
    isHoliday(
      date,
      holidayEmployeeKey,
      holidaysData,
      holidayPolicies,
      employeeHolidayMappings
    )
  ) {
    return { status: "PH", locked: true };
  }

  // Employee-specific weekly off logic
  if (employeeContext) {
    const weeklyOffType = isWeeklyOffForEmployee(
      date,
      employeeContext,
      weeklyOffPolicies,
      employeeWeeklyOffMappings
    );

    if (
      weeklyOffType === "full" ||
      weeklyOffType === "first_half" ||
      weeklyOffType === "second_half"
    ) {
      const statusMap = {
        full: "WO",
        first_half: "WOPR",
        second_half: "PRWO",
      };
      const defaultStatus = statusMap[weeklyOffType];

      const allowOverride = getAllowOverrideForEmployee(
        date,
        employeeContext,
        weeklyOffPolicies,
        employeeWeeklyOffMappings
      );

      return { status: defaultStatus, locked: !allowOverride };
    }
  }

  // Fallback for global weekly offs (not employee-specific)
  if (!employeeContext && isWeeklyOff(date, weeklyOffPolicies)) {
    return { status: "WO", locked: false };
  }

  return { status: "PR", locked: false };
};

const getDefaultStatus = (
  date,
  employeeContext = null,
  weeklyOffPolicies = [],
  employeeWeeklyOffMappings = {},
  holidaysData = [],
  holidayPolicies = [],
  employeeHolidayMappings = {}
) => {
  // For backwards compatibility, return just the status string
  const result = getDefaultStatusWithLock(
    date,
    employeeContext,
    weeklyOffPolicies,
    employeeWeeklyOffMappings,
    holidaysData,
    holidayPolicies,
    employeeHolidayMappings
  );
  return result.status;
};

// Utility function to format date as DD/MM/YYYY
const formatDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "";
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

// Attendance table UI should keep the old (pre-DD/MM) format.
// Exports must remain strict DD/MM/YYYY, so use this only for table cells.
// const formatDateForTableUI = (dateString) => {
//   if (!dateString) return "";
//   const date = new Date(dateString);
//   if (isNaN(date.getTime())) return "";
//   return date.toLocaleDateString();
// };

// Utility function to format date as dd-mmm-yy
const formatDateForTableUI = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  const day = date.getDate().toString().padStart(2, "0");
  const month = date.toLocaleDateString("en-US", { month: "short" });
  const year = date.getFullYear().toString().slice(-2);
  return `${day}-${month}-${year}`;
};

const isDateInSelectedMonth = (dateString, selectedYear, selectedMonth) => {
  if (!dateString) return false;
  const d = new Date(dateString);
  if (isNaN(d.getTime())) return false;
  return d.getFullYear() === selectedYear && d.getMonth() === selectedMonth;
};


const getMonthRangeValue = (year, month) => {
  const paddedMonth = (month + 1).toString().padStart(2, "0");
  const start = `${year}-${paddedMonth}-01`;
  const endDay = new Date(year, month + 1, 0)
    .getDate()
    .toString()
    .padStart(2, "0");
  const end = `${year}-${paddedMonth}-${endDay}`;
  return `${start}/${end}`;
};

const MONTH_NAMES_SHORT = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const MONTH_NAMES_LONG = [
  "january",
  "february",
  "march",
  "april",
  "may",
  "june",
  "july",
  "august",
  "september",
  "october",
  "november",
  "december",
];

/**
 * Parse the attendance template "Month" cell (MM/YYYY, Excel serial, Date, MMM-YYYY, etc.).
 * @returns {{ year: number, month: number } | null} month is 0–11
 */
const parseAttendanceTemplateMonthValue = (raw) => {
  if (raw === undefined || raw === null) return null;
  if (typeof raw === "string" && raw.trim() === "") return null;

  if (raw instanceof Date && !isNaN(raw.getTime())) {
    return { year: raw.getFullYear(), month: raw.getMonth() };
  }

  if (typeof raw === "number" && Number.isFinite(raw)) {
    try {
      const o = XLSX.SSF.parse_date_code(raw);
      if (o && typeof o.y === "number" && o.m != null) {
        const m1 = Number(o.m);
        if (m1 >= 1 && m1 <= 12) {
          return { year: o.y, month: m1 - 1 };
        }
      }
    } catch (e) {
      /* fall through */
    }
    return null;
  }

  const s = String(raw).trim();

  let m = s.match(/^(\d{1,2})\s*[/.-]\s*(\d{4})$/);
  if (m) {
    const mm = parseInt(m[1], 10);
    const yyyy = parseInt(m[2], 10);
    if (mm >= 1 && mm <= 12 && yyyy >= 1900 && yyyy <= 2100) {
      return { year: yyyy, month: mm - 1 };
    }
  }

  m = s.match(/^(\d{4})\s*[/.-]\s*(\d{1,2})$/);
  if (m) {
    const yyyy = parseInt(m[1], 10);
    const mm = parseInt(m[2], 10);
    if (mm >= 1 && mm <= 12 && yyyy >= 1900 && yyyy <= 2100) {
      return { year: yyyy, month: mm - 1 };
    }
  }

  const hyphenMatch = s.match(
    /^(\d{1,2}|[A-Za-z]+)\s*[-/.\s]+\s*(\d{4})$/
  );
  if (hyphenMatch) {
    const a = hyphenMatch[1];
    const y = parseInt(hyphenMatch[2], 10);
    if (!isNaN(parseInt(a, 10)) && String(a).length <= 2) {
      const mm = parseInt(a, 10);
      if (mm >= 1 && mm <= 12 && y >= 1900 && y <= 2100) {
        return { year: y, month: mm - 1 };
      }
    } else {
      const short = MONTH_NAMES_SHORT.findIndex(
        (name) => name.toLowerCase() === a.slice(0, 3).toLowerCase()
      );
      if (short !== -1 && y >= 1900 && y <= 2100) {
        return { year: y, month: short };
      }
      const longIdx = MONTH_NAMES_LONG.findIndex(
        (name) => name === a.toLowerCase()
      );
      if (longIdx !== -1 && y >= 1900 && y <= 2100) {
        return { year: y, month: longIdx };
      }
    }
  }

  const d = new Date(s);
  if (!isNaN(d.getTime())) {
    return { year: d.getFullYear(), month: d.getMonth() };
  }

  return null;
};

/**
 * Ensure every data row's "Month" column matches the selected Import Month/Year in the dialog.
 */
const validateAttendanceTemplateMonthColumn = (
  rows,
  headers,
  expectedYear,
  expectedMonth0
) => {
  const monthCol =
    headers.find((h) => String(h).trim().toLowerCase() === "month") ||
    headers.find((h) =>
      /^(month|month_year|period)$/i.test(String(h).trim())
    );

  if (!monthCol) {
    return {
      ok: false,
      message:
        'The file must include a "Month" column (e.g. 03/2026) that matches Import Month and Import Year.',
    };
  }

  const idCol =
    headers.find((h) => /employee[\s_]*id|emp[\s_]*id/i.test(h)) ||
    headers.find((h) =>
      /employee[\s_]*code|emp[\s_]*code|employeecode|empcode|code/i.test(h)
    ) ||
    headers.find((h) => !/sl[\s_.-]*no|serial/i.test(h)) ||
    headers[0];

  const seen = new Map();
  const issues = [];

  rows.forEach((r, idx) => {
    const rawId = r[idCol];
    if (
      rawId === undefined ||
      rawId === null ||
      String(rawId).trim() === ""
    ) {
      return;
    }

    const cell = r[monthCol];
    if (cell === undefined || cell === null || String(cell).trim() === "") {
      issues.push(`Row ${idx + 2}: "Month" is empty.`);
      return;
    }

    const parsed = parseAttendanceTemplateMonthValue(cell);
    if (!parsed) {
      issues.push(
        `Row ${idx + 2}: "Month" value "${cell}" is invalid. Use MM/YYYY (e.g. 03/2026).`
      );
      return;
    }

    const key = `${parsed.year}-${parsed.month}`;
    seen.set(key, (seen.get(key) || 0) + 1);
  });

  if (issues.length > 0) {
    const head = issues.slice(0, 5).join(" ");
    const more =
      issues.length > 5 ? ` …and ${issues.length - 5} more issue(s).` : "";
    return { ok: false, message: head + more };
  }

  if (seen.size === 0) {
    return {
      ok: false,
      message:
        "No employee rows with a valid Month were found. Check the file format.",
    };
  }

  if (seen.size > 1) {
    const labels = Array.from(seen.keys()).map((k) => {
      const [y, mo] = k.split("-").map(Number);
      return `${MONTH_NAMES_SHORT[mo]} ${y}`;
    });
    return {
      ok: false,
      message: `The file mixes multiple months (${labels.join(
        ", "
      )}). All rows must be for one calendar month.`,
    };
  }

  const [fileYear, fileMonth0] = Array.from(seen.keys())[0]
    .split("-")
    .map(Number);

  if (fileYear !== expectedYear || fileMonth0 !== expectedMonth0) {
    return {
      ok: false,
      message: `This file is for ${MONTH_NAMES_LONG[fileMonth0]} ${fileYear}, but you selected ${MONTH_NAMES_LONG[expectedMonth0]} ${expectedYear}. Change Import Month/Year or use the template for the correct month.`,
    };
  }

  return { ok: true };
};

// Default single-day attendance statuses. This will be extended at runtime
// with leave types fetched from the backend so the UI stays in sync.
let attendanceStatuses = ["PR", "LOP", "WO", "PH"];

// Normalize status for calculations (treat LWP same as LOP for pay calculations)
const normalizeStatus = (status) => {
  if (status === "LWP") return "LOP";
  return status;
};

// Status label mapping. Only PR is default; LOP, LWP, WO, PH etc. are added
// from the company's leave types (policy) so export/summary don't show codes not in leave types.
let statusDefinitions = { PR: "Present", LOP: "Loss of Pay" };

// Optional color mapping populated from leave types from API (code -> color hex/string)
let statusColorMap = {};

// Function to combine statuses intelligently
const combineStatuses = (firstHalf, secondHalf) => {
  if (firstHalf === secondHalf) {
    return firstHalf; // Same status for both halves = full day
  }

  // ✅ For different statuses (half-day), combine with slash
  // Example: "PR" + "LW" = "PR/LW" (not "PRLW")
  // This ensures the combined status is still readable and parseable
  return `${firstHalf}/${secondHalf}`;
};

/** Employee list table (Employee.jsx): row borders, flat row bg; hover = red text via .employee-table CSS */
function employeeListTbodyDivideClasses(darkMode, hasActiveFilters) {
  return `${
    darkMode
      ? hasActiveFilters
        ? "divide-redhalo-navy"
        : "divide-[#4C4C4C]"
      : hasActiveFilters
        ? "divide-gray-300"
        : "divide-gray-200"
  }`;
}

function employeeListRowClasses(
  index,
  darkMode,
  hasActiveFilters,
  hoveredRowIndex = null
) {
  const borderColor = darkMode
    ? hasActiveFilters
      ? "border-redhalo-navy"
      : "border-[#4C4C4C]"
    : hasActiveFilters
      ? "border-gray-300"
      : "border-[#e4e8ee]";
  const rowBase = darkMode ? "bg-[#2C2C2C]" : "bg-[#fafafa]";
  const isCoordinatedHover =
    hoveredRowIndex !== null && hoveredRowIndex === index;
  const activeClass = isCoordinatedHover ? "attendance-row-active" : "";
  return `employee-table-row ${borderColor} border-b ${rowBase} ${activeClass}`;
}

const ATTENDANCE_LEFT_COL_WIDTHS = {
  actions: 48,
  slNo: 48,
  empCode: 80,
  name: 112,
  department: 88,
  designation: 96,
  branch: 80,
  doj: 80,
  dol: 80,
};

/** Freeze panes from Name through DOL (incl. dept / designation / branch); day grid scrolls to the right. */
const ATTENDANCE_FROZEN_COLUMNS = [
  "actions",
  "slNo",
  "empCode",
  "name",
  "department",
  "designation",
  "branch",
  "doj",
  "dol",
];
const ATTENDANCE_LAST_FROZEN_COLUMN = "dol";

/** Daily day columns (1–31) hidden; use employee list + summary panel only. */
const SHOW_ATTENDANCE_DAY_GRID = false;

const ATTENDANCE_LEFT_PANEL_MIN_WIDTH = Object.values(
  ATTENDANCE_LEFT_COL_WIDTHS
).reduce((sum, w) => sum + w, 0);

function buildAttendanceStickyLeftMap(
  widths = ATTENDANCE_LEFT_COL_WIDTHS
) {
  const map = {};
  let left = 0;
  for (const key of ATTENDANCE_FROZEN_COLUMNS) {
    map[key] = left;
    left += widths[key] ?? 0;
  }
  return map;
}

function attendanceFrozenStyle(
  columnKey,
  stickyLeftMap,
  { darkMode, isHeader }
) {
  const left = stickyLeftMap[columnKey];
  if (left === undefined) return {};
  return {
    position: "sticky",
    left: `${left}px`,
    zIndex: isHeader ? 50 : 30,
    backgroundColor: isHeader
      ? darkMode
        ? "#3a3a3a"
        : "#e6eaef"
      : darkMode
        ? "#2C2C2C"
        : "#fafafa",
  };
}

function attendanceFrozenClass(columnKey, stickyLeftMap) {
  if (stickyLeftMap[columnKey] === undefined) return "";
  return "employee-table-sticky-col";
}

// Memoized Attendance Cell Component
const AttendanceCell = React.memo(
  ({
    day,
    employee,
    selectedYear,
    selectedMonth,
    joinDateComponents,
    exitDateComponents,
    weeklyOffPolicies,
    employeeWeeklyOffMappings,
    holidaysData,
    holidayPolicies,
    employeeHolidayMappings,
    currentStatus,
    isLOPReversed = false,
    isEditing,
    isLocked,
    darkMode,
    isSelected,
    onCellClick,
    onCellDoubleClick,
    rowIndex = 0,
    isRowHovered = false,
  }) => {
    // Build a real JS Date for this day in the selected month/year
    const actualDate = new Date(selectedYear, selectedMonth, day);
    const currentDate = actualDate;
    const dayDate = currentDate.toISOString().slice(0, 10);

    // Compare date components directly to avoid timezone issues
    let isDisabled = false;
    if (joinDateComponents) {
      const currentYear = selectedYear;
      const currentMonth = selectedMonth;
      const currentDay = day;
      const joinYear = joinDateComponents.year;
      const joinMonth = joinDateComponents.month;
      const joinDay = joinDateComponents.day;

      const currentDateNum =
        currentYear * 10000 + (currentMonth + 1) * 100 + currentDay;
      const joinDateNum = joinYear * 10000 + (joinMonth + 1) * 100 + joinDay;

      const isBeforeJoinDate = currentDateNum < joinDateNum;

      const exitYear = exitDateComponents.year;
      const exitMonth = exitDateComponents.month;
      const exitDay = exitDateComponents.day;
      const exitDateNum = exitYear * 10000 + (exitMonth + 1) * 100 + exitDay;
      const isAfterExitDate = currentDateNum > exitDateNum;

      isDisabled = isBeforeJoinDate || isAfterExitDate;
    }

    // Also check if employee is relieved after this date
    const relievedDate =
      employee.relievedDate ||
      employee.exitDate ||
      employee.resignationDate ||
      employee.relievingDate;
    let isRelievedAfterDate = false;
    if (relievedDate) {
      const rd = new Date(relievedDate);
      if (currentDate > rd) {
        isRelievedAfterDate = true;
        isDisabled = true; // Disable editing if relieved
      }
    }

    // Compute weekly-off for this employee & date
    const isWOForEmp = isWeeklyOffForEmployee(
      currentDate,
      employee,
      weeklyOffPolicies,
      employeeWeeklyOffMappings
    );

    // Compute default status that respects WO
    const defaultStatus = isWOForEmp
      ? "WO"
      : getDefaultStatus(
          currentDate,
          employee,
          weeklyOffPolicies,
          employeeWeeklyOffMappings,
          holidaysData,
          holidayPolicies,
          employeeHolidayMappings
        );

    const policyEnforcedStatuses = ["PH", "WO", "WOPR", "PRWO"];
    // Prefer a manual entry when present; only fall back to policy default when
    // there's no manual value. Previously policy-enforced statuses always
    // overrode manual marks which prevented UI from reflecting user changes.
    let displayStatus =
      typeof currentStatus === "string"
        ? currentStatus
        : policyEnforcedStatuses.includes(defaultStatus)
        ? defaultStatus
        : defaultStatus;

    // If before joining date, override to show "-"
    const isBeforeJoinDate =
      joinDateComponents &&
      selectedYear * 10000 + (selectedMonth + 1) * 100 + day <
        joinDateComponents.year * 10000 +
          (joinDateComponents.month + 1) * 100 +
          joinDateComponents.day;

    if (isBeforeJoinDate) {
      displayStatus = "-";
    }

    // If relieved after this date, override to show "-"
    if (isRelievedAfterDate) {
      displayStatus = "-";
    }

    // Map canonical backend status -> company custom code for display
    const { mapping: attendanceMapping, exportMapping: attendanceExportMapping } = useAttendanceCodeConfig();
    let mappedDisplayStatus = displayStatus;
    if (
      attendanceMapping &&
      typeof displayStatus === "string" &&
      displayStatus.trim() !== ""
    ) {
      const lookup = String(displayStatus).toUpperCase().trim();
      if (attendanceMapping[lookup]) {
        mappedDisplayStatus = attendanceMapping[lookup];
      } else {
        // Try mapping split parts for combined statuses like PRWO, WOPR, PRLOP etc.
        try {
          const parts = splitIntoTwoCodes(displayStatus);
          if (parts && parts.length === 2) {
            const p0 =
              attendanceMapping[String(parts[0] || "").toUpperCase()] ||
              parts[0];
            const p1 =
              attendanceMapping[String(parts[1] || "").toUpperCase()] ||
              parts[1];
            mappedDisplayStatus = `${p0 || ""}${p1 || ""}`;
          }
        } catch (e) {
          // ignore split mapping errors and fall back to original displayStatus
        }
      }
    }

    // Weekly Off locking logic
    const isWeeklyOffVariant =
      displayStatus === "WO" ||
      displayStatus === "WOPR" ||
      displayStatus === "PRWO";

    const allowOverrideWeeklyOff = getAllowOverrideForEmployee(
      currentDate,
      employee,
      weeklyOffPolicies,
      employeeWeeklyOffMappings,
      employee.id
    );

    // When policy has "Override Weekly Off" unchecked, WO is always locked for
    // all employees assigned to that policy (including assign-to-all). Do not
    // unlock based on hadManualEntry so that saved/API-loaded WO stays locked.
    const isWeeklyOffLocked =
      isWeeklyOffVariant && !allowOverrideWeeklyOff;

    // Try to intelligently split combined statuses into two codes for display
    const twoParts =
      typeof mappedDisplayStatus === "string"
        ? splitIntoTwoCodes(mappedDisplayStatus)
        : null;
    const getStatusColor = (status) => {
      if (!status || typeof status !== "string")
        return { className: "text-gray-700 border-gray-300", style: {} };
      const norm = normalizeStatus(status).toUpperCase();
      
      // If backend provided a color for this status, use inline style
      if (statusColorMap && statusColorMap[norm]) {
        const color = statusColorMap[norm];
        // If color is a hex code or valid color string, apply it
        if (color && (color.startsWith("#") || color.startsWith("rgb") || /^[a-zA-Z]+$/.test(color))) {
          return {
            className: "border",
            style: {
              color: color,
              borderColor: color,
            },
          };
        }
        // If it's a Tailwind class, return as className
        return { className: color, style: {} };
      }

      // Heuristic color mapping for statuses without custom colors
      if (
        norm.includes("LOP") ||
        norm.includes("LWP") ||
        norm.includes("LO") ||
        norm.includes("LW")
      ) {
        return { className: "text-red-700 border-gray-300", style: {} };
      }

      const colors = {
        PR: "text-emerald-700",
        PH: "text-amber-700",
        WO: "text-redhalo-navy",
      };

      return { className: colors[norm] || "text-gray-700 border-gray-300", style: {} };
    };

    return (
      <td
        className="px-1 py-1 text-center"
        style={{
          height: "32px",
          minWidth: "48px",
          width: "48px",
          maxWidth: "48px",
          boxSizing: "border-box",
        }}
        onClick={() => {
          if (!isDisabled && !isWeeklyOffLocked && !isLocked && isEditing) {
            onCellClick(
              employee.id,
              day,
              displayStatus,
              employee.name,
              null,
              dayDate
            );
          }
        }}
      >
        {isDisabled ? (
          <span className="text-gray-400">-</span>
        ) : (
          <div
            className={`border rounded text-xs px-1 py-1 ${
              isWeeklyOffLocked || isLocked
                ? "cursor-not-allowed opacity-60"
                : "cursor-pointer"
            } ${(() => {
              const colorInfo = getStatusColor(displayStatus);
              return colorInfo.className;
            })()            } ${
              darkMode
                ? "bg-[#2C2C2C] border-[#3C3C3C]"
                : "bg-[#fafafa] border-gray-300"
            } ${
              isLOPReversed
                ? "border-dashed border-2 border-amber-500 bg-amber-50/50"
                : ""
            } ${
              darkMode && isLOPReversed ? "border-amber-400 bg-amber-900/20" : ""
            } ${
              isEditing && !isWeeklyOffLocked && !isLocked
                ? "hover:border-redhalo-red hover:bg-redhalo-mint/50"
                : ""
            } transition-all duration-200 w-12 h-8 flex items-center justify-center ${
              isSelected ? "ring-2 ring-redhalo-red/50" : ""
            }`}
            onClick={(e) => {
              if (isWeeklyOffLocked || isLocked) return;
              onCellClick(
                employee.id,
                day,
                displayStatus,
                employee.name,
                e,
                dayDate
              );
            }}
            onDoubleClick={() => {
              if (isWeeklyOffLocked || isLocked) return;
              onCellDoubleClick(employee.id, day, displayStatus, employee.name);
            }}
            style={(() => {
              const colorInfo = getStatusColor(displayStatus);
              return colorInfo.style || {};
            })()}
          >
            {twoParts ? (
              <div className="flex flex-col items-center justify-center leading-tight text-[10px] font-semibold text-center">
                <span>{twoParts[0]}</span>
                <span>{twoParts[1]}</span>
              </div>
            ) : (
              <div className="text-xs font-semibold text-center">
                {mappedDisplayStatus}
              </div>
            )}
          </div>
        )}
      </td>
    );
  },
  (prevProps, nextProps) => {
    // Optimized comparison - only check essential props that affect rendering
    if (prevProps.currentStatus !== nextProps.currentStatus) return false;
    if (prevProps.isLOPReversed !== nextProps.isLOPReversed) return false;
    if (prevProps.isSelected !== nextProps.isSelected) return false;
    if (prevProps.rowIndex !== nextProps.rowIndex) return false;
    if (prevProps.isRowHovered !== nextProps.isRowHovered) return false;
    if (prevProps.isEditing !== nextProps.isEditing) return false;
    if (prevProps.isLocked !== nextProps.isLocked) return false;
    if (prevProps.darkMode !== nextProps.darkMode) return false;
    if (prevProps.employee.id !== nextProps.employee.id) return false;
    if (prevProps.day !== nextProps.day) return false;
    if (prevProps.selectedYear !== nextProps.selectedYear) return false;
    if (prevProps.selectedMonth !== nextProps.selectedMonth) return false;
    // Reference equality is sufficient for policies (they're regenerated when changed)
    if (prevProps.weeklyOffPolicies !== nextProps.weeklyOffPolicies)
      return false;
    if (
      prevProps.employeeWeeklyOffMappings !==
      nextProps.employeeWeeklyOffMappings
    )
      return false;
    if (prevProps.holidaysData !== nextProps.holidaysData) return false;
    if (prevProps.employeeHolidayMappings !== nextProps.employeeHolidayMappings)
      return false;
    return true;
  }
);

// Split a combined status string into two probable status codes.
// Heuristic:
// 1. Prefer splits where one or both parts match known statuses (attendanceStatuses or statusDefinitions).
// 2. Prefer parts of length 2 or 3 (common code lengths).
// 3. Fallback to prefer right-side length 3, then 2, then middle split.
function splitIntoTwoCodes(raw) {
  if (!raw || typeof raw !== "string") return null;
  const s = raw.trim();

  // NEW: Check for slash-separated format first (PR/LW)
  if (s.includes("/")) {
    const parts = s.split("/");
    if (parts.length === 2 && parts[0] && parts[1]) {
      return [parts[0].trim(), parts[1].trim()];
    }
  }

  const n = s.length;
  if (n < 4) return null; // too short to be two codes

  const isKnown = (code) => {
    if (!code) return false;
    // 🔧 FIX: Check both original and custom codes
    const upperCode = String(code).toUpperCase();

    // Check in attendanceStatuses (includes both original and custom codes)
    if (attendanceStatuses && attendanceStatuses.includes(upperCode))
      return true;

    // Check in statusDefinitions (includes both original and custom codes)
    if (
      statusDefinitions &&
      Object.prototype.hasOwnProperty.call(statusDefinitions, upperCode)
    )
      return true;

    // Also check common attendance codes to handle cases where definitions haven't loaded yet
    const knownCodes = [
      "PR",
      "LOP",
      // "LO",
      // "LW",
      "LWP",
      "WO",
      "PH",
      // "PHS",
      // "HD",
      // "HDS",
      // "CO",
      // "CL",
      // "EL",
      // "TL",
    ];
    if (knownCodes.includes(upperCode)) return true;

    return false;
  };

  let best = null;
  for (let i = 1; i < n; i++) {
    const left = s.slice(0, i);
    const right = s.slice(i);
    if (left.length < 1 || right.length < 1) continue;
    // score
    let score = 0;
    if (isKnown(left)) score += 4;
    if (isKnown(right)) score += 4;
    if (left.length === 3) score += 1;
    if (right.length === 3) score += 1;
    if (left.length === 2) score += 0.5;
    if (right.length === 2) score += 0.5;
    // penalize very short parts
    if (left.length === 1) score -= 2;
    if (right.length === 1) score -= 2;

    if (!best || score > best.score) {
      best = { left, right, score, i };
    }
  }

  if (best && best.score > 0) return [best.left, best.right];

  // fallback: prefer splits that give right-side length 3, then 2
  for (const preferredRight of [3, 2]) {
    for (let i = 1; i < n; i++) {
      const left = s.slice(0, i);
      const right = s.slice(i);
      if (right.length === preferredRight && left.length >= 1)
        return [left, right];
    }
  }

  // last resort: split near middle
  const mid = Math.floor(n / 2);
  return [s.slice(0, mid), s.slice(mid)];
}

// Memoized Attendance Row Component
// Memoized date parsing helper
const parseDateComponents = (dateStr) => {
  if (!dateStr) return null;
  try {
    // Try ISO format first (YYYY-MM-DD or YYYY-MM-DDTHH:mm:ss)
    const isoMatch = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (isoMatch) {
      return {
        year: parseInt(isoMatch[1], 10),
        month: parseInt(isoMatch[2], 10) - 1,
        day: parseInt(isoMatch[3], 10),
      };
    }

    // Try DD-MMM-YY format (08-Dec-25)
    const ddMmmYyMatch = dateStr.match(/^(\d{1,2})-([A-Za-z]{3})-(\d{2,4})$/);
    if (ddMmmYyMatch) {
      const monthNames = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      const day = parseInt(ddMmmYyMatch[1], 10);
      const month = monthNames.findIndex(
        (m) => m.toLowerCase() === ddMmmYyMatch[2].toLowerCase()
      );
      let year = parseInt(ddMmmYyMatch[3], 10);

      // Handle 2-digit year (25 -> 2025)
      if (year < 100) {
        year = year < 50 ? 2000 + year : 1900 + year;
      }

      if (month !== -1) {
        return {
          year: year,
          month: month,
          day: day,
        };
      }
    }

    // Try DD/MM/YYYY format (08/12/2025)
    const ddMmYyyyMatch = dateStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (ddMmYyyyMatch) {
      return {
        year: parseInt(ddMmYyyyMatch[3], 10),
        month: parseInt(ddMmYyyyMatch[2], 10) - 1,
        day: parseInt(ddMmYyyyMatch[1], 10),
      };
    }
  } catch (error) {
    console.error("Error parsing date:", dateStr, error);
  }
  return null;
};

// Timezone-safe day extraction for date-like values (Date/ISO/string)
const getDayOfMonthSafe = (dateValue) => {
  if (!dateValue) return null;
  const parts = parseDateComponents(String(dateValue));
  if (parts && Number.isFinite(parts.day)) return parts.day;
  const d = new Date(dateValue);
  if (isNaN(d.getTime())) return null;
  return d.getDate();
};

const AttendanceRow = React.memo(
  ({
    employee,
    days,
    selectedYear,
    selectedMonth,
    weeklyOffPolicies,
    employeeWeeklyOffMappings,
    holidaysData,
    holidayPolicies,
    employeeHolidayMappings,
    employeeAttendanceData,
    employeeLOPReversalDays = {},
    isEditing,
    isLocked,
    darkMode,
    selectedCell,
    onCellClick,
    onCellDoubleClick,
    style,
    rowIndex = 0,
    hasActiveFilters = false,
    hoveredRowIndex = null,
    onRowMouseEnter,
  }) => {
    // Memoize date parsing to avoid recalculation on every render
    const joinDateComponents = useMemo(() => {
      const parsed = parseDateComponents(employee.joinDate);
      return parsed;
    }, [employee.joinDate, employee.name]);

    const exitDateComponents = useMemo(() => {
      const exitRaw =
        employee.exitDate ||
        employee.relievedDate ||
        employee.resignationDate ||
        employee.relievingDate;
      return (
        parseDateComponents(exitRaw) || {
          year: 9999,
          month: 11,
          day: 31,
        }
      );
    }, [
      employee.exitDate,
      employee.relievedDate,
      employee.resignationDate,
      employee.relievingDate,
    ]);

    const isRowHovered =
      hoveredRowIndex !== null && hoveredRowIndex === rowIndex;

    return (
      <tr
        className={employeeListRowClasses(
          rowIndex,
          darkMode,
          hasActiveFilters,
          hoveredRowIndex
        )}
        style={{ height: "32px", ...(style || {}) }}
        onMouseEnter={onRowMouseEnter}
      >
        {days.map((day) => (
          <AttendanceCell
            key={day}
            day={day}
            employee={employee}
            selectedYear={selectedYear}
            selectedMonth={selectedMonth}
            joinDateComponents={joinDateComponents}
            exitDateComponents={exitDateComponents}
            weeklyOffPolicies={weeklyOffPolicies}
            employeeWeeklyOffMappings={employeeWeeklyOffMappings}
            holidaysData={holidaysData}
            holidayPolicies={holidayPolicies}
            employeeHolidayMappings={employeeHolidayMappings}
            currentStatus={employeeAttendanceData?.[day]}
            isLOPReversed={employeeLOPReversalDays[day] || employeeLOPReversalDays[String(day)]}
            isEditing={isEditing}
            isLocked={isLocked}
            darkMode={darkMode}
            isSelected={
              selectedCell.employeeId === employee.id &&
              selectedCell.day === day
            }
            onCellClick={onCellClick}
            onCellDoubleClick={onCellDoubleClick}
            rowIndex={rowIndex}
            isRowHovered={isRowHovered}
          />
        ))}
      </tr>
    );
  },
  (prevProps, nextProps) => {
    // Optimize re-renders by checking only essential props
    if (prevProps.hoveredRowIndex !== nextProps.hoveredRowIndex) return false;
    if (prevProps.rowIndex !== nextProps.rowIndex) return false;
    if (prevProps.hasActiveFilters !== nextProps.hasActiveFilters) return false;
    if (prevProps.employee.id !== nextProps.employee.id) return false;
    if (prevProps.isEditing !== nextProps.isEditing) return false;
    if (prevProps.isLocked !== nextProps.isLocked) return false;
    if (prevProps.darkMode !== nextProps.darkMode) return false;
    if (prevProps.selectedYear !== nextProps.selectedYear) return false;
    if (prevProps.selectedMonth !== nextProps.selectedMonth) return false;
    // Check if attendance data changed for this employee
    if (prevProps.employeeAttendanceData !== nextProps.employeeAttendanceData)
      return false;
    if (prevProps.employeeLOPReversalDays !== nextProps.employeeLOPReversalDays)
      return false;
    // Check selected cell
    if (prevProps.selectedCell.employeeId !== nextProps.selectedCell.employeeId)
      return false;
    if (prevProps.selectedCell.day !== nextProps.selectedCell.day) return false;
    // Reference equality for policies
    if (prevProps.weeklyOffPolicies !== nextProps.weeklyOffPolicies)
      return false;
    if (
      prevProps.employeeWeeklyOffMappings !==
      nextProps.employeeWeeklyOffMappings
    )
      return false;
    if (prevProps.holidaysData !== nextProps.holidaysData) return false;
    if (prevProps.employeeHolidayMappings !== nextProps.employeeHolidayMappings)
      return false;
    return true;
  }
);

// Attendance Status Modal Component
const AttendanceStatusModal = ({
  darkMode,
  isOpen,
  onClose,
  onSave,
  employeeId,
  day,
  currentStatus,
  employeeName,
  selectedYear,
  selectedMonth,
  weeklyOffPolicies = [],
  employeeWeeklyOffMappings = {},
  holidaysData = [],
  holidayPolicies = [],
  employeeHolidayMappings = {},
}) => {
  const [firstHalfStatus, setFirstHalfStatus] = useState("PR");
  const [secondHalfStatus, setSecondHalfStatus] = useState("PR");
  const [confirmOverride, setConfirmOverride] = useState(false);

  useEffect(() => {
    if (currentStatus) {
      // Parse current status - check if it's a combination or single status
      if (typeof currentStatus === "string" && currentStatus.includes("/")) {
        // ✅ NEW FORMAT: Slash-separated half-day (e.g., "PR/LW")
        const parts = currentStatus.split("/");
        if (parts.length === 2) {
          setFirstHalfStatus(parts[0].trim());
          setSecondHalfStatus(parts[1].trim());
        } else {
          setFirstHalfStatus(currentStatus);
          setSecondHalfStatus(currentStatus);
        }
      } else if (
        typeof currentStatus === "string" &&
        currentStatus.length > 3 &&
        !attendanceStatuses.includes(currentStatus)
      ) {
        // OLD FORMAT: Combined without slash (legacy support for "PRLWP", "PRLOP", etc.)
        // Try to match against known status codes
        let first = null;
        let second = null;

        // Check if it starts with a 3-letter code (LOP, LWP)
        if (currentStatus.length >= 6) {
          const potential3Letter = currentStatus.substring(0, 3);
          if (attendanceStatuses.includes(potential3Letter)) {
            first = potential3Letter;
            second = currentStatus.substring(3);
          }
        }

        // If not found, try 2-letter codes
        if (!first) {
          first = currentStatus.substring(0, 2);
          second = currentStatus.substring(2);
        }

        setFirstHalfStatus(first);
        setSecondHalfStatus(second);
      } else {
        setFirstHalfStatus(currentStatus);
        setSecondHalfStatus(currentStatus);
      }
    } else {
      // Reset to default when no current status
      setFirstHalfStatus("PR");
      setSecondHalfStatus("PR");
    }
  }, [currentStatus, isOpen]); // Added isOpen dependency to reset when modal opens

  const handleSave = () => {
    const combinedStatus = combineStatuses(firstHalfStatus, secondHalfStatus);
    onSave(employeeId, day, combinedStatus, confirmOverride);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div
        className={`${
          darkMode ? "bg-[#2C2C2C] text-[#E0E0E0]" : "bg-white text-gray-900"
        } p-6 rounded-lg shadow-xl w-96`}
      >
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Attendance Status - Day {day}</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X size={24} />
          </button>
        </div>

        <div className="mb-4">
          <p className="text-sm text-gray-600 mb-2">Employee: {employeeName}</p>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">
              First Half Status
            </label>
            <select
              value={firstHalfStatus}
              onChange={(e) => setFirstHalfStatus(e.target.value)}
              className={`w-full p-2 border rounded ${
                darkMode
                  ? "bg-[#1C1C1C] border-[#3C3C3C]"
                  : "bg-white border-gray-300"
              }`}
            >
              {attendanceStatuses.map((status) => (
                <option key={status} value={status}>
                  {status} - {statusDefinitions[status]}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">
              Second Half Status
            </label>
            <select
              value={secondHalfStatus}
              onChange={(e) => setSecondHalfStatus(e.target.value)}
              className={`w-full p-2 border rounded ${
                darkMode
                  ? "bg-[#1C1C1C] border-[#3C3C3C]"
                  : "bg-white border-gray-300"
              }`}
            >
              {attendanceStatuses.map((status) => (
                <option key={status} value={status}>
                  {status} - {statusDefinitions[status]}
                </option>
              ))}
            </select>
          </div>

          <div className="bg-gray-100 p-3 rounded">
            <p className="text-sm font-medium mb-1">Preview:</p>
            <p className="text-sm">
              First Half:{" "}
              <span className="font-semibold">{firstHalfStatus}</span> | Second
              Half: <span className="font-semibold">{secondHalfStatus}</span>
            </p>
          </div>
        </div>

        {/* Override checkbox shown when this day is a policy-default WO and locked */}
        {(() => {
          try {
            if (day && employeeId != null) {
              const dateObj = new Date(selectedYear, selectedMonth, day);
              const def = getDefaultStatusWithLock(
                dateObj,
                employeeId,
                weeklyOffPolicies,
                employeeWeeklyOffMappings,
                holidaysData,
                holidayPolicies,
                employeeHolidayMappings
              );
              const isLocked = def && def.locked;
              const defaultStatus = def && def.status;
              const combined = combineStatuses(
                firstHalfStatus,
                secondHalfStatus
              );
              const isAttemptingOverride = combined !== defaultStatus;
              if (isLocked && isAttemptingOverride) {
                return (
                  <div className="mt-3 rounded border border-amber-200 bg-amber-50 p-3">
                    <label className="flex items-center space-x-2 text-sm">
                      <input
                        type="checkbox"
                        checked={confirmOverride}
                        onChange={(e) => setConfirmOverride(e.target.checked)}
                        className="form-checkbox"
                      />
                      <span>
                        This is a policy weekly-off and is locked by default.
                        Check to confirm you want to override it.
                      </span>
                    </label>
                  </div>
                );
              }
            }
          } catch (e) {
            console.debug("StatusModal override compute failed", e);
          }
          return null;
        })()}

        <div className="mt-6 flex justify-end space-x-2">
          <button
            onClick={onClose}
            className={`px-4 py-2 rounded transition-colors font-medium ${
              darkMode
                ? "bg-gray-700 text-[#E0E0E0] hover:bg-gray-600"
                : "bg-gray-200 text-gray-900 hover:bg-gray-300"
            }`}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className={`px-4 py-2 rounded transition-colors font-medium flex items-center ${
              darkMode
                ? "rh-btn-primary hover:opacity-95"
                : "rh-btn-primary hover:opacity-95"
            }`}
          >
            <Check size={16} className="mr-1" />
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

const AttendanceTable = React.memo(
  ({
    employees,
    darkMode,
    selectedMonth,
    selectedYear,
    attendanceData,
    setAttendanceData,
    isEditing,
    isLocked = false,
    totals,
    onActionClick,
    selectedDate,
    setSelectedDate,
    weeklyOffPolicies = [],
    employeeWeeklyOffMappings = {},
    holidaysData = [],
    holidayPolicies = [],
    employeeHolidayMappings = {},
    leaveTypes = [],
    onApplyLeave = null,
    onLOPReversal = null,
    onLeaveAdjustment = null,
    onLeaveBalance = null,
    onViewAttendance = null,
    overrideMap = {},
    setOverrideMap = () => {},
    page = 1,
    itemsPerPage = 50,
    serverSidePagination = false,
    changedRecordsRef = null, // ✅ Added to track changes
    lopReversalDaysMap = {},
    attendanceLOPReversalMap = {}, // { employeeId: { day: true } } to show LOP-reversed cells differently
    // callback for child table to request the next chunk
    onLoadMore = null,
    // flag to indicate parent is currently loading more
    infiniteLoading = false,
    // parent indicates initial/refresh loading state (but employees may exist)
    employeesLoading = false,
    // flag from parent indicating server has more pages
    hasMoreEmployees = false,
    hasActiveFilters = false,
    attendanceStatusesState = ["PR", "LOP", "WO", "PH"],
    statusDefinitionsState = { PR: "Present" },
    statusColorMapState = {},
    departmentsList = [],
    designationsList = [],
    branchesList = [],
  }) => {
    const { mapping: attendanceMapping, reverseMapping } =
      useAttendanceCodeConfig();
    const attendanceStatuses = attendanceStatusesState;
    const statusDefinitions = statusDefinitionsState;
    const statusColorMap = statusColorMapState;
    const wrapperRef = React.useRef(null);
    const tableScrollTimeoutRef = React.useRef(null);
    const [tableScrolling, setTableScrolling] = useState(false);
    const [hoveredRowIndex, setHoveredRowIndex] = useState(null);
    const [columnWidths, setColumnWidths] = useState(() => {
      try {
        const saved = localStorage.getItem("attendanceTableColumnWidths");
        if (saved) {
          return { ...ATTENDANCE_LEFT_COL_WIDTHS, ...JSON.parse(saved) };
        }
      } catch (_) {
        /* ignore */
      }
      return { ...ATTENDANCE_LEFT_COL_WIDTHS };
    });
    const [sortField, setSortField] = useState(null);
    const [sortDirection, setSortDirection] = useState("asc");

    const saveColumnWidths = useCallback((next) => {
      setColumnWidths(next);
      try {
        localStorage.setItem(
          "attendanceTableColumnWidths",
          JSON.stringify(next)
        );
      } catch (_) {
        /* ignore */
      }
    }, []);

    const handleColumnResize = useCallback(
      (column, newWidth) => {
        saveColumnWidths({
          ...columnWidths,
          [column]: Math.max(48, newWidth),
        });
      },
      [columnWidths, saveColumnWidths]
    );

    const handleSort = useCallback((field) => {
      if (sortField === field) {
        setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
      } else {
        setSortField(field);
        setSortDirection("asc");
      }
    }, [sortField]);

    const attendanceThSortClass = useCallback(
      (field) => {
        const base = "employee-table-th-sortable";
        return sortField === field
          ? `${base} employee-table-th-sorted`
          : base;
      },
      [sortField]
    );

    const attendanceStickyLeft = useMemo(
      () => buildAttendanceStickyLeftMap(columnWidths),
      [columnWidths]
    );

    const attendanceLeftPanelMinWidth = useMemo(
      () =>
        ATTENDANCE_FROZEN_COLUMNS.reduce(
          (sum, key) => sum + (columnWidths[key] ?? 0),
          0
        ),
      [columnWidths]
    );

    const handleTableAreaScroll = useCallback(() => {
      setTableScrolling(true);
      if (tableScrollTimeoutRef.current) {
        clearTimeout(tableScrollTimeoutRef.current);
      }
      tableScrollTimeoutRef.current = setTimeout(() => {
        setTableScrolling(false);
      }, 900);
    }, []);

    useEffect(
      () => () => {
        if (tableScrollTimeoutRef.current) {
          clearTimeout(tableScrollTimeoutRef.current);
        }
      },
      []
    );
    const daysInMonth = Math.min(
      31,
      new Date(selectedYear, selectedMonth + 1, 0).getDate()
    );
    // Force remove any out-of-range day keys from data BEFORE rendering
    const validDays = Array.from({ length: daysInMonth }, (_, i) => i + 1);
    const days = validDays;

    // Debug: log days and attendanceData keys to investigate extra columns
    useEffect(() => {
      try {
        console.debug(
          "AttendanceTable DEBUG: daysInMonth",
          daysInMonth,
          "days.length",
          days.length
        );
        if (employees && employees.length > 0) {
          const sampleEmp = employees[0];
          const keys = Object.keys(
            (attendanceData && attendanceData[sampleEmp.id]) || {}
          )
            .map((k) => parseInt(k, 10))
            .sort((a, b) => a - b);
          console.debug(
            "AttendanceTable DEBUG: sampleEmp.id",
            sampleEmp.id,
            "attendance keys:",
            keys
          );
        }
      } catch (e) {
        console.warn("AttendanceTable DEBUG error", e);
      }
    }, [daysInMonth, days.length, attendanceData, employees]);

    // Clean attendanceData: keep only numeric day keys within 1..daysInMonth
    const cleanedAttendanceData = useMemo(() => {
      const out = {};
      if (!attendanceData || typeof attendanceData !== "object") return out;
      Object.entries(attendanceData).forEach(([empId, daysObj]) => {
        const filtered = {};
        Object.entries(daysObj || {}).forEach(([k, v]) => {
          const dayNum = parseInt(k, 10);
          if (!Number.isNaN(dayNum) && dayNum >= 1 && dayNum <= daysInMonth) {
            filtered[dayNum] = v; // Keep as number for consistent lookup
          }
        });
        out[empId] = filtered;
      });
      return out;
    }, [attendanceData, daysInMonth]);

    // Map to track explicit overrides user confirmed in modal: { "<empId>_<day>": true }

    // Safety check for employees
    const safeEmployees =
      employees && Array.isArray(employees) ? employees : [];

    const sortedEmployees = useMemo(() => {
      if (!sortField) return safeEmployees;
      const dir = sortDirection === "asc" ? 1 : -1;
      const list = [...safeEmployees];
      const getDept = (emp) =>
        getNameByIdFromList(
          emp.departmentId,
          departmentsList,
          emp,
          "department"
        ) || "";
      const getDesig = (emp) =>
        getNameByIdFromList(
          emp.designationId,
          designationsList,
          emp,
          "designation"
        ) || "";
      const getBranch = (emp) =>
        getNameByIdFromList(emp.branchId, branchesList, emp, "branch") || "";
      list.sort((a, b) => {
        let av = "";
        let bv = "";
        switch (sortField) {
          case "empCode":
            av = a.code || a.empCode || "";
            bv = b.code || b.empCode || "";
            break;
          case "name":
            av = a.name || "";
            bv = b.name || "";
            break;
          case "department":
            av = getDept(a);
            bv = getDept(b);
            break;
          case "designation":
            av = getDesig(a);
            bv = getDesig(b);
            break;
          case "branch":
            av = getBranch(a);
            bv = getBranch(b);
            break;
          case "doj":
            av = a.joinDate || "";
            bv = b.joinDate || "";
            break;
          case "dol":
            av =
              a.relievedDate ||
              a.exitDate ||
              a.resignationDate ||
              a.relievingDate ||
              "";
            bv =
              b.relievedDate ||
              b.exitDate ||
              b.resignationDate ||
              b.relievingDate ||
              "";
            break;
          default:
            return 0;
        }
        return String(av).localeCompare(String(bv), undefined, {
          numeric: true,
          sensitivity: "base",
        }) * dir;
      });
      return list;
    }, [
      safeEmployees,
      sortField,
      sortDirection,
      departmentsList,
      designationsList,
      branchesList,
    ]);

    // Infinite-scroll sentinel handling inside table (per-chunk prefetch)
    const CHUNK_SIZE = 50;
    const PREFETCH = 20; // trigger when user reaches (chunkEnd - PREFETCH)
    const chunksLoaded = Math.floor(safeEmployees.length / CHUNK_SIZE);
    let sentinelAfterIndex = null;
    if (chunksLoaded >= 1) {
      sentinelAfterIndex = chunksLoaded * CHUNK_SIZE - PREFETCH - 1; // 0-based index
      if (sentinelAfterIndex < 0) sentinelAfterIndex = 0;
    }

    const sentinelRef = React.useRef(null);
    const sentinelVisible = useIntersectionObserver(
      sentinelRef,
      // observe within parent vertical scroll container if available
      {
        root: wrapperRef.current ? wrapperRef.current.parentElement : null,
        rootMargin: "200px",
        threshold: 0,
      },
      Boolean(
        onLoadMore &&
          sentinelAfterIndex !== null &&
          !infiniteLoading &&
          hasMoreEmployees
      )
    );

    // When sentinel becomes visible, ask parent to load next chunk
    React.useEffect(() => {
      if (!sentinelVisible) return;
      if (!onLoadMore) return;
      if (infiniteLoading) return;
      if (!hasMoreEmployees) return;
      try {
        onLoadMore();
      } catch (e) {
        console.warn("onLoadMore failed:", e);
      }
    }, [sentinelVisible, onLoadMore, infiniteLoading, hasMoreEmployees]);

    // (pagination is handled at parent level - Attendance page)

    // Debug: log rendering counts and any out-of-range day keys
    useEffect(() => {
      try {
        const rowsShown = (safeEmployees && safeEmployees.length) || 0;
        const totalEmployeesInData = attendanceData
          ? Object.keys(attendanceData).length
          : 0;

        const extraKeysPerEmp = {};
        Object.entries(attendanceData || {}).forEach(([empId, daysObj]) => {
          const rawKeys = Object.keys(daysObj || {})
            .map((k) => parseInt(k, 10))
            .filter((n) => !Number.isNaN(n));
          const outOfRange = rawKeys.filter((k) => k < 1 || k > daysInMonth);
          if (outOfRange.length > 0) extraKeysPerEmp[empId] = outOfRange;
        });

        console.debug(
          "AttendanceTable DEBUG: rowsShown=",
          rowsShown,
          "daysInMonth=",
          daysInMonth,
          "days.length=",
          days.length,
          "attendanceDataEmployees=",
          totalEmployeesInData,
          "extraKeysPerEmp=",
          extraKeysPerEmp
        );

        if (rowsShown > 0) {
          const sample = safeEmployees.slice(0, 6).map((emp) => {
            const rawKeys = Object.keys(
              (attendanceData && attendanceData[emp.id]) || {}
            )
              .map((k) => parseInt(k, 10))
              .filter((n) => !Number.isNaN(n))
              .sort((a, b) => a - b);
            const cleanedKeys = Object.keys(cleanedAttendanceData[emp.id] || {})
              .map((k) => parseInt(k, 10))
              .sort((a, b) => a - b);
            return {
              id: emp.id,
              rawCount: rawKeys.length,
              cleanedCount: cleanedKeys.length,
              rawKeysTail: rawKeys.slice(-6),
            };
          });
          console.debug("AttendanceTable DEBUG: sampleEmployees=", sample);
          // DOM-level diagnostics: compare header th count and first body row td count
          try {
            const headerEl = daysScrollRef.current;
            const bodyEl = daysScrollRef.current;
            const headerThCount = headerEl
              ? headerEl.querySelectorAll("th").length
              : null;
            const firstRowTdCount = bodyEl
              ? bodyEl.querySelector("tbody tr")
                ? bodyEl.querySelector("tbody tr").querySelectorAll("td").length
                : 0
              : null;

            console.debug(
              "AttendanceTable DEBUG: headerThCount=",
              headerThCount,
              "firstRowTdCount=",
              firstRowTdCount
            );

            if (
              headerThCount !== null &&
              firstRowTdCount !== null &&
              headerThCount !== firstRowTdCount
            ) {
              console.warn(
                "AttendanceTable MISMATCH: header columns (th) != body columns (td)",
                { headerThCount, firstRowTdCount }
              );
            }
          } catch (domErr) {
            console.warn("AttendanceTable DOM diagnostic error", domErr);
          }
        }
      } catch (e) {
        console.warn("AttendanceTable DEBUG error", e);
      }
    }, [
      safeEmployees,
      daysInMonth,
      days.length,
      attendanceData,
      cleanedAttendanceData,
    ]);

    // Modal state
    const [showStatusModal, setShowStatusModal] = useState(false);
    const [modalData, setModalData] = useState({
      employeeId: null,
      day: null,
      currentStatus: "",
      employeeName: "",
    });

    // Dropdown state for single-click status selection
    const [showDropdown, setShowDropdown] = useState(false);

    // 🧠 Persist the selected date across refresh
    useEffect(() => {
      if (selectedDate) {
        localStorage.setItem("selectedDate", selectedDate);
      }
    }, [selectedDate]);

    const [dropdownData, setDropdownData] = useState({
      employeeId: null,
      day: null,
      currentStatus: "",
      employeeName: "",
      position: { x: 0, y: 0 },
    });

    // Track selected cell (employee + day) within this table component so only one box is highlighted
    const [selectedCell, setSelectedCell] = useState({
      employeeId: null,
      day: null,
    });

    // Track which employee's action dropdown is open and its position
    const [openActionDropdown, setOpenActionDropdown] = useState(null);
    const [dropdownPosition, setDropdownPosition] = useState({
      top: 0,
      left: 0,
    });
    const actionButtonRefs = React.useRef({});

    const headerScrollRef = React.useRef(null);
    const bodyScrollRef = React.useRef(null);
    const bottomScrollRef = React.useRef(null);
    const daysScrollRef = React.useRef(null);

    const daysGridWidthPx = days.length * 48;

    const syncDaysScrollLeft = (scrollLeft, source) => {
      if (headerScrollRef.current && source !== "header") {
        headerScrollRef.current.scrollLeft = scrollLeft;
      }
      if (bodyScrollRef.current && source !== "body") {
        bodyScrollRef.current.scrollLeft = scrollLeft;
      }
      if (bottomScrollRef.current && source !== "bottom") {
        bottomScrollRef.current.scrollLeft = scrollLeft;
      }
    };

    const handleHeaderScroll = (e) => {
      syncDaysScrollLeft(e.target.scrollLeft, "header");
    };

    const handleBodyScroll = (e) => {
      syncDaysScrollLeft(e.target.scrollLeft, "body");
    };

    const handleBottomScroll = (e) => {
      syncDaysScrollLeft(e.target.scrollLeft, "bottom");
    };

    // Memoize calculatePayDays to avoid recalculation
    const calculatePayDays = useCallback(
      (employeeId) => {
        const attendanceMap = cleanedAttendanceData[employeeId] || {};
        const paidCodes = new Set(["PR", "PH", "WO"]);
        const unpaidCodes = new Set(["LOP", "LWP"]);

        (leaveTypes || []).forEach((lt) => {
          if (!lt || !lt.code) return;
          const code = String(lt.code).trim().toUpperCase();
          if (lt.isPaid === true) {
            paidCodes.add(code);
          } else {
            unpaidCodes.add(code);
          }
        });

        let paidDays = 0;

        const empContext =
          safeEmployees.find((e) => String(e.id) === String(employeeId)) ||
          employeeId;

        // Employment window bounds (inclusive): join date to relieved/exit date.
        const joinDateValue =
          empContext?.joinDate ||
          empContext?.joiningDate ||
          empContext?.dateOfJoin;
        const relievedDateValue =
          empContext?.relievedDate ||
          empContext?.exitDate ||
          empContext?.resignationDate ||
          empContext?.relievingDate ||
          empContext?.dateOfRelieving;

        let joinDateNum = null;
        let relievedDateNum = null;
        try {
          const joinParts = parseDateComponents(joinDateValue);
          if (joinParts) {
            joinDateNum =
              joinParts.year * 10000 +
              (joinParts.month + 1) * 100 +
              joinParts.day;
          }
        } catch (_) {
          joinDateNum = null;
        }
        try {
          const relievedParts = parseDateComponents(relievedDateValue);
          if (relievedParts) {
            relievedDateNum =
              relievedParts.year * 10000 +
              (relievedParts.month + 1) * 100 +
              relievedParts.day;
          }
        } catch (_) {
          relievedDateNum = null;
        }

        for (let day = 1; day <= daysInMonth; day++) {
          const currentDate = new Date(selectedYear, selectedMonth, day);
          const currentDateNum =
            selectedYear * 10000 + (selectedMonth + 1) * 100 + day;

          // Skip days outside employee employment period in this month.
          if (joinDateNum != null && currentDateNum < joinDateNum) continue;
          if (relievedDateNum != null && currentDateNum > relievedDateNum) continue;

          const defaultStatusResult = getDefaultStatusWithLock(
            currentDate,
            empContext,
            weeklyOffPolicies,
            employeeWeeklyOffMappings,
            holidaysData,
            holidayPolicies,
            employeeHolidayMappings
          );

          let status = attendanceMap[day] || defaultStatusResult.status;

          if (!status || typeof status !== "string") continue;

          // Pay Days = Total Paid Days (not subtracting unpaid days)
          const processStatus = (s, weight) => {
            const normalized = normalizeStatus(s).toUpperCase();
            // Only count paid days - skip unpaid days
            if (paidCodes.has(normalized)) {
              paidDays += weight;
            }
            // Unpaid codes (LOP, LWP, etc.) are not counted at all
          };

          const twoParts = splitIntoTwoCodes(status);
          if (twoParts) {
            processStatus(twoParts[0], 0.5);
            processStatus(twoParts[1], 0.5);
          } else {
            processStatus(status, 1);
          }
        }
        return paidDays;
      },
      [
        cleanedAttendanceData,
        daysInMonth,
        selectedYear,
        selectedMonth,
        weeklyOffPolicies,
        employeeWeeklyOffMappings,
        holidaysData,
        holidayPolicies,
        employeeHolidayMappings,
        leaveTypes,
        safeEmployees,
      ]
    );

    // Memoize calculateStatusCounts to avoid recalculation
    const calculateStatusCounts = useCallback(
      (employeeId) => {
        const attendanceMap = cleanedAttendanceData[employeeId] || {};

        // Initialize counts dynamically from statusDefinitions (robust to runtime leave types)
        const counts = {};
        try {
          Object.keys(statusDefinitions || {}).forEach((k) => {
            if (k && typeof k === "string") counts[k.toUpperCase()] = 0;
          });
        } catch (e) {
          // fallback to common keys
          ["PR", "PH", "WO", "CL", "EL", "CO", "CW", "LOP"].forEach(
            (k) => (counts[k] = 0)
          );
        }

        // Get employee object to check join date and relieving date
        const employee = employees?.find(
          (e) => String(e.id) === String(employeeId)
        );

        for (let day = 1; day <= daysInMonth; day++) {
          const currentDate = new Date(selectedYear, selectedMonth, day);

          // CRITICAL: Skip days before employee joining date
          const joinDateValue =
            employee?.joinDate ||
            employee?.joiningDate ||
            employee?.dateOfJoin;
          if (joinDateValue) {
            try {
              const joinDateParts = parseDateComponents(joinDateValue);
              if (joinDateParts) {
                const joinDate = new Date(
                  joinDateParts.year,
                  joinDateParts.month,
                  joinDateParts.day
                );
                if (!isNaN(joinDate.getTime())) {
                  const currentDateNum =
                    selectedYear * 10000 + (selectedMonth + 1) * 100 + day;
                  const joinDateNum =
                    joinDate.getFullYear() * 10000 +
                    (joinDate.getMonth() + 1) * 100 +
                    joinDate.getDate();
                  if (currentDateNum < joinDateNum) {
                    continue; // Skip days before joining
                  }
                }
              }
            } catch (e) {
              // If date parsing fails, continue anyway
            }
          }

          // Skip days after employee relieving date
          const relievedDate =
            employee?.relievedDate ||
            employee?.exitDate ||
            employee?.resignationDate ||
            employee?.relievingDate;
          if (relievedDate) {
            try {
              const relievedDateParts = parseDateComponents(relievedDate);
              if (relievedDateParts) {
                const rd = new Date(
                  relievedDateParts.year,
                  relievedDateParts.month,
                  relievedDateParts.day
                );
                if (!isNaN(rd.getTime())) {
                  const currentDateNum =
                    selectedYear * 10000 + (selectedMonth + 1) * 100 + day;
                  const relievedDateNum =
                    rd.getFullYear() * 10000 +
                    (rd.getMonth() + 1) * 100 +
                    rd.getDate();
                  if (currentDateNum > relievedDateNum) {
                    continue; // Skip days after relieving
                  }
                }
              }
            } catch (e) {
              // If date parsing fails, continue anyway
            }
          }

          const isWOForEmp = isWeeklyOffForEmployee(
            currentDate,
            employeeId,
            weeklyOffPolicies,
            employeeWeeklyOffMappings
          );

          const defaultStatus = isWOForEmp
            ? "WO"
            : getDefaultStatus(
                currentDate,
                employeeId,
                weeklyOffPolicies,
                employeeWeeklyOffMappings,
                holidaysData,
                holidayPolicies,
                employeeHolidayMappings
              );

          let status = attendanceMap?.[day] || defaultStatus;
          if (!status || typeof status !== "string") continue;

          // Normalize incoming status to uppercase and canonical form
          let raw = status.trim().toUpperCase();

          // IMPORTANT: Convert mapped status codes back to original codes
          // If backend returns "PHS" (mapped), convert to "PH" (original) for counting
          if (raw && reverseMapping && reverseMapping[raw]) {
            raw = reverseMapping[raw].toUpperCase();
          }

          // Handle combined/half-day statuses using splitIntoTwoCodes function
          // This properly handles formats like "PR/BL", "PR/LOP", etc.
          const twoParts = splitIntoTwoCodes(raw);
          if (twoParts && twoParts.length === 2) {
            // Split status (half-day): each part gets 0.5 weight
            const first = normalizeStatus(twoParts[0].trim()).toUpperCase();
            const second = normalizeStatus(twoParts[1].trim()).toUpperCase();

            // Initialize counts if not present
            if (counts[first] === undefined) counts[first] = 0;
            if (counts[second] === undefined) counts[second] = 0;
            
            counts[first] += 0.5;
            counts[second] += 0.5;
          } else {
            // Full day status
            const normalized = normalizeStatus(raw).toUpperCase();
            if (counts[normalized] === undefined) counts[normalized] = 0;
            counts[normalized] += 1;
          }
        }

        return counts;
      },
      [
        cleanedAttendanceData,
        daysInMonth,
        selectedYear,
        selectedMonth,
        weeklyOffPolicies,
        employeeWeeklyOffMappings,
        holidaysData,
        holidayPolicies,
        employeeHolidayMappings,
        attendanceStatuses,
        statusDefinitions,
        reverseMapping,
        employees,
      ]
    );

    // Summary table should not show:
    // - inactive leave types when all values are 0 for everyone
    // - deleted leave types (not present in leaveTypes list, or flagged as deleted)
    // Active leave types should be shown even if currently 0 (so users can still track them).
    const summaryVisibleLeaveCodes = useMemo(() => {
      // Always-fixed non-leave columns in the summary
      const fixedCodes = new Set(["PR", "PH", "WO", "LOP"]);

      const codeToLeaveType = new Map();
      const orderedCodes = [];
      const seen = new Set();

      (leaveTypes || []).forEach((lt) => {
        if (!lt) return;
        const rawCode = lt.code || lt.shortCode || lt.id;
        if (!rawCode) return;
        const code = String(rawCode).trim().toUpperCase();
        if (!code || fixedCodes.has(code) || seen.has(code)) return;
        seen.add(code);
        codeToLeaveType.set(code, lt);
        orderedCodes.push(code);
      });

      if (orderedCodes.length === 0) return [];

      // Aggregate totals across all employees for these codes (used only for inactive filtering)
      const totalsByCode = {};
      sortedEmployees.forEach((emp) => {
        const counts = calculateStatusCounts(emp.id) || {};
        orderedCodes.forEach((code) => {
          const v = Number(counts[code]) || 0;
          if (v !== 0) totalsByCode[code] = (totalsByCode[code] || 0) + v;
        });
      });

      return orderedCodes.filter((code) => {
        const lt = codeToLeaveType.get(code);
        if (lt && (lt.deletedAt || lt.deleted_at || lt.isDeleted === true)) {
          return false;
        }
        return true;
      });
    }, [leaveTypes, sortedEmployees, calculateStatusCounts]);

    // Memoize handlers to prevent unnecessary re-renders
    const handleAttendanceChange = useCallback(
      (employeeId, day, value) => {
        if (!isEditing || isLocked) return;
        // Prevent changing locked Weekly Off (WO/WOPR/PRWO) when override not allowed
        const isWeeklyOffVariant = (status) =>
          status === "WO" || status === "WOPR" || status === "PRWO";
        const currentDate = new Date(selectedYear, selectedMonth, day);
        const empObj =
          (safeEmployees &&
            safeEmployees.find((e) => String(e.id) === String(employeeId))) ||
          employeeId;
        const allowOverrideWeeklyOff = getAllowOverrideForEmployee(
          currentDate,
          empObj,
          weeklyOffPolicies,
          employeeWeeklyOffMappings,
          employeeId
        );

        // Compute weekly-off for this employee & date
        const isWOForEmp = isWeeklyOffForEmployee(
          currentDate,
          empObj,
          weeklyOffPolicies,
          employeeWeeklyOffMappings
        );

        // Compute default status that respects WO
        const defaultStatus = isWOForEmp
          ? "WO"
          : getDefaultStatus(
              currentDate,
              empObj,
              weeklyOffPolicies,
              employeeWeeklyOffMappings,
              holidaysData,
              holidayPolicies,
              employeeHolidayMappings
            );

        const existingStatus =
          attendanceData?.[employeeId]?.[day] || defaultStatus;
        const hadManualEntry = !!(
          attendanceData &&
          attendanceData[String(employeeId)] &&
          attendanceData[String(employeeId)][day] !== undefined
        );

        // Debug: log attempted change context so we can see why UI may block
        try {
          console.info("handleAttendanceChange -> attempt", {
            employeeId,
            day,
            value,
            existingStatus,
            defaultStatus,
            isWOForEmp,
            allowOverrideWeeklyOff,
            hadManualEntry,
          });
        } catch (e) {
          /* ignore logging errors */
        }
        if (
          isWeeklyOffVariant(existingStatus) &&
          !allowOverrideWeeklyOff
        ) {
          console.info("Attendance: blocked change due to locked weekly-off", {
            employeeId,
            existingStatus,
            isWOForEmp,
            allowOverrideWeeklyOff,
          });
          return; // locked weekly off when policy does not allow override
        }
        try {
          console.info("Attendance: applying change to UI", {
            employeeId,
            day,
            value,
          });
        } catch (e) {
          /* ignore logging errors */
        }
        const updatedState = setAttendanceData((prev) => {
          const key = String(employeeId);
          const empPrev = (prev && (prev[key] || prev[employeeId])) || {};
          const newState = {
            ...prev,
            [key]: {
              ...empPrev,
              [day]: value,
            },
          };

          // Track this change so save doesn't need to scan all records
          try {
            const changeKey = `${employeeId}_${day}`;
            if (changedRecordsRef && changedRecordsRef.current) {
              changedRecordsRef.current.set(changeKey, {
                employeeId,
                day,
                newStatus: value,
              });
            }
          } catch (e) {
            console.debug("Failed to track change", e);
          }

          return newState;
        });
      },
      [
        isEditing,
        isLocked,
        weeklyOffPolicies,
        employeeWeeklyOffMappings,
        safeEmployees,
        selectedYear,
        selectedMonth,
        holidaysData,
        holidayPolicies,
        employeeHolidayMappings,
        attendanceData,
        setAttendanceData,
      ]
    );

    const handleDoubleClick = useCallback(
      (employeeId, day, currentStatus, employeeName) => {
        if (!isEditing || isLocked) return;
        // Close dropdown if it's open
        setShowDropdown(false);
        setModalData({ employeeId, day, currentStatus, employeeName });
        setShowStatusModal(true);
      },
      [isEditing, isLocked]
    );

    const handleSingleClick = useCallback(
      (employeeId, day, currentStatus, employeeName, event) => {
        if (!isEditing || isLocked) return;

        // Close dropdown if clicking on the same cell
        if (
          showDropdown &&
          dropdownData.employeeId === employeeId &&
          dropdownData.day === day
        ) {
          setShowDropdown(false);
          return;
        }

        // Get position for dropdown - handle null event
        if (!event || !event.target) {
          // If no event, don't show dropdown (this happens when clicking on disabled cells)
          return;
        }

        const rect = event.target.getBoundingClientRect();
        const position = {
          x: rect.left + rect.width / 2,
          y: rect.bottom + 5,
        };

        setDropdownData({
          employeeId,
          day,
          currentStatus,
          employeeName,
          position,
        });
        setShowDropdown(true);
      },
      [isEditing, isLocked, showDropdown, dropdownData.employeeId, dropdownData.day]
    );

    const handleModalSave = useCallback(
      (employeeId, day, status, overrideFlag = false) => {
        // If user confirmed override, store it temporarily so save payload can include it
        if (overrideFlag) {
          setOverrideMap((prev) => ({
            ...prev,
            [`${employeeId}_${day}`]: true,
          }));
          try {
            toast.success(
              "Override confirmed — sending override flag to server"
            );
          } catch (e) {
            console.debug("Toast failed for override confirmation", e);
          }
          try {
            console.info("Override confirmed for cell", {
              key: `${employeeId}_${day}`,
              employeeId,
              day,
            });
          } catch (e) {
            console.debug("Failed to log override confirmation", e);
          }
        }
        handleAttendanceChange(employeeId, day, status);
        setShowStatusModal(false);
      },
      [handleAttendanceChange]
    );

    const handleDropdownSelect = useCallback(
      (status) => {
        handleAttendanceChange(
          dropdownData.employeeId,
          dropdownData.day,
          status
        );
        setShowDropdown(false);
      },
      [handleAttendanceChange, dropdownData.employeeId, dropdownData.day]
    );

    // Update dropdown position on scroll
    useEffect(() => {
      const updateDropdownPosition = () => {
        if (
          openActionDropdown &&
          actionButtonRefs.current[openActionDropdown]
        ) {
          const button = actionButtonRefs.current[openActionDropdown];
          const rect = button.getBoundingClientRect();
          setDropdownPosition({
            top: rect.bottom + window.scrollY + 4,
            left: rect.left + window.scrollX,
          });
        }
      };

      if (openActionDropdown) {
        updateDropdownPosition();
        window.addEventListener("scroll", updateDropdownPosition, true);
        window.addEventListener("resize", updateDropdownPosition);

        return () => {
          window.removeEventListener("scroll", updateDropdownPosition, true);
          window.removeEventListener("resize", updateDropdownPosition);
        };
      }
    }, [openActionDropdown]);

    // Close dropdown when clicking outside
    useEffect(() => {
      const handleClickOutside = (event) => {
        if (showDropdown && !event.target.closest(".status-dropdown")) {
          setShowDropdown(false);
        }
        // Close action dropdown when clicking outside
        if (openActionDropdown && !event.target.closest(".action-dropdown")) {
          setOpenActionDropdown(null);
        }
      };

      if (showDropdown || openActionDropdown) {
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
          document.removeEventListener("mousedown", handleClickOutside);
        };
      }
    }, [showDropdown, openActionDropdown]);

    const getStatusColor = (status) => {
      if (!status || typeof status !== "string") {
        return { className: "text-gray-700 border-gray-300", style: {} };
      }

      const norm = normalizeStatus(status).toUpperCase();

      // If backend provided a color for this status, use inline style
      if (statusColorMap && statusColorMap[norm]) {
        const color = statusColorMap[norm];
        // If color is a hex code or valid color string, apply it
        if (color && (color.startsWith("#") || color.startsWith("rgb") || /^[a-zA-Z]+$/.test(color))) {
          return {
            className: "border",
            style: {
              color: color,
              borderColor: color,
            },
          };
        }
        // If it's a Tailwind class, return as className
        return { className: color, style: {} };
      }

      const colors = {
        PR: "text-emerald-700 border-emerald-300",
        PH: "text-amber-700 border-amber-300",
        WO: "text-redhalo-navy border-redhalo-teal/70",
        LOP: "text-red-700 border-red-300",
      };

      // Handle combined statuses
      if (
        norm.includes("LOP") ||
        norm.includes("LWP") ||
        norm.includes("LO") ||
        norm.includes("LW")
      ) {
        return { className: "text-red-700", style: {} };
      }

      return { className: colors[norm] || "text-gray-700", style: {} };
    };

    const rowHeight = 32;
    const headerRowPx = 38;
    const visibleRowCount = Math.min(10, Math.max(safeEmployees.length, 1));
    const tableBodyMaxHeightPx =
      visibleRowCount * rowHeight + headerRowPx + visibleRowCount + 16;

    const listRef = useRef(null);
    const [listHeight, setListHeight] = useState(400);

    useEffect(() => {
      const el = daysScrollRef.current;
      const update = () => {
        try {
          setListHeight(el ? Math.max(200, el.clientHeight) : 400);
        } catch (e) {
          setListHeight(400);
        }
      };
      update();
      let ro = null;
      if (el && typeof ResizeObserver !== "undefined") {
        ro = new ResizeObserver(() => update());
        ro.observe(el);
      }
      window.addEventListener("resize", update);
      return () => {
        window.removeEventListener("resize", update);
        if (ro) ro.disconnect();
      };
    }, [safeEmployees.length]);

    // Forward ref wrapper to allow react-window to render a <tbody>
    const TbodyVirtual = React.useMemo(
      () =>
        React.forwardRef(function TbodyVirtualInner(props, ref) {
          return <tbody ref={ref} {...props} />;
        }),
      []
    );

    return (
      <div
        ref={wrapperRef}
        className={`employee-table-scroll-area flex min-h-0 w-full min-w-0 flex-1 flex-col ${
          darkMode ? "employee-table-scroll-area-dark" : ""
        } ${tableScrolling ? "is-scrolling" : ""}`}
      >
        <div
          className="relative flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto overflow-x-auto"
          onScroll={handleTableAreaScroll}
          onMouseLeave={() => setHoveredRowIndex(null)}
        >
          <table
            className={`employee-table attendance-unified-grid w-full min-w-full border-collapse text-sm leading-tight ${
              darkMode ? "employee-table-dark" : ""
            } ${
              darkMode
                ? "bg-[#2C2C2C] text-[#E0E0E0]"
                : "bg-[#fafafa] text-gray-800"
            }`}
          >
                <thead
                  className={`sticky top-0 z-20 border-b ${
                    darkMode
                      ? "border-[#4C4C4C] bg-[#3a3a3a]"
                      : "border-[#d7dee8] bg-[#e6eaef]"
                  }`}
                >
                  <tr className="h-[38px]" style={{ height: `${headerRowPx}px` }}>
                    {[
                      ["actions", "Actions", "center", false],
                      ["slNo", "Sl No", "center", false],
                      ["empCode", "Emp Code", "center", true],
                      ["name", "Name", "left", true],
                      ["department", "Department", "left", true],
                      ["designation", "Designation", "left", true],
                      ["branch", "Branch", "left", true],
                      ["doj", "DOJ", "center", true],
                      ["dol", "DOL", "center", true],
                    ].map(([key, label, align, sortable]) => (
                      <th
                        key={key}
                        className={`relative h-[38px] px-2 py-0 align-middle text-xs font-bold uppercase tracking-wide ${
                          align === "left" ? "text-left" : "text-center"
                        } ${
                          sortable ? attendanceThSortClass(key) : ""
                        } ${attendanceFrozenClass(key, attendanceStickyLeft)}`}
                        style={{
                          height: `${headerRowPx}px`,
                          width: `${columnWidths[key]}px`,
                          minWidth: `${columnWidths[key]}px`,
                          maxWidth: `${columnWidths[key]}px`,
                          ...attendanceFrozenStyle(key, attendanceStickyLeft, {
                            darkMode,
                            isHeader: true,
                            isHover: false,
                          }),
                        }}
                        onClick={
                          sortable ? () => handleSort(key) : undefined
                        }
                        title={
                          sortable
                            ? `Sort by ${label} (click to toggle ascending / descending)`
                            : label
                        }
                      >
                        {label}
                        <div
                          className="absolute right-0 top-0 h-full w-1 cursor-col-resize bg-transparent opacity-0 transition-opacity hover:bg-redhalo-mint/500 hover:opacity-100"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            const startX = e.clientX;
                            const startWidth = columnWidths[key];
                            const onMove = (ev) => {
                              handleColumnResize(
                                key,
                                startWidth + (ev.clientX - startX)
                              );
                            };
                            const onUp = () => {
                              document.removeEventListener("mousemove", onMove);
                              document.removeEventListener("mouseup", onUp);
                            };
                            document.addEventListener("mousemove", onMove);
                            document.addEventListener("mouseup", onUp);
                          }}
                        />
                      </th>
                    ))}
                    <th
                      className="h-[38px] w-12 max-w-[48px] px-1 py-0 align-middle text-center text-xs font-bold uppercase leading-tight tracking-wide"
                      title="Present"
                      style={{ height: `${headerRowPx}px` }}
                    >
                      {attendanceMapping && attendanceMapping["PR"]
                        ? attendanceMapping["PR"]
                        : "PR"}
                    </th>
                    <th
                      className="h-[38px] w-12 max-w-[48px] px-1 py-0 align-middle text-center text-xs font-bold uppercase leading-tight tracking-wide"
                      title="Public Holiday"
                      style={{ height: `${headerRowPx}px` }}
                    >
                      {attendanceMapping && attendanceMapping["PH"]
                        ? attendanceMapping["PH"]
                        : "PH"}
                    </th>
                    <th
                      className="h-[38px] w-12 max-w-[48px] px-1 py-0 align-middle text-center text-xs font-bold uppercase leading-tight tracking-wide"
                      title="Weekly Off"
                      style={{ height: `${headerRowPx}px` }}
                    >
                      {attendanceMapping && attendanceMapping["WO"]
                        ? attendanceMapping["WO"]
                        : "WO"}
                    </th>
                    {summaryVisibleLeaveCodes.map((code) => (
                      <th
                        key={`hdr-${code}`}
                        className="h-[38px] w-12 max-w-[48px] px-1 py-0 align-middle text-center text-xs font-bold uppercase leading-tight tracking-wide"
                        title={
                          (statusDefinitions && statusDefinitions[code]) ||
                          code
                        }
                        style={{ height: `${headerRowPx}px` }}
                      >
                        {attendanceMapping && attendanceMapping[code]
                          ? attendanceMapping[code]
                          : code}
                      </th>
                    ))}
                    <th
                      className="h-[38px] w-12 max-w-[48px] px-1 py-0 align-middle text-center text-xs font-bold uppercase leading-tight tracking-wide"
                      title="Loss of Pay"
                      style={{ height: `${headerRowPx}px` }}
                    >
                      {attendanceMapping && attendanceMapping["LOP"]
                        ? attendanceMapping["LOP"]
                        : "LOP"}
                    </th>
                    <th
                      className="h-[38px] w-16 max-w-[64px] px-1 py-0 align-middle text-center text-xs font-bold uppercase leading-tight tracking-wide"
                      title="Pay days"
                      style={{ height: `${headerRowPx}px` }}
                    >
                      Pay Days
                    </th>
                    <th
                      className="h-[38px] w-14 max-w-[56px] px-1 py-0 align-middle text-center text-xs font-bold uppercase leading-tight tracking-wide"
                      title="LOP Reversal Days (for selected payroll month)"
                      style={{ height: `${headerRowPx}px` }}
                    >
                      LOP Rev
                    </th>
                  </tr>
                </thead>
                <tbody
                  className={employeeListTbodyDivideClasses(
                    darkMode,
                    hasActiveFilters
                  )}
                >
                  {sortedEmployees.map((employee, index) => {
                    const joinDate = new Date(employee.joinDate);
                    const exitDate = employee.exitDate
                      ? new Date(employee.exitDate)
                      : new Date(9999, 11, 31);
                    let serialNumber;
                    if (itemsPerPage === "all") {
                      // When showing all, serial number is just the index + 1
                      serialNumber = index + 1;
                    } else {
                      const perPage = parseInt(itemsPerPage, 10) || 50;
                      serialNumber = (page - 1) * perPage + (index + 1);
                    }
                    // 🚀 REMOVED: calculateStatusCounts was called but never used in left panel
                    // This was causing 500+ expensive calculations per render
                    // Status counts only needed for the main table, not the left panel
                    const statusCounts = calculateStatusCounts(employee.id);
                    const frozenTd = (key, className, children, extraStyle = {}) => (
                      <td
                        className={`px-1 py-1 whitespace-nowrap align-middle border-b ${className} ${attendanceFrozenClass(
                          key,
                          attendanceStickyLeft
                        )}`}
                        style={{
                          height: "32px",
                          ...attendanceFrozenStyle(key, attendanceStickyLeft, {
                            darkMode,
                            isHeader: false,
                          }),
                          ...extraStyle,
                        }}
                      >
                        {children}
                      </td>
                    );
                    const row = (
                      <tr
                        key={employee.id}
                        className={employeeListRowClasses(
                          index,
                          darkMode,
                          hasActiveFilters,
                          hoveredRowIndex
                        )}
                        style={{ height: "32px" }}
                        onMouseEnter={() => setHoveredRowIndex(index)}
                      >
                        <td
                          className={`px-1 py-1 w-12 text-center align-middle ${attendanceFrozenClass(
                            "actions",
                            attendanceStickyLeft
                          )}`}
                          style={{
                            height: "32px",
                            position: "relative",
                            ...attendanceFrozenStyle("actions", attendanceStickyLeft, {
                              darkMode,
                              isHeader: false,
                            }),
                          }}
                        >
                          <div className="relative action-dropdown">
                            <button
                              ref={(el) => {
                                if (el)
                                  actionButtonRefs.current[employee.id] = el;
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                const button = e.currentTarget;
                                const rect = button.getBoundingClientRect();
                                if (openActionDropdown === employee.id) {
                                  setOpenActionDropdown(null);
                                } else {
                                  setOpenActionDropdown(employee.id);
                                  setDropdownPosition({
                                    top: rect.bottom + window.scrollY + 4,
                                    left: rect.left + window.scrollX,
                                  });
                                }
                              }}
                              className={`w-8 h-8 rounded-full border border-gray-300 text-gray-600 hover:bg-gray-200 transition-colors duration-200 flex items-center justify-center ${
                                darkMode
                                  ? "bg-[#2C2C2C] border-[#3C3C3C] text-[#E0E0E0] hover:bg-[#3C3C3C]"
                                  : "bg-gray-100 hover:bg-gray-200"
                              }`}
                              title={isLocked ? "Employee Actions (LOP Reversal available for locked months)" : "Employee Actions"}
                            >
                              <MoreVertical className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                        {frozenTd("slNo", "w-12 text-center", serialNumber)}
                        {frozenTd("empCode", "w-20 text-center", employee.code)}
                        {frozenTd("name", "w-28 text-left truncate max-w-[7rem]", employee.name)}
                        {frozenTd(
                          "department",
                          "w-24 text-left truncate max-w-[6rem]",
                          getNameByIdFromList(
                            employee.departmentId,
                            departmentsList,
                            employee,
                            "department"
                          ) || "—"
                        )}
                        {frozenTd(
                          "designation",
                          "w-24 text-left truncate max-w-[6rem]",
                          getNameByIdFromList(
                            employee.designationId,
                            designationsList,
                            employee,
                            "designation"
                          ) || "—"
                        )}
                        {frozenTd(
                          "branch",
                          "w-20 text-left truncate max-w-[5rem]",
                          getNameByIdFromList(
                            employee.branchId,
                            branchesList,
                            employee,
                            "branch"
                          ) || "—"
                        )}
                        {frozenTd(
                          "doj",
                          "w-20 text-center",
                          formatDateForTableUI(employee.joinDate)
                        )}
                        {frozenTd(
                          "dol",
                          "w-20 text-center",
                          (() => {
                            const relievedOrResignedDate =
                              employee.relievedDate ||
                              employee.exitDate ||
                              employee.resignationDate ||
                              employee.relievingDate;
                            return isDateInSelectedMonth(
                              relievedOrResignedDate,
                              selectedYear,
                              selectedMonth
                            )
                              ? formatDateForTableUI(relievedOrResignedDate)
                              : "";
                          })()
                        )}
                        <td
                          className="h-8 w-12 max-w-[48px] px-1 py-0.5 text-center text-xs font-semibold tabular-nums align-middle border-b"
                          style={{ height: "32px" }}
                        >
                          {statusCounts.PR ?? 0}
                        </td>
                        <td
                          className="h-8 w-12 max-w-[48px] px-1 py-0.5 text-center text-xs font-semibold tabular-nums align-middle border-b"
                          style={{ height: "32px" }}
                        >
                          {statusCounts.PH ?? 0}
                        </td>
                        <td
                          className="h-8 w-12 max-w-[48px] px-1 py-0.5 text-center text-xs font-semibold tabular-nums align-middle border-b"
                          style={{ height: "32px" }}
                        >
                          {statusCounts.WO ?? 0}
                        </td>
                        {summaryVisibleLeaveCodes.map((code) => (
                          <td
                            key={`cnt-${employee.id}-${code}`}
                            className="h-8 w-12 max-w-[48px] px-1 py-0.5 text-center text-xs font-semibold tabular-nums align-middle border-b"
                            style={{ height: "32px" }}
                            title={
                              (statusDefinitions &&
                                statusDefinitions[code]) ||
                              code
                            }
                          >
                            {statusCounts[code] ?? 0}
                          </td>
                        ))}
                        <td
                          className="h-8 w-12 max-w-[48px] px-1 py-0.5 text-center text-xs font-semibold tabular-nums align-middle border-b"
                          style={{ height: "32px" }}
                        >
                          {statusCounts.LOP ?? 0}
                        </td>
                        <td
                          className="h-8 w-16 max-w-[64px] px-1 py-0.5 text-center text-xs font-bold tabular-nums whitespace-nowrap align-middle border-b"
                          style={{ height: "32px" }}
                        >
                          {calculatePayDays(employee.id)}
                        </td>
                        <td
                          className="h-8 w-14 max-w-[56px] px-1 py-0.5 text-center text-xs font-semibold tabular-nums whitespace-nowrap align-middle border-b"
                          style={{ height: "32px" }}
                          title="LOP Reversal days for this payroll month"
                        >
                          {lopReversalDaysMap[employee.id] ??
                            lopReversalDaysMap[String(employee.id)] ??
                            0}
                        </td>
                      </tr>
                    );

                    // Optionally render a sentinel row after the calculated index so the
                    // IntersectionObserver can trigger prefetch when the user reaches
                    // around the 70th, 170th, ... employees.
                    if (
                      sentinelAfterIndex !== null &&
                      index === sentinelAfterIndex
                    ) {
                      return (
                        <React.Fragment key={`frag-${employee.id}`}>
                          {row}
                          <tr key={`sentinel-${chunksLoaded}`}>
                            <td
                              colSpan={
                                9 +
                                3 +
                                summaryVisibleLeaveCodes.length +
                                3
                              }
                              style={{ padding: 0, height: 1 }}
                            >
                              <div
                                ref={sentinelRef}
                                style={{ height: 1, width: "100%" }}
                                aria-hidden="true"
                              />
                            </td>
                          </tr>
                        </React.Fragment>
                      );
                    }

                    return row;
                  })}
                </tbody>
          </table>

            {/* Middle Scrollable Table - Days (1–31); hidden when SHOW_ATTENDANCE_DAY_GRID is false */}
            {SHOW_ATTENDANCE_DAY_GRID && (
            <div className="flex min-h-0 min-w-0 flex-1 flex-col">
              {/* Sticky Header OUTSIDE scroll */}
              <div
                ref={headerScrollRef}
                onScroll={handleHeaderScroll}
                className="sticky top-0 z-10 overflow-x-auto overflow-y-hidden scrollbar-hide"
              >
                <table
                  className={`employee-table min-w-full border-collapse text-sm leading-tight ${
                    darkMode
                      ? "bg-[#2C2C2C] text-[#E0E0E0]"
                      : "bg-[#fafafa] text-gray-800"
                  }`}
                  style={{ minWidth: "max-content", tableLayout: "fixed" }}
                >
                  <thead
                    className={`border-b ${
                      darkMode
                        ? "border-[#4C4C4C] bg-[#3a3a3a]"
                        : "border-[#d7dee8] bg-[#e6eaef]"
                    }`}
                  >
                    <tr className="h-[38px]" style={{ height: `${headerRowPx}px` }}>
                      {days.map((day) => {
                        // Build a real JS Date for this day in the selected month/year
                        const actualDate = new Date(
                          selectedYear,
                          selectedMonth,
                          day
                        );
                        const currentDate = actualDate;
                        const isWO = safeEmployees.some((emp) =>
                          isWeeklyOffForEmployee(
                            currentDate,
                            emp,
                            weeklyOffPolicies,
                            employeeWeeklyOffMappings
                          )
                        );

                        const isPH = isHoliday(
                          currentDate,
                          null,
                          holidaysData,
                          holidayPolicies,
                          employeeHolidayMappings
                        );
                        const dayName = currentDate.toLocaleDateString(
                          "en-US",
                          {
                            weekday: "short",
                          }
                        );
                        const dayLetter =
                          dayName === "Sun" ? "Su" : dayName.charAt(0);
                        return (
                          <th
                            key={day}
                            className={`h-[38px] px-0.5 py-0 align-middle text-center text-xs font-bold uppercase tracking-wide relative ${
                              isPH ? "bg-red-100 text-red-700" : ""
                            }`}
                            style={{
                              height: `${headerRowPx}px`,
                              minWidth: "48px",
                              width: "48px",
                              maxWidth: "48px",
                              boxSizing: "border-box",
                            }}
                          >
                            <div className="flex flex-col items-center justify-center gap-0 leading-none">
                              <span className="text-[11px] font-bold leading-none">
                                {day}
                              </span>
                              <span
                                className={`text-[10px] leading-none ${
                                  darkMode ? "text-[#A6A9C8]" : "text-gray-500"
                                }`}
                              >
                                {dayLetter}
                              </span>
                            </div>
                            {isPH && (
                              <div className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full"></div>
                            )}
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                </table>
              </div>

              <div
                ref={bodyScrollRef}
                onScroll={handleBodyScroll}
                className="overflow-x-auto overflow-y-hidden scrollbar-hide"
              >
                <table
                  className={`employee-table min-w-full border-collapse text-sm leading-tight ${
                    darkMode
                      ? "bg-[#2C2C2C] text-[#E0E0E0]"
                      : "bg-[#fafafa] text-gray-800"
                  }`}
                  style={{ minWidth: "max-content", tableLayout: "fixed" }}
                >
                  <tbody
                    className={employeeListTbodyDivideClasses(
                      darkMode,
                      hasActiveFilters
                    )}
                  >
                    {(() => {
                      // Debug: Check for duplicate employee IDs
                      const idCounts = {};
                      safeEmployees.forEach((emp) => {
                        idCounts[emp.id] = (idCounts[emp.id] || 0) + 1;
                      });
                      const duplicates = Object.entries(idCounts).filter(
                        ([id, count]) => count > 1
                      );
                      if (duplicates.length > 0) {
                        console.warn(
                          "🔴 DUPLICATE EMPLOYEES DETECTED:",
                          duplicates
                        );
                      }
                      return null;
                    })()}
                    {sortedEmployees.map((employee, index) => (
                      <AttendanceRow
                        key={employee.id}
                        employee={employee}
                        days={days}
                        selectedYear={selectedYear}
                        selectedMonth={selectedMonth}
                        weeklyOffPolicies={weeklyOffPolicies}
                        employeeWeeklyOffMappings={employeeWeeklyOffMappings}
                        holidaysData={holidaysData}
                        holidayPolicies={holidayPolicies}
                        employeeHolidayMappings={employeeHolidayMappings}
                        employeeAttendanceData={
                          cleanedAttendanceData[employee.id]
                        }
                        employeeLOPReversalDays={
                          attendanceLOPReversalMap[employee.id] ||
                          attendanceLOPReversalMap[String(employee.id)] ||
                          {}
                        }
                        isEditing={isEditing}
                        isLocked={isLocked}
                        darkMode={darkMode}
                        selectedCell={selectedCell}
                        onCellClick={handleSingleClick}
                        onCellDoubleClick={handleDoubleClick}
                        rowIndex={index}
                        hasActiveFilters={hasActiveFilters}
                        hoveredRowIndex={hoveredRowIndex}
                        onRowMouseEnter={() => setHoveredRowIndex(index)}
                      />
                    ))}
                  </tbody>
                </table>
              </div>

              <div
                className={`sticky bottom-0 z-10 shrink-0 ${
                  darkMode ? "bg-[#2C2C2C]" : "bg-[#fafafa]"
                }`}
                style={{
                  borderTop: darkMode
                    ? "1px solid #4C4C4C"
                    : "1px solid #d7dee8",
                }}
              >
                <div
                  ref={bottomScrollRef}
                  onScroll={handleBottomScroll}
                  className="overflow-x-auto overflow-y-hidden scrollbar-hide"
                  style={{ height: "14px" }}
                >
                  <div style={{ width: `${daysGridWidthPx}px`, height: 1 }} />
                </div>
              </div>
            </div>
            )}
        </div>

        {/* (table-level pagination removed; pagination is rendered at parent layout) */}

        {/* Attendance Status Modal */}
        <AttendanceStatusModal
          darkMode={darkMode}
          isOpen={showStatusModal}
          onClose={() => setShowStatusModal(false)}
          onSave={handleModalSave}
          employeeId={modalData.employeeId}
          day={modalData.day}
          currentStatus={modalData.currentStatus}
          employeeName={modalData.employeeName}
          selectedYear={selectedYear}
          selectedMonth={selectedMonth}
          weeklyOffPolicies={weeklyOffPolicies}
          employeeWeeklyOffMappings={employeeWeeklyOffMappings}
          holidaysData={holidaysData}
          holidayPolicies={holidayPolicies}
          employeeHolidayMappings={employeeHolidayMappings}
        />

        {/* Status Dropdown */}
        {showDropdown && (
          <div
            className="fixed z-50"
            style={{
              left: `${dropdownData.position.x - 45}px`,
              top: `${dropdownData.position.y}px`,
            }}
          >
            <div
              className={`status-dropdown ${
                darkMode
                  ? "bg-[#2C2C2C] text-[#E0E0E0] border-[#3C3C3C]"
                  : "bg-white text-gray-900 border-gray-300"
              } border rounded-md shadow-lg p-1`}
            >
              <div className="text-xs font-medium mb-1 text-center px-1">
                {dropdownData.employeeName} - Day {dropdownData.day}
              </div>
              <div className="grid grid-cols-2 gap-1">
                {(() => {
                  const coreStatuses = ["PR", "LOP", "WO", "PH"];
                  const employee = safeEmployees.find(
                    (e) => String(e.id) === String(dropdownData.employeeId)
                  );
                  const genderFilteredCodes = filterLeaveTypesForEmployeeGender(
                    leaveTypes || [],
                    employee
                  )
                    .filter((lt) => {
                      const isActive =
                        lt.isActive !== undefined ? lt.isActive : true;
                      return isActive;
                    })
                    .map((lt) => {
                      const code = lt.code || lt.shortCode || lt.id;
                      return code ? String(code).toUpperCase() : null;
                    })
                    .filter(Boolean);
                  // Include global attendanceStatuses so dropdown always shows SL, EL, CL etc.
                  // even if leaveTypes prop is empty or not yet loaded (same fetch updates both).
                  const fromGlobal = Array.isArray(attendanceStatusesState)
                    ? attendanceStatusesState.map((c) => String(c).toUpperCase())
                    : [];
                  const dropdownStatuses = [
                    ...new Set([
                      ...coreStatuses,
                      ...genderFilteredCodes,
                      ...fromGlobal,
                    ]),
                  ];
                  return dropdownStatuses.map((status) => {
                    const canonical = String(status || "").toUpperCase();
                    const displayCode =
                      (attendanceMapping && attendanceMapping[canonical]) ||
                      status;
                    const title =
                      (statusDefinitionsState &&
                        statusDefinitionsState[canonical]) ||
                      statusDefinitionsState[status] ||
                      "";
                    return (
                      <button
                        key={status}
                        onClick={() => handleDropdownSelect(status)}
                        className={`rounded px-2 py-1 text-xs transition-colors ${
                          dropdownData.currentStatus === status
                            ? darkMode
                              ? "bg-redhalo-navy text-redhalo-mint"
                              : "bg-redhalo-mint/90 text-redhalo-navy"
                            : darkMode
                            ? "hover:bg-gray-700"
                            : "hover:bg-gray-100"
                        }`}
                        title={title}
                      >
                        {displayCode}
                      </button>
                    );
                  });
                })()}
              </div>
            </div>
          </div>
        )}

        {/* Floating Action Dropdown Menu */}
        {openActionDropdown &&
          (() => {
            const employee = safeEmployees.find(
              (emp) => emp.id === openActionDropdown
            );
            if (!employee) return null;

            const empCode =
              employee.code || employee.empCode || employee.employeeId || "";
            const empName = employee.user?.name || employee.name || "";

            return (
              <>
                <div
                  className={`fixed shadow-xl z-[9999] ${
                    darkMode
                      ? "bg-[#2C2C2C] border border-[#3C3C3C] text-[#E0E0E0]"
                      : "bg-white border border-gray-300 text-gray-800"
                  }`}
                  style={{
                    top: `${dropdownPosition.top}px`,
                    left: `${dropdownPosition.left}px`,
                    minWidth: "180px",
                    boxShadow: darkMode
                      ? "0 4px 6px -1px rgba(0, 0, 0, 0.3), 0 2px 4px -1px rgba(0, 0, 0, 0.2)"
                      : "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
                  }}
                >
                  {/* Header with Employee Code, Name, and Close Button */}
                  <div
                    className={`flex items-center justify-between px-4 py-2.5 border-b ${
                      darkMode
                        ? "border-[#3C3C3C] bg-[#1C1C1C]"
                        : "border-gray-200 bg-gray-50"
                    }`}
                  >
                    <div className="flex-1 min-w-0">
                      <div
                        className={`text-xs font-semibold truncate ${
                          darkMode ? "text-[#E0E0E0]" : "text-gray-800"
                        }`}
                      >
                        {empCode}
                      </div>
                      <div
                        className={`text-xs truncate ${
                          darkMode ? "text-gray-400" : "text-gray-600"
                        }`}
                      >
                        {empName}
                      </div>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenActionDropdown(null);
                      }}
                      className={`ml-2 p-1 rounded hover:opacity-70 transition-opacity ${
                        darkMode
                          ? "text-red-400 hover:bg-[#3C3C3C] hover:text-red-300"
                          : "text-red-500 hover:bg-red-50 hover:text-red-600"
                      }`}
                      title="Close"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Action Items: Apply Leave (disabled when month locked), LOP Reversal (always enabled for locked months) */}
                  <div>
                    {onViewAttendance && (
                      <button
                        type="button"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          onViewAttendance(employee);
                          setOpenActionDropdown(null);
                        }}
                        className={`w-full text-left px-4 py-2.5 text-xs font-medium transition-colors flex items-center cursor-pointer ${
                          darkMode
                            ? "text-[#E0E0E0] hover:bg-[#3C3C3C]"
                            : "text-gray-700 hover:bg-gray-100"
                        }`}
                      >
                        View calendar
                      </button>
                    )}
                    {onApplyLeave && (
                      <button
                        type="button"
                        disabled={isLocked}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (isLocked) return;
                          onApplyLeave(employee);
                          setOpenActionDropdown(null);
                        }}
                        className={`w-full text-left px-4 py-2.5 text-xs font-medium transition-colors flex items-center ${
                          isLocked
                            ? "cursor-not-allowed opacity-60"
                            : "cursor-pointer hover:opacity-90"
                        } ${
                          darkMode
                            ? "text-[#E0E0E0] hover:bg-[#3C3C3C]"
                            : "text-gray-700 hover:bg-gray-100"
                        }`}
                      >
                        Apply Leave
                      </button>
                    )}
                    {onLOPReversal && (
                      <button
                        type="button"
                        disabled={(() => {
                          const now = new Date();
                          const isCurrentMonth =
                            selectedYear === now.getFullYear() &&
                            selectedMonth === now.getMonth(); // selectedMonth is 0-based
                          return isLocked || !isCurrentMonth;
                        })()}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          const now = new Date();
                          const isCurrentMonth =
                            selectedYear === now.getFullYear() &&
                            selectedMonth === now.getMonth();
                          if (isLocked || !isCurrentMonth) return;
                          onLOPReversal(employee);
                          setOpenActionDropdown(null);
                        }}
                        className={`w-full text-left px-4 py-2.5 text-xs font-medium transition-colors flex items-center border-t ${
                          (() => {
                            const now = new Date();
                            const isCurrentMonth =
                              selectedYear === now.getFullYear() &&
                              selectedMonth === now.getMonth();
                            return isLocked || !isCurrentMonth
                              ? "cursor-not-allowed opacity-60"
                              : "cursor-pointer hover:opacity-90";
                          })()
                        } ${
                          darkMode
                            ? "border-[#3C3C3C] text-[#E0E0E0] hover:bg-[#3C3C3C]"
                            : "border-gray-200 text-gray-700 hover:bg-gray-100"
                        }`}
                        title={(() => {
                          const now = new Date();
                          const isCurrentMonth =
                            selectedYear === now.getFullYear() &&
                            selectedMonth === now.getMonth();
                          if (!isCurrentMonth) return "LOP Reversal is allowed only in the current month";
                          if (isLocked) return "Attendance is locked for this month";
                          return "LOP Reversal";
                        })()}
                      >
                        LOP Reversal
                      </button>
                    )}
                    {onLeaveAdjustment && (
                      <button
                        type="button"
                        disabled={isLocked}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (isLocked) return;
                          onLeaveAdjustment(employee);
                          setOpenActionDropdown(null);
                        }}
                        className={`w-full text-left px-4 py-2.5 text-xs font-medium transition-colors flex items-center border-t ${
                          isLocked
                            ? "cursor-not-allowed opacity-60"
                            : "cursor-pointer hover:opacity-90"
                        } ${
                          darkMode
                            ? "border-[#3C3C3C] text-[#E0E0E0] hover:bg-[#3C3C3C]"
                            : "border-gray-200 text-gray-700 hover:bg-gray-100"
                        }`}
                      >
                        Leave Adjustment
                      </button>
                    )}
                    {onLeaveBalance && (
                      <button
                        type="button"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          onLeaveBalance(employee);
                          setOpenActionDropdown(null);
                        }}
                        className={`w-full text-left px-4 py-2.5 text-xs font-medium hover:opacity-90 transition-colors flex items-center cursor-pointer border-t ${
                          darkMode
                            ? "border-[#3C3C3C] text-[#E0E0E0] hover:bg-[#3C3C3C]"
                            : "border-gray-200 text-gray-700 hover:bg-gray-100"
                        }`}
                      >
                        Leave Balance
                      </button>
                    )}
                  </div>
                </div>
              </>
            );
          })()}
      </div>
    );
  }
);

const MarkAllPopup = ({
  darkMode,
  onClose,
  onMarkAll,
  daysInMonth,
  allEmployees,
}) => {
  const [markType, setMarkType] = useState("day"); // 'day', 'employee', 'all'
  const [selectedDay, setSelectedDay] = useState(1);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [employeeSearchTerm, setEmployeeSearchTerm] = useState("");
  const [markAllSearchReadonlyBarrier, setMarkAllSearchReadonlyBarrier] =
    useState(true);
  const [status, setStatus] = useState("PR");
  const [overrideWeeklyOff, setOverrideWeeklyOff] = useState(false);
  const [confirmationModal, setConfirmationModal] = useState({
    isOpen: false,
    title: "",
    message: "",
    onConfirm: null,
  });

  // Filter employees based on search term
  const filteredEmployeesForMarkAll = (
    allEmployees && Array.isArray(allEmployees) ? allEmployees : []
  ).filter((employee) => {
    if (!employeeSearchTerm) return true;

    const empName = employee.user?.name || employee.name || "";
    const empCode = employee.empCode || employee.employeeId || "";
    const mobile = employee.user?.phone || "";
    const email = employee.email || "";

    return (
      empName.toLowerCase().includes(employeeSearchTerm.toLowerCase()) ||
      empCode.toLowerCase().includes(employeeSearchTerm.toLowerCase()) ||
      mobile.toLowerCase().includes(employeeSearchTerm.toLowerCase()) ||
      email.toLowerCase().includes(employeeSearchTerm.toLowerCase())
    );
  });

  const handleMarkAll = () => {
    const options = { overrideWeeklyOff };
    if (markType === "day") {
      onMarkAll(selectedDay, status, options);
    } else if (markType === "employee") {
      if (selectedEmployees.length === 0) {
        alert("Please select at least one employee");
        return;
      }
      onMarkAll("employee", selectedEmployees, status, options);
    } else if (markType === "all") {
      onMarkAll("all", null, status, options);
    }
    onClose();
  };

  const handleClearAll = () => {
    setConfirmationModal({
      isOpen: true,
      title: "Clear All Attendance",
      message:
        'Are you sure you want to clear all attendance data? This will set all employees and days to "-" status.',
        onConfirm: () => {
        onMarkAll("clear", null, "-", { overrideWeeklyOff: false });
        onClose();
        setConfirmationModal({ ...confirmationModal, isOpen: false });
      },
    });
  };

  const closeConfirmation = () => {
    setConfirmationModal({ ...confirmationModal, isOpen: false });
  };

  const handleSelectAllEmployees = () => {
    if (selectedEmployees.length === filteredEmployeesForMarkAll.length) {
      setSelectedEmployees([]);
    } else {
      setSelectedEmployees(filteredEmployeesForMarkAll.map((emp) => emp.id));
    }
  };

  const handleEmployeeSelection = (employeeId) => {
    setSelectedEmployees((prev) =>
      prev.includes(employeeId)
        ? prev.filter((id) => id !== employeeId)
        : [...prev, employeeId]
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <div
        className={`${
          darkMode ? "bg-[#2C2C2C] text-[#E0E0E0]" : "bg-white text-gray-900"
        } p-6 rounded-lg shadow-xl w-96`}
      >
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Mark All</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X size={24} />
          </button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Mark Type</label>
            <select
              value={markType}
              onChange={(e) => setMarkType(e.target.value)}
              className={`w-full p-2 border rounded ${
                darkMode
                  ? "bg-[#1C1C1C] border-[#3C3C3C]"
                  : "bg-white border-gray-300"
              }`}
            >
              <option value="day">
                Day-wise (All employees for selected day)
              </option>
              <option value="employee">
                Employee-wise (Selected employee for all days)
              </option>
              <option value="all">All (All employees for all days)</option>
            </select>
          </div>

          {markType === "day" && (
            <div>
              <label className="block text-sm font-medium mb-1">
                Select Day
              </label>
              <select
                value={selectedDay}
                onChange={(e) => setSelectedDay(parseInt(e.target.value))}
                className={`w-full p-2 border rounded ${
                  darkMode
                    ? "bg-[#1C1C1C] border-[#3C3C3C]"
                    : "bg-white border-gray-300"
                }`}
              >
                {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(
                  (day) => (
                    <option key={day?.id ?? day} value={day?.id ?? day}>
                      {day?.name ?? day}
                    </option>
                  )
                )}
              </select>
            </div>
          )}

          {markType === "employee" && (
            <div>
              <label className="block text-sm font-medium mb-1">
                Select Employees
              </label>

              {/* Search Input */}
                <div className="relative mb-2">
                <Search
                  className={`absolute left-2 top-1/2 transform -translate-y-1/2 ${
                    darkMode ? "text-gray-400" : "text-gray-400"
                  } w-4 h-4`}
                />
                <input
                  type="search"
                  name="att_markall_filter_q"
                  id="admin-attendance-markall-filter"
                  role="searchbox"
                  autoComplete="off"
                  autoCorrect="off"
                  spellCheck={false}
                  inputMode="search"
                  data-lpignore="true"
                  data-1p-ignore="true"
                  data-form-type="other"
                  readOnly={markAllSearchReadonlyBarrier}
                  onFocus={() => setMarkAllSearchReadonlyBarrier(false)}
                  placeholder="Search employees..."
                  value={employeeSearchTerm}
                  onChange={(e) => setEmployeeSearchTerm(e.target.value)}
                  className={`w-full pl-8 pr-3 py-2 border rounded text-sm ${
                    darkMode
                      ? "bg-[#1C1C1C] border-[#3C3C3C] text-[#E0E0E0]"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                />
              </div>

              {/* Select All Checkbox */}
              <div className="mb-2">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={
                      selectedEmployees.length ===
                        filteredEmployeesForMarkAll.length &&
                      filteredEmployeesForMarkAll.length > 0
                    }
                    onChange={handleSelectAllEmployees}
                    className="rounded border-gray-300 text-redhalo-red focus:ring-redhalo-red/35"
                  />
                  <span className="text-sm font-medium">
                    Select All ({filteredEmployeesForMarkAll.length} employees)
                  </span>
                </label>
              </div>

              {/* Employee List - taller so more employees visible */}
              <div className="max-h-56 overflow-y-auto border rounded p-2">
                {filteredEmployeesForMarkAll.length === 0 ? (
                  <p
                    className={`text-sm text-center ${
                      darkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    No employees found
                  </p>
                ) : (
                  filteredEmployeesForMarkAll.map((employee) => (
                    <label
                      key={employee.id}
                      className="flex items-center space-x-2 p-1 hover:bg-gray-100 text-sm"
                    >
                      <input
                        type="checkbox"
                        checked={selectedEmployees.includes(employee.id)}
                        onChange={() => handleEmployeeSelection(employee.id)}
                        className="rounded border-gray-300 text-redhalo-red focus:ring-redhalo-red/35"
                      />
                      <span className="flex-1">
                        {employee.user?.name || employee.name} (
                        {employee.empCode || employee.employeeId})
                      </span>
                    </label>
                  ))
                )}
              </div>

              {selectedEmployees.length > 0 && (
                <div
                  className={`mt-2 p-2 rounded text-sm ${
                    darkMode
                      ? "bg-redhalo-navy text-redhalo-mint"
                      : "rh-accent-soft"
                  }`}
                >
                  Selected: {selectedEmployees.length} employee(s)
                </div>
              )}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium mb-1">Status</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className={`w-full p-2 border rounded ${
                darkMode
                  ? "bg-[#1C1C1C] border-[#3C3C3C]"
                  : "bg-white border-gray-300"
              }`}
            >
              {attendanceStatuses.map((status) => (
                <option key={status} value={status}>
                  {status} - {statusDefinitions[status]}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-start gap-2">
            <input
              type="checkbox"
              id="mark-all-override-wo"
              checked={overrideWeeklyOff}
              onChange={(e) => setOverrideWeeklyOff(e.target.checked)}
              className="mt-1 rounded border-gray-300 text-redhalo-red focus:ring-redhalo-red/35"
            />
            <label
              htmlFor="mark-all-override-wo"
              className={`text-sm ${darkMode ? "text-[#E0E0E0]" : "text-gray-700"}`}
            >
              Override weekly off policies (applies to WO/PH days too;)
            </label>
          </div>

          <div
            className={`p-3 rounded ${
              darkMode ? "bg-gray-700" : "bg-gray-100"
            }`}
          >
            <p className="text-sm font-medium mb-1">Preview:</p>
            <p className="text-sm">
              {markType === "day" &&
                `Mark ${status} for day ${selectedDay} for all employees`}
              {markType === "employee" &&
                selectedEmployees.length > 0 &&
                `Mark ${status} for all days for ${selectedEmployees.length} selected employee(s)`}
              {markType === "employee" &&
                selectedEmployees.length === 0 &&
                `Select employees to mark ${status} for all days`}
              {markType === "all" &&
                `Mark ${status} for all days for all employees`}
              {overrideWeeklyOff && " (including weekly off & holiday days)"}
            </p>
          </div>
        </div>
        <div className="mt-6 flex justify-between items-center gap-2">
          <button
            type="button"
            onClick={handleClearAll}
            className={`px-4 py-2 rounded-lg font-medium ${
              darkMode
                ? "bg-[#4C4C4C] text-[#E0E0E0] hover:bg-[#5C5C5C]"
                : "bg-gray-200 text-gray-800 hover:bg-gray-300"
            }`}
          >
            Clear All
          </button>
          <div className="flex space-x-2">
            <button
              type="button"
              onClick={onClose}
              className={`px-4 py-2 rounded-lg font-medium ${
                darkMode
                  ? "bg-[#4C4C4C] text-[#E0E0E0] hover:bg-[#5C5C5C]"
                  : "bg-gray-200 text-gray-800 hover:bg-gray-300"
              }`}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleMarkAll}
              disabled={
                markType === "employee" && selectedEmployees.length === 0
              }
              className="px-4 py-2 rounded-lg font-medium rh-btn-primary hover:opacity-95 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-redhalo-red/50 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-[#1C1C1C]"
            >
              Mark All
            </button>
          </div>
        </div>
      </div>
      <ConfirmationModal
        isOpen={confirmationModal.isOpen}
        onClose={closeConfirmation}
        onConfirm={confirmationModal.onConfirm}
        title={confirmationModal.title}
        message={confirmationModal.message}
        darkMode={darkMode}
      />
    </div>
  );
};

const UploadPopup = ({ darkMode, onClose, onUpload, onDownloadTemplate, selectedMonth, selectedYear }) => {
  const currentMonth = selectedMonth ?? new Date().getMonth();
  const currentYear = selectedYear ?? new Date().getFullYear();
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [importMonth, setImportMonth] = useState(currentMonth);
  const [importYear, setImportYear] = useState(currentYear);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState("");

  // Keep import month/year in sync with the main DatePicker (selected month/year)
  useEffect(() => {
    setImportMonth(currentMonth);
    setImportYear(currentYear);
  }, [currentMonth, currentYear]);

  const handleFileChange = (e) => {
    setSelectedFile(e.target.files[0]);
    setError(null);
  };

  const handleUpload = async () => {
    if (selectedFile) {
      try {
        setUploading(true);
        setError(null);
        setUploadProgress(0);
        setUploadStatus("Preparing file...");
        // Send month as 0-indexed (caller will convert as needed) and year
        await onUpload(selectedFile, importYear, importMonth, {
          onProgress: (progress, status) => {
            setUploadProgress(progress);
            setUploadStatus(status);
          },
        });
        onClose();
      } catch (err) {
        setError(err.message || "Failed to upload file");
      } finally {
        setUploading(false);
        setUploadProgress(0);
        setUploadStatus("");
      }
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      // Pass the selected month and year to the download handler
      // Month is 0-indexed from the state, so pass as-is
      await onDownloadTemplate(importMonth, importYear);
    } catch (err) {
      setError(err.message || "Failed to download template");
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <div
        className={`${
          darkMode ? "bg-[#2C2C2C] text-[#E0E0E0]" : "bg-white text-gray-900"
        } p-6 rounded-lg shadow-xl w-96`}
      >
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Upload Attendance</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X size={24} />
          </button>
        </div>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1">
                Import Month *
              </label>
              <select
                value={importMonth}
                onChange={(e) => setImportMonth(parseInt(e.target.value))}
                className={`w-full p-2 border rounded ${
                  darkMode
                    ? "bg-[#1C1C1C] border-[#3C3C3C]"
                    : "bg-white border-gray-300"
                }`}
              >
                {Array.from({ length: 12 }, (_, i) => (
                  <option key={i} value={i}>
                    {new Date(0, i).toLocaleDateString("en-US", {
                      month: "long",
                    })}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                Import Year *
              </label>
              <select
                value={importYear}
                onChange={(e) => setImportYear(parseInt(e.target.value))}
                className={`w-full p-2 border rounded ${
                  darkMode
                    ? "bg-[#1C1C1C] border-[#3C3C3C]"
                    : "bg-white border-gray-300"
                }`}
              >
                {Array.from(
                  { length: 6 },
                  (_, idx) => new Date().getFullYear() - 3 + idx
                ).map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">
              Select File
            </label>
            <input
              type="file"
              onChange={handleFileChange}
              accept=".xlsx,.xls,.csv"
              className={`w-full p-2 border rounded ${
                darkMode
                  ? "bg-[#1C1C1C] border-[#3C3C3C]"
                  : "bg-white border-gray-300"
              }`}
            />
          </div>

          <div className="flex justify-center">
            <button
              onClick={handleDownloadTemplate}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg ${
                darkMode
                  ? "rh-btn-primary"
                  : "rh-accent-soft"
              } hover:bg-opacity-80 transition-colors duration-200`}
            >
              <Download size={16} />
              <span>Download Template</span>
            </button>
          </div>
        </div>
        {uploading && (
          <div className="mt-4 space-y-2">
            <div className="flex items-center justify-between">
              <span
                className={`text-sm font-medium ${
                  darkMode ? "text-gray-400" : "text-gray-600"
                }`}
              >
                {uploadStatus}
              </span>
              <span
                className={`text-sm font-semibold ${
                  darkMode ? "text-redhalo-mint" : "text-redhalo-navy"
                }`}
              >
                {uploadProgress}%
              </span>
            </div>
            <div
              className={`w-full h-3 rounded-full overflow-hidden ${
                darkMode ? "bg-gray-700" : "bg-gray-200"
              }`}
            >
              <div
                className="h-full bg-gradient-to-r from-redhalo-orange to-redhalo-red rounded-full transition-all duration-300"
                style={{ width: `${uploadProgress}%` }}
              ></div>
            </div>
          </div>
        )}
        {error && <div className="mt-2 text-red-500 text-sm">{error}</div>}
        <div className="mt-6 flex justify-end space-x-2">
          <button
            onClick={onClose}
            disabled={uploading}
            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleUpload}
            disabled={!selectedFile || uploading}
            className="rh-btn-primary hover:opacity-95 px-4 py-2 rounded disabled:opacity-50"
          >
            {uploading ? "Uploading..." : "Upload"}
          </button>
        </div>
      </div>
    </div>
  );
};

const UploadLeavePopup = ({
  darkMode,
  onClose,
  onUploadLeave,
  onDownloadLeaveTemplate,
  showAlert,
}) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [importMonth, setImportMonth] = useState(new Date().getMonth());
  const [importYear, setImportYear] = useState(new Date().getFullYear());
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState("");
  const [uploadErrors, setUploadErrors] = useState([]);
  const [showErrorDetails, setShowErrorDetails] = useState(false);
  const [successCount, setSuccessCount] = useState(0);

  const handleFileChange = (e) => {
    setSelectedFile(e.target.files[0]);
    setError(null);
  };

  const handleUpload = async () => {
    if (selectedFile) {
      try {
        setUploading(true);
        setError(null);
        setUploadProgress(0);
        setUploadStatus("Preparing file...");
        const result = await onUploadLeave(
          selectedFile,
          importMonth,
          importYear,
          {
            onProgress: (progress, status) => {
              setUploadProgress(progress);
              setUploadStatus(status);
            },
          }
        );
        console.log("result :", result);
        // Check if there were errors
        if (result && result.errorCount > 0) {
          setUploadErrors(result.errors);
          setSuccessCount(result.processedCount);
          setShowErrorDetails(true);
        } else {
          showAlert(
            `✅ Leave details processed successfully for ${
              result?.processedCount || 0
            } entries.`
          );
          onClose();
        }
      } catch (err) {
        setError(err.message || "Failed to upload leave details");
      } finally {
        setUploading(false);
        setUploadProgress(0);
        setUploadStatus("");
      }
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      await onDownloadLeaveTemplate();
    } catch (err) {
      setError(err.message || "Failed to download leave template");
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <div
        className={`${
          darkMode ? "bg-[#2C2C2C] text-[#E0E0E0]" : "bg-white text-gray-900"
        } p-6 rounded-lg shadow-xl w-96`}
      >
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Upload Leave Details</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X size={24} />
          </button>
        </div>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1">
                Import Month *
              </label>
              <select
                value={importMonth}
                onChange={(e) => setImportMonth(parseInt(e.target.value) + 1)}
                className={`w-full p-2 border rounded ${
                  darkMode
                    ? "bg-[#1C1C1C] border-[#3C3C3C]"
                    : "bg-white border-gray-300"
                }`}
              >
                {Array.from({ length: 12 }, (_, i) => (
                  <option key={i} value={i}>
                    {new Date(0, i).toLocaleDateString("en-US", {
                      month: "long",
                    })}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                Import Year *
              </label>
              <select
                value={importYear}
                onChange={(e) => setImportYear(parseInt(e.target.value))}
                className={`w-full p-2 border rounded ${
                  darkMode
                    ? "bg-[#1C1C1C] border-[#3C3C3C]"
                    : "bg-white border-gray-300"
                }`}
              >
                {Array.from({ length: 5 }, (_, i) => {
                  const year = new Date().getFullYear() - 2 + i;
                  return (
                    <option key={year?.id ?? year} value={year?.id ?? year}>
                      {year?.name ?? year}
                    </option>
                  );
                })}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">
              Select File
            </label>
            <input
              type="file"
              onChange={handleFileChange}
              accept=".xlsx,.xls,.csv"
              className={`w-full p-2 border rounded ${
                darkMode
                  ? "bg-[#1C1C1C] border-[#3C3C3C]"
                  : "bg-white border-gray-300"
              }`}
            />
          </div>

          <div className="flex justify-center">
            <button
              onClick={handleDownloadTemplate}
              className={`flex items-center space-x-2 rounded-lg border px-4 py-2 transition-colors duration-200 ${
                darkMode
                  ? "border-redhalo-red/40 bg-redhalo-navy/30 text-redhalo-mint hover:bg-redhalo-navy/50"
                  : "border-redhalo-teal/40 bg-redhalo-mint/50 text-redhalo-navy hover:bg-redhalo-mint/70"
              }`}
            >
              <Download size={16} />
              <span>Download Leave Template</span>
            </button>
          </div>

          <div
            className={`p-3 rounded ${
              darkMode ? "bg-gray-700" : "bg-gray-100"
            }`}
          >
            <p className="text-sm font-medium mb-1">Template Format:</p>
            <ul className="text-xs space-y-1">
              <li>• Employee Code</li>
              <li>• Employee Name</li>
              <li>• Date From (dd/mm/yyyy)</li>
              <li>• Date To (dd/mm/yyyy)</li>
              <li>• Leave Type (PL, CL, EL, CO, CW, LOP, LWP)</li>
              <li>• Leave Period (FD, FH, SH)</li>
            </ul>
            <div className="mt-2 text-xs">
              <p>
                <strong>Leave Period:</strong> FD = Full Day, FH = First Half,
                SH = Second Half
              </p>
            </div>
          </div>
        </div>
        {uploading && (
          <div className="mt-4 space-y-2">
            <div className="flex items-center justify-between">
              <span
                className={`text-sm font-medium ${
                  darkMode ? "text-gray-400" : "text-gray-600"
                }`}
              >
                {uploadStatus}
              </span>
              <span
                className={`text-sm font-semibold ${
                  darkMode ? "text-redhalo-mint" : "text-redhalo-navy"
                }`}
              >
                {uploadProgress}%
              </span>
            </div>
            <div
              className={`w-full h-3 rounded-full overflow-hidden ${
                darkMode ? "bg-gray-700" : "bg-gray-200"
              }`}
            >
              <div
                className="h-full bg-gradient-to-r from-redhalo-orange to-redhalo-red rounded-full transition-all duration-300"
                style={{ width: `${uploadProgress}%` }}
              ></div>
            </div>
          </div>
        )}
        {error && <div className="mt-2 text-red-500 text-sm">{error}</div>}
        <div className="mt-6 flex justify-end space-x-2">
          <button
            onClick={onClose}
            disabled={uploading}
            className={`px-4 py-2 rounded ${
              darkMode
                ? "bg-gray-700 text-[#E0E0E0]"
                : "bg-gray-200 text-gray-900"
            } ${uploading && "opacity-50 cursor-not-allowed"}`}
          >
            Cancel
          </button>
          <button
            onClick={handleUpload}
            disabled={!selectedFile || uploading}
            className={`rh-btn-primary rounded px-4 py-2 ${
              (!selectedFile || uploading) && "cursor-not-allowed opacity-50"
            }`}
          >
            {uploading ? "Uploading..." : "Upload Leave Details"}
          </button>
        </div>
      </div>

      {/* Error Details Modal */}
      {showErrorDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
          <div
            className={`${
              darkMode
                ? "bg-[#2C2C2C] text-[#E0E0E0]"
                : "bg-white text-gray-900"
            } p-6 rounded-lg shadow-xl max-w-2xl w-full max-h-96 overflow-y-auto`}
          >
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">
                ❌ Upload Errors - {uploadErrors.length} issues found
              </h2>
              <button
                onClick={() => setShowErrorDetails(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={24} />
              </button>
            </div>

            <div className="mb-4 p-3 rounded-md bg-emerald-50 text-emerald-900">
              ✅ Successfully processed: <strong>{successCount} entries</strong>
            </div>

            <div className="mb-4 text-sm font-medium">Invalid Rows:</div>

            <div className="space-y-2">
              {uploadErrors.map((error, idx) => (
                <div
                  key={idx}
                  className={`p-3 rounded border-l-4 ${
                    darkMode
                      ? "bg-red-900 bg-opacity-30 border-red-500 text-red-200"
                      : "bg-red-50 border-red-500 text-red-800"
                  }`}
                >
                  <div className="font-semibold text-sm">
                    {error.split(":")[0]}
                  </div>
                  <div className="text-sm mt-1">
                    {error.substring(error.indexOf(":") + 1).trim()}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <button
                onClick={() => setShowErrorDetails(false)}
                className={`px-4 py-2 rounded ${
                  darkMode
                    ? "bg-gray-700 text-white"
                    : "bg-gray-200 text-gray-800"
                }`}
              >
                Close
              </button>
              <button
                onClick={() => {
                  setShowErrorDetails(false);
                  onClose();
                }}
                className={`px-4 py-2 rounded ${
                  darkMode ? "rh-btn-primary" : "rh-btn-primary"
                }`}
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Alert = ({ message, type, onClose }) => {
  return (
    <div
      className={`fixed bottom-4 right-4 p-4 rounded-md shadow-md ${
        type === "success" ? "bg-emerald-500" : "bg-redhalo-red"
      } text-white`}
    >
      {message}
      <button onClick={onClose} className="ml-2 font-bold">
        &times;
      </button>
    </div>
  );
};

// Confirmation Modal Component
const ConfirmationModal = ({
  isOpen,
  onClose,
  onConfirm,
  onCancel,
  title = "Confirm Action",
  message = "Are you sure you want to proceed?",
  confirmText = "Yes",
  cancelText = "No",
  darkMode = false,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={onClose}
      />

      {/* Modal */}
      <div
        className={`relative z-10 w-full max-w-md mx-4 rounded-lg shadow-xl ${
          darkMode
            ? "bg-[#2C2C2C] border border-[#3C3C3C]"
            : "bg-white border border-gray-200"
        }`}
      >
        {/* Header */}
        <div
          className={`px-6 py-4 border-b ${
            darkMode ? "border-[#3C3C3C]" : "border-gray-200"
          }`}
        >
          <h3
            className={`text-lg font-semibold ${
              darkMode ? "text-[#E0E0E0]" : "text-gray-900"
            }`}
          >
            {title}
          </h3>
        </div>

        {/* Body */}
        <div className="px-6 py-4">
          <p
            className={`text-sm ${
              darkMode ? "text-gray-400" : "text-gray-600"
            }`}
          >
            {message}
          </p>
        </div>

        {/* Footer */}
        <div
          className={`px-6 py-4 border-t flex justify-end space-x-3 ${
            darkMode ? "border-[#3C3C3C]" : "border-gray-200"
          }`}
        >
          <button
            onClick={() => {
              try {
                if (typeof onCancel === "function") onCancel();
              } catch (e) {
                /* ignore */
              }
              onClose();
            }}
            className={`px-4 py-2 rounded text-sm font-medium border transition-colors ${
              darkMode
                ? "bg-[#2C2C2C] text-[#E0E0E0] border-[#3C3C3C] hover:bg-[#3C3C3C]"
                : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
            }`}
          >
            {cancelText}
          </button>
          <button
            onClick={onConfirm}
            className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
              darkMode
                ? "rh-btn-primary hover:opacity-95"
                : "rh-btn-primary hover:opacity-95"
            }`}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

const LEAVE_DURATION_LABELS = {
  full_day: "Full Day",
  first_half: "First Half",
  second_half: "Second Half",
};

const LeaveRequestReviewModal = ({
  darkMode,
  row,
  onClose,
  onApprove,
  onReject,
  onCancel,
}) => {
  if (!row) return null;
  const fd = row.formData || {};
  const isPending = String(row.status || "").toLowerCase() === "pending";
  const inputCls = darkMode
    ? "mt-1 w-full rounded-md border border-[#3C3C4C] bg-[#1D1D1D] px-2 py-1.5 text-sm text-gray-100"
    : "mt-1 w-full rounded-md border border-gray-300 bg-gray-50 px-2 py-1.5 text-sm text-gray-800";

  return (
    <div className="fixed inset-0 z-[10020] flex items-center justify-center bg-black/45 p-4">
      <div
        className={`w-full max-w-xl rounded-lg border shadow-xl ${
          darkMode ? "border-[#3C3C4C] bg-[#121212] text-gray-100" : "border-gray-200 bg-white text-black"
        }`}
      >
        <div className={`flex items-center justify-between border-b px-4 py-3 ${darkMode ? "border-[#3C3C4C]" : "border-gray-200"}`}>
          <div>
            <h3 className={`text-sm font-semibold ${darkMode ? "text-gray-100" : "text-gray-900"}`}>
              Leave Request
            </h3>
            <p className={`mt-0.5 text-xs ${darkMode ? "text-gray-400" : "text-gray-600"}`}>
              {row.employeeName || "-"} (Code: {row.employeeCode || "-"})
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className={`rounded-md border px-2 py-1 text-xs ${darkMode ? "border-[#3C3C4C] text-gray-200 hover:bg-[#1a1a1a]" : "border-gray-200 text-gray-700 hover:bg-gray-50"}`}
          >
            Close
          </button>
        </div>

        <div className="space-y-3 p-4">
          <div className="grid grid-cols-2 gap-3">
            <label className={`text-xs ${darkMode ? "text-gray-300" : "text-gray-700"}`}>
              Leave Type
              <input readOnly value={fd.leaveType || "-"} className={inputCls} />
            </label>
            <label className={`text-xs ${darkMode ? "text-gray-300" : "text-gray-700"}`}>
              Leave Duration
              <input
                readOnly
                value={LEAVE_DURATION_LABELS[fd.leaveDuration] || fd.leaveDuration || "-"}
                className={inputCls}
              />
            </label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className={`text-xs ${darkMode ? "text-gray-300" : "text-gray-700"}`}>
              From Date
              <input readOnly value={fd.fromDate || "-"} className={inputCls} />
            </label>
            <label className={`text-xs ${darkMode ? "text-gray-300" : "text-gray-700"}`}>
              To Date
              <input readOnly value={fd.toDate || "-"} className={inputCls} />
            </label>
          </div>
          <label className={`block text-xs ${darkMode ? "text-gray-300" : "text-gray-700"}`}>
            Reason
            <textarea readOnly rows={3} value={fd.reason || ""} className={inputCls} />
          </label>
          {fd.notes ? (
            <label className={`block text-xs ${darkMode ? "text-gray-300" : "text-gray-700"}`}>
              Notes
              <textarea readOnly rows={2} value={fd.notes} className={inputCls} />
            </label>
          ) : null}
          <div className={`text-xs ${darkMode ? "text-gray-400" : "text-gray-600"}`}>
            Status: <span className="font-semibold">{row.status || "Pending"}</span>
            {" · "}
            Pending at: <span className="font-semibold">{row.pendingAt || "-"}</span>
            {row.submittedAt ? (
              <>
                {" · "}
                Submitted: <span className="font-semibold">{row.submittedAt}</span>
              </>
            ) : null}
          </div>

          {Array.isArray(row.approvals) && row.approvals.length > 0 && (
            <div className={`rounded-md border p-3 ${darkMode ? "border-[#3C3C4C]" : "border-gray-200"}`}>
              <p className={`mb-2 text-xs font-semibold uppercase ${darkMode ? "text-gray-300" : "text-gray-700"}`}>
                Approval trail
              </p>
              <div className="space-y-2">
                {row.approvals.map((step) => (
                  <div key={`${step.stepOrder}-${step.role}`} className={`text-xs ${darkMode ? "text-gray-300" : "text-gray-700"}`}>
                    Step {step.stepOrder}: {step.roleLabel || step.role} —{" "}
                    <span className="font-semibold capitalize">{step.action || "pending"}</span>
                    {step.approverName ? ` by ${step.approverName}` : ""}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className={`rounded-md border px-3 py-1.5 text-xs font-medium ${
                darkMode
                  ? "border-[#3C3C4C] bg-[#1D1D1D] text-gray-200 hover:bg-[#2a2a2a]"
                  : "border-gray-300 bg-gray-100 text-gray-800 hover:bg-gray-200"
              }`}
            >
              Close
            </button>
            {isPending && (row.canAct !== false) && (
              <>
                <button
                  type="button"
                  onClick={() => onCancel(row)}
                  className={`rounded-md border px-3 py-1.5 text-xs font-medium ${
                    darkMode
                      ? "border-[#3C3C4C] bg-[#1D1D1D] text-gray-200 hover:bg-[#2a2a2a]"
                      : "border-gray-300 bg-gray-100 text-gray-800 hover:bg-gray-200"
                  }`}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => onReject(row)}
                  className="rounded-md border border-rose-300 bg-rose-50 px-3 py-1.5 text-xs font-medium text-rose-800 hover:bg-rose-100"
                >
                  Reject
                </button>
                <button
                  type="button"
                  onClick={() => onApprove(row)}
                  className="rounded-md border border-redhalo-red bg-redhalo-red px-3 py-1.5 text-xs font-medium text-white hover:opacity-95"
                >
                  Approve
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const getEmployeeDbId = (employee) => {
  if (!employee) return null;
  const raw = employee.id ?? employee.employeeId;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
};

const getEmployeeGender = (employee) =>
  employee?.user?.gender || employee?.gender || "";

const filterLeaveTypesForEmployeeGender = (leaveTypes, employee) =>
  filterLeaveTypesByGender(leaveTypes, getEmployeeGender(employee));

const mergeCompanyLeaveTypesForEmployee = (
  apiLeaveTypes,
  companyLeaveTypes,
  employee
) => {
  const companyById = new Map(
    (companyLeaveTypes || [])
      .filter((leaveType) => leaveType?.id)
      .map((leaveType) => [Number(leaveType.id), leaveType])
  );

  const entitled = (apiLeaveTypes || [])
    .map((leaveType) => {
      if (!leaveType?.id) return null;
      const companyLt = companyById.get(Number(leaveType.id));
      const isActive =
        leaveType.isActive !== undefined
          ? leaveType.isActive
          : companyLt?.isActive !== undefined
            ? companyLt.isActive
            : true;
      if (!isActive) return null;
      return {
        id: leaveType.id,
        name: leaveType.name || leaveType.leaveTypeName || companyLt?.name,
        code: leaveType.code || companyLt?.code,
        genderApplicability:
          leaveType.genderApplicability ?? companyLt?.genderApplicability,
      };
    })
    .filter(Boolean);

  return filterLeaveTypesForEmployeeGender(entitled, employee);
};

// Apply Leave Popup Component
const ApplyLeavePopup = ({
  darkMode,
  isOpen,
  onClose,
  onApplyLeave,
  allEmployees,
  departmentsList,
  branchesList,
  singleEmployee = null,
  leaveTypes = [],
}) => {
  const { mapping: attendanceMapping } = useAttendanceCodeConfig();
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [leaveStatus, setLeaveStatus] = useState("CL");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [leaveReason, setLeaveReason] = useState("");
  const [employeeSearchTerm, setEmployeeSearchTerm] = useState("");
  const [leavePopupSearchReadonlyBarrier, setLeavePopupSearchReadonlyBarrier] =
    useState(true);
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [selectedBranches, setSelectedBranches] = useState([]);
  const [applyToAll, setApplyToAll] = useState(false);
  const [allEmployeesList, setAllEmployeesList] = useState([]);
  const [loadingAllEmployees, setLoadingAllEmployees] = useState(false);

  // Fetch all employees for the leave popup (not just filtered table employees)
  useEffect(() => {
    if (!isOpen) return; // Only fetch when popup is open

    const fetchAllEmployees = async () => {
      try {
        setLoadingAllEmployees(true);
        // Fetch all employees without pagination or filters
        const response = await employeeService.getEmployees({
          page: 1,
          limit: 10000, // Large limit to get all employees
          excludeEmploymentTypes: "External,external",
        });
        if (response.success && response.employees) {
          setAllEmployeesList(response.employees);
        } else if (response.success && response.data) {
          setAllEmployeesList(response.data);
        } else {
          // Fallback to prop if API fails
          setAllEmployeesList(allEmployees || []);
        }
      } catch (error) {
        console.error("Error fetching all employees for leave popup:", error);
        // Fallback to prop if API fails
        setAllEmployeesList(allEmployees || []);
      } finally {
        setLoadingAllEmployees(false);
      }
    };

    fetchAllEmployees();
  }, [isOpen, allEmployees]);

  // Auto-select employees when departments/branches are selected
  useEffect(() => {
    // Use allEmployeesList if available, otherwise fall back to allEmployees prop
    const employeesToCheck = allEmployeesList.length > 0 ? allEmployeesList : (allEmployees || []);
    if (!employeesToCheck || !Array.isArray(employeesToCheck) || employeesToCheck.length === 0) return;

    // If departments or branches are selected, auto-select matching employees
    if (selectedDepartments.length > 0 || selectedBranches.length > 0) {
      const employeesToSelect = employeesToCheck
        .filter((employee) => {
          const departmentMatch =
            selectedDepartments.length === 0 ||
            selectedDepartments.includes(employee.departmentId);
          const branchMatch =
            selectedBranches.length === 0 ||
            selectedBranches.includes(employee.branchId);
          return departmentMatch && branchMatch;
        })
        .map((emp) => emp.id);

      // Auto-select employees from selected departments/branches
      // This replaces the current selection to match the selected departments/branches
      setSelectedEmployees(employeesToSelect);
    }
    // Note: We don't clear selection when departments/branches are cleared
    // to allow users to manually select employees even after clearing filters
  }, [selectedDepartments, selectedBranches, allEmployeesList, allEmployees]);

  // Generate leave status options from policy-created leave types only (exclude LOP, WO, PH)
  const leaveStatusOptions = useMemo(() => {
    const excludeCodes = new Set(["LOP", "WO", "PH"]);
    const seen = new Set();
    const combined = [];

    const applicableLeaveTypes = singleEmployee
      ? filterLeaveTypesForEmployeeGender(leaveTypes, singleEmployee)
      : (leaveTypes || []).filter((lt) => {
          const applicability = String(
            lt?.genderApplicability || "all"
          ).toLowerCase();
          if (applicability === "female" || applicability === "male") {
            return false;
          }
          if (isFemaleOnlyLeaveType(lt) || isPaternityLeaveType(lt)) {
            return false;
          }
          return true;
        });

    if (applicableLeaveTypes.length > 0) {
      applicableLeaveTypes.forEach((lt) => {
        const isActive = lt.isActive !== undefined ? lt.isActive : true;
        if (isActive !== true) return;

        let code = (lt.code || lt.shortCode || lt.id || "").toUpperCase();
        if (!code || code === "PR") return;
        if (excludeCodes.has(code)) return;

        const mappedCode =
          attendanceMapping && attendanceMapping[code]
            ? attendanceMapping[code]
            : code;
        if (!seen.has(mappedCode)) {
          combined.push(mappedCode);
          seen.add(mappedCode);
        }
      });
    }

    return combined;
  }, [leaveTypes, attendanceMapping, singleEmployee]);

  // Handle multiple department selection
  const handleDepartmentSelection = (departmentId) => {
    setSelectedDepartments((prev) =>
      prev.includes(departmentId)
        ? prev.filter((id) => id !== departmentId)
        : [...prev, departmentId]
    );
  };

  // Handle multiple branch selection
  const handleBranchSelection = (branchId) => {
    setSelectedBranches((prev) =>
      prev.includes(branchId)
        ? prev.filter((id) => id !== branchId)
        : [...prev, branchId]
    );
  };

  // Handle select all departments
  const handleSelectAllDepartments = () => {
    const allDepartmentIds = (
      departmentsList && Array.isArray(departmentsList) ? departmentsList : []
    ).map((dept) => dept.id);
    setSelectedDepartments((prev) =>
      prev.length === allDepartmentIds.length ? [] : allDepartmentIds
    );
  };

  // Handle select all branches
  const handleSelectAllBranches = () => {
    const allBranchIds = (
      branchesList && Array.isArray(branchesList) ? branchesList : []
    ).map((branch) => branch.id);
    setSelectedBranches((prev) =>
      prev.length === allBranchIds.length ? [] : allBranchIds
    );
  };

  // Filter employees based on search term only (for display)
  // When nothing is selected, show all employees for local search
  // Use allEmployeesList if available, otherwise fall back to allEmployees prop
  const employeesForDisplay = allEmployeesList.length > 0 ? allEmployeesList : (allEmployees || []);
  const filteredEmployeesForLeave = employeesForDisplay.filter((employee) => {
    // If no search term, show all employees
    if (!employeeSearchTerm) return true;

    const empName = employee.user?.name || employee.name || "";
    const empCode = employee.empCode || employee.employeeId || "";
    const mobile = employee.user?.phone || "";
    const email = employee.email || "";

    const searchMatch =
      empName.toLowerCase().includes(employeeSearchTerm.toLowerCase()) ||
      empCode.toLowerCase().includes(employeeSearchTerm.toLowerCase()) ||
      mobile.toLowerCase().includes(employeeSearchTerm.toLowerCase()) ||
      email.toLowerCase().includes(employeeSearchTerm.toLowerCase());

    return searchMatch;
  });

  const handleApplyLeave = () => {
    if (!fromDate || !toDate) {
      toast.error("Please fill all required fields");
      return;
    }

    // For single employee, no need to check selectedEmployees
    if (
      !singleEmployee &&
      !applyToAll &&
      selectedEmployees.length === 0 &&
      selectedDepartments.length === 0 &&
      selectedBranches.length === 0
    ) {
      toast.error("Please select at least one employee, department, or branch");
      return;
    }

    const from = new Date(fromDate);
    const to = new Date(toDate);

    if (from > to) {
      toast.error("From date cannot be after To date");
      return;
    }

    // Calculate employees to apply leave to and validate join/relieved dates
    let employeesToApply = [];
    let employeesForValidation = [];

    if (singleEmployee) {
      console.log("Single employee mode. singleEmployee:", singleEmployee);
      employeesToApply = [singleEmployee.id];
      employeesForValidation = [singleEmployee];
    } else if (applyToAll) {
      employeesToApply = filteredEmployeesForLeave.map((emp) => emp.id);
      employeesForValidation = filteredEmployeesForLeave;
    } else {
      // Only use employees that are actually checked in selectedEmployees
      // The auto-selection from departments/branches is already reflected in selectedEmployees
      // If user unchecks an employee, it should not be included
      const employeesForDisplay = allEmployeesList.length > 0 ? allEmployeesList : (allEmployees || []);
      const selectedEmpObjs = employeesForDisplay.filter((emp) =>
        selectedEmployees.includes(emp.id)
      );

      employeesToApply = selectedEmpObjs.map((emp) => emp.id);
      employeesForValidation = selectedEmpObjs;

      // Debug logging
      console.log("Leave Application Debug:", {
        selectedEmployees: selectedEmployees.length,
        selectedDepartments: selectedDepartments.length,
        selectedBranches: selectedBranches.length,
        totalEmployeesToApply: employeesToApply.length,
      });
    }

    // Validate leave dates against employee join and relieved dates
    const invalidEmployees = [];
    console.log("Employees for validation:", employeesForValidation);
    console.log("Total employees to validate:", employeesForValidation.length);

    employeesForValidation.forEach((emp, index) => {
      console.log(`Processing employee ${index}:`, emp);

      if (!emp) {
        console.log(`Employee at index ${index} is null or undefined`);
        return;
      }

      // Try both field names: joinDate (from filtered) or joiningDate (from full employee)
      const joinDateValue = emp.joinDate || emp.joiningDate;
      const exitDateValue = emp.exitDate || emp.relievingDate;

      const empJoinDate = joinDateValue ? new Date(joinDateValue) : null;
      const empExitDate = exitDateValue ? new Date(exitDateValue) : null;

      const empName =
        emp.name || emp.user?.name || emp.empCode || emp.code || "";

      console.log(
        "Validating employee:",
        empName,
        "Join Date string:",
        joinDateValue,
        "Parsed Join Date:",
        empJoinDate,
        "From Date:",
        from
      );

      if (empJoinDate && from < empJoinDate) {
        invalidEmployees.push(
          `${empName}: Leave start date (${formatDate(from)}) is before joining date (${formatDate(empJoinDate)})`
        );
      }
      if (empExitDate && to > empExitDate) {
        invalidEmployees.push(
          `${empName}: Leave end date (${formatDate(to)}) is after relieving date (${formatDate(empExitDate)})`
        );
      }
    });

    console.log("Invalid employees found:", invalidEmployees);
    if (invalidEmployees.length > 0) {
      const errorMsg =
        "Cannot apply leaves for the following reasons:\n\n" +
        invalidEmployees.slice(0, 5).join("\n") +
        (invalidEmployees.length > 5
          ? `\n...and ${invalidEmployees.length - 5} more issues`
          : "");
      console.log("Showing error toast:", errorMsg);
      // Show error toast instead of alert
      toast.error(errorMsg, {
        duration: 5000,
        style: {
          maxWidth: "500px",
          whiteSpace: "pre-line",
        },
      });
      return;
    }

    onApplyLeave({
      employeeIds: employeesToApply,
      leaveStatus,
      fromDate,
      toDate,
      leaveReason,
      departments: selectedDepartments,
      branches: selectedBranches,
    });

    // Reset form
    setSelectedEmployees([]);
    setFromDate("");
    setToDate("");
    setLeaveReason("");
    setEmployeeSearchTerm("");
    setSelectedDepartments([]);
    setSelectedBranches([]);
    setApplyToAll(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div
        className={`${
          darkMode ? "bg-[#2C2C2C] text-[#E0E0E0]" : "bg-white text-gray-900"
        } rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col`}
      >
        {/* Header - Fixed */}
        <div className="flex justify-between items-center p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-bold">
            {singleEmployee
              ? `Apply Leave - ${singleEmployee.name}`
              : "Apply Leave"}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <X size={24} />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-4 pb-24">

        <div className="mb-4">
          <p
            className={`text-sm ${
              darkMode ? "text-gray-300" : "text-gray-600"
            }`}
          >
            {singleEmployee
              ? `Apply leave for ${singleEmployee.name} (${singleEmployee.code}) with date range selection. PR (Present) is excluded as it's the default status.`
              : "Apply leaves for multiple employees with date range selection. Select employees individually, by department/branch, or apply to all filtered employees. PR (Present) is excluded as it's the default status."}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
          <div>
            <label className="block text-xs font-medium mb-1">
              From Date *
            </label>
            <DatePicker
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              required
              darkMode={darkMode}
              label="From Date"
              className={`w-full p-1 border rounded text-xs ${
                darkMode
                  ? "bg-gray-700 border-gray-600"
                  : "bg-white border-gray-300"
              }`}
            />
          </div>

          <div>
            <label className="block text-xs font-medium mb-1">To Date *</label>
            <DatePicker
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              required
              darkMode={darkMode}
              label="To Date"
              className={`w-full p-1 border rounded text-xs ${
                darkMode
                  ? "bg-gray-700 border-gray-600"
                  : "bg-white border-gray-300"
              }`}
            />
          </div>

          <div>
            <label className="block text-xs font-medium mb-1">
            Select Leave Type *
            </label>
            <select
              value={leaveStatus}
              onChange={(e) => setLeaveStatus(e.target.value)}
              className={`w-full p-1 border rounded text-xs ${
                darkMode
                  ? "bg-gray-700 border-gray-600"
                  : "bg-white border-gray-300"
              }`}
            >
              {leaveStatusOptions.map((status) => (
                <option key={status} value={status}>
                  {status} - {statusDefinitions[status]}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium mb-1">Reason</label>
            <input
              type="text"
              value={leaveReason}
              onChange={(e) => setLeaveReason(e.target.value)}
              placeholder="Leave reason (optional)"
              className={`w-full p-1 border rounded text-xs ${
                darkMode
                  ? "bg-gray-700 border-gray-600"
                  : "bg-white border-gray-300"
              }`}
            />
          </div>
        </div>

        {/* Bulk Selection Options - Only show for bulk selection */}
        {!singleEmployee && (
          <div className="mb-4">
            <h3
              className={`text-sm font-medium mb-2 ${
                darkMode ? "text-white" : "text-gray-800"
              }`}
            >
              Bulk Selection Options
            </h3>
            <p
              className={`text-xs mb-3 ${
                darkMode ? "text-gray-400" : "text-gray-500"
              }`}
            >
              Select departments/branches to apply leave to all employees in
              those groups, or use individual employee selection below.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-medium mb-1">
                  Departments
                </label>
                <div
                  className={`border rounded p-2 max-h-32 overflow-y-auto ${
                    darkMode
                      ? "bg-gray-700 border-gray-600"
                      : "bg-white border-gray-300"
                  }`}
                >
                  <div className="mb-2">
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={
                          selectedDepartments.length ===
                            (departmentsList && Array.isArray(departmentsList)
                              ? departmentsList.length
                              : 0) &&
                          (departmentsList && Array.isArray(departmentsList)
                            ? departmentsList.length
                            : 0) > 0
                        }
                        onChange={handleSelectAllDepartments}
                        className="rounded border-gray-300 text-redhalo-red focus:ring-redhalo-red/35"
                      />
                      <span className="text-xs font-medium">Select All</span>
                    </label>
                  </div>
                  {(departmentsList && Array.isArray(departmentsList)
                    ? departmentsList
                    : []
                  ).map((dept) => (
                    <label
                      key={dept.id}
                      className="flex items-center space-x-2 p-1 hover:bg-gray-100 text-xs"
                    >
                      <input
                        type="checkbox"
                        checked={selectedDepartments.includes(dept.id)}
                        onChange={() => handleDepartmentSelection(dept.id)}
                        className="rounded border-gray-300 text-redhalo-red focus:ring-redhalo-red/35"
                      />
                      <span>{dept.name}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium mb-1">
                  Branches
                </label>
                <div
                  className={`border rounded p-2 max-h-32 overflow-y-auto ${
                    darkMode
                      ? "bg-gray-700 border-gray-600"
                      : "bg-white border-gray-300"
                  }`}
                >
                  <div className="mb-2">
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={
                          selectedBranches.length ===
                            (branchesList && Array.isArray(branchesList)
                              ? branchesList.length
                              : 0) &&
                          (branchesList && Array.isArray(branchesList)
                            ? branchesList.length
                            : 0) > 0
                        }
                        onChange={handleSelectAllBranches}
                        className="rounded border-gray-300 text-redhalo-red focus:ring-redhalo-red/35"
                      />
                      <span className="text-xs font-medium">Select All</span>
                    </label>
                  </div>
                  {(branchesList && Array.isArray(branchesList)
                    ? branchesList
                    : []
                  ).map((branch) => (
                    <label
                      key={branch.id}
                      className="flex items-center space-x-2 p-1 hover:bg-gray-100 text-xs"
                    >
                      <input
                        type="checkbox"
                        checked={selectedBranches.includes(branch.id)}
                        onChange={() => handleBranchSelection(branch.id)}
                        className="rounded border-gray-300 text-redhalo-red focus:ring-redhalo-red/35"
                      />
                      <span>{branch.name}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="flex items-center">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={applyToAll}
                    onChange={(e) => setApplyToAll(e.target.checked)}
                    className="rounded border-gray-300 text-redhalo-red focus:ring-redhalo-red/35"
                  />
                  <span className="text-xs">
                    Apply to all filtered employees
                  </span>
                </label>
              </div>
            </div>
          </div>
        )}

        {/* Employee Search and Selection - Only show for bulk selection */}
        {!singleEmployee && (
          <div className="mb-4">
            <label className="block text-xs font-medium mb-1">
              Search Employees
            </label>
            <div className="relative mb-2">
              <Search
                className={`absolute left-2 top-1/2 transform -translate-y-1/2 ${
                  darkMode ? "text-gray-400" : "text-gray-400"
                } w-3 h-3`}
              />
              <input
                type="search"
                name="att_bulk_leave_filter_q"
                id="admin-attendance-bulk-leave-filter"
                role="searchbox"
                autoComplete="off"
                autoCorrect="off"
                spellCheck={false}
                inputMode="search"
                data-lpignore="true"
                data-1p-ignore="true"
                data-form-type="other"
                readOnly={leavePopupSearchReadonlyBarrier}
                onFocus={() => setLeavePopupSearchReadonlyBarrier(false)}
                placeholder="Search by employee code, name, or mobile number"
                value={employeeSearchTerm}
                onChange={(e) => setEmployeeSearchTerm(e.target.value)}
                className={`w-full pl-6 pr-2 py-1 border rounded text-xs ${
                  darkMode
                    ? "bg-[#2C2C2C] border-[#3C3C3C] text-[#E0E0E0]"
                    : "bg-white border-gray-300 text-gray-900"
                }`}
              />
            </div>

            {!applyToAll && (
              <>
                <label className="block text-xs font-medium mb-1">
                  Select Employees *
                </label>
                <div className="max-h-40 overflow-y-auto border rounded p-1">
                  {filteredEmployeesForLeave.map((employee) => (
                    <label
                      key={employee.id}
                      className="flex items-center space-x-1 p-1 hover:bg-gray-100 text-xs"
                    >
                      <input
                        type="checkbox"
                        checked={selectedEmployees.includes(employee.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedEmployees([
                              ...selectedEmployees,
                              employee.id,
                            ]);
                          } else {
                            setSelectedEmployees(
                              selectedEmployees.filter(
                                (id) => id !== employee.id
                              )
                            );
                          }
                        }}
                        className="rounded border-gray-300 text-redhalo-red focus:ring-redhalo-red/35"
                      />
                      <span>
                        {employee.user?.name || employee.name} (
                        {employee.empCode || employee.employeeId})
                      </span>
                    </label>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        </div>

        {/* Sticky Action Buttons */}
        <div className={`sticky bottom-0 left-0 right-0 z-10 px-4 py-3 border-t ${
          darkMode 
            ? "bg-[#2C2C2C] border-gray-700" 
            : "bg-white border-gray-200"
        } flex justify-end space-x-3`}>
          <button
            onClick={onClose}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              darkMode
                ? "bg-gray-700 text-gray-200 hover:bg-gray-600"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            Cancel
          </button>
          <button
            onClick={handleApplyLeave}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              darkMode
                ? "rh-btn-primary hover:opacity-95"
                : "rh-btn-primary hover:opacity-95"
            }`}
          >
            Apply Leave
          </button>
        </div>
      </div>
    </div>
  );
};

const formatLeaveDays = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0";
  return Number.isInteger(n) ? String(n) : n.toFixed(2);
};

const formatAdjustmentDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

// Leave Adjustment Popup: debit/credit leave for an employee with history
const LeaveAdjustmentPopup = ({
  darkMode,
  isOpen,
  onClose,
  employee,
  leaveTypes = [],
  attendanceStructureService,
  onSuccess,
}) => {
  const [selectedLeaveTypeId, setSelectedLeaveTypeId] = useState("");
  const [adjustmentType, setAdjustmentType] = useState("credit");
  const [days, setDays] = useState("");
  const [remarks, setRemarks] = useState("");
  const [saving, setSaving] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyList, setHistoryList] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [eligibleLeaveTypes, setEligibleLeaveTypes] = useState([]);
  const [employeeLeaveGroups, setEmployeeLeaveGroups] = useState([]);
  const [entitlementLoading, setEntitlementLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setSelectedLeaveTypeId("");
      setAdjustmentType("credit");
      setDays("");
      setRemarks("");
      setShowHistoryModal(false);
      setHistoryList([]);
      setEligibleLeaveTypes([]);
      setEmployeeLeaveGroups([]);
    }
  }, [isOpen]);

  const employeeDbId = getEmployeeDbId(employee);

  useEffect(() => {
    if (!isOpen || !employeeDbId || !attendanceStructureService?.getEmployeeLeaveTypes) {
      return;
    }
    let cancelled = false;
    setEntitlementLoading(true);
    attendanceStructureService
      .getEmployeeLeaveTypes(employeeDbId)
      .then((res) => {
        if (cancelled) return;
        const list = Array.isArray(res) ? res : res?.data ?? [];
        setEligibleLeaveTypes(list);
        setEmployeeLeaveGroups(res?.leaveGroups ?? []);
      })
      .catch(() => {
        if (!cancelled) {
          setEligibleLeaveTypes([]);
          setEmployeeLeaveGroups([]);
        }
      })
      .finally(() => {
        if (!cancelled) setEntitlementLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, employeeDbId, employee, attendanceStructureService]);

  const leaveTypeOptions = useMemo(
    () =>
      mergeCompanyLeaveTypesForEmployee(
        eligibleLeaveTypes,
        leaveTypes,
        employee
      ),
    [eligibleLeaveTypes, leaveTypes, employee]
  );

  const handleSave = async () => {
    const leaveTypeId = selectedLeaveTypeId ? Number(selectedLeaveTypeId) : null;
    const daysNum = Number(days);
    if (!employeeDbId || !leaveTypeId || !Number.isFinite(daysNum) || daysNum <= 0) return;
    setSaving(true);
    try {
      const result = await attendanceStructureService.createLeaveAdjustment({
        employeeId: employeeDbId,
        leaveTypeId,
        type: adjustmentType,
        days: daysNum,
        remarks: remarks.trim() || undefined,
      });
      if (!result?.success && !result?.data) {
        throw new Error(result?.message || "Adjustment was not saved");
      }
      toast.success(
        adjustmentType === "credit"
          ? `${daysNum} day(s) credited — open Leave Balance to see Adjusted (Cr)`
          : `${daysNum} day(s) debited — open Leave Balance to see Adjusted (Dr)`
      );
      onSuccess?.({ employeeId: employeeDbId, leaveTypeId, type: adjustmentType, days: daysNum });
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Failed to save leave adjustment");
    } finally {
      setSaving(false);
    }
  };

  const loadHistory = useCallback(async () => {
    if (!employeeDbId || !attendanceStructureService?.getLeaveAdjustmentHistory) return;
    setHistoryLoading(true);
    try {
      const data = await attendanceStructureService.getLeaveAdjustmentHistory(employeeDbId);
      setHistoryList(Array.isArray(data) ? data : data?.list ?? data?.history ?? []);
    } catch {
      setHistoryList([]);
    } finally {
      setHistoryLoading(false);
    }
  }, [employeeDbId, attendanceStructureService]);

  const openHistory = () => {
    setShowHistoryModal(true);
    loadHistory();
  };

  if (!isOpen) return null;
  const canSave = selectedLeaveTypeId && days && Number(days) > 0;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div
        className={`rounded-lg shadow-xl w-full max-w-md flex flex-col max-h-[90vh] ${
          darkMode ? "bg-[#2C2C2C] text-[#E0E0E0]" : "bg-white text-gray-900"
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={`flex items-center justify-between px-4 py-3 border-b ${darkMode ? "border-[#3C3C3C]" : "border-gray-200"}`}>
          <h2 className="text-lg font-semibold">Leave Adjustment</h2>
          <button onClick={onClose} className={`p-1 rounded ${darkMode ? "hover:bg-[#3C3C3C]" : "hover:bg-gray-100"}`}>
            <X size={20} />
          </button>
        </div>
        {employee && (
          <p className={`px-4 py-2 text-sm ${darkMode ? "text-gray-400" : "text-gray-600"}`}>
            {employee.employeeName || employee.name || `Employee #${employee.id}`}
            {employeeLeaveGroups.length > 0 && (
              <span className={`block text-xs mt-1 ${darkMode ? "text-gray-500" : "text-gray-500"}`}>
                Leave groups: {employeeLeaveGroups.map((g) => g.name).join(", ")}
              </span>
            )}
          </p>
        )}
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4">
          {!entitlementLoading && leaveTypeOptions.length === 0 && (
            <p className={`text-sm rounded px-3 py-2 ${darkMode ? "bg-amber-900/30 text-amber-200" : "bg-amber-50 text-amber-800"}`}>
              No leave types are available for this employee. Assign a leave group with leave types and ensure gender on the employee profile matches gender-specific leave rules.
            </p>
          )}
          <div>
            <label className={`block text-sm font-medium mb-1 ${darkMode ? "text-gray-300" : "text-gray-700"}`}>Leave type</label>
            <select
              value={selectedLeaveTypeId}
              onChange={(e) => setSelectedLeaveTypeId(e.target.value)}
              disabled={entitlementLoading || leaveTypeOptions.length === 0}
              className={`w-full border rounded px-3 py-2 text-sm ${darkMode ? "bg-[#1C1C1C] border-[#3C3C3C]" : "bg-white border-gray-300"}`}
            >
              <option value="">
                {entitlementLoading ? "Loading leave types..." : "Select leave type"}
              </option>
              {leaveTypeOptions.map((lt) => (
                <option key={lt.id} value={lt.id}>
                  {lt.name || lt.leaveTypeName || lt.code || lt.id}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={`block text-sm font-medium mb-1 ${darkMode ? "text-gray-300" : "text-gray-700"}`}>Type</label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="adjType"
                  checked={adjustmentType === "credit"}
                  onChange={() => setAdjustmentType("credit")}
                />
                <span>Credit</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="adjType"
                  checked={adjustmentType === "debit"}
                  onChange={() => setAdjustmentType("debit")}
                />
                <span>Debit</span>
              </label>
            </div>
          </div>
          <div>
            <label className={`block text-sm font-medium mb-1 ${darkMode ? "text-gray-300" : "text-gray-700"}`}>Days</label>
            <input
              type="number"
              min={1}
              value={days}
              onChange={(e) => setDays(e.target.value)}
              className={`w-full border rounded px-3 py-2 text-sm ${darkMode ? "bg-[#1C1C1C] border-[#3C3C3C]" : "bg-white border-gray-300"}`}
              placeholder="Number of days"
            />
          </div>
          <div>
            <label className={`block text-sm font-medium mb-1 ${darkMode ? "text-gray-300" : "text-gray-700"}`}>Remarks (optional)</label>
            <input
              type="text"
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              className={`w-full border rounded px-3 py-2 text-sm ${darkMode ? "bg-[#1C1C1C] border-[#3C3C3C]" : "bg-white border-gray-300"}`}
              placeholder="Remarks"
            />
          </div>
        </div>
        <div className={`flex justify-end gap-2 px-4 py-3 border-t ${darkMode ? "border-[#3C3C3C]" : "border-gray-200"}`}>
          <button
            type="button"
            onClick={openHistory}
            className={`px-4 py-2 rounded text-sm font-medium ${darkMode ? "bg-gray-700 hover:bg-gray-600" : "bg-gray-200 hover:bg-gray-300"}`}
          >
            View History
          </button>
          <button onClick={onClose} className={`px-4 py-2 rounded text-sm font-medium ${darkMode ? "bg-gray-700 hover:bg-gray-600" : "bg-gray-200 hover:bg-gray-300"}`}>
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!canSave || saving}
            className="px-4 py-2 rounded text-sm font-medium rh-btn-primary hover:opacity-95 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>

      {showHistoryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10001]" onClick={() => setShowHistoryModal(false)}>
          <div
            className={`max-w-lg w-full max-h-[70vh] overflow-hidden flex flex-col rounded-lg shadow-xl ${darkMode ? "bg-[#2C2C2C] text-[#E0E0E0]" : "bg-white text-gray-900"}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={`flex items-center justify-between px-4 py-3 border-b ${darkMode ? "border-[#3C3C3C]" : "border-gray-200"}`}>
              <h3 className="text-base font-semibold">Adjustment History</h3>
              <button onClick={() => setShowHistoryModal(false)} className={`p-1 rounded ${darkMode ? "hover:bg-[#3C3C3C]" : "hover:bg-gray-100"}`}>
                <X size={18} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {historyLoading ? (
                <p className={`text-sm ${darkMode ? "text-gray-400" : "text-gray-500"}`}>Loading...</p>
              ) : historyList.length === 0 ? (
                <p className={`text-sm ${darkMode ? "text-gray-400" : "text-gray-500"}`}>No adjustment history found.</p>
              ) : (
                <ul className="space-y-2 text-sm">
                  {historyList.map((r) => (
                    <li key={r.id ?? `${r.createdAt}-${r.leaveTypeId}-${r.type}-${r.category || ""}`} className={`border-b pb-2 ${darkMode ? "border-[#3C3C3C]" : "border-gray-200"}`}>
                      <span className="font-medium">{formatAdjustmentDate(r.createdAt || r.date)}</span>
                      {r.leaveTypeName ? ` · ${r.leaveTypeName}` : ""}
                      {" – "}
                      {r.category === "opening_balance" ? (
                        <span className="text-blue-700">
                          Opening balance · {formatLeaveDays(r.days ?? r.daysCount ?? 0)} day(s)
                          {r.year ? ` (${r.year})` : ""}
                        </span>
                      ) : (
                        <>
                          <span className={String(r.type).toLowerCase() === "debit" ? "text-red-600" : "text-green-700"}>
                            {String(r.type).toLowerCase() === "debit" ? "Debit (Dr)" : "Credit (Cr)"}
                          </span>
                          {" "}
                          {formatLeaveDays(r.days ?? r.daysCount ?? 0)} day(s)
                        </>
                      )}
                      {r.remarks && <span className={`block text-xs mt-1 ${darkMode ? "text-gray-500" : "text-gray-500"}`}>{r.remarks}</span>}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Leave Balance Popup: yearly calendar view by month with opening/credited/availed/adjusted/encashed/closing
const LeaveBalancePopup = ({
  darkMode,
  isOpen,
  onClose,
  employee,
  leaveTypes = [],
  attendanceStructureService,
  refreshKey = 0,
}) => {
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedLeaveTypeId, setSelectedLeaveTypeId] = useState("");
  const [balanceData, setBalanceData] = useState([]);
  const [balancePolicy, setBalancePolicy] = useState(null);
  const [balanceEntitlement, setBalanceEntitlement] = useState(null);
  const [loading, setLoading] = useState(false);
  const [eligibleLeaveTypes, setEligibleLeaveTypes] = useState([]);
  const [employeeLeaveGroups, setEmployeeLeaveGroups] = useState([]);
  const [entitlementLoading, setEntitlementLoading] = useState(false);
  const [balanceYearOptions, setBalanceYearOptions] = useState([]);
  const [balanceStartDate, setBalanceStartDate] = useState(null);
  const [balanceRounding, setBalanceRounding] = useState("full_day");

  const companyServiceDate = useMemo(() => getCompanyServiceCommencementDate(), []);
  const employeeDoj = useMemo(() => getEmployeeDoj(employee), [employee]);

  const monthLabels = useMemo(() => {
    const labels = [];
    const firstMonth = getFirstEligibleMonthInYear(
      selectedYear,
      balanceStartDate
    );
    if (firstMonth == null) return labels;
    const lastMonth =
      selectedYear < currentYear ? 12 : Math.min(currentMonth, 12);
    for (let m = firstMonth; m <= lastMonth; m += 1) {
      labels.push({
        month: m,
        label: new Date(selectedYear, m - 1, 1).toLocaleString("default", {
          month: "short",
          year: "numeric",
        }),
      });
    }
    return labels;
  }, [selectedYear, currentYear, currentMonth, balanceStartDate]);

  useEffect(() => {
    if (!isOpen) {
      setSelectedYear(currentYear);
      setSelectedLeaveTypeId("");
      setBalanceData([]);
      setBalancePolicy(null);
      setBalanceEntitlement(null);
      setEligibleLeaveTypes([]);
      setEmployeeLeaveGroups([]);
      setBalanceYearOptions([]);
      setBalanceStartDate(null);
      setBalanceRounding("full_day");
    }
  }, [isOpen, currentYear]);

  const employeeDbId = getEmployeeDbId(employee);

  useEffect(() => {
    if (!isOpen || !employeeDbId || !attendanceStructureService?.getEmployeeLeaveTypes) {
      return;
    }
    let cancelled = false;
    setEntitlementLoading(true);
    attendanceStructureService
      .getEmployeeLeaveTypes(employeeDbId)
      .then((res) => {
        if (cancelled) return;
        const list = Array.isArray(res) ? res : res?.data ?? [];
        setEligibleLeaveTypes(list);
        setEmployeeLeaveGroups(res?.leaveGroups ?? []);
        const merged = mergeCompanyLeaveTypesForEmployee(list, leaveTypes, employee);
        if (merged.length === 1) {
          setSelectedLeaveTypeId(String(merged[0].id));
        }
      })
      .catch(() => {
        if (!cancelled) {
          setEligibleLeaveTypes([]);
          setEmployeeLeaveGroups([]);
        }
      })
      .finally(() => {
        if (!cancelled) setEntitlementLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, employeeDbId, employee, attendanceStructureService, refreshKey, leaveTypes]);

  const leaveTypeOptions = useMemo(
    () =>
      mergeCompanyLeaveTypesForEmployee(
        eligibleLeaveTypes,
        leaveTypes,
        employee
      ),
    [eligibleLeaveTypes, leaveTypes, employee]
  );

  useEffect(() => {
    if (!isOpen || !employeeDbId) return;
    const leaveTypeId = selectedLeaveTypeId ? Number(selectedLeaveTypeId) : null;
    if (!leaveTypeId) {
      setBalanceData([]);
      setBalancePolicy(null);
      setBalanceEntitlement(null);
      setBalanceYearOptions([]);
      setBalanceStartDate(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    attendanceStructureService
      .getLeaveBalance(employeeDbId, selectedYear, leaveTypeId)
      .then((data) => {
        if (cancelled) return;
        const list = Array.isArray(data) ? data : data?.months ?? data?.list ?? [];
        setBalanceData(list);
        setBalancePolicy(data?.policy ?? null);
        setBalanceEntitlement(data?.entitlement ?? null);
        setBalanceRounding(data?.policy?.balanceRounding ?? "full_day");
        const effectiveFrom = data?.policy?.effectiveFrom ?? null;
        const startDate =
          data?.balanceStartDate
            ? new Date(`${data.balanceStartDate}T12:00:00`)
            : resolveLeaveBalanceStartDate({
                serviceCommencementDate: companyServiceDate,
                employeeDoj,
                effectiveFromDate: effectiveFrom,
              });
        setBalanceStartDate(startDate);
        const yearOptions =
          Array.isArray(data?.yearOptions) && data.yearOptions.length
            ? data.yearOptions
            : getLeaveBalanceYearOptions({ startDate });
        setBalanceYearOptions(yearOptions);
        if (yearOptions.length && !yearOptions.includes(selectedYear)) {
          setSelectedYear(yearOptions[0]);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setBalanceData([]);
          toast.error(
            err?.response?.data?.message || err?.message || "Failed to load leave balance"
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [isOpen, employeeDbId, selectedYear, selectedLeaveTypeId, attendanceStructureService, refreshKey, companyServiceDate, employeeDoj]);

  const yearOptions = balanceYearOptions.length
    ? balanceYearOptions
    : getLeaveBalanceYearOptions({ startDate: balanceStartDate });

  const getRow = (monthNum) => {
    const row = balanceData.find((r) => Number(r.month) === monthNum);
    if (row?.ineligible) {
      return { ineligible: true };
    }
    return {
      openingBalance: row?.openingBalance ?? 0,
      credited: row?.credited ?? 0,
      availed: row?.availed ?? 0,
      adjustedDebit: row?.adjustedDebit ?? 0,
      adjustedCredit: row?.adjustedCredit ?? 0,
      encashed: row?.encashed ?? 0,
      closingBalance: row?.closingBalance ?? 0,
    };
  };

  const formatBalanceCell = (value, ineligible) => {
    if (ineligible || value == null) return "—";
    return roundLeaveDaysForDisplay(value, balanceRounding);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div
        className={`rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col ${darkMode ? "bg-[#2C2C2C] text-[#E0E0E0]" : "bg-white text-gray-900"}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={`flex items-center justify-between px-4 py-3 border-b ${darkMode ? "border-[#3C3C3C]" : "border-gray-200"}`}>
          <h2 className="text-lg font-semibold">Leave Balance</h2>
          <button onClick={onClose} className={`p-1 rounded ${darkMode ? "hover:bg-[#3C3C3C]" : "hover:bg-gray-100"}`}>
            <X size={20} />
          </button>
        </div>
        {employee && (
          <p className={`px-4 py-2 text-sm ${darkMode ? "text-gray-400" : "text-gray-600"}`}>
            {employee.employeeName || employee.name || `Employee #${employee.id}`}
            {employeeLeaveGroups.length > 0 && (
              <span className={`block text-xs mt-1 ${darkMode ? "text-gray-500" : "text-gray-500"}`}>
                Leave groups: {employeeLeaveGroups.map((g) => g.name).join(", ")}
              </span>
            )}
          </p>
        )}
        {!entitlementLoading && leaveTypeOptions.length === 0 && (
          <p className={`mx-4 mb-2 text-sm rounded px-3 py-2 ${darkMode ? "bg-amber-900/30 text-amber-200" : "bg-amber-50 text-amber-800"}`}>
            No leave types are available for this employee. Assign a leave group with leave types and ensure gender on the employee profile matches gender-specific leave rules.
          </p>
        )}
        <div className="px-4 py-3 flex flex-wrap gap-4 items-center">
          <div>
            <label className={`block text-xs font-medium mb-1 ${darkMode ? "text-gray-400" : "text-gray-600"}`}>Year</label>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(Number(e.target.value))}
              className={`border rounded px-3 py-2 text-sm ${darkMode ? "bg-[#1C1C1C] border-[#3C3C3C]" : "bg-white border-gray-300"}`}
            >
              {yearOptions.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={`block text-xs font-medium mb-1 ${darkMode ? "text-gray-400" : "text-gray-600"}`}>Leave type</label>
            <select
              value={selectedLeaveTypeId}
              onChange={(e) => setSelectedLeaveTypeId(e.target.value)}
              disabled={entitlementLoading || leaveTypeOptions.length === 0}
              className={`border rounded px-3 py-2 text-sm min-w-[160px] ${darkMode ? "bg-[#1C1C1C] border-[#3C3C3C]" : "bg-white border-gray-300"}`}
            >
              <option value="">
                {entitlementLoading ? "Loading leave types..." : "Select leave type"}
              </option>
              {leaveTypeOptions.map((lt) => (
                <option key={lt.id} value={lt.id}>
                  {lt.name || lt.leaveTypeName || lt.code || lt.id}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="flex-1 overflow-auto px-4 pb-4">
          {loading ? (
            <p className={`text-sm py-4 ${darkMode ? "text-gray-400" : "text-gray-500"}`}>Loading...</p>
          ) : !selectedLeaveTypeId ? (
            <p className={`text-sm py-4 ${darkMode ? "text-gray-400" : "text-gray-500"}`}>Select a leave type to view balance.</p>
          ) : monthLabels.length === 0 ? (
            <p className={`text-sm py-4 ${darkMode ? "text-gray-400" : "text-gray-500"}`}>
              No balance months available for {selectedYear}. Check service commencement, leave type effective date, and employee DOJ.
            </p>
          ) : (
            <div className="overflow-x-auto">
              {balanceStartDate && (
                <p className={`text-xs mb-3 ${darkMode ? "text-gray-500" : "text-gray-500"}`}>
                  Balance applies from{" "}
                  {balanceStartDate.toLocaleDateString(undefined, {
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                  })}{" "}
                  (latest of service commencement, leave type effective date, and employee DOJ).
                </p>
              )}
              {balanceEntitlement && !balanceEntitlement.hasPolicy && (
                <p className={`text-xs mb-3 ${darkMode ? "text-amber-400/90" : "text-amber-700"}`}>
                  No leave policy is configured for this leave type in the employee&apos;s leave group. Credited will stay 0 until you configure it (gear icon on the leave group).
                </p>
              )}
              {balancePolicy && (
                <p className={`text-xs mb-3 ${darkMode ? "text-amber-400/90" : "text-amber-700"}`}>
                  Policy: {balancePolicy.creditFrequency} · {balancePolicy.creditMethod}
                  {balancePolicy.effectiveFrom ? ` · effective from ${balancePolicy.effectiveFrom}` : ""}
                  {balancePolicy.creditOn ? ` · credits on ${balancePolicy.creditOn}` : ""}
                  {balancePolicy.creditMethod === "Based on Attendance" && balancePolicy.attendanceDaysRequired
                    ? ` · ${balancePolicy.leavesCreditedPerAttendanceDays} day(s) per ${balancePolicy.attendanceDaysRequired} attendance day(s)`
                    : ""}
                </p>
              )}
              <table className={`w-full text-sm border-collapse ${darkMode ? "border-[#3C3C3C]" : "border-gray-200"}`}>
                <thead>
                  <tr className={`border-b ${darkMode ? "border-[#3C3C3C] bg-[#1C1C1C]" : "border-gray-200 bg-gray-50"}`}>
                    <th className="text-left py-2 px-2 font-medium">Month</th>
                    <th className="text-right py-2 px-2 font-medium">Opening</th>
                    <th className="text-right py-2 px-2 font-medium">Credited</th>
                    <th className="text-right py-2 px-2 font-medium">Availed</th>
                    <th className="text-right py-2 px-2 font-medium">Adjusted (Dr)</th>
                    <th className="text-right py-2 px-2 font-medium">Adjusted (Cr)</th>
                    <th className="text-right py-2 px-2 font-medium">Encashed</th>
                    <th className="text-right py-2 px-2 font-medium">Closing</th>
                  </tr>
                </thead>
                <tbody>
                  {monthLabels.map(({ month, label }) => {
                    const r = getRow(month);
                    return (
                      <tr key={month} className={`border-b ${darkMode ? "border-[#3C3C3C]" : "border-gray-200"}`}>
                        <td className="py-2 px-2 font-medium">{label}</td>
                        <td className="text-right py-2 px-2">{formatBalanceCell(r.openingBalance, r.ineligible)}</td>
                        <td className="text-right py-2 px-2">{formatBalanceCell(r.credited, r.ineligible)}</td>
                        <td className="text-right py-2 px-2">{formatBalanceCell(r.availed, r.ineligible)}</td>
                        <td className="text-right py-2 px-2">{formatBalanceCell(r.adjustedDebit, r.ineligible)}</td>
                        <td className="text-right py-2 px-2">{formatBalanceCell(r.adjustedCredit, r.ineligible)}</td>
                        <td className="text-right py-2 px-2">{formatBalanceCell(r.encashed, r.ineligible)}</td>
                        <td className="text-right py-2 px-2 font-medium">{formatBalanceCell(r.closingBalance, r.ineligible)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// LOP Reversal Popup: reverse LOP/absent in a locked month and assign payroll month
const LOPReversalPopup = ({
  darkMode,
  isOpen,
  onClose,
  employee,
  attendanceLocks = [],
  attendanceService,
  leaveTypes = [],
  onSuccess,
  selectedYear = null,
  selectedMonth = null,
  weeklyOffPolicies = [],
  employeeWeeklyOffMappings = {},
  holidaysData = [],
  holidayPolicies = [],
  employeeHolidayMappings = {},
}) => {
  const [reversalMonth, setReversalMonth] = useState(null); // { month: 1-12, year }
  const [payrollMonth, setPayrollMonth] = useState(null); // { month: 1-12, year }
  const [attendanceMap, setAttendanceMap] = useState({}); // { day: status }
  const [originalAttendanceMap, setOriginalAttendanceMap] = useState({}); // snapshot for true reversal delta
  const [editableDaysMap, setEditableDaysMap] = useState({}); // { day: true } based on initial LOP days for current session
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editDay, setEditDay] = useState(null); // day number for which dropdown is open
  const [showHalfDayModal, setShowHalfDayModal] = useState(false);
  const [halfDayEditDay, setHalfDayEditDay] = useState(null);
  const [halfDayFirst, setHalfDayFirst] = useState("");
  const [halfDaySecond, setHalfDaySecond] = useState("");
  const [halfDayFirstEditable, setHalfDayFirstEditable] = useState(false);
  const [halfDaySecondEditable, setHalfDaySecondEditable] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyRecords, setHistoryRecords] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const lockedMonths = useMemo(() => {
    if (!attendanceLocks || !Array.isArray(attendanceLocks)) return [];
    const normalized = attendanceLocks
      .map((lock) => ({ month: lock.month, year: lock.year }))
      .filter(
        (lm) =>
          Number.isFinite(Number(lm.month)) && Number.isFinite(Number(lm.year))
      )
      .map((lm) => ({
        month: Number(lm.month),
        year: Number(lm.year),
      }))
      .sort((a, b) => (a.year !== b.year ? a.year - b.year : a.month - b.month));

    // Prevent selecting future/invalid months in the popup:
    // Only allow months strictly BEFORE the currently selected payroll month.
    if (selectedYear == null || selectedMonth == null) return normalized;
    const currentPayrollMonthKey = Number(selectedYear) * 12 + Number(selectedMonth + 1); // selectedMonth is 0-based

    // Limit months to the employee's employment period (DOJ -> relieved/termination).
    // If relieved date is missing, allow up to Infinity.
    const joinDate =
      employee?.joiningDate ||
      employee?.joinDate ||
      employee?.user?.joiningDate ||
      null;
    const relievedDate =
      employee?.relievedDate ||
      employee?.exitDate ||
      employee?.resignationDate ||
      employee?.relievingDate ||
      null;

    const toMonthKey = (d) => {
      if (!d) return null;
      const dt = new Date(d);
      if (isNaN(dt.getTime())) return null;
      return dt.getFullYear() * 12 + (dt.getMonth() + 1); // 1-12 month
    };

    const joinMonthKey = toMonthKey(joinDate);
    const relievedMonthKey = toMonthKey(relievedDate);

    return normalized.filter((lm) => {
      const lockedMonthKey = Number(lm.year) * 12 + Number(lm.month); // lm.month is 1-based (DB/lock)
      const withinEmployment =
        (joinMonthKey == null || lockedMonthKey >= joinMonthKey) &&
        (relievedMonthKey == null || lockedMonthKey <= relievedMonthKey);

      return lockedMonthKey < currentPayrollMonthKey && withinEmployment;
    });
  }, [attendanceLocks, selectedYear, selectedMonth, employee]);

  const reversalDaysInMonth = useMemo(() => {
    if (!reversalMonth) return 31;
    return new Date(reversalMonth.year, reversalMonth.month, 0).getDate();
  }, [reversalMonth]);

  // Payroll month: only current month (unlocked)
  const payrollMonthOptions = useMemo(() => {
    const now = new Date();
    return [{
      month: now.getMonth() + 1,
      year: now.getFullYear(),
      label: now.toLocaleString("default", { month: "long", year: "numeric" }),
    }];
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setReversalMonth(null);
      setPayrollMonth(null);
      setAttendanceMap({});
      setOriginalAttendanceMap({});
      setEditableDaysMap({});
      setEditDay(null);
      setShowHistoryModal(false);
      setHistoryRecords([]);
      return;
    }
    const now = new Date();
    setPayrollMonth({ month: now.getMonth() + 1, year: now.getFullYear() });
    // Do not auto-select reversal month; user chooses which month to reverse LOP for
    setReversalMonth(null);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !employee || !reversalMonth) {
      setAttendanceMap({});
      return;
    }
    const daysInMonth = new Date(reversalMonth.year, reversalMonth.month, 0).getDate();
    let cancelled = false;
    setLoading(true);
    attendanceService
      .getAttendanceHistory(
        reversalMonth.month,
        reversalMonth.year,
        1,
        500,
        "",
        [employee.id]
      )
      .then((res) => {
        if (cancelled) return;
        const map = {};
        const editableSeed = {};
        for (let d = 1; d <= daysInMonth; d++) map[String(d)] = "-";
        let data = res?.data ?? res?.attendance ?? res?.records ?? (Array.isArray(res) ? res : null);
        if (data && typeof data === "object" && !Array.isArray(data)) {
          data = data.data || data.attendance || (Array.isArray(data) ? data : []);
        }
        const list = Array.isArray(data) ? data : (res?.data?.data && Array.isArray(res.data.data) ? res.data.data : []);
        (list || []).forEach((record) => {
          const empId = record.employee_id ?? record.employeeId ?? record.empId;
          if (empId != null && String(empId) !== String(employee.id)) return;
          let day = null;
          if (record.date) {
            const d = new Date(record.date);
            if (!isNaN(d.getTime())) day = d.getDate();
          } else if (record.day != null) day = Number(record.day);
          if (day == null || day < 1 || day > daysInMonth) return;
          const status = record.status ?? record.attendanceStatus ?? record.code ?? "-";
          map[String(day)] = status;
          // Keep editability based on initially loaded LOP day(s) for this popup session.
          if (isEditableLOPStatus(status)) editableSeed[String(day)] = true;
        });

        // Fill missing days with effective PR/WO/PH (so calendar shows full attendance),
        // but do not overwrite real values and keep '-' outside employment dates.
        try {
          const joinDate =
            employee?.joiningDate ||
            employee?.joinDate ||
            employee?.user?.joiningDate;
          const relievedDate =
            employee?.relievedDate ||
            employee?.exitDate ||
            employee?.resignationDate ||
            employee?.relievingDate;
          let joinNum = 0;
          let relieveNum = 99999999;
          if (joinDate) {
            const jd = new Date(joinDate);
            if (!isNaN(jd.getTime()))
              joinNum =
                jd.getFullYear() * 10000 + (jd.getMonth() + 1) * 100 + jd.getDate();
          }
          if (relievedDate) {
            const rd = new Date(relievedDate);
            if (!isNaN(rd.getTime()))
              relieveNum =
                rd.getFullYear() * 10000 + (rd.getMonth() + 1) * 100 + rd.getDate();
          }

          for (let day = 1; day <= daysInMonth; day++) {
            const curNum =
              reversalMonth.year * 10000 + reversalMonth.month * 100 + day;
            if (curNum < joinNum || curNum > relieveNum) continue;
            if (map[String(day)] && map[String(day)] !== "-") continue;
            const dateObj = new Date(reversalMonth.year, reversalMonth.month - 1, day);
            const def = getDefaultStatusWithLock(
              dateObj,
              employee,
              weeklyOffPolicies,
              employeeWeeklyOffMappings,
              holidaysData,
              holidayPolicies,
              employeeHolidayMappings
            );
            map[String(day)] = def?.status || "PR";
          }
        } catch (e) {
          // If policy lookups fail, keep raw map.
        }

        setAttendanceMap(map);
        setOriginalAttendanceMap(map);
        setEditableDaysMap(editableSeed);
      })
      .catch(() => {
        if (!cancelled) {
          const map = {};
          for (let d = 1; d <= daysInMonth; d++) map[String(d)] = "-";
          setAttendanceMap(map);
          setOriginalAttendanceMap({});
          setEditableDaysMap({});
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [
    isOpen,
    employee?.id,
    reversalMonth?.month,
    reversalMonth?.year,
    weeklyOffPolicies,
    employeeWeeklyOffMappings,
    holidaysData,
    holidayPolicies,
    employeeHolidayMappings,
  ]);

  // LOP reversal editability: only true LOP cells (exclude LWP).
  const isEditableLOPStatus = (status) => {
    if (!status || status === "-" || status === "") return false;
    const u = (status || "").toUpperCase();
    return (u === "LOP" || u.includes("LOP")) && !(u === "LWP" || u.includes("LWP"));
  };

  // Status detection helper used for display decisions only (LOP + LWP).
  const isLOPOrLWPStatus = (status) => {
    if (!status || status === "-" || status === "") return false;
    const u = (status || "").toUpperCase();
    return u === "LOP" || u === "LWP" || u.includes("LOP") || u.includes("LWP");
  };

  const getPopupStatusColor = useCallback((status) => {
    if (!status || status === "-") return { className: "text-gray-600 border-gray-300", style: {} };
    const norm = normalizeStatus(status).toUpperCase();

    if (statusColorMap && statusColorMap[norm]) {
      const color = statusColorMap[norm];
      if (color && (color.startsWith("#") || color.startsWith("rgb") || /^[a-zA-Z]+$/.test(color))) {
        return { className: "border", style: { color, borderColor: color } };
      }
      return { className: color, style: {} };
    }

    if (norm.includes("LOP") || norm.includes("LWP")) {
      return { className: "text-red-700 border-red-300", style: {} };
    }
    if (norm.includes("WO")) return { className: "text-redhalo-navy border-redhalo-teal/70", style: {} };
    if (norm.includes("PH")) return { className: "text-amber-700 border-amber-300", style: {} };
    if (norm.includes("PR")) return { className: "text-emerald-700 border-emerald-300", style: {} };
    return { className: "text-gray-700 border-gray-300", style: {} };
  }, []);

  // Inline dropdown options like outside calendar: simple single codes only.
  const reversalStatusOptions = useMemo(() => {
    const opts = [];
    const add = (v) => {
      if (!v) return;
      const val = String(v).toUpperCase();
      if (!opts.some((x) => x.value === val)) opts.push({ value: val });
    };

    // Core statuses shown in outside picker
    add("PR");
    add("LOP");
    add("WO");
    add("PH");
    add("CL");

    (leaveTypes || []).forEach((lt) => {
      const code = (lt.code || lt.shortCode || lt.id || "").toUpperCase();
      // Exclude duplicate/core and LWP from inline picker
      if (
        !code ||
        code === "PR" ||
        code === "LWP" ||
        code === "WO" ||
        code === "PH" ||
        lt?.isActive === false ||
        lt?.is_active === false
      ) {
        return;
      }

      // Single-code leave type option
      add(code);
    });

    return opts;
  }, [leaveTypes]);

  const handleDayStatusChange = (day, newStatus) => {
    setAttendanceMap((prev) => ({ ...prev, [String(day)]: newStatus }));
    setEditDay(null);
  };

  const reversalSingleCodeOptions = useMemo(() => {
    // Single-click picker should look like outside status panel (single codes only).
    const seen = new Set();
    const out = [];
    (reversalStatusOptions || []).forEach((opt) => {
      const v = String(opt?.value || "").toUpperCase();
      if (!v || v.includes("/")) return;
      if (seen.has(v)) return;
      seen.add(v);
      out.push(v);
    });
    return out;
  }, [reversalStatusOptions]);

  const reversalHalfCodeOptions = useMemo(() => {
    const ordered = ["PR", "CL"];
    const safeFromLeaveTypes = [];
    (leaveTypes || []).forEach((lt) => {
      const code = (lt?.code || lt?.shortCode || lt?.id || "").toUpperCase();
      if (!code) return;
      if (code === "PR" || code === "LWP" || code === "WO" || code === "PH") return;
      if (lt?.isActive === false || lt?.is_active === false) return;
      safeFromLeaveTypes.push(code);
    });

    // Allow selecting LOP itself for the editable half
    const set = new Set(["LOP", ...ordered, ...safeFromLeaveTypes]);
    return Array.from(set).filter(Boolean);
  }, [leaveTypes]);

  const halfCodeLabel = useCallback(
    (code) => {
      return String(code || "").toUpperCase();
    },
    [leaveTypes]
  );

  const openHalfDayEditor = useCallback(
    (day, currentStatus) => {
      let parts = splitIntoTwoCodes(currentStatus);

      // Support full-day LOP double-click: initialize as LOP/LOP
      if ((!Array.isArray(parts) || parts.length !== 2) && isEditableLOPStatus(currentStatus)) {
        parts = ["LOP", "LOP"];
      }
      if (!Array.isArray(parts) || parts.length !== 2) return;

      const [first, second] = parts;
      const firstEditable = isEditableLOPStatus(first);
      const secondEditable = isEditableLOPStatus(second);

      // Only open when at least one half is LOP.
      if (!firstEditable && !secondEditable) return;

      setHalfDayEditDay(day);
      setHalfDayFirst(first || "");
      setHalfDaySecond(second || "");
      setHalfDayFirstEditable(firstEditable);
      setHalfDaySecondEditable(secondEditable);
      setShowHalfDayModal(true);
      setEditDay(null);
    },
    [isEditableLOPStatus]
  );

  const saveHalfDayEditor = useCallback(() => {
    if (halfDayEditDay == null) return;
    if (!halfDayFirst || !halfDaySecond) return;
    const first = String(halfDayFirst || "").toUpperCase().trim();
    const second = String(halfDaySecond || "").toUpperCase().trim();
    // If both halves are same code, collapse to full-day single status.
    const combined = first === second ? first : `${first}/${second}`;
    setAttendanceMap((prev) => ({ ...prev, [String(halfDayEditDay)]: combined }));
    setShowHalfDayModal(false);
    setHalfDayEditDay(null);
    setEditDay(null);
  }, [halfDayEditDay, halfDayFirst, halfDaySecond]);

  const getLOPUnits = useCallback((rawStatus) => {
    const status = String(rawStatus || "").toUpperCase().trim();
    if (!status || status === "-" || status === "") return 0;
    const parts = splitIntoTwoCodes(status);
    const isLOPish = (s) => {
      const u = String(s || "").toUpperCase();
      return u === "LOP" || u === "LWP" || u.includes("LOP") || u.includes("LWP");
    };
    if (Array.isArray(parts) && parts.length === 2) {
      let units = 0;
      if (isLOPish(parts[0])) units += 0.5;
      if (isLOPish(parts[1])) units += 0.5;
      return units;
    }
    return isLOPish(status) ? 1 : 0;
  }, []);

  const fetchLOPReversalHistory = useCallback(() => {
    if (!employee?.id || !attendanceService) return;
    setHistoryLoading(true);
    const now = new Date();
    const promises = [];
    for (let i = 0; i < 12; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      promises.push(
        attendanceService
          .getAttendanceHistory(d.getMonth() + 1, d.getFullYear(), 1, 500, "", [employee.id])
          .then((res) => {
            const raw = res?.data ?? (Array.isArray(res) ? res : []);
            const arr = Array.isArray(raw) ? raw : (raw?.data && Array.isArray(raw.data) ? raw.data : []);
            return (arr || []).filter(
              (r) => String(r.employee_id || r.employeeId) === String(employee.id) &&
                (r.remarks || "").includes("LOP Reversal")
            );
          })
          .catch(() => [])
      );
    }
    Promise.all(promises).then((arrays) => {
      const flat = arrays.flat();
      const byDate = flat
        .map((r) => ({ date: r.date, status: r.status, remarks: r.remarks || "" }))
        .filter((r) => r.remarks.includes("Payroll month:"))
        .sort((a, b) => (b.date || "").localeCompare(a.date || ""));
      setHistoryRecords(byDate);
      setHistoryLoading(false);
    });
  }, [employee?.id, attendanceService]);

  const handleSubmit = async () => {
    if (!employee || !reversalMonth) {
      toast.error("Attendance month is required.");
      return;
    }
    const yearMonthPrefix = `${reversalMonth.year}-${String(reversalMonth.month).padStart(2, "0")}-`;
    const employeeAttendance = Object.entries(attendanceMap)
      .map(([day, status]) => {
        const originalStatus = originalAttendanceMap[String(day)] ?? "-";
        const oldUnits = getLOPUnits(originalStatus);
        const newUnits = getLOPUnits(status);
        const reversedUnits = Math.max(0, oldUnits - newUnits);
        const originalParts = splitIntoTwoCodes(originalStatus);
        const currentParts = splitIntoTwoCodes(status);
        const isLOPish = (s) => {
          const u = String(s || "").toUpperCase().trim();
          return u === "LOP" || u === "LWP" || u.includes("LOP") || u.includes("LWP");
        };

        // No change, or no actual LOP reduction => don't count/save as reversal.
        if (String(status || "").toUpperCase() === String(originalStatus || "").toUpperCase()) return null;
        if (reversedUnits <= 0) return null;

        // Persist half marker in remarks so UI/report can render FH/SH even if status is collapsed.
        let reversedHalf = "";
        if (reversedUnits === 0.5 && Array.isArray(originalParts) && originalParts.length === 2) {
          const [oldFirst, oldSecond] = originalParts;
          const [newFirst, newSecond] = Array.isArray(currentParts) && currentParts.length === 2
            ? currentParts
            : [status, status];
          if (isLOPish(oldFirst) && !isLOPish(newFirst)) reversedHalf = "FH";
          if (isLOPish(oldSecond) && !isLOPish(newSecond)) reversedHalf = reversedHalf || "SH";
          if (!reversedHalf) {
            if (isLOPish(oldFirst) && !isLOPish(oldSecond)) reversedHalf = "FH";
            else if (!isLOPish(oldFirst) && isLOPish(oldSecond)) reversedHalf = "SH";
          }
        }

        const payrollTag = payrollMonth
          ? `${payrollMonth.year}-${String(payrollMonth.month).padStart(2, "0")}`
          : "";
        const halfTag = reversedHalf ? `; Reversed half: ${reversedHalf}` : "";
        return {
          employeeId: employee.id,
          date: `${yearMonthPrefix}${String(day).padStart(2, "0")}`,
          status,
          remarks: payrollTag
            ? `LOP Reversal; Payroll month: ${payrollTag}; Reversed days: ${reversedUnits}${halfTag}`
            : `LOP Reversal; Reversed days: ${reversedUnits}${halfTag}`,
        };
      })
      .filter(Boolean);
    if (employeeAttendance.length === 0) {
      toast.error("No LOP reversal changes to save.");
      return;
    }
    setSaving(true);
    try {
      const payload = { employeeAttendance, isLOPReversal: true };
      const response = await attendanceService.bulkMarkAttendance(payload);
      const count = response?.count ?? (Array.isArray(response?.data) ? response.data.length : 0);
      const skipped = response?.skipped ?? [];
      if (response && (response.success !== false) && (count > 0 || skipped.length === 0)) {
        toast.success("LOP Reversal saved successfully.");
        onSuccess && onSuccess();
        onClose();
      } else if (response && response.success !== false && count === 0 && skipped.length > 0) {
        toast.error("Could not save: selected month may not be locked or rows could not be updated.");
      } else {
        toast.error(response?.message || "Failed to save LOP reversal.");
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to save LOP reversal.");
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const empName = employee?.user?.name || employee?.name || "";
  const empCode = employee?.code || employee?.empCode || employee?.employeeId || "";

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
      <div
        className={`max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col rounded-lg shadow-xl ${
          darkMode ? "bg-[#2C2C2C] text-[#E0E0E0]" : "bg-white text-gray-900"
        }`}
      >
        <div className={`flex items-center justify-between px-4 py-3 border-b ${darkMode ? "border-[#3C3C3C]" : "border-gray-200"}`}>
          <h2 className="text-lg font-semibold">LOP Reversal</h2>
          <button onClick={onClose} className={`p-1 rounded ${darkMode ? "hover:bg-[#3C3C3C]" : "hover:bg-gray-100"}`}>
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <p className={`text-sm ${darkMode ? "text-gray-400" : "text-gray-600"}`}>
              {empCode} – {empName}
            </p>
            <button
              type="button"
              onClick={() => {
                setShowHistoryModal(true);
                fetchLOPReversalHistory();
              }}
              className={`text-sm px-3 py-1.5 rounded border font-medium ${
                darkMode ? "border-[#3C3C3C] hover:bg-[#3C3C3C]" : "border-gray-300 hover:bg-gray-100"
              }`}
            >
              View History
            </button>
          </div>

          <p className={`text-sm mb-3 ${darkMode ? "text-gray-400" : "text-gray-600"}`}>
            Select a locked month, then use the calendar below to change LOP days: click a LOP day and choose the new status (e.g. PR, CL).
          </p>
          <div className="mb-4">
            <label className={`block text-sm font-medium mb-1 ${darkMode ? "text-gray-300" : "text-gray-700"}`}>
              Month for LOP reversal
            </label>
            <select
              value={reversalMonth ? `${reversalMonth.year}-${reversalMonth.month}` : ""}
              onChange={(e) => {
                const v = e.target.value;
                if (!v) setReversalMonth(null);
                else {
                  const [y, m] = v.split("-").map(Number);
                  setReversalMonth({ year: y, month: m });
                }
              }}
              className={`w-full max-w-xs p-2 border rounded text-sm ${
                darkMode ? "bg-[#1C1C1C] border-[#3C3C3C]" : "bg-white border-gray-300"
              }`}
            >
              <option value="">Select month to reverse LOP</option>
              {lockedMonths.map((lm) => (
                <option key={`${lm.year}-${lm.month}`} value={`${lm.year}-${lm.month}`}>
                  {new Date(lm.year, lm.month - 1, 1).toLocaleString("default", { month: "long", year: "numeric" })}
                </option>
              ))}
            </select>
            {lockedMonths.length === 0 && (
              <p className={`text-xs mt-1 ${darkMode ? "text-amber-400" : "text-amber-700"}`}>
                No locked months available. LOP Reversal is for locked attendance months only.
              </p>
            )}
          </div>

          {reversalMonth ? (
            <>
              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? "text-gray-300" : "text-gray-700"}`}>
                  Calendar – existing attendance; only LOP days are editable (change to PR, CL, or other leave)
                </label>
                {loading ? (
                  <p className={`text-sm ${darkMode ? "text-gray-400" : "text-gray-500"}`}>Loading attendance...</p>
                ) : (
                  <div className="overflow-x-auto border rounded p-2">
                    <div className="grid grid-cols-7 gap-0.5 text-center text-xs">
                      {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((wd) => (
                        <div key={wd} className={`py-1 font-medium ${darkMode ? "text-gray-400" : "text-gray-500"}`}>{wd}</div>
                      ))}
                      {(() => {
                        const firstDay = new Date(reversalMonth.year, reversalMonth.month - 1, 1).getDay();
                        const days = reversalDaysInMonth;
                        const cells = [];
                        for (let i = 0; i < firstDay; i++) cells.push(<div key={`empty-${i}`} className="p-1" />);
                        for (let day = 1; day <= days; day++) {
                          const status = attendanceMap[String(day)] ?? "-";
                          const halfParts = splitIntoTwoCodes(status);
                          const isHalfDayCell =
                            Array.isArray(halfParts) && halfParts.length === 2;
                          const dayInitiallyEditable = !!editableDaysMap[String(day)];
                          // Only full-day LOP cells open inline dropdown.
                          // Half-day cells are edited via double-click modal.
                          const fullDayEditable =
                            dayInitiallyEditable && !isHalfDayCell;
                          const isHalfLOP =
                            dayInitiallyEditable && isHalfDayCell;
                          const isOpenDropdown = editDay === day;
                          const colorInfo = getPopupStatusColor(status);
                          cells.push(
                            <div
                              key={day}
                              className={`min-w-[40px] p-1 flex flex-col items-center ${
                                fullDayEditable ? "cursor-pointer" : ""
                              }`}
                            >
                              <span className={`text-xs ${darkMode ? "text-gray-400" : "text-gray-500"}`}>{day}</span>
                              {fullDayEditable ? (
                                <div className="relative w-full">
                                  <span
                                    onClick={() =>
                                      setEditDay((prev) => (prev === day ? null : day))
                                    }
                                    onDoubleClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      setEditDay(null);
                                      openHalfDayEditor(day, status);
                                    }}
                                    className={`inline-block border rounded px-1 py-0.5 text-xs w-full ${colorInfo.className} ${darkMode ? "bg-[#2C2C2C]" : "bg-white"} ${
                                      darkMode ? "hover:bg-[#3C3C3C]" : "hover:bg-gray-100"
                                    }`}
                                    style={colorInfo.style || {}}
                                  >
                                    {status === "-" ? "—" : status}
                                  </span>
                                  {isOpenDropdown && (
                                    <div
                                      className={`absolute z-50 mt-1 w-[170px] rounded-md border shadow-lg p-1 ${
                                        darkMode
                                          ? "bg-[#1C1C1C] border-[#3C3C3C]"
                                          : "bg-white border-gray-300"
                                      }`}
                                    >
                                      <div className="grid grid-cols-2 gap-0.5">
                                        {reversalSingleCodeOptions.map((code) => (
                                          <button
                                            key={code}
                                            type="button"
                                            onClick={() => handleDayStatusChange(day, code)}
                                            className={`text-xs rounded px-2 py-1 text-left ${
                                              String(status).toUpperCase() === code
                                                ? "rh-btn-primary"
                                                : darkMode
                                                ? "text-[#E0E0E0] hover:bg-[#3C3C3C]"
                                                : "text-gray-700 hover:bg-gray-100"
                                            }`}
                                          >
                                            {code}
                                          </button>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <span
                                  onDoubleClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    if (isHalfLOP) openHalfDayEditor(day, status);
                                  }}
                                  className={`inline-block border rounded px-1 py-0.5 text-xs w-full ${colorInfo.className} ${
                                    darkMode ? "opacity-90 bg-[#2C2C2C]" : "bg-white"
                                  }`}
                                  style={colorInfo.style || {}}
                                  title={isHalfLOP ? "Double-click to change LOP half-day" : ""}
                                >
                                  {status === "-" ? (
                                    "—"
                                  ) : isHalfDayCell ? (
                                    <div className="flex flex-col items-center leading-tight">
                                      <span>{halfParts?.[0] ?? ""}</span>
                                      <span>{halfParts?.[1] ?? ""}</span>
                                    </div>
                                  ) : (
                                    status
                                  )}
                                </span>
                              )}
                            </div>
                          );
                        }
                        return cells;
                      })()}
                    </div>
                  </div>
                )}
              </div>

              <div>
                <label className={`block text-sm font-medium mb-1 ${darkMode ? "text-gray-300" : "text-gray-700"}`}>
                  Payroll month
                </label>
                <p className={`text-sm py-2 ${darkMode ? "text-[#E0E0E0]" : "text-gray-900"}`}>
                  {payrollMonthOptions[0]?.label ?? (payrollMonth ? `${payrollMonth.year}-${String(payrollMonth.month).padStart(2, "0")}` : "—")}
                </p>
                <p className={`text-xs mt-1 ${darkMode ? "text-gray-500" : "text-gray-500"}`}>
                  Reversed LOP will be reflected in the current month (unlocked).
                </p>
              </div>
            </>
          ) : null}
        </div>

        {showHistoryModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000]" onClick={() => setShowHistoryModal(false)}>
            <div
              className={`max-w-lg w-full max-h-[70vh] overflow-hidden flex flex-col rounded-lg shadow-xl ${darkMode ? "bg-[#2C2C2C] text-[#E0E0E0]" : "bg-white text-gray-900"}`}
              onClick={(e) => e.stopPropagation()}
            >
              <div className={`flex items-center justify-between px-4 py-3 border-b ${darkMode ? "border-[#3C3C3C]" : "border-gray-200"}`}>
                <h3 className="text-base font-semibold">LOP Reversal History</h3>
                <button onClick={() => setShowHistoryModal(false)} className={`p-1 rounded ${darkMode ? "hover:bg-[#3C3C3C]" : "hover:bg-gray-100"}`}>
                  <X size={18} />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-4">
                {historyLoading ? (
                  <p className={`text-sm ${darkMode ? "text-gray-400" : "text-gray-500"}`}>Loading...</p>
                ) : historyRecords.length === 0 ? (
                  <p className={`text-sm ${darkMode ? "text-gray-400" : "text-gray-500"}`}>No LOP reversal history found.</p>
                ) : (
                  <ul className="space-y-2 text-sm">
                    {historyRecords.slice(0, 50).map((r, i) => (
                      <li key={i} className={`border-b pb-2 ${darkMode ? "border-[#3C3C3C]" : "border-gray-200"}`}>
                        <span className="font-medium">{r.date}</span> – {r.status}
                        {r.remarks && <span className={`block text-xs mt-1 ${darkMode ? "text-gray-500" : "text-gray-500"}`}>{r.remarks}</span>}
                      </li>
                    ))}
                    {historyRecords.length > 50 && (
                      <li className={`text-xs ${darkMode ? "text-gray-500" : "text-gray-500"}`}>... and {historyRecords.length - 50} more</li>
                    )}
                  </ul>
                )}
              </div>
            </div>
          </div>
        )}

        {showHalfDayModal && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000]"
            onClick={() => setShowHalfDayModal(false)}
          >
            <div
              className={`max-w-lg w-full overflow-hidden flex flex-col rounded-lg shadow-xl ${
                darkMode ? "bg-[#2C2C2C] text-[#E0E0E0]" : "bg-white text-gray-900"
              }`}
              onClick={(e) => e.stopPropagation()}
            >
              <div
                className={`flex items-center justify-between px-4 py-3 border-b ${
                  darkMode ? "border-[#3C3C3C]" : "border-gray-200"
                }`}
              >
                <h3 className="text-base font-semibold">
                  Attendance Status - Day {halfDayEditDay ?? ""}
                </h3>
                <button
                  onClick={() => setShowHalfDayModal(false)}
                  className={`p-1 rounded ${darkMode ? "hover:bg-[#3C3C3C]" : "hover:bg-gray-100"}`}
                >
                  <X size={18} />
                </button>
              </div>

              <div className="p-4 space-y-4">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${darkMode ? "text-gray-300" : "text-gray-700"}`}>
                    First Half Status
                  </label>
                  <select
                    value={halfDayFirst}
                    onChange={(e) => setHalfDayFirst(e.target.value)}
                    disabled={!halfDayFirstEditable}
                    className={`w-full p-2 border rounded text-sm ${
                      darkMode ? "bg-[#1C1C1C] border-[#3C3C3C]" : "bg-white border-gray-300"
                    } ${!halfDayFirstEditable ? "opacity-60 cursor-not-allowed" : ""}`}
                  >
                    {reversalHalfCodeOptions.map((code) => (
                      <option key={code} value={code}>
                        {String(code).toUpperCase()}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${darkMode ? "text-gray-300" : "text-gray-700"}`}>
                    Second Half Status
                  </label>
                  <select
                    value={halfDaySecond}
                    onChange={(e) => setHalfDaySecond(e.target.value)}
                    disabled={!halfDaySecondEditable}
                    className={`w-full p-2 border rounded text-sm ${
                      darkMode ? "bg-[#1C1C1C] border-[#3C3C3C]" : "bg-white border-gray-300"
                    } ${!halfDaySecondEditable ? "opacity-60 cursor-not-allowed" : ""}`}
                  >
                    {reversalHalfCodeOptions.map((code) => (
                      <option key={code} value={code}>
                        {String(code).toUpperCase()}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <p className={`text-sm font-medium mb-1 ${darkMode ? "text-gray-300" : "text-gray-700"}`}>
                    Preview:
                  </p>
                  <p className={`text-sm ${darkMode ? "text-gray-200" : "text-gray-800"}`}>
                    First Half: {halfDayFirst} / Second Half: {halfDaySecond}
                  </p>
                </div>
              </div>

              <div className={`flex justify-end gap-2 px-4 py-3 border-t ${darkMode ? "border-[#3C3C3C]" : "border-gray-200"}`}>
                <button
                  onClick={() => setShowHalfDayModal(false)}
                  className={`px-4 py-2 rounded text-sm font-medium ${
                    darkMode ? "bg-gray-700 hover:bg-gray-600 text-white" : "bg-gray-200 hover:bg-gray-300 text-gray-800"
                  }`}
                >
                  Cancel
                </button>
                <button
                  onClick={saveHalfDayEditor}
                  disabled={!halfDayEditDay}
                  className={`px-4 py-2 rounded text-sm font-medium ${
                    darkMode ? "rh-btn-primary hover:opacity-95" : "rh-btn-primary hover:opacity-95"
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        )}

        <div className={`flex justify-end gap-2 px-4 py-3 border-t ${darkMode ? "border-[#3C3C3C]" : "border-gray-200"}`}>
          <button
            onClick={onClose}
            className={`px-4 py-2 rounded text-sm font-medium ${
              darkMode ? "bg-gray-700 hover:bg-gray-600" : "bg-gray-200 hover:bg-gray-300"
            }`}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!reversalMonth || loading || saving}
            className={`px-4 py-2 rounded text-sm font-medium ${
              darkMode ? "rh-btn-primary hover:opacity-95" : "rh-btn-primary hover:opacity-95"
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

// Attendance Structures Content Component
const AttendanceStructuresContent = ({
  activeStructureTab,
  darkMode,
  employeeWeeklyOffMappings,
  setEmployeeWeeklyOffMappings,
  parentServiceCommencesOnDate = null,
  parentWeeklyOffPolicies = [],
  parentHolidayPolicies = [],
  parentHolidays = [],
  parentLeaveTypes = [],
  parentLeaveGroups = [],
  parentHolidayBank = [],
  parentBranches = [],
  parentDepartments = [],
  parentDesignations = [],
  parentGrades = [],
  parentSubDepartments = [],
  parentCategories = [],
  parentEmploymentTypes = [],
}) => {
  const { mapping: attendanceMapping } = useAttendanceCodeConfig();
  const [weeklyOffs, setWeeklyOffs] = useState({
    monday: false,
    tuesday: false,
    wednesday: false,
    thursday: false,
    friday: false,
    saturday: false,
    sunday: false,
  });
  const [holidays, setHolidays] = useState([]);
  const [newHoliday, setNewHoliday] = useState({
    date: "",
    name: "",
    type: "public",
  });

  // New state for sub-tabs and policies
  const [weeklyOffPolicies, setWeeklyOffPolicies] = useState([]);
  const [holidayPolicies, setHolidayPolicies] = useState([]);
  const [showWeeklyOffModal, setShowWeeklyOffModal] = useState(false);
  const [showHolidayModal, setShowHolidayModal] = useState(false);
  const [isSavingWeeklyOffPolicy, setIsSavingWeeklyOffPolicy] = useState(false);
  const [isSavingHolidayPolicy, setIsSavingHolidayPolicy] = useState(false);
  const [isSavingLeaveType, setIsSavingLeaveType] = useState(false);
  const [isSavingLeaveGroup, setIsSavingLeaveGroup] = useState(false);
  const [editingWeeklyOffPolicy, setEditingWeeklyOffPolicy] = useState(null);
  const [editingHolidayPolicy, setEditingHolidayPolicy] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [employeeSearchTerm, setEmployeeSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [dataUpdateTrigger, setDataUpdateTrigger] = useState(0); // Force re-render when data loads

  // Confirmation Modal State
  const [confirmationModal, setConfirmationModal] = useState({
    isOpen: false,
    title: "",
    message: "",
    onConfirm: null,
    confirmText: "Yes",
    cancelText: "No",
  });

  // Helper function to show confirmation modal
  const showConfirmation = (
    title,
    message,
    onConfirm,
    confirmText = "Yes",
    cancelText = "No"
  ) => {
    setConfirmationModal({
      isOpen: true,
      title,
      message,
      onConfirm: () => {
        setConfirmationModal({ ...confirmationModal, isOpen: false });
        onConfirm();
      },
      confirmText,
      cancelText,
    });
  };

  const closeConfirmation = () => {
    setConfirmationModal({ ...confirmationModal, isOpen: false });
  };

  // Leave Management States
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [leaveGroups, setLeaveGroups] = useState([]);
  const [showLeaveTypeModal, setShowLeaveTypeModal] = useState(false);
  const [showLeaveGroupModal, setShowLeaveGroupModal] = useState(false);
  const [configuringLeaveType, setConfiguringLeaveType] = useState(null);
  const [expandedLTCSection, setExpandedLTCSection] = useState("basic");
  const [editingLeaveType, setEditingLeaveType] = useState(null);
  const [editingLeaveGroup, setEditingLeaveGroup] = useState(null);
  const [showOpeningBalanceUploadModal, setShowOpeningBalanceUploadModal] = useState(false);
  const [openingBalanceUploadLeaveGroup, setOpeningBalanceUploadLeaveGroup] = useState(null);

  // Master data states
  const [branchesList, setBranchesList] = useState([]);
  const [departmentsList, setDepartmentsList] = useState([]);
  const [designationsList, setDesignationsList] = useState([]);
  const [gradesList, setGradesList] = useState([]);
  const [subDepartmentsList, setSubDepartmentsList] = useState([]);
  const [categoriesList, setCategoriesList] = useState([]);
  const [employmentTypes, setEmploymentTypes] = useState([]);

  // Track what data has been loaded to avoid refetching
  const [dataLoaded, setDataLoaded] = useState({
    weeklyOffPolicies: false,
    holidayPolicies: false,
    holidays: false,
    holidayBank: false,
    leaveTypes: false,
    leaveGroups: false,
    masterData: false,
  });

  // Initialize local state from parent props so modal shows immediately
  useEffect(() => {
    if (parentWeeklyOffPolicies && parentWeeklyOffPolicies.length) {
      setWeeklyOffPolicies(parentWeeklyOffPolicies);
      setDataLoaded((d) => ({ ...d, weeklyOffPolicies: true }));
    }
    if (parentHolidayPolicies && parentHolidayPolicies.length) {
      setHolidayPolicies(parentHolidayPolicies);
      setDataLoaded((d) => ({ ...d, holidayPolicies: true }));
    }
    if (parentHolidays && parentHolidays.length) {
      setHolidays(parentHolidays);
      setDataLoaded((d) => ({ ...d, holidays: true }));
    }
    if (parentLeaveTypes && parentLeaveTypes.length) {
      setLeaveTypes(parentLeaveTypes);
      setDataLoaded((d) => ({ ...d, leaveTypes: true }));
    }
    if (parentLeaveGroups && parentLeaveGroups.length) {
      setLeaveGroups(parentLeaveGroups);
      setDataLoaded((d) => ({ ...d, leaveGroups: true }));
    }
    if (parentHolidayBank && parentHolidayBank.length) {
      setHolidayBank(parentHolidayBank);
      setDataLoaded((d) => ({ ...d, holidayBank: true }));
    }
    if (parentBranches && parentBranches.length)
      setBranchesList(parentBranches);
    if (parentDepartments && parentDepartments.length)
      setDepartmentsList(parentDepartments);
    if (parentDesignations && parentDesignations.length)
      setDesignationsList(parentDesignations);
    if (parentGrades && parentGrades.length) setGradesList(parentGrades);
    if (parentSubDepartments && parentSubDepartments.length)
      setSubDepartmentsList(parentSubDepartments);
    if (parentCategories && parentCategories.length)
      setCategoriesList(parentCategories);
    if (parentEmploymentTypes && parentEmploymentTypes.length)
      setEmploymentTypes(parentEmploymentTypes);
  }, [
    parentWeeklyOffPolicies,
    parentHolidayPolicies,
    parentHolidays,
    parentLeaveTypes,
    parentLeaveGroups,
    parentHolidayBank,
    parentBranches,
    parentDepartments,
    parentDesignations,
    parentGrades,
    parentSubDepartments,
    parentCategories,
    parentEmploymentTypes,
  ]);

  // Weekly Off Policy Form State
  const [weeklyOffPolicyForm, setWeeklyOffPolicyForm] = useState({
    name: "",
    effectiveDate: "",
    description: "",
    // allowOverride (UI checkbox) maps to backend field overridePolicy
    allowOverride: false,
    weeklyOffs: {
      monday: { enabled: false, type: "full" },
      tuesday: { enabled: false, type: "full" },
      wednesday: { enabled: false, type: "full" },
      thursday: { enabled: false, type: "full" },
      friday: { enabled: false, type: "full" },
      saturday: { enabled: false, type: "full" },
      sunday: { enabled: false, type: "full" },
    },
    assignedTo: {
      allEmployees: false,
      branches: [],
      departments: [],
      designations: [],
      grades: [],
      subDepartments: [],
      categories: [],
      employmentTypes: [],
      employees: [],
    },
  });

  // Leave Type Form State
  const [leaveTypeForm, setLeaveTypeForm] = useState({
    name: "",
    code: "",
    description: "",
    color: "#3B82F6",
    isActive: true,
    isPaid: true,
    requiresApproval: true,
    maxConsecutiveDays: "",
    category: "general", // general, statutory, compensatory
    genderApplicability: "all", // all, male, female
    isStatutory: false,
    statutoryRules: {
      lifetimeLimit: "",
      lifetimeLimitCount: "",
      applicableGender: "all",
      specialConditions: "",
    },
  });

  // Default per-leave-type configuration (9 sections) for Create Leave Group
  const getDefaultLeaveTypeConfig = (leaveType) => ({
    basic: {
      leaveName: leaveType?.name ?? "",
      leaveCode: leaveType?.code ?? "",
      leaveCategory: "Paid",
      genderSpecific: "All",
      applicableEmployeeType: [],
      applicableDepartments: [],
      applicableLocations: [],
      effectiveFromDate: "",
      carryForwardAllowed: "No",
    },
    credit: {
      creditFrequency: "Yearly",
      creditMethod: "Fixed Days",
      daysToCredit: "",
      attendanceDaysRequired: "",
      leavesCreditedPerAttendanceDays: "",
      proRataMidJoining: "No",
      creditOn: "Start of Period",
    },
    accumulation: {
      maximumAccumulationLimit: "",
      carryForwardLimit: "",
      expiryRuleEnabled: "No",
      expiryHow: "Lapse Immediately",
      lapseMonth: "",
      encashmentAllowed: "No",
      encashmentLimit: "",
      encashmentCalculationMethod: "Basic",
    },
    usageRules: {
      minimumLeaveUnits: "1",
      maximumLeavePerRequest: "",
      consecutiveLeaveLimit: "",
      sandwichRule: "Disable",
      clubbingRestrictions: [],
      advanceLeaveAllowed: "No",
      advanceLeaveLimit: "",
      negativeBalanceAllowed: "No",
      negativeBalanceLimit: "",
      noticePeriodRestriction: "No",
      noticePeriodDays: "",
      probationRestriction: "No",
      probationDays: "",
      availOnlyFromNextYear: "No",
      balanceRounding: "full_day",
    },
    approvalWorkflow: {
      enableSeparateWorkflow: "No",
      approvalMethod: "workflow",
      needWorkflowSteps: false,
      reminderDays: 7,
      workflowParallelApproval: false,
      workflowEscalationAfterDays: 3,
      workflowAutoApprovalIfNoAction: false,
      workflowRejectionWithReason: true,
      workflowReinitiationAllowed: true,
      workflowSteps: [
        { role: "reporting_manager", slaDays: 3, notifyOnly: false },
      ],
      cancellationWorkflowSteps: [
        { role: "reporting_manager", slaDays: 3, notifyOnly: false },
      ],
    },
    leaveAdjustmentModule: {
      manualCreditOption: "No",
      manualDebitOption: "No",
      bulkAdjustmentUploadExcel: "No",
      reasonMandatory: "Yes",
      auditTrailRequired: "Yes",
      adminOnlyAccess: "Yes",
    },
    openingBalanceUpload: {
      bulkUploadOption: "No",
      validationDuplicateEntries: "Yes",
      overrideOrMerge: "merge",
      effectiveDateControl: "Yes",
      maintainAdjustmentHistory: "Yes",
    },
    leaveLedger: {
      employeeWiseLeaveLedger: "Yes",
      yearWiseFilter: "Yes",
      downloadOption: "Yes",
    },
    leaveYearConfig: {
      leaveYearType: "Calendar Year",
      customStartMonth: "",
      customEndMonth: "",
      autoResetRules: "",
      carryForwardAutoProcessingJob: "No",
    },
  });

  const [leaveGroupForm, setLeaveGroupForm] = useState({
    name: "",
    description: "",
    leaveTypes: [],
    leaveTypeConfigurations: {},
    assignedTo: {
      allEmployees: false,
      branches: [],
      departments: [],
      designations: [],
      grades: [],
      subDepartments: [],
      categories: [],
      employmentTypes: [],
      employees: [],
    },
    configurations: {
      accumulation: {
        enabled: true,
        method: "yearly", // yearly, monthly, quarterly
        amount: "",
        carryForward: true,
        maxCarryForward: "",
      },
      allotment: {
        method: "fixed", // fixed, pro-rata, based_on_joining
        amount: "",
        frequency: "yearly", // yearly, monthly, quarterly
        rounding: "full_day", // full_day, half_day
      },
      availment: {
        minDaysAdvance: "",
        maxDaysAdvance: "",
        minDaysPerRequest: "1",
        maxDaysPerRequest: "",
        allowHalfDay: false,
        allowWeekends: false,
        allowHolidays: false,
        rounding: "full_day", // full_day, half_day
      },
      lapse: {
        enabled: true,
        lapseDate: "year_end", // year_end, specific_date, months_after_allotment
        lapseAfterMonths: "",
        lapseOnDate: "",
      },
      leaveWorkflow: defaultLeaveGroupWorkflow(),
    },
  });

  // Holiday Policy Form State
  const [holidayPolicyForm, setHolidayPolicyForm] = useState({
    name: "",
    effectiveDate: "",
    description: "",
    holidays: [],
    assignedTo: {
      allEmployees: false,
      branches: [],
      departments: [],
      designations: [],
      grades: [],
      subDepartments: [],
      categories: [],
      employmentTypes: [],
      employees: [],
    },
  });

  const [newHolidayForm, setNewHolidayForm] = useState({
    date: "",
    name: "",
    type: "national", // national or festival
    description: "",
  });

  // Holiday Bank State
  const [holidayBank, setHolidayBank] = useState([]);
  const [showHolidayBankModal, setShowHolidayBankModal] = useState(false);
  const [showHolidayImportModal, setShowHolidayImportModal] = useState(false);
  const [newHolidayBankForm, setNewHolidayBankForm] = useState({
    date: "",
    name: "",
    type: "national", // national or festival
    description: "",
  });

  const handleWeeklyOffChange = (day) => {
    setWeeklyOffs((prev) => ({
      ...prev,
      [day]: !prev[day],
    }));
  };

  const handleAddHoliday = () => {
    if (newHoliday.date && newHoliday.name) {
      setHolidays((prev) => [...prev, { ...newHoliday, id: Date.now() }]);
      setNewHoliday({ date: "", name: "", type: "public" });
      toast.success("Holiday added successfully");
    } else {
      toast.error("Please fill all fields");
    }
  };

  const handleDeleteHoliday = async (id) => {
    // Note: This function may not be needed if holidays are only managed through policies
    // For now, we'll keep it but it should use API if needed
    setHolidays((prev) => prev.filter((holiday) => holiday.id !== id));
    toast.success("Holiday deleted successfully");
  };

  const handleSaveSettings = async () => {
    // This function is kept for backward compatibility
    // Weekly offs and holidays are now managed through policies
    // No action needed as policies handle this
    toast.success("Settings are managed through policies");
  };

  // Weekly Off Policy Functions
  const handleWeeklyOffPolicySubmit = () => {
    if (!weeklyOffPolicyForm.name || !weeklyOffPolicyForm.effectiveDate) {
      toast.error("Please fill all required fields");
      return;
    }

    // Check if attendance is locked for the effective month
    const effectiveDate = new Date(weeklyOffPolicyForm.effectiveDate);
    const effectiveMonth = effectiveDate.getMonth();
    const effectiveYear = effectiveDate.getFullYear();

    // Lock check will be handled by backend - no need to check here

    // Check for duplicate policy names
    const existingPolicy = weeklyOffPolicies.find(
      (p) =>
        p.name.toLowerCase() === weeklyOffPolicyForm.name.toLowerCase() &&
        p.id !== editingWeeklyOffPolicy?.id
    );

    if (existingPolicy) {
      toast.error(
        `A policy with the name "${weeklyOffPolicyForm.name}" already exists.`
      );
      return;
    }

    // Check for overlapping assignments
    const overlappingPolicies = weeklyOffPolicies.filter((p) => {
      if (p.id === editingWeeklyOffPolicy?.id) return false;

      // Check if any assignment criteria overlap
      const currentAssignments = weeklyOffPolicyForm.assignedTo;
      const policyAssignments = p.assignedTo;

      // Check branches overlap
      if (
        currentAssignments.branches.length > 0 &&
        policyAssignments.branches.length > 0
      ) {
        const branchOverlap = currentAssignments.branches.some((branchId) =>
          policyAssignments.branches.includes(branchId)
        );
        if (branchOverlap) return true;
      }

      // Check departments overlap
      if (
        currentAssignments.departments.length > 0 &&
        policyAssignments.departments.length > 0
      ) {
        const deptOverlap = currentAssignments.departments.some((deptId) =>
          policyAssignments.departments.includes(deptId)
        );
        if (deptOverlap) return true;
      }

      // Check designations overlap
      if (
        currentAssignments.designations.length > 0 &&
        policyAssignments.designations.length > 0
      ) {
        const designationOverlap = currentAssignments.designations.some(
          (designationId) =>
            policyAssignments.designations.includes(designationId)
        );
        if (designationOverlap) return true;
      }

      // Check grades overlap
      if (
        currentAssignments.grades.length > 0 &&
        policyAssignments.grades.length > 0
      ) {
        const gradeOverlap = currentAssignments.grades.some((gradeId) =>
          policyAssignments.grades.includes(gradeId)
        );
        if (gradeOverlap) return true;
      }

      // Check sub-departments overlap
      if (
        currentAssignments.subDepartments.length > 0 &&
        policyAssignments.subDepartments.length > 0
      ) {
        const subDeptOverlap = currentAssignments.subDepartments.some(
          (subDeptId) => policyAssignments.subDepartments.includes(subDeptId)
        );
        if (subDeptOverlap) return true;
      }

      // Check categories overlap
      if (
        currentAssignments.categories.length > 0 &&
        policyAssignments.categories.length > 0
      ) {
        const categoryOverlap = currentAssignments.categories.some(
          (categoryId) => policyAssignments.categories.includes(categoryId)
        );
        if (categoryOverlap) return true;
      }

      // Check employment types overlap
      if (
        currentAssignments.employmentTypes.length > 0 &&
        policyAssignments.employmentTypes.length > 0
      ) {
        const empTypeOverlap = currentAssignments.employmentTypes.some(
          (empTypeId) => policyAssignments.employmentTypes.includes(empTypeId)
        );
        if (empTypeOverlap) return true;
      }

      // Check employees overlap
      if (
        currentAssignments.employees.length > 0 &&
        policyAssignments.employees.length > 0
      ) {
        const employeeOverlap = currentAssignments.employees.some(
          (employeeId) => policyAssignments.employees.includes(employeeId)
        );
        if (employeeOverlap) return true;
      }

      // Check if both are assigned to all employees
      if (currentAssignments.allEmployees && policyAssignments.allEmployees) {
        return true;
      }

      return false;
    });

    if (overlappingPolicies.length > 0) {
      toast.error(
        `This policy assignment overlaps with existing policy "${overlappingPolicies[0].name}". Please ensure unique assignments.`
      );
      return;
    }

    // Create or update policy via API
    const savePolicy = async () => {
      try {
        setIsSavingWeeklyOffPolicy(true);
        setLoading(true);
        let response;

        if (editingWeeklyOffPolicy) {
          const payload = {
            ...weeklyOffPolicyForm,
            overridePolicy: weeklyOffPolicyForm.allowOverride === true,
          };
          response = await attendanceStructureService.updateWeeklyOffPolicy(
            editingWeeklyOffPolicy.id,
            payload
          );
          if (response.success) {
            toast.success("Weekly off policy updated successfully");

            // Close modal and reset form immediately after success
            setShowWeeklyOffModal(false);
            setEditingWeeklyOffPolicy(null);
            setWeeklyOffPolicyForm({
              name: "",
              effectiveDate: "",
              description: "",
              allowOverride: false,
              weeklyOffs: {
                monday: { enabled: false, type: "full" },
                tuesday: { enabled: false, type: "full" },
                wednesday: { enabled: false, type: "full" },
                thursday: { enabled: false, type: "full" },
                friday: { enabled: false, type: "full" },
                saturday: { enabled: false, type: "full" },
                sunday: { enabled: false, type: "full" },
              },
              assignedTo: {
                allEmployees: false,
                branches: [],
                departments: [],
                designations: [],
                grades: [],
                subDepartments: [],
                categories: [],
                employmentTypes: [],
                employees: [],
              },
            });

            // Refresh data after closing modal
            await refreshData("weeklyOffPolicies");

            // Notify parent component to refresh employee mappings
            window.dispatchEvent(new CustomEvent("weeklyOffPolicyUpdated"));
          }
        } else {
          const payload = {
            ...weeklyOffPolicyForm,
            overridePolicy: weeklyOffPolicyForm.allowOverride === true,
          };
          response = await attendanceStructureService.createWeeklyOffPolicy(
            payload
          );
          if (response.success) {
            toast.success("Weekly off policy created successfully");

            // Log the response to see if it includes mappings
            console.log("Policy creation response:", response);

            // Close modal and reset form immediately after success
            setShowWeeklyOffModal(false);
            setEditingWeeklyOffPolicy(null);
            setWeeklyOffPolicyForm({
              name: "",
              effectiveDate: "",
              description: "",
              allowOverride: false,
              weeklyOffs: {
                monday: { enabled: false, type: "full" },
                tuesday: { enabled: false, type: "full" },
                wednesday: { enabled: false, type: "full" },
                thursday: { enabled: false, type: "full" },
                friday: { enabled: false, type: "full" },
                saturday: { enabled: false, type: "full" },
                sunday: { enabled: false, type: "full" },
              },
              assignedTo: {
                allEmployees: false,
                branches: [],
                departments: [],
                designations: [],
                grades: [],
                subDepartments: [],
                categories: [],
                employmentTypes: [],
                employees: [],
              },
            });

            // Refresh data after closing modal
            await refreshData("weeklyOffPolicies");

            // Manually refresh employee mappings to ensure they're up to date
            console.log(
              "Manually refreshing employee mappings after policy creation"
            );
            const employeeWeeklyOffResponse =
              await attendanceStructureService.getEmployeeWeeklyOffPolicies();
            if (employeeWeeklyOffResponse.success) {
              console.debug(
                "Manual refresh - employeeWeeklyOffResponse raw data:",
                employeeWeeklyOffResponse.data
              );
              const normalizedMappings = normalizeEmployeeWeeklyOffMapping(
                employeeWeeklyOffResponse.data || {}
              );
              console.debug(
                "Manual refresh - employeeWeeklyOffResponse normalized mappings:",
                normalizedMappings
              );
              setEmployeeWeeklyOffMappings(normalizedMappings);
            }

            // Notify parent component to refresh employee mappings
            window.dispatchEvent(new CustomEvent("weeklyOffPolicyUpdated"));
          }
        }
      } catch (error) {
        console.error("Error saving weekly off policy:", error);
        const errorMessage =
          error.response?.data?.message ||
          error.message ||
          "Failed to save policy";
        toast.error(errorMessage);
      } finally {
        setLoading(false);
        setIsSavingWeeklyOffPolicy(false);
      }
    };

    savePolicy();
  };

  const handleEditWeeklyOffPolicy = async (policy) => {
    // Load master data if needed for assignment
    await fetchAllMasterData();
    setEditingWeeklyOffPolicy(policy);
    setWeeklyOffPolicyForm({
      ...policy,
      // Map backend overridePolicy flag to UI allowOverride checkbox
      allowOverride: policy.overridePolicy === true,
    });
    setShowWeeklyOffModal(true);
  };

  const handleDeleteWeeklyOffPolicy = async (id) => {
    showConfirmation(
      "Delete Weekly Off Policy",
      "Are you sure you want to delete this policy?",
      async () => {
        try {
          setLoading(true);
          const response =
            await attendanceStructureService.deleteWeeklyOffPolicy(id);
          if (response.success) {
            toast.success("Weekly off policy deleted successfully");
            await refreshData("weeklyOffPolicies"); // Refresh data
          }
        } catch (error) {
          console.error("Error deleting weekly off policy:", error);
          const errorMessage =
            error.response?.data?.message ||
            error.message ||
            "Failed to delete policy";
          toast.error(errorMessage);
        } finally {
          setLoading(false);
        }
      }
    );
  };

  // Holiday Policy Functions
  const handleHolidayPolicySubmit = () => {
    if (!holidayPolicyForm.name || !holidayPolicyForm.effectiveDate) {
      toast.error("Please fill all required fields");
      return;
    }

    if (holidayPolicyForm.holidays.length === 0) {
      toast.error("Please select at least one holiday from the bank");
      return;
    }

    // Check if attendance is locked for the effective month
    const effectiveDate = new Date(holidayPolicyForm.effectiveDate);
    const effectiveMonth = effectiveDate.getMonth();
    const effectiveYear = effectiveDate.getFullYear();

    // Create or update holiday policy via API
    const saveHolidayPolicy = async () => {
      try {
        setIsSavingHolidayPolicy(true);
        setLoading(true);
        let response;

        if (editingHolidayPolicy) {
          response = await attendanceStructureService.updateHolidayPolicy(
            editingHolidayPolicy.id,
            holidayPolicyForm
          );
          if (response.success) {
            toast.success("Holiday policy updated successfully");

            // Close modal and reset immediately
            setShowHolidayModal(false);
            setEditingHolidayPolicy(null);
            setHolidayPolicyForm({
              name: "",
              effectiveDate: "",
              description: "",
              holidays: [],
              assignedTo: {
                allEmployees: false,
                branches: [],
                departments: [],
                designations: [],
                grades: [],
                subDepartments: [],
                categories: [],
                employmentTypes: [],
                employees: [],
              },
            });

            // Refresh data after closing modal
            await refreshData("holidayPolicies");
          }
        } else {
          // Log payload and response for debugging toast visibility / API issues
          console.log("Creating holiday policy - payload:", holidayPolicyForm);
          response = await attendanceStructureService.createHolidayPolicy(
            holidayPolicyForm
          );
          console.log("createHolidayPolicy response:", response);
          if (response && response.success) {
            console.log("About to toast success for holiday create:", response);
            toast.success("Holiday policy created successfully", {
              id: "holiday-create-success",
              duration: 8000,
            });

            // Close modal and reset immediately
            setShowHolidayModal(false);
            setEditingHolidayPolicy(null);
            setHolidayPolicyForm({
              name: "",
              effectiveDate: "",
              description: "",
              holidays: [],
              assignedTo: {
                allEmployees: false,
                branches: [],
                departments: [],
                designations: [],
                grades: [],
                subDepartments: [],
                categories: [],
                employmentTypes: [],
                employees: [],
              },
            });

            // Refresh data after closing modal
            await refreshData("holidayPolicies");
          } else {
            console.warn(
              "Holiday policy create failed or returned unexpected response:",
              response
            );
            // Show a defensive error toast so users get feedback when create fails
            toast.error(response?.message || "Failed to create holiday policy");
          }
        }
      } catch (error) {
        console.error("Error saving holiday policy:", error);
        // Log full server response for easier debugging of validation errors
        console.log(
          "createHolidayPolicy error.response:",
          error.response?.data,
          "status:",
          error.response?.status
        );
        const errorMessage =
          error.response?.data?.message ||
          // Some APIs return validation errors in `errors` or `data` fields
          (error.response?.data?.errors &&
            JSON.stringify(error.response.data.errors)) ||
          error.message ||
          "Failed to save policy";
        toast.error(errorMessage);
      } finally {
        setLoading(false);
        setIsSavingHolidayPolicy(false);
      }
    };

    saveHolidayPolicy();
  };

  const handleEditHolidayPolicy = async (policy) => {
    // Load master data if needed for assignment
    await fetchAllMasterData();
    setEditingHolidayPolicy(policy);
    setHolidayPolicyForm(policy);
    setShowHolidayModal(true);
  };

  const handleDeleteHolidayPolicy = async (id) => {
    showConfirmation(
      "Delete Holiday Policy",
      "Are you sure you want to delete this policy?",
      async () => {
        try {
          setLoading(true);
          const response = await attendanceStructureService.deleteHolidayPolicy(
            id
          );
          if (response.success) {
            toast.success("Holiday policy deleted successfully");
            await refreshData("holidayPolicies"); // Refresh data
          }
        } catch (error) {
          console.error("Error deleting holiday policy:", error);
          const errorMessage =
            error.response?.data?.message ||
            error.message ||
            "Failed to delete policy";
          toast.error(errorMessage);
        } finally {
          setLoading(false);
        }
      }
    );
  };

  // Leave Type Management Functions
  const handleLeaveTypeSubmit = () => {
    if (!leaveTypeForm.name || !leaveTypeForm.code) {
      toast.error("Please fill all required fields");
      return;
    }

    // ✅ Validate leave code: must be exactly 2 letters (e.g., "CL", "EL", "LW")
    if (leaveTypeForm.code.length !== 2) {
      toast.error("Leave code must be exactly 2 letters (e.g., CL, EL, LW)");
      return;
    }

    if (!/^[A-Z]{2}$/.test(leaveTypeForm.code)) {
      toast.error("Leave code must contain only uppercase letters");
      return;
    }

    // Create or update leave type via API
    const saveLeaveType = async () => {
      try {
        setIsSavingLeaveType(true);
        setLoading(true);
        let response;

        if (editingLeaveType) {
          response = await attendanceStructureService.updateLeaveType(
            editingLeaveType.id,
            leaveTypeForm
          );
          if (response.success) {
            toast.success("Leave type updated successfully");

            // Close modal and reset immediately
            setShowLeaveTypeModal(false);
            setEditingLeaveType(null);
            setLeaveTypeForm({
              code: "",
              description: "",
              color: "#3B82F6",
              isActive: true,
              isPaid: true,
              requiresApproval: true,
              maxConsecutiveDays: "",
              category: "general",
              genderApplicability: "all",
              isStatutory: false,
              statutoryRules: {
                lifetimeLimit: "",
                lifetimeLimitCount: "",
                applicableGender: "all",
                specialConditions: "",
              },
            });

            // Refresh data after closing modal
            await refreshData("leaveTypes");
            // Notify parent components to refresh their leave-type caches
            try {
              window.dispatchEvent(new Event("leaveTypesUpdated"));
            } catch (e) {
              console.debug("leaveTypesUpdated dispatch failed", e);
            }
          }
        } else {
          response = await attendanceStructureService.createLeaveType(
            leaveTypeForm
          );
          if (response.success) {
            toast.success("Leave type created successfully");

            // Close modal and reset immediately
            setShowLeaveTypeModal(false);
            setEditingLeaveType(null);
            setLeaveTypeForm({
              code: "",
              description: "",
              color: "#3B82F6",
              isActive: true,
              isPaid: true,
              requiresApproval: true,
              maxConsecutiveDays: "",
              category: "general",
              genderApplicability: "all",
              isStatutory: false,
              statutoryRules: {
                lifetimeLimit: "",
                lifetimeLimitCount: "",
                applicableGender: "all",
                specialConditions: "",
              },
            });

            // Refresh data after closing modal
            await refreshData("leaveTypes");
            try {
              window.dispatchEvent(new Event("leaveTypesUpdated"));
            } catch (e) {
              console.debug("leaveTypesUpdated dispatch failed", e);
            }
          }
        }
      } catch (error) {
        console.error("Error saving leave type:", error);
        const errorMessage =
          error.response?.data?.message ||
          error.message ||
          "Failed to save leave type";
        toast.error(errorMessage);
      } finally {
        setLoading(false);
        setIsSavingLeaveType(false);
      }
    };

    saveLeaveType();
  };

  const handleEditLeaveType = async (leaveType) => {
    // Load master data if needed
    await fetchAllMasterData();
    setEditingLeaveType(leaveType);
    setLeaveTypeForm(leaveType);
    setShowLeaveTypeModal(true);
  };

  const handleDeleteLeaveType = async (id) => {
    // Find the leave type to check its assignments
    const leaveType = leaveTypes.find((lt) => lt.id === id);

    if (!leaveType) {
      toast.error("Leave type not found");
      return;
    }

    // Check if leave type is used in any leave groups
    const isUsedInGroups = leaveGroups.some((group) =>
      group.leaveTypes.some((lt) => lt.id === id)
    );

    if (isUsedInGroups) {
      toast.error(
        "Cannot delete leave type as it is being used in leave groups"
      );
      return;
    }

    // Check if leave type is assigned to users
    const assignedTo = leaveType.assignedTo || {};
    const isAssignedToUsers =
      assignedTo.allEmployees === true ||
      (assignedTo.employees && assignedTo.employees.length > 0) ||
      (assignedTo.branches && assignedTo.branches.length > 0) ||
      (assignedTo.departments && assignedTo.departments.length > 0) ||
      (assignedTo.designations && assignedTo.designations.length > 0) ||
      (assignedTo.grades && assignedTo.grades.length > 0) ||
      (assignedTo.subDepartments && assignedTo.subDepartments.length > 0) ||
      (assignedTo.categories && assignedTo.categories.length > 0) ||
      (assignedTo.employmentTypes && assignedTo.employmentTypes.length > 0);

    if (isAssignedToUsers) {
      toast.error("Cannot delete: Leave type is assigned to users");
      return;
    }

    // If not assigned to users, proceed with deletion via API
    try {
      setLoading(true);
      const response = await attendanceStructureService.deleteLeaveType(id);

      if (response.success) {
        toast.success("Leave type deleted successfully");
        await refreshData("leaveTypes"); // Refresh the leave types list
      } else {
        toast.error(response.message || "Failed to delete leave type");
      }
    } catch (error) {
      console.error("Error deleting leave type:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to delete leave type";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Leave Group Management Functions
  const handleLeaveGroupSubmit = () => {
    if (!leaveGroupForm.name || leaveGroupForm.leaveTypes.length === 0) {
      toast.error(
        "Please fill all required fields and select at least one leave type"
      );
      return;
    }

    // Check for duplicate leave group names
    const existingGroup = leaveGroups.find(
      (lg) =>
        lg.name.toLowerCase() === leaveGroupForm.name.toLowerCase() &&
        lg.id !== editingLeaveGroup?.id
    );

    if (existingGroup) {
      toast.error(`A leave group with this name already exists.`);
      return;
    }

    const normalizedAssignedTo = leaveGroupForm.assignedTo?.allEmployees
      ? {
          allEmployees: true,
          branches: [],
          departments: [],
          designations: [],
          grades: [],
          subDepartments: [],
          categories: [],
          employmentTypes: [],
          employees: [],
        }
      : leaveGroupForm.assignedTo;

    const leaveGroupPayload = {
      ...leaveGroupForm,
      assignedTo: normalizedAssignedTo,
    };

    // Create or update leave group via API
    const saveLeaveGroup = async () => {
      try {
        setIsSavingLeaveGroup(true);
        setLoading(true);
        let response;

        if (editingLeaveGroup) {
          response = await attendanceStructureService.updateLeaveGroup(
            editingLeaveGroup.id,
            leaveGroupPayload
          );
          if (response.success) {
            toast.success("Leave group updated successfully");

            // Close modal and reset immediately
            setShowLeaveGroupModal(false);
            setEditingLeaveGroup(null);
            setLeaveGroupForm({
              name: "",
              description: "",
              leaveTypes: [],
              leaveTypeConfigurations: {},
              assignedTo: {
                allEmployees: false,
                branches: [],
                departments: [],
                designations: [],
                grades: [],
                subDepartments: [],
                categories: [],
                employmentTypes: [],
                employees: [],
              },
              configurations: {
                accumulation: {
                  enabled: true,
                  method: "yearly",
                  amount: "",
                  carryForward: true,
                  maxCarryForward: "",
                },
                allotment: {
                  method: "fixed",
                  amount: "",
                  frequency: "yearly",
                  rounding: "full_day",
                },
                availment: {
                  minDaysAdvance: "",
                  maxDaysAdvance: "",
                  minDaysPerRequest: "1",
                  maxDaysPerRequest: "",
                  allowHalfDay: false,
                  allowWeekends: false,
                  allowHolidays: false,
                  rounding: "full_day",
                },
                lapse: {
                  enabled: true,
                  lapseDate: "year_end",
                  lapseAfterMonths: "",
                  lapseOnDate: "",
                },
                leaveWorkflow: defaultLeaveGroupWorkflow(),
              },
            });

            // Refresh data after closing modal
            await refreshData("leaveGroups");
          }
        } else {
          response = await attendanceStructureService.createLeaveGroup(
            leaveGroupPayload
          );
          if (response.success) {
            toast.success("Leave group created successfully");

            // Close modal and reset immediately
            setShowLeaveGroupModal(false);
            setEditingLeaveGroup(null);
            setLeaveGroupForm({
              name: "",
              description: "",
              leaveTypes: [],
              leaveTypeConfigurations: {},
              assignedTo: {
                allEmployees: false,
                branches: [],
                departments: [],
                designations: [],
                grades: [],
                subDepartments: [],
                categories: [],
                employmentTypes: [],
                employees: [],
              },
              configurations: {
                accumulation: {
                  enabled: true,
                  method: "yearly",
                  amount: "",
                  carryForward: true,
                  maxCarryForward: "",
                },
                allotment: {
                  method: "fixed",
                  amount: "",
                  frequency: "yearly",
                  rounding: "full_day",
                },
                availment: {
                  minDaysAdvance: "",
                  maxDaysAdvance: "",
                  minDaysPerRequest: "1",
                  maxDaysPerRequest: "",
                  allowHalfDay: false,
                  allowWeekends: false,
                  allowHolidays: false,
                  rounding: "full_day",
                },
                lapse: {
                  enabled: true,
                  lapseDate: "year_end",
                  lapseAfterMonths: "",
                  lapseOnDate: "",
                },
                leaveWorkflow: defaultLeaveGroupWorkflow(),
              },
            });

            // Refresh data after closing modal
            await refreshData("leaveGroups");
          }
        }
      } catch (error) {
        console.error("Error saving leave group:", error);
        const errorMessage =
          error.response?.data?.message ||
          error.message ||
          "Failed to save leave group";
        toast.error(errorMessage);
      } finally {
        setLoading(false);
        setIsSavingLeaveGroup(false);
      }
    };

    saveLeaveGroup();
  };

  const handleEditLeaveGroup = async (leaveGroup) => {
    // Load master data if needed for assignment
    await fetchAllMasterData();
    setEditingLeaveGroup(leaveGroup);

    const normalizedLeaveTypes = (leaveGroup.leaveTypes || []).map((lt) => {
      if (typeof lt === "object" && lt !== null) return lt;
      const found = leaveTypes.find((type) => type.id === lt);
      return found || { id: lt };
    });

    const configs = { ...(leaveGroup.leaveTypeConfigurations || {}) };
    normalizedLeaveTypes.forEach((lt) => {
      if (!configs[lt.id]) configs[lt.id] = getDefaultLeaveTypeConfig(lt);
    });

    setLeaveGroupForm({
      ...leaveGroup,
      leaveTypes: normalizedLeaveTypes,
      leaveTypeConfigurations: configs,
      configurations: {
        ...(leaveGroup.configurations || {}),
        leaveWorkflow: normalizeLeaveGroupWorkflow(
          leaveGroup.configurations?.leaveWorkflow
        ),
      },
    });
    setShowLeaveGroupModal(true);
  };

  const updateLeaveGroupWorkflow = (leaveWorkflow) => {
    setLeaveGroupForm((prev) => ({
      ...prev,
      configurations: {
        ...(prev.configurations || {}),
        leaveWorkflow,
      },
    }));
  };

  const updateLeaveTypeConfig = (leaveTypeId, section, updates) => {
    setLeaveGroupForm((prev) => ({
      ...prev,
      leaveTypeConfigurations: {
        ...(prev.leaveTypeConfigurations || {}),
        [leaveTypeId]: {
          ...(prev.leaveTypeConfigurations?.[leaveTypeId] || {}),
          [section]: {
            ...(prev.leaveTypeConfigurations?.[leaveTypeId]?.[section] || {}),
            ...updates,
          },
        },
      },
    }));
  };

  const handleDeleteLeaveGroup = async (id) => {
    showConfirmation(
      "Delete Leave Group",
      "Are you sure you want to delete this leave group?",
      async () => {
        try {
          setLoading(true);
          const response = await attendanceStructureService.deleteLeaveGroup(
            id
          );
          if (response.success) {
            toast.success("Leave group deleted successfully");
            await refreshData("leaveGroups"); // Refresh data
          }
        } catch (error) {
          console.error("Error deleting leave group:", error);
          const errorMessage =
            error.response?.data?.message ||
            error.message ||
            "Failed to delete leave group";
          toast.error(errorMessage);
        } finally {
          setLoading(false);
        }
      }
    );
  };
  // Note: Employee assignments are now handled automatically by the backend
  // when policies are created/updated, so these apply functions are no longer needed

  const handleAddHolidayToPolicy = () => {
    if (!newHolidayForm.date || !newHolidayForm.name) {
      toast.error("Please fill all required fields");
      return;
    }

    const holiday = {
      id: Date.now(),
      ...newHolidayForm,
    };

    setHolidayPolicyForm((prev) => ({
      ...prev,
      holidays: [...prev.holidays, holiday],
    }));

    setNewHolidayForm({
      date: "",
      name: "",
      type: "national",
      description: "",
    });

    toast.success("Holiday added to policy");
  };

  // Holiday Bank Functions
  const handleAddToHolidayBank = async () => {
    if (!newHolidayBankForm.date || !newHolidayBankForm.name) {
      toast.error("Please fill all required fields");
      return;
    }

    try {
      setLoading(true);
      const response = await attendanceStructureService.createHolidayBank(
        newHolidayBankForm
      );
      if (response.success) {
        toast.success("Holiday added to bank");
        setNewHolidayBankForm({
          date: "",
          name: "",
          type: "national",
          description: "",
        });
        await refreshData("holidayBank"); // Refresh data
      }
    } catch (error) {
      console.error("Error adding holiday to bank:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to add holiday";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveFromHolidayBank = async (id) => {
    showConfirmation(
      "Remove Holiday from Bank",
      "Are you sure you want to remove this holiday from the bank?",
      async () => {
        try {
          setLoading(true);
          const response = await attendanceStructureService.deleteHolidayBank(
            id
          );
          if (response.success) {
            toast.success("Holiday removed from bank");
            await refreshData("holidayBank"); // Refresh data
          }
        } catch (error) {
          console.error("Error removing holiday from bank:", error);
          const errorMessage =
            error.response?.data?.message ||
            error.message ||
            "Failed to remove holiday";
          toast.error(errorMessage);
        } finally {
          setLoading(false);
        }
      }
    );
  };

  const handleAddHolidayFromBank = (holiday) => {
    setHolidayPolicyForm((prev) => ({
      ...prev,
      holidays: [...prev.holidays, holiday],
    }));
    toast.success("Holiday added to policy from bank");
  };

  const handleHolidayImport = async (file) => {
    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);

      const importedHolidays = jsonData
        .map((row, index) => ({
          id: Date.now() + index,
          name: row["Holiday Name"] || row["Name"] || "",
          date: row["Date"] || row["Holiday Date"] || "",
          type: row["Type"] || row["Holiday Type"] || "national",
          description: row["Description"] || "",
        }))
        .filter((holiday) => holiday.name && holiday.date);

      // Validate date format and convert to proper format
      const validHolidays = importedHolidays.map((holiday) => {
        let dateStr = holiday.date;
        // Handle different date formats
        if (typeof dateStr === "number") {
          // Excel serial date
          dateStr = XLSX.SSF.parse_date_code(dateStr);
          dateStr = `${dateStr.y}-${String(dateStr.m).padStart(
            2,
            "0"
          )}-${String(dateStr.d).padStart(2, "0")}`;
        } else if (typeof dateStr === "string") {
          // Try to parse various date formats; use local date components to avoid timezone shift
          const date = new Date(dateStr);
          if (!isNaN(date.getTime())) {
            dateStr =
              date.getFullYear() +
              "-" +
              String(date.getMonth() + 1).padStart(2, "0") +
              "-" +
              String(date.getDate()).padStart(2, "0");
          }
        }

        return {
          ...holiday,
          date: dateStr,
          type:
            holiday.type.toLowerCase() === "festival" ? "festival" : "national",
        };
      });

      setHolidayBank((prev) => [...prev, ...validHolidays]);
      toast.success(`Successfully imported ${validHolidays.length} holidays`);
      setShowHolidayImportModal(false);
    } catch (error) {
      console.error("Error importing holidays:", error);
      toast.error("Failed to import holidays. Please check the file format.");
    }
  };

  const handleDownloadHolidayTemplate = async () => {
    try {
      const templateData = [
        {
          "Sl No": 1,
          "Holiday Name": "New Year",
          Date: "01/01/2024",
          Type: "National",
          Description: "New Year Celebration",
        },
        {
          "Sl No": 2,
          "Holiday Name": "Independence Day",
          Date: "15/08/2024",
          Type: "National",
          Description: "Independence Day",
        },
        {
          "Sl No": 3,
          "Holiday Name": "Diwali",
          Date: "01/11/2024",
          Type: "Festival",
          Description: "Festival of Lights",
        },
      ];

      const worksheet = XLSX.utils.json_to_sheet(templateData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Holidays");
      const now = new Date();
      XLSX.writeFile(
        workbook,
        buildAttendanceExcelFilenameStatic(
          "Holiday_Template",
          now.getMonth(),
          now.getFullYear()
        )
      );
      toast.success("Holiday template downloaded successfully");
    } catch (error) {
      console.error("Error downloading template:", error);
      toast.error("Failed to download template");
    }
  };

  const handleRemoveHolidayFromPolicy = (holidayId) => {
    setHolidayPolicyForm((prev) => ({
      ...prev,
      holidays: prev.holidays.filter((h) => h.id !== holidayId),
    }));
  };

  // Employee selection functions
  const handleEmployeeSelection = (employeeId) => {
    setSelectedEmployees((prev) =>
      prev.includes(employeeId)
        ? prev.filter((id) => id !== employeeId)
        : [...prev, employeeId]
    );
  };

  const handleSelectAllEmployees = () => {
    setWeeklyOffPolicyForm((prev) => ({
      ...prev,
      assignedTo: {
        ...prev.assignedTo,
        allEmployees: !prev.assignedTo.allEmployees,
      },
    }));
  };

  const handleSelectAllEmployeesForHoliday = () => {
    setHolidayPolicyForm((prev) => ({
      ...prev,
      assignedTo: {
        ...prev.assignedTo,
        allEmployees: !prev.assignedTo.allEmployees,
      },
    }));
  };

  // Function to apply weekly off policy to assigned employees
  // Note: applyWeeklyOffPolicy function removed - backend handles assignments automatically

  const filteredEmployees = (
    employees && Array.isArray(employees) ? employees : []
  ).filter(
    (employee) =>
      employee.user?.name
        ?.toLowerCase()
        .includes(employeeSearchTerm.toLowerCase()) ||
      employee.empCode?.toLowerCase().includes(employeeSearchTerm.toLowerCase())
  );

  // Use refs to track loading state and prevent duplicate calls
  const loadingRef = useRef({
    weeklyOffPolicies: false,
    holidayPolicies: false,
    holidays: false,
    holidayBank: false,
    leaveTypes: false,
    leaveGroups: false,
  });

  // Track if component is ready to load data (modal is visible)
  const [isReady, setIsReady] = useState(false);

  // Set ready state after a brief delay to ensure modal is visible
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsReady(true);
    }, 100); // Small delay to ensure modal is fully rendered

    return () => clearTimeout(timer);
  }, []);

  // Lazy load data based on active tab - only when component is ready
  useEffect(() => {
    // Don't load data until component is ready (modal is visible)
    if (!isReady) return;

    let isMounted = true;

    const loadTabData = async () => {
      try {
        let hasPendingRequests = false;

        switch (activeStructureTab) {
          case "weeklyOff": {
            // Only load weekly off policies if not already loaded and not currently loading
            if (
              !dataLoaded.weeklyOffPolicies &&
              !loadingRef.current.weeklyOffPolicies
            ) {
              hasPendingRequests = true;
              loadingRef.current.weeklyOffPolicies = true;
              // Start the request but don't wait for it
              attendanceStructureService
                .getWeeklyOffPolicies()
                .then((response) => {
                  if (isMounted && response.success) {
                    // Update state - this should trigger a re-render
                    const policies = response.data || [];
                    setWeeklyOffPolicies(policies);
                    setDataLoaded((prev) => ({
                      ...prev,
                      weeklyOffPolicies: true,
                    }));
                    // Force a re-render by updating trigger state
                    setDataUpdateTrigger((prev) => prev + 1);
                    console.log("Weekly off policies loaded:", policies.length);
                  }
                })
                .catch((error) => {
                  console.error("Error loading weekly off policies:", error);
                  if (isMounted) {
                    loadingRef.current.weeklyOffPolicies = false;
                  }
                })
                .finally(() => {
                  if (isMounted) {
                    loadingRef.current.weeklyOffPolicies = false;
                  }
                });
            } else if (dataLoaded.weeklyOffPolicies) {
              // Data already loaded, ensure UI shows it
              // This handles the case when switching back to the tab
              hasPendingRequests = false;
            }
            break;
          }

          case "holiday": {
            // Load holiday-related data in parallel, update UI as each completes
            if (
              !dataLoaded.holidayPolicies &&
              !loadingRef.current.holidayPolicies
            ) {
              hasPendingRequests = true;
              loadingRef.current.holidayPolicies = true;
              attendanceStructureService
                .getHolidayPolicies()
                .then((response) => {
                  if (isMounted && response.success) {
                    setHolidayPolicies(response.data || []);
                    setDataLoaded((prev) => ({
                      ...prev,
                      holidayPolicies: true,
                    }));
                    setDataUpdateTrigger((prev) => prev + 1);
                  }
                })
                .catch((error) => {
                  console.error("Error loading holiday policies:", error);
                })
                .finally(() => {
                  loadingRef.current.holidayPolicies = false;
                });
            }

            if (!dataLoaded.holidays && !loadingRef.current.holidays) {
              hasPendingRequests = true;
              loadingRef.current.holidays = true;
              attendanceStructureService
                .getHolidays()
                .then((response) => {
                  if (isMounted && response.success) {
                    setHolidays(response.data || []);
                    setDataLoaded((prev) => ({
                      ...prev,
                      holidays: true,
                    }));
                    setDataUpdateTrigger((prev) => prev + 1);
                  }
                })
                .catch((error) => {
                  console.error("Error loading holidays:", error);
                })
                .finally(() => {
                  loadingRef.current.holidays = false;
                });
            }

            if (!dataLoaded.holidayBank && !loadingRef.current.holidayBank) {
              hasPendingRequests = true;
              loadingRef.current.holidayBank = true;
              attendanceStructureService
                .getHolidayBank()
                .then((response) => {
                  if (isMounted && response.success) {
                    setHolidayBank(response.data || []);
                    setDataLoaded((prev) => ({
                      ...prev,
                      holidayBank: true,
                    }));
                    setDataUpdateTrigger((prev) => prev + 1);
                  }
                })
                .catch((error) => {
                  console.error("Error loading holiday bank:", error);
                })
                .finally(() => {
                  loadingRef.current.holidayBank = false;
                });
            }
            break;
          }

          case "leaveManagement": {
            // Load leave-related data in parallel, update UI as each completes
            if (!dataLoaded.leaveTypes && !loadingRef.current.leaveTypes) {
              hasPendingRequests = true;
              loadingRef.current.leaveTypes = true;
              attendanceStructureService
                .getLeaveTypes()
                .then((response) => {
                  if (isMounted && response.success) {
                    setLeaveTypes(response.data || []);
                    setDataLoaded((prev) => ({
                      ...prev,
                      leaveTypes: true,
                    }));
                    setDataUpdateTrigger((prev) => prev + 1);
                  }
                })
                .catch((error) => {
                  console.error("Error loading leave types:", error);
                })
                .finally(() => {
                  loadingRef.current.leaveTypes = false;
                });
            }

            if (!dataLoaded.leaveGroups && !loadingRef.current.leaveGroups) {
              hasPendingRequests = true;
              loadingRef.current.leaveGroups = true;
              attendanceStructureService
                .getLeaveGroups()
                .then((response) => {
                  if (isMounted && response.success) {
                    setLeaveGroups(response.data || []);
                    setDataLoaded((prev) => ({
                      ...prev,
                      leaveGroups: true,
                    }));
                    setDataUpdateTrigger((prev) => prev + 1);
                  }
                })
                .catch((error) => {
                  console.error("Error loading leave groups:", error);
                })
                .finally(() => {
                  loadingRef.current.leaveGroups = false;
                });
            }
            break;
          }

          case "attendancePolicy": {
            if (!dataLoaded.masterData) {
              fetchAllMasterData();
            }
            break;
          }

          default:
            break;
        }

        // Only show loading if we actually started requests
        // Set it to false immediately so UI can render while data loads
        if (hasPendingRequests) {
          setLoading(true);
          // Set loading to false immediately after starting requests
          // This allows UI to render while data loads in the background
          setTimeout(() => {
            if (isMounted) {
              setLoading(false);
            }
          }, 50);
        } else {
          setLoading(false);
        }
      } catch (error) {
        console.error("Error fetching attendance structure data:", error);
        toast.error("Failed to fetch attendance structure data");
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadTabData();

    // Cleanup function to prevent state updates if component unmounts
    return () => {
      isMounted = false;
    };
  }, [activeStructureTab, isReady]); // Added isReady to ensure data loads only when modal is visible

  // Function to fetch all master data (lazy load only when needed)
  const fetchAllMasterData = async () => {
    // If already loaded, skip
    if (dataLoaded.masterData) {
      return;
    }

    try {
      // Fetch all master data in parallel for better performance
      const [
        employeesResponse,
        branchesResponse,
        departmentsResponse,
        designationsResponse,
        gradesResponse,
        subDepartmentsResponse,
        categoriesResponse,
      ] = await Promise.all([
        // Request a limited employee payload (small fields + page/limit) to avoid fetching the entire org
        // If backend ignores `fields` it will still accept page/limit; adjust as needed for your API
        employeeService.getEmployees({
          limit: 500,
          page: 1,
          fields: "id,name,code,joinDate,exitDate",
          excludeEmploymentTypes: "External,external",
        }),
        branchService.getBranches(),
        departmentService.getDepartments(),
        designationService.getDesignations(),
        gradesService.getGrades(),
        subDepartmentService.getAllSubDepartments(),
        categoryService.getAllCategories(),
      ]);

      if (employeesResponse.success) {
        setEmployees(employeesResponse.data || []);
      }

      if (branchesResponse.success) {
        setBranchesList(branchesResponse.data || []);
      }

      if (departmentsResponse.success) {
        setDepartmentsList(departmentsResponse.data || []);
      }

      if (designationsResponse.success) {
        setDesignationsList(designationsResponse.data || []);
      }

      if (gradesResponse.success) {
        setGradesList(gradesResponse.data || []);
      }

      if (subDepartmentsResponse.success) {
        setSubDepartmentsList(subDepartmentsResponse.data || []);
      }

      if (categoriesResponse.success) {
        setCategoriesList(categoriesResponse.data || []);
      }

      // Set employment types from Employee.jsx (no API call needed)
      setEmploymentTypes([
        { id: "Full-time", name: "Full-time" },
        { id: "Part-time", name: "Part-time" },
        { id: "Contract", name: "Contract" },
        { id: "Intern", name: "Intern" },
        { id: "Consultant", name: "Consultant" },
      ]);

      // Mark master data as loaded
      setDataLoaded((prev) => ({
        ...prev,
        masterData: true,
      }));
    } catch (error) {
      console.error("Error fetching master data:", error);
      toast.error("Failed to fetch master data");
    }
  };

  // Function to refresh specific data after save/delete operations
  const refreshData = async (dataType) => {
    try {
      switch (dataType) {
        case "weeklyOffPolicies": {
          const weeklyOffResponse =
            await attendanceStructureService.getWeeklyOffPolicies();
          if (weeklyOffResponse.success) {
            const rawPolicies = weeklyOffResponse.data || [];
            // Normalize to ensure overridePolicy boolean is present and log for diagnostics
            const normalizedPolicies = rawPolicies.map((p) => ({
              ...p,
              overridePolicy: p.overridePolicy === true,
            }));
            console.log(
              "WeeklyOffPolicies refresh: overridePolicy flags",
              normalizedPolicies.map((p) => ({
                id: p.id,
                overridePolicy: p.overridePolicy,
              }))
            );
            setWeeklyOffPolicies(normalizedPolicies);
          }
          break;
        }
        case "holidayPolicies": {
          const holidayPolicyResponse =
            await attendanceStructureService.getHolidayPolicies();
          if (holidayPolicyResponse.success) {
            setHolidayPolicies(holidayPolicyResponse.data || []);
          }
          break;
        }
        case "holidays": {
          const holidaysResponse =
            await attendanceStructureService.getHolidays();
          if (holidaysResponse.success) {
            setHolidays(holidaysResponse.data || []);
          }
          break;
        }
        case "holidayBank": {
          const holidayBankResponse =
            await attendanceStructureService.getHolidayBank();
          if (holidayBankResponse.success) {
            setHolidayBank(holidayBankResponse.data || []);
          }
          break;
        }
        case "leaveTypes": {
          const leaveTypesResponse =
            await attendanceStructureService.getLeaveTypes();
          if (leaveTypesResponse.success) {
            setLeaveTypes(leaveTypesResponse.data || []);
          }
          break;
        }
        case "leaveGroups": {
          const leaveGroupsResponse =
            await attendanceStructureService.getLeaveGroups();
          if (leaveGroupsResponse.success) {
            setLeaveGroups(leaveGroupsResponse.data || []);
          }
          break;
        }
        default:
          break;
      }
    } catch (error) {
      console.error(`Error refreshing ${dataType}:`, error);
    }
  };

  // Data is now managed through API calls, no need for localStorage sync

  const renderContent = () => {
    const isConfigTab =
      activeStructureTab === "codesAndDevices" ||
      activeStructureTab === "generalSettings" ||
      activeStructureTab === "attendancePolicy" ||
      activeStructureTab === "shift" ||
      activeStructureTab === "weeklyOff" ||
      activeStructureTab === "holiday";
    return (
      <>
        {!darkMode && (
          <style>
            {`
              .attendance-form-readable label,
              .attendance-form-readable p,
              .attendance-form-readable h1,
              .attendance-form-readable h2,
              .attendance-form-readable h3,
              .attendance-form-readable h4,
              .attendance-form-readable h5,
              .attendance-form-readable h6,
              .attendance-form-readable small {
                color: #0f172a;
              }
              .attendance-form-readable input,
              .attendance-form-readable select,
              .attendance-form-readable textarea {
                color: #0f172a;
              }
              .attendance-form-readable input::placeholder,
              .attendance-form-readable textarea::placeholder {
                color: #475569;
              }
            `}
          </style>
        )}
        <div className="flex h-full attendance-form-readable">
          {/* Left Side - Form */}
          <div
            className={`${
              isConfigTab ? "w-full" : "w-3/4 border-r border-gray-300"
            } overflow-y-auto`}
          >
            <div
              className={
                activeStructureTab === "codesAndDevices"
                  ? "h-full min-h-0"
                  : "p-6"
              }
            >
              {renderFormContent()}
            </div>
          </div>

          {/* Right Side - List (hidden for configuration tab) */}
          {!isConfigTab && (
            <div className="w-1/4 overflow-y-auto">
              <div className="p-6">{renderListContent()}</div>
            </div>
          )}
        </div>
      </>
    );
  };

  const renderListContent = () => {
    if (
      activeStructureTab === "weeklyOff" ||
      activeStructureTab === "holiday" ||
      activeStructureTab === "shift"
    ) {
      return null;
    }

    switch (activeStructureTab) {
      case "weeklyOff":
        return null;

      case "holiday":
        return (
          <div className="space-y-4">
            {/* Holiday Bank */}
            <div>
              <div className="flex justify-between items-center mb-3">
                <h3
                  className={`text-base font-semibold ${
                    darkMode ? "text-white" : "text-gray-800"
                  }`}
                >
                  Holiday Bank
                </h3>
                <div className="flex space-x-1">
                  <button
                    onClick={() => setShowHolidayImportModal(true)}
                    className={`px-2 py-1 text-xs rounded ${
                      darkMode
                        ? "rh-btn-primary"
                        : "rh-btn-primary"
                    } hover:opacity-95`}
                  >
                    Import
                  </button>
                  <button
                    onClick={() => setShowHolidayBankModal(true)}
                    className={`rounded px-2 py-1 text-xs rh-btn-primary hover:opacity-95`}
                  >
                    Add
                  </button>
                </div>
              </div>
              <div
                className={`rounded-lg ${
                  darkMode ? "bg-gray-700" : "bg-gray-50"
                }`}
              >
                {holidayBank.length === 0 ? (
                  <p
                    className={`p-3 text-center text-xs ${
                      darkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    No holidays in bank yet
                  </p>
                ) : (
                  holidayBank.map((holiday) => (
                    <div
                      key={holiday.id}
                      className={`p-3 border-b ${
                        darkMode ? "border-gray-600" : "border-gray-200"
                      }`}
                    >
                      <div className="space-y-2">
                        <h4
                          className={`font-semibold text-sm ${
                            darkMode ? "text-white" : "text-gray-800"
                          }`}
                        >
                          {holiday.name}
                        </h4>
                        <p
                          className={`text-xs ${
                            darkMode ? "text-gray-300" : "text-gray-600"
                          }`}
                        >
                          {formatDate(holiday.date)}
                        </p>
                        <div className="flex space-x-1">
                          <button
                            onClick={() => handleAddHolidayFromBank(holiday)}
                            className={`px-2 py-1 text-xs rounded ${
                              darkMode
                                ? "rh-btn-primary"
                                : "rh-btn-primary"
                            } hover:opacity-95`}
                          >
                            Add to Policy
                          </button>
                          <button
                            onClick={() =>
                              handleRemoveFromHolidayBank(holiday.id)
                            }
                            className={`px-2 py-1 text-xs rounded ${
                              darkMode
                                ? "bg-red-600 text-white"
                                : "bg-red-500 text-white"
                            } hover:bg-red-700`}
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        );

      case "leaveManagement":
        return (
          <div className="space-y-4">
            {/* Leave Types */}
            <div>
              <div className="flex justify-between items-center mb-3">
                <h3
                  className={`text-base font-semibold ${
                    darkMode ? "text-white" : "text-gray-800"
                  }`}
                >
                  Leave Types
                </h3>
                <button
                  onClick={async () => {
                    await fetchAllMasterData();
                    setShowLeaveTypeModal(true);
                  }}
                  className={`px-2 py-1 text-xs rounded ${
                    darkMode
                      ? "rh-btn-primary"
                      : "rh-btn-primary"
                  } hover:opacity-95`}
                >
                  Create
                </button>
              </div>
              <div
                className={`rounded-lg ${
                  darkMode ? "bg-gray-700" : "bg-gray-50"
                }`}
              >
                {leaveTypes.length === 0 ? (
                  <p
                    className={`p-3 text-center text-xs ${
                      darkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    No leave types created yet
                  </p>
                ) : (
                  leaveTypes.map((leaveType) => (
                    <div
                      key={leaveType.id}
                      className={`p-3 border-b ${
                        darkMode ? "border-gray-600" : "border-gray-200"
                      }`}
                    >
                      <div className="space-y-2">
                        <div className="flex items-center space-x-2">
                          <div
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: leaveType.color }}
                          ></div>
                          <h4
                            className={`font-semibold text-sm ${
                              darkMode ? "text-white" : "text-gray-800"
                            }`}
                          >
                            {leaveType.name}
                          </h4>
                        </div>
                        <p
                          className={`text-xs ${
                            darkMode ? "text-gray-300" : "text-gray-600"
                          }`}
                        >
                          {leaveType.code} •{" "}
                          {leaveType.isPaid ? "Paid" : "Unpaid"}
                        </p>
                        <div className="flex space-x-1">
                          <button
                            onClick={() => handleEditLeaveType(leaveType)}
                            className={`px-2 py-1 text-xs rounded ${
                              darkMode
                                ? "rh-btn-primary"
                                : "rh-btn-primary"
                            } hover:opacity-95`}
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteLeaveType(leaveType.id)}
                            className={`px-2 py-1 text-xs rounded ${
                              darkMode
                                ? "bg-red-600 text-white"
                                : "bg-red-500 text-white"
                            } hover:bg-red-700`}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        );

      // configuration and generalSettings handled inside renderFormContent

      case "generalSettings":
        return null;

      case "attendancePolicy":
        return null;

      default:
        return null;
    }
  };

  const renderFormContent = () => {
    if (activeStructureTab === "weeklyOff") {
      return (
        <WeeklyOffStructureTab
          darkMode={darkMode}
          weeklyOffPolicies={weeklyOffPolicies}
          fetchAllMasterData={fetchAllMasterData}
          setShowWeeklyOffModal={setShowWeeklyOffModal}
          handleEditWeeklyOffPolicy={handleEditWeeklyOffPolicy}
          handleDeleteWeeklyOffPolicy={handleDeleteWeeklyOffPolicy}
          formatDate={formatDate}
        />
      );
    }

    if (activeStructureTab === "holiday") {
      return (
        <HolidayStructureTab
          darkMode={darkMode}
          holidayPolicies={holidayPolicies}
          holidayBank={holidayBank}
          fetchAllMasterData={fetchAllMasterData}
          setShowHolidayModal={setShowHolidayModal}
          setShowHolidayImportModal={setShowHolidayImportModal}
          setShowHolidayBankModal={setShowHolidayBankModal}
          handleEditHolidayPolicy={handleEditHolidayPolicy}
          handleDeleteHolidayPolicy={handleDeleteHolidayPolicy}
          handleAddHolidayFromBank={handleAddHolidayFromBank}
          handleRemoveFromHolidayBank={handleRemoveFromHolidayBank}
          formatDate={formatDate}
        />
      );
    }

    switch (activeStructureTab) {
      case "weeklyOff":
        return (
          <div className="space-y-6">
            {/* Weekly Off Policies Section */}
            <div>
              <div className="flex justify-between items-center mb-4">
                <h3
                  className={`text-lg font-semibold ${
                    darkMode ? "text-white" : "text-gray-800"
                  }`}
                >
                  Weekly Off Policies
                </h3>
                <button
                  onClick={async () => {
                    await fetchAllMasterData();
                    setShowWeeklyOffModal(true);
                  }}
                  className={`px-4 py-2 rh-btn-primary rounded hover:opacity-95 transition-colors`}
                >
                  Create New Policy
                </button>
              </div>
              <p
                className={`text-sm ${
                  darkMode ? "text-gray-300" : "text-gray-600"
                }`}
              >
                Create and configure weekly off policies for your organization.
                View saved policies below.
              </p>

              {/* Weekly Off Policies List */}
              <div
                className={`rounded-lg ${
                  darkMode ? "bg-gray-700" : "bg-gray-50"
                } mt-4`}
              >
                {weeklyOffPolicies.length === 0 ? (
                  <p
                    className={`p-3 text-center text-xs ${
                      darkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    No weekly off policies created yet
                  </p>
                ) : (
                  weeklyOffPolicies.map((policy) => (
                    <div
                      key={policy.id}
                      className={`p-4 border-b ${
                        darkMode ? "border-gray-600" : "border-gray-200"
                      } hover:bg-gray-50 dark:hover:bg-gray-600`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <h4
                          className={`font-semibold text-base ${
                            darkMode ? "text-white" : "text-gray-800"
                          }`}
                        >
                          {policy.name}
                        </h4>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleEditWeeklyOffPolicy(policy)}
                            className={`px-3 py-1 text-xs rounded-full ${
                              darkMode
                                ? "rh-btn-primary"
                                : "rh-btn-primary"
                            } hover:opacity-95`}
                          >
                            Edit
                          </button>
                          <button
                            onClick={() =>
                              handleDeleteWeeklyOffPolicy(policy.id)
                            }
                            className={`px-3 py-1 text-xs rounded-full ${
                              darkMode
                                ? "bg-red-600 text-white"
                                : "bg-red-500 text-white"
                            } hover:bg-red-700`}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2 mb-2">
                        <span
                          className={`px-2 py-1 text-xs rounded-full ${
                            darkMode
                              ? "rh-accent-soft"
                              : "rh-accent-soft"
                          }`}
                        >
                          Effective:{" "}
                          {formatDate(policy.effectiveDate)}
                        </span>
                        {policy.assignedTo.allEmployees ? (
                          <span
                            className={`px-2 py-1 text-xs rounded-full ${
                              darkMode
                                ? "bg-redhalo-mint/90 text-redhalo-navy"
                                : "bg-redhalo-mint/90 text-redhalo-navy"
                            }`}
                          >
                            All Employees
                          </span>
                        ) : (
                          <>
                            {policy.assignedTo.branches.length > 0 && (
                              <span
                                className={`px-2 py-1 text-xs rounded-full ${
                                  darkMode
                                    ? "bg-emerald-50 text-emerald-900"
                                    : "bg-emerald-50 text-emerald-900"
                                }`}
                              >
                                {policy.assignedTo.branches.length} Branches
                              </span>
                            )}
                            {policy.assignedTo.departments.length > 0 && (
                              <span
                                className={`px-2 py-1 text-xs rounded-full ${
                                  darkMode
                                    ? "bg-amber-100 text-amber-900"
                                    : "bg-amber-100 text-amber-900"
                                }`}
                              >
                                {policy.assignedTo.departments.length}{" "}
                                Departments
                              </span>
                            )}
                            {policy.assignedTo.employees.length > 0 && (
                              <span
                                className={`px-2 py-1 text-xs rounded-full ${
                                  darkMode
                                    ? "rh-accent-soft"
                                    : "rh-accent-soft"
                                }`}
                              >
                                {policy.assignedTo.employees.length} Employees
                              </span>
                            )}
                          </>
                        )}
                      </div>
                      {policy.description && (
                        <p
                          className={`text-sm ${
                            darkMode ? "text-gray-300" : "text-gray-600"
                          }`}
                        >
                          {policy.description}
                        </p>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        );

      case "holiday":
        return (
          <div className="space-y-6">
            {/* Holiday Policies Section */}
            <div>
              <div className="flex justify-between items-center mb-4">
                <h3
                  className={`text-lg font-semibold ${
                    darkMode ? "text-white" : "text-gray-800"
                  }`}
                >
                  Holiday Policies
                </h3>
                <button
                  onClick={async () => {
                    await fetchAllMasterData();
                    setShowHolidayModal(true);
                  }}
                  className={`px-4 py-2 bg-redhalo-navy text-white rounded-md hover:bg-redhalo-navy-light transition-colors`}
                >
                  Create Holiday Policy
                </button>
              </div>
              <p
                className={`text-sm ${
                  darkMode ? "text-gray-300" : "text-gray-600"
                }`}
              >
                Create holiday policies and assign them to employees or
                organizational units. View saved policies below.
              </p>

              {/* Holiday Policies List */}
              <div
                className={`rounded-lg ${
                  darkMode ? "bg-gray-700" : "bg-gray-50"
                } mt-4`}
              >
                {holidayPolicies.length === 0 ? (
                  <p
                    className={`p-3 text-center text-xs ${
                      darkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    No holiday policies created yet
                  </p>
                ) : (
                  holidayPolicies.map((policy) => (
                    <div
                      key={policy.id}
                      className={`p-4 border-b ${
                        darkMode ? "border-gray-600" : "border-gray-200"
                      } hover:bg-gray-50 dark:hover:bg-gray-600`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <h4
                          className={`font-semibold text-base ${
                            darkMode ? "text-white" : "text-gray-800"
                          }`}
                        >
                          {policy.name}
                        </h4>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleEditHolidayPolicy(policy)}
                            className={`px-3 py-1 text-xs rounded-full ${
                              darkMode
                                ? "rh-btn-primary"
                                : "rh-btn-primary"
                            } hover:opacity-95`}
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteHolidayPolicy(policy.id)}
                            className={`px-3 py-1 text-xs rounded-full ${
                              darkMode
                                ? "bg-red-600 text-white"
                                : "bg-red-500 text-white"
                            } hover:bg-red-700`}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2 mb-2">
                        <span
                          className={`px-2 py-1 text-xs rounded-full ${
                            darkMode
                              ? "rh-accent-soft"
                              : "rh-accent-soft"
                          }`}
                        >
                          {policy.holidays.length} Holidays
                        </span>
                        {policy.assignedTo.allEmployees ? (
                          <span
                            className={`px-2 py-1 text-xs rounded-full ${
                              darkMode
                                ? "bg-redhalo-mint/90 text-redhalo-navy"
                                : "bg-redhalo-mint/90 text-redhalo-navy"
                            }`}
                          >
                            All Employees
                          </span>
                        ) : (
                          <>
                            {policy.assignedTo.branches.length > 0 && (
                              <span
                                className={`px-2 py-1 text-xs rounded-full ${
                                  darkMode
                                    ? "bg-emerald-50 text-emerald-900"
                                    : "bg-emerald-50 text-emerald-900"
                                }`}
                              >
                                {policy.assignedTo.branches.length} Branches
                              </span>
                            )}
                            {policy.assignedTo.departments.length > 0 && (
                              <span
                                className={`px-2 py-1 text-xs rounded-full ${
                                  darkMode
                                    ? "bg-amber-100 text-amber-900"
                                    : "bg-amber-100 text-amber-900"
                                }`}
                              >
                                {policy.assignedTo.departments.length}{" "}
                                Departments
                              </span>
                            )}
                            {policy.assignedTo.employees.length > 0 && (
                              <span
                                className={`px-2 py-1 text-xs rounded-full ${
                                  darkMode
                                    ? "rh-accent-soft"
                                    : "rh-accent-soft"
                                }`}
                              >
                                {policy.assignedTo.employees.length} Employees
                              </span>
                            )}
                          </>
                        )}
                      </div>
                      {policy.description && (
                        <p
                          className={`text-sm ${
                            darkMode ? "text-gray-300" : "text-gray-600"
                          }`}
                        >
                          {policy.description}
                        </p>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        );

      case "shift":
        return (
          <ShiftStructureTab
            darkMode={darkMode}
            branchesList={branchesList}
            departmentsList={departmentsList}
            designationsList={designationsList}
            subDepartmentsList={subDepartmentsList}
            gradesList={gradesList}
            categoriesList={categoriesList}
          />
        );

      case "attendance":
        return <AttendanceRulesTab darkMode={darkMode} />;

      case "leave":
        return (
          <div>
            <h3
              className={`text-lg font-semibold mb-4 ${
                darkMode ? "text-white" : "text-gray-800"
              }`}
            >
              Leave Policies
            </h3>
            <p className={`${darkMode ? "text-gray-300" : "text-gray-600"}`}>
              Configure leave types and policies.
            </p>
            {/* Add leave policies configuration here */}
          </div>
        );

      case "leaveManagement":
        return (
          <div className="space-y-6">
            {/* Leave Groups Section */}
            <div>
              <div className="flex justify-between items-center mb-4">
                <h3
                  className={`text-lg font-semibold ${
                    darkMode ? "text-white" : "text-gray-800"
                  }`}
                >
                  Leave Groups
                </h3>
                <button
                  onClick={async () => {
                    await fetchAllMasterData();
                    setShowLeaveGroupModal(true);
                  }}
                  className={`px-4 py-2 rh-btn-primary rounded hover:opacity-95 transition-colors`}
                >
                  Create Leave Group
                </button>
              </div>
              <p
                className={`text-sm ${
                  darkMode ? "text-gray-300" : "text-gray-600"
                }`}
              >
                Create leave groups with detailed configurations for
                accumulation, allotment, availment, and lapse settings. Assign
                leave groups to employees or organizational units. View saved
                leave groups below.
              </p>

              {/* Leave Groups List */}
              <div
                className={`rounded-lg ${
                  darkMode ? "bg-gray-700" : "bg-gray-50"
                } mt-4`}
              >
                {leaveGroups.length === 0 ? (
                  <p
                    className={`p-3 text-center text-xs ${
                      darkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    No leave groups created yet
                  </p>
                ) : (
                  leaveGroups.map((leaveGroup) => (
                    <div
                      key={leaveGroup.id}
                      className={`p-4 border-b ${
                        darkMode ? "border-gray-600" : "border-gray-200"
                      } hover:bg-gray-50 dark:hover:bg-gray-600`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <h4
                          className={`font-semibold text-base ${
                            darkMode ? "text-white" : "text-gray-800"
                          }`}
                        >
                          {leaveGroup.name}
                        </h4>
                        <div className="flex items-center space-x-2">
                          <button
                            type="button"
                            onClick={() => {
                              setOpeningBalanceUploadLeaveGroup(leaveGroup);
                              setShowOpeningBalanceUploadModal(true);
                            }}
                            className={`p-1.5 rounded ${
                              darkMode
                                ? "bg-gray-600 text-gray-200 hover:bg-gray-500"
                                : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                            }`}
                            title="Upload opening balance"
                          >
                            <Upload size={16} />
                          </button>
                          <button
                            onClick={() => handleEditLeaveGroup(leaveGroup)}
                            className={`px-3 py-1 text-xs rounded-full ${
                              darkMode
                                ? "rh-btn-primary"
                                : "rh-btn-primary"
                            } hover:opacity-95`}
                          >
                            Edit
                          </button>
                          <button
                            onClick={() =>
                              handleDeleteLeaveGroup(leaveGroup.id)
                            }
                            className={`px-3 py-1 text-xs rounded-full ${
                              darkMode
                                ? "bg-red-600 text-white"
                                : "bg-red-500 text-white"
                            } hover:bg-red-700`}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2 mb-2">
                        <span
                          className={`px-2 py-1 text-xs rounded-full ${
                            darkMode
                              ? "rh-accent-soft"
                              : "rh-accent-soft"
                          }`}
                        >
                          {leaveGroup.leaveTypes.length} Leave Types
                        </span>
                        {leaveGroup.assignedTo.allEmployees ? (
                          <span
                            className={`px-2 py-1 text-xs rounded-full ${
                              darkMode
                                ? "bg-redhalo-mint/90 text-redhalo-navy"
                                : "bg-redhalo-mint/90 text-redhalo-navy"
                            }`}
                          >
                            All Employees
                          </span>
                        ) : (
                          <>
                            {leaveGroup.assignedTo.branches.length > 0 && (
                              <span
                                className={`px-2 py-1 text-xs rounded-full ${
                                  darkMode
                                    ? "bg-emerald-50 text-emerald-900"
                                    : "bg-emerald-50 text-emerald-900"
                                }`}
                              >
                                {leaveGroup.assignedTo.branches.length} Branches
                              </span>
                            )}
                            {leaveGroup.assignedTo.departments.length > 0 && (
                              <span
                                className={`px-2 py-1 text-xs rounded-full ${
                                  darkMode
                                    ? "bg-amber-100 text-amber-900"
                                    : "bg-amber-100 text-amber-900"
                                }`}
                              >
                                {leaveGroup.assignedTo.departments.length}{" "}
                                Departments
                              </span>
                            )}
                            {leaveGroup.assignedTo.employees.length > 0 && (
                              <span
                                className={`px-2 py-1 text-xs rounded-full ${
                                  darkMode
                                    ? "rh-accent-soft"
                                    : "rh-accent-soft"
                                }`}
                              >
                                {leaveGroup.assignedTo.employees.length}{" "}
                                Employees
                              </span>
                            )}
                          </>
                        )}
                      </div>
                      {leaveGroup.description && (
                        <p
                          className={`text-sm ${
                            darkMode ? "text-gray-300" : "text-gray-600"
                          }`}
                        >
                          {leaveGroup.description}
                        </p>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>

            {showOpeningBalanceUploadModal && openingBalanceUploadLeaveGroup && (
              <OpeningBalanceUploadModal
                darkMode={darkMode}
                leaveGroup={openingBalanceUploadLeaveGroup}
                onClose={() => {
                  setShowOpeningBalanceUploadModal(false);
                  setOpeningBalanceUploadLeaveGroup(null);
                }}
              />
            )}
          </div>
        );

      case "codesAndDevices":
        return <ConfigurationStructureTab darkMode={darkMode} />;

      case "attendancePolicy":
        return (
          <AttendancePolicyComponent
            darkMode={darkMode}
            mode="policy"
            branchesList={branchesList}
            departmentsList={departmentsList}
            designationsList={designationsList}
            subDepartmentsList={subDepartmentsList}
            gradesList={gradesList}
            categoriesList={categoriesList}
          />
        );

      case "generalSettings":
        return (
          <GeneralSettingsTab
            darkMode={darkMode}
            parentServiceCommencesOnDate={parentServiceCommencesOnDate}
          />
        );

      default:
        return null;
    }
  };

  return (
    <>
      {renderContent()}

      {/* Weekly Off Policy Modal */}
      {showWeeklyOffModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div
            className={`rounded-lg shadow-xl w-[70vw] h-[70vh] flex flex-col ${
              darkMode ? "bg-gray-800 text-white" : "bg-white text-gray-800"
            }`}
          >
            {/* Fixed Header */}
            <div className="flex justify-between items-center p-6 border-b border-gray-300 flex-shrink-0">
              <h2 className="text-xl font-bold">
                {editingWeeklyOffPolicy
                  ? "Edit Weekly Off Policy"
                  : "Create Weekly Off Policy"}
              </h2>
              <button
                onClick={() => {
                  setShowWeeklyOffModal(false);
                  setEditingWeeklyOffPolicy(null);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={24} />
              </button>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Policy Name *
                    </label>
                    <input
                      type="text"
                      value={weeklyOffPolicyForm.name}
                      onChange={(e) =>
                        setWeeklyOffPolicyForm((prev) => ({
                          ...prev,
                          name: e.target.value,
                        }))
                      }
                      className={`w-full p-2 border rounded ${
                        darkMode
                          ? "bg-gray-700 border-gray-600"
                          : "bg-white border-gray-300"
                      }`}
                      placeholder="Enter policy name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Effective Date *
                    </label>
                    <DatePicker
                      value={weeklyOffPolicyForm.effectiveDate}
                      onChange={(e) =>
                        setWeeklyOffPolicyForm((prev) => ({
                          ...prev,
                          effectiveDate: e.target.value,
                        }))
                      }
                      label="Effective Date"
                      required
                      className={`w-full p-2 border rounded ${
                        darkMode
                          ? "bg-gray-700 border-gray-600"
                          : "bg-white border-gray-300"
                      }`}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">
                    Description
                  </label>
                  <textarea
                    value={weeklyOffPolicyForm.description}
                    onChange={(e) =>
                      setWeeklyOffPolicyForm((prev) => ({
                        ...prev,
                        description: e.target.value,
                      }))
                    }
                    className={`w-full p-2 border rounded ${
                      darkMode
                        ? "bg-gray-700 border-gray-600"
                        : "bg-white border-gray-300"
                    }`}
                    rows={3}
                    placeholder="Enter policy description"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">
                    Weekly Off Configuration
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {Object.entries(weeklyOffPolicyForm.weeklyOffs).map(
                      ([day, config]) => (
                        <div
                          key={day}
                          className={`p-3 border rounded ${
                            darkMode ? "border-gray-600" : "border-gray-300"
                          }`}
                        >
                          <div className="flex items-center space-x-2 mb-2">
                            <input
                              type="checkbox"
                              checked={config.enabled}
                              onChange={(e) =>
                                setWeeklyOffPolicyForm((prev) => ({
                                  ...prev,
                                  weeklyOffs: {
                                    ...prev.weeklyOffs,
                                    [day]: {
                                      ...prev.weeklyOffs[day],
                                      enabled: e.target.checked,
                                    },
                                  },
                                }))
                              }
                              className="rounded border-gray-300 text-redhalo-red focus:ring-redhalo-red/35"
                            />
                            <span className="capitalize font-medium">
                              {day}
                            </span>
                          </div>
                          {config.enabled && (
                            <select
                              value={config.type}
                              onChange={(e) =>
                                setWeeklyOffPolicyForm((prev) => ({
                                  ...prev,
                                  weeklyOffs: {
                                    ...prev.weeklyOffs,
                                    [day]: {
                                      ...prev.weeklyOffs[day],
                                      type: e.target.value,
                                    },
                                  },
                                }))
                              }
                              className={`w-full p-1 text-xs border rounded ${
                                darkMode
                                  ? "bg-gray-700 border-gray-600"
                                  : "bg-white border-gray-300"
                              }`}
                            >
                              <option value="full">Full Day</option>
                              <option value="first_half">First Half</option>
                              <option value="second_half">Second Half</option>
                            </select>
                          )}
                        </div>
                      )
                    )}
                    <div className="mt-3">
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={weeklyOffPolicyForm.allowOverride}
                          onChange={(e) =>
                            setWeeklyOffPolicyForm((prev) => ({
                              ...prev,
                              allowOverride: e.target.checked,
                            }))
                          }
                          className="rounded border-gray-300 text-redhalo-red focus:ring-redhalo-red/35"
                        />
                        <span className="font-medium">Override Weekly Off</span>
                      </label>
                      <p className="text-xs text-gray-500 mt-1">
                        Allow manual override of Weekly Off for employees
                        assigned to this policy.
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">
                    Assignment
                  </label>
                  <div className="space-y-4">
                    {/* This IIFE (Immediately Invoked Function Expression) lets us define variables inside JSX */}
                    {(() => {
                      const { assignedTo } = weeklyOffPolicyForm;

                      const isLevel1Selected = assignedTo.allEmployees;

                      return (
                        <div className="space-y-4">
                          <label className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              checked={weeklyOffPolicyForm.assignedTo.allEmployees}
                              onChange={handleSelectAllEmployees}
                              className="rounded border-gray-300 text-redhalo-red focus:ring-redhalo-red/35"
                            />
                            <span>Assign to all employees</span>
                          </label>

                          {!isLevel1Selected && (
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                              {/* Branches */}
                              <div>
                                <label className="block text-xs font-medium mb-1">
                                  Branches
                                </label>
                                <div
                                  className={`border rounded p-1 max-h-32 overflow-y-auto ${
                                    darkMode
                                      ? "bg-gray-700 border-gray-600"
                                      : "bg-white border-gray-300"
                                  }`}
                                >
                                  {branchesList.map((branch) => (
                                    <label
                                      key={branch.id}
                                      className="flex items-center space-x-1 p-0.5 hover:bg-gray-100 text-xs"
                                    >
                                      <input
                                        type="checkbox"
                                        checked={weeklyOffPolicyForm.assignedTo.branches.includes(
                                          branch.id
                                        )}
                                        onChange={() => {
                                          const currentBranches =
                                            weeklyOffPolicyForm.assignedTo
                                              .branches;
                                          const newBranches =
                                            currentBranches.includes(branch.id)
                                              ? currentBranches.filter(
                                                  (id) => id !== branch.id
                                                )
                                              : [...currentBranches, branch.id];
                                          setWeeklyOffPolicyForm((prev) => ({
                                            ...prev,
                                            assignedTo: {
                                              ...prev.assignedTo,
                                              branches: newBranches,
                                            },
                                          }));
                                        }}
                                        className="rounded border-gray-300 text-redhalo-red focus:ring-redhalo-red/35 w-3 h-3"
                                      />
                                      <span className="truncate">
                                        {branch.name}
                                      </span>
                                    </label>
                                  ))}
                                </div>
                              </div>

                              {/* Departments */}
                              <div>
                                <label className="block text-xs font-medium mb-1">
                                  Departments
                                </label>
                                <div
                                  className={`border rounded p-1 max-h-32 overflow-y-auto ${
                                    darkMode
                                      ? "bg-gray-700 border-gray-600"
                                      : "bg-white border-gray-300"
                                  }`}
                                >
                                  {departmentsList.map((dept) => (
                                    <label
                                      key={dept.id}
                                      className="flex items-center space-x-1 p-0.5 hover:bg-gray-100 text-xs"
                                    >
                                      <input
                                        type="checkbox"
                                        checked={weeklyOffPolicyForm.assignedTo.departments.includes(
                                          dept.id
                                        )}
                                        onChange={() => {
                                          const currentDepartments =
                                            weeklyOffPolicyForm.assignedTo
                                              .departments;
                                          const newDepartments =
                                            currentDepartments.includes(dept.id)
                                              ? currentDepartments.filter(
                                                  (id) => id !== dept.id
                                                )
                                              : [
                                                  ...currentDepartments,
                                                  dept.id,
                                                ];
                                          setWeeklyOffPolicyForm((prev) => ({
                                            ...prev,
                                            assignedTo: {
                                              ...prev.assignedTo,
                                              departments: newDepartments,
                                            },
                                          }));
                                        }}
                                        className="rounded border-gray-300 text-redhalo-red focus:ring-redhalo-red/35 w-3 h-3"
                                      />
                                      <span className="truncate">
                                        {dept.name}
                                      </span>
                                    </label>
                                  ))}
                                </div>
                              </div>

                              {/* Designations */}
                              <div>
                                <label className="block text-xs font-medium mb-1">
                                  Designations
                                </label>
                                <div
                                  className={`border rounded p-1 max-h-32 overflow-y-auto ${
                                    darkMode
                                      ? "bg-gray-700 border-gray-600"
                                      : "bg-white border-gray-300"
                                  }`}
                                >
                                  {designationsList.map((designation) => (
                                    <label
                                      key={designation.id}
                                      className="flex items-center space-x-1 p-0.5 hover:bg-gray-100 text-xs"
                                    >
                                      <input
                                        type="checkbox"
                                        checked={weeklyOffPolicyForm.assignedTo.designations.includes(
                                          designation.id
                                        )}
                                        onChange={() => {
                                          const currentDesignations =
                                            weeklyOffPolicyForm.assignedTo
                                              .designations;
                                          const newDesignations =
                                            currentDesignations.includes(
                                              designation.id
                                            )
                                              ? currentDesignations.filter(
                                                  (id) => id !== designation.id
                                                )
                                              : [
                                                  ...currentDesignations,
                                                  designation.id,
                                                ];
                                          setWeeklyOffPolicyForm((prev) => ({
                                            ...prev,
                                            assignedTo: {
                                              ...prev.assignedTo,
                                              designations: newDesignations,
                                            },
                                          }));
                                        }}
                                        className="rounded border-gray-300 text-redhalo-red focus:ring-redhalo-red/35 w-3 h-3"
                                      />
                                      <span className="truncate">
                                        {designation.name}
                                      </span>
                                    </label>
                                  ))}
                                </div>
                              </div>

                              {/* Grades */}
                              <div>
                                <label className="block text-xs font-medium mb-1">
                                  Grades
                                </label>
                                <div
                                  className={`border rounded p-1 max-h-32 overflow-y-auto ${
                                    darkMode
                                      ? "bg-gray-700 border-gray-600"
                                      : "bg-white border-gray-300"
                                  }`}
                                >
                                  {gradesList.map((grade) => (
                                    <label
                                      key={grade.id}
                                      className="flex items-center space-x-1 p-0.5 hover:bg-gray-100 text-xs"
                                    >
                                      <input
                                        type="checkbox"
                                        checked={weeklyOffPolicyForm.assignedTo.grades.includes(
                                          grade.id
                                        )}
                                        onChange={() => {
                                          const currentGrades =
                                            weeklyOffPolicyForm.assignedTo
                                              .grades;
                                          const newGrades =
                                            currentGrades.includes(grade.id)
                                              ? currentGrades.filter(
                                                  (id) => id !== grade.id
                                                )
                                              : [...currentGrades, grade.id];
                                          setWeeklyOffPolicyForm((prev) => ({
                                            ...prev,
                                            assignedTo: {
                                              ...prev.assignedTo,
                                              grades: newGrades,
                                            },
                                          }));
                                        }}
                                        className="rounded border-gray-300 text-redhalo-red focus:ring-redhalo-red/35 w-3 h-3"
                                      />
                                      <span className="truncate">
                                        {grade.name}
                                      </span>
                                    </label>
                                  ))}
                                </div>
                              </div>

                              {/* Sub-Departments */}
                              <div>
                                <label className="block text-xs font-medium mb-1">
                                  Sub-Departments
                                </label>
                                <div
                                  className={`border rounded p-1 max-h-32 overflow-y-auto ${
                                    darkMode
                                      ? "bg-gray-700 border-gray-600"
                                      : "bg-white border-gray-300"
                                  }`}
                                >
                                  {subDepartmentsList.map((subDept) => (
                                    <label
                                      key={subDept.id}
                                      className="flex items-center space-x-1 p-0.5 hover:bg-gray-100 text-xs"
                                    >
                                      <input
                                        type="checkbox"
                                        checked={weeklyOffPolicyForm.assignedTo.subDepartments.includes(
                                          subDept.id
                                        )}
                                        onChange={() => {
                                          const currentSubDepts =
                                            weeklyOffPolicyForm.assignedTo
                                              .subDepartments;
                                          const newSubDepts =
                                            currentSubDepts.includes(subDept.id)
                                              ? currentSubDepts.filter(
                                                  (id) => id !== subDept.id
                                                )
                                              : [
                                                  ...currentSubDepts,
                                                  subDept.id,
                                                ];
                                          setWeeklyOffPolicyForm((prev) => ({
                                            ...prev,
                                            assignedTo: {
                                              ...prev.assignedTo,
                                              subDepartments: newSubDepts,
                                            },
                                          }));
                                        }}
                                        className="rounded border-gray-300 text-redhalo-red focus:ring-redhalo-red/35 w-3 h-3"
                                      />
                                      <span className="truncate">
                                        {subDept.name}
                                      </span>
                                    </label>
                                  ))}
                                </div>
                              </div>

                              {/* Categories */}
                              <div>
                                <label className="block text-xs font-medium mb-1">
                                  Categories
                                </label>
                                <div
                                  className={`border rounded p-1 max-h-32 overflow-y-auto ${
                                    darkMode
                                      ? "bg-gray-700 border-gray-600"
                                      : "bg-white border-gray-300"
                                  }`}
                                >
                                  {categoriesList.map((category) => (
                                    <label
                                      key={category.id}
                                      className="flex items-center space-x-1 p-0.5 hover:bg-gray-100 text-xs"
                                    >
                                      <input
                                        type="checkbox"
                                        checked={weeklyOffPolicyForm.assignedTo.categories.includes(
                                          category.id
                                        )}
                                        onChange={() => {
                                          const currentCategories =
                                            weeklyOffPolicyForm.assignedTo
                                              .categories;
                                          const newCategories =
                                            currentCategories.includes(
                                              category.id
                                            )
                                              ? currentCategories.filter(
                                                  (id) => id !== category.id
                                                )
                                              : [
                                                  ...currentCategories,
                                                  category.id,
                                                ];
                                          setWeeklyOffPolicyForm((prev) => ({
                                            ...prev,
                                            assignedTo: {
                                              ...prev.assignedTo,
                                              categories: newCategories,
                                            },
                                          }));
                                        }}
                                        className="rounded border-gray-300 text-redhalo-red focus:ring-redhalo-red/35 w-3 h-3"
                                      />
                                      <span className="truncate">
                                        {category.name}
                                      </span>
                                    </label>
                                  ))}
                                </div>
                              </div>

                              {/* Employment Types */}
                              <div>
                                <label className="block text-xs font-medium mb-1">
                                  Employment Types
                                </label>
                                <div
                                  className={`border rounded p-1 max-h-32 overflow-y-auto ${
                                    darkMode
                                      ? "bg-gray-700 border-gray-600"
                                      : "bg-white border-gray-300"
                                  }`}
                                >
                                  {employmentTypes.map((empType) => (
                                    <label
                                      key={empType.id}
                                      className="flex items-center space-x-1 p-0.5 hover:bg-gray-100 text-xs"
                                    >
                                      <input
                                        type="checkbox"
                                        checked={weeklyOffPolicyForm.assignedTo.employmentTypes.includes(
                                          empType.id
                                        )}
                                        onChange={() => {
                                          const currentEmpTypes =
                                            weeklyOffPolicyForm.assignedTo
                                              .employmentTypes;
                                          const newEmpTypes =
                                            currentEmpTypes.includes(empType.id)
                                              ? currentEmpTypes.filter(
                                                  (id) => id !== empType.id
                                                )
                                              : [
                                                  ...currentEmpTypes,
                                                  empType.id,
                                                ];
                                          setWeeklyOffPolicyForm((prev) => ({
                                            ...prev,
                                            assignedTo: {
                                              ...prev.assignedTo,
                                              employmentTypes: newEmpTypes,
                                            },
                                          }));
                                        }}
                                        className="rounded border-gray-300 text-redhalo-red focus:ring-redhalo-red/35 w-3 h-3"
                                      />
                                      <span className="truncate">
                                        {empType.name}
                                      </span>
                                    </label>
                                  ))}
                                </div>
                              </div>
                            </div>
                          )}

                          {!isLevel1Selected && (
                            <div>
                              <label className="block text-xs font-medium mb-1">
                                Select Specific Employees
                              </label>
                              <input
                                type="text"
                                placeholder="Search employees..."
                                value={employeeSearchTerm}
                                onChange={(e) =>
                                  setEmployeeSearchTerm(e.target.value)
                                }
                                className={`w-full p-1 text-xs border rounded mb-1 ${
                                  darkMode
                                    ? "bg-gray-700 border-gray-600"
                                    : "bg-white border-gray-300"
                                }`}
                              />
                              <div className="max-h-24 overflow-y-auto border rounded">
                                {filteredEmployees.map((employee) => (
                                  <label
                                    key={employee.id}
                                    className="flex items-center space-x-1 p-0.5 hover:bg-gray-100 text-xs"
                                  >
                                    <input
                                      type="checkbox"
                                      checked={weeklyOffPolicyForm.assignedTo.employees.includes(
                                        employee.id
                                      )}
                                      onChange={() => {
                                        const currentEmployees =
                                          weeklyOffPolicyForm.assignedTo
                                            .employees;
                                        const newEmployees =
                                          currentEmployees.includes(employee.id)
                                            ? currentEmployees.filter(
                                                (id) => id !== employee.id
                                              )
                                            : [
                                                ...currentEmployees,
                                                employee.id,
                                              ];
                                        setWeeklyOffPolicyForm((prev) => ({
                                          ...prev,
                                          assignedTo: {
                                            ...prev.assignedTo,
                                            employees: newEmployees,
                                          },
                                        }));
                                      }}
                                      className="rounded border-gray-300 text-redhalo-red focus:ring-redhalo-red/35 w-3 h-3"
                                    />
                                    <span className="truncate">
                                      {employee.user?.name} ({employee.empCode})
                                    </span>
                                  </label>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </div>
            </div>

            {/* Fixed Action Buttons */}
            <div className="flex justify-end space-x-2 p-6 border-t border-gray-300 flex-shrink-0">
              <button
                onClick={() => {
                  setShowWeeklyOffModal(false);
                  setEditingWeeklyOffPolicy(null);
                }}
                className={`px-4 py-2 rounded ${
                  darkMode
                    ? "bg-gray-700 text-white"
                    : "bg-gray-200 text-gray-800"
                }`}
              >
                Cancel
              </button>
              <button
                onClick={handleWeeklyOffPolicySubmit}
                disabled={isSavingWeeklyOffPolicy}
                className={`px-4 py-2 rh-btn-primary rounded hover:opacity-95 ${
                  isSavingWeeklyOffPolicy ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                {isSavingWeeklyOffPolicy
                  ? editingWeeklyOffPolicy
                    ? "Updating..."
                    : "Creating..."
                  : editingWeeklyOffPolicy
                  ? "Update Policy"
                  : "Create Policy"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Holiday Policy Modal */}
      {showHolidayModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div
            className={`rounded-lg shadow-xl w-[70vw] h-[70vh] flex flex-col ${
              darkMode ? "bg-gray-800 text-white" : "bg-white text-gray-800"
            }`}
          >
            {/* Fixed Header */}
            <div className="flex justify-between items-center p-6 border-b border-gray-300 flex-shrink-0">
              <h2 className="text-xl font-bold">
                {editingHolidayPolicy
                  ? "Edit Holiday Policy"
                  : "Create Holiday Policy"}
              </h2>
              <button
                onClick={() => {
                  setShowHolidayModal(false);
                  setEditingHolidayPolicy(null);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={24} />
              </button>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Policy Name *
                    </label>
                    <input
                      type="text"
                      value={holidayPolicyForm.name}
                      onChange={(e) =>
                        setHolidayPolicyForm((prev) => ({
                          ...prev,
                          name: e.target.value,
                        }))
                      }
                      className={`w-full p-2 border rounded ${
                        darkMode
                          ? "bg-gray-700 border-gray-600"
                          : "bg-white border-gray-300"
                      }`}
                      placeholder="Enter policy name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Effective Date *
                    </label>
                    <DatePicker
                      value={holidayPolicyForm.effectiveDate}
                      onChange={(e) =>
                        setHolidayPolicyForm((prev) => ({
                          ...prev,
                          effectiveDate: e.target.value,
                        }))
                      }
                      label="Effective Date"
                      required
                      className={`w-full p-2 border rounded ${
                        darkMode
                          ? "bg-gray-700 border-gray-600"
                          : "bg-white border-gray-300"
                      }`}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">
                    Description
                  </label>
                  <textarea
                    value={holidayPolicyForm.description}
                    onChange={(e) =>
                      setHolidayPolicyForm((prev) => ({
                        ...prev,
                        description: e.target.value,
                      }))
                    }
                    className={`w-full p-2 border rounded ${
                      darkMode
                        ? "bg-gray-700 border-gray-600"
                        : "bg-white border-gray-300"
                    }`}
                    rows={3}
                    placeholder="Enter policy description"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">
                    Select Holidays from Bank
                  </label>

                  {/* Holiday Selection from Bank */}
                  <div
                    className={`p-4 rounded-lg ${
                      darkMode ? "bg-gray-700" : "bg-gray-50"
                    } mb-4`}
                  >
                    <div className="flex justify-between items-center mb-3">
                      <h5
                        className={`font-medium ${
                          darkMode ? "text-white" : "text-gray-800"
                        }`}
                      >
                        Available Holidays
                      </h5>
                      <button
                        onClick={() => setShowHolidayBankModal(true)}
                        className="px-3 py-1 text-sm rh-btn-primary rounded hover:opacity-95"
                      >
                        Add New Holiday to Bank
                      </button>
                    </div>

                    {holidayBank.length === 0 ? (
                      <p
                        className={`p-4 text-center ${
                          darkMode ? "text-gray-400" : "text-gray-500"
                        }`}
                      >
                        No holidays in bank yet. Add holidays to the bank first.
                      </p>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-40 overflow-y-auto">
                        {holidayBank.map((holiday) => (
                          <label
                            key={holiday.id}
                            className="flex items-center space-x-2 p-2 hover:bg-gray-100 rounded"
                          >
                            <input
                              type="checkbox"
                              checked={holidayPolicyForm.holidays.some(
                                (h) => h.id === holiday.id
                              )}
                              onChange={() => {
                                const currentHolidays =
                                  holidayPolicyForm.holidays;
                                const isSelected = currentHolidays.some(
                                  (h) => h.id === holiday.id
                                );
                                if (isSelected) {
                                  setHolidayPolicyForm((prev) => ({
                                    ...prev,
                                    holidays: prev.holidays.filter(
                                      (h) => h.id !== holiday.id
                                    ),
                                  }));
                                } else {
                                  setHolidayPolicyForm((prev) => ({
                                    ...prev,
                                    holidays: [...prev.holidays, holiday],
                                  }));
                                }
                              }}
                              className="rounded border-gray-300 text-redhalo-red focus:ring-redhalo-red/35"
                            />
                            <div className="flex-1">
                              <span
                                className={`font-medium ${
                                  darkMode ? "text-white" : "text-gray-800"
                                }`}
                              >
                                {holiday.name}
                              </span>
                              <span
                                className={`ml-2 text-sm ${
                                  darkMode ? "text-gray-300" : "text-gray-600"
                                }`}
                              >
                                ({formatDate(holiday.date)})
                              </span>
                              <span
                                className={`ml-2 px-2 py-1 text-xs rounded ${
                                  holiday.type === "national"
                                    ? "rh-accent-soft"
                                    : "bg-redhalo-mint/90 text-redhalo-navy"
                                }`}
                              >
                                {holiday.type === "national"
                                  ? "National"
                                  : "Festival"}
                              </span>
                            </div>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Selected Holidays List */}
                  <div
                    className={`rounded-lg ${
                      darkMode ? "bg-gray-700" : "bg-gray-50"
                    }`}
                  >
                    <h5
                      className={`font-medium p-3 border-b ${
                        darkMode
                          ? "border-gray-600 text-white"
                          : "border-gray-200 text-gray-800"
                      }`}
                    >
                      Selected Holidays ({holidayPolicyForm.holidays.length})
                    </h5>
                    {holidayPolicyForm.holidays.length === 0 ? (
                      <p
                        className={`p-4 text-center ${
                          darkMode ? "text-gray-400" : "text-gray-500"
                        }`}
                      >
                        No holidays selected yet
                      </p>
                    ) : (
                      holidayPolicyForm.holidays.map((holiday) => (
                        <div
                          key={holiday.id}
                          className={`p-3 border-b ${
                            darkMode ? "border-gray-600" : "border-gray-200"
                          } flex justify-between items-center`}
                        >
                          <div>
                            <span
                              className={`font-medium ${
                                darkMode ? "text-white" : "text-gray-800"
                              }`}
                            >
                              {holiday.name}
                            </span>
                            <span
                              className={`ml-2 ${
                                darkMode ? "text-gray-300" : "text-gray-600"
                              }`}
                            >
                              - {formatDate(holiday.date)}
                            </span>
                            <span
                              className={`ml-2 px-2 py-1 text-xs rounded ${
                                holiday.type === "national"
                                  ? "rh-accent-soft"
                                  : "bg-redhalo-mint/90 text-redhalo-navy"
                              }`}
                            >
                              {holiday.type === "national"
                                ? "National"
                                : "Festival"}
                            </span>
                          </div>
                          <button
                            onClick={() => {
                              setHolidayPolicyForm((prev) => ({
                                ...prev,
                                holidays: prev.holidays.filter(
                                  (h) => h.id !== holiday.id
                                ),
                              }));
                            }}
                            className="text-red-600 hover:text-red-800"
                          >
                            Remove
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">
                    Assignment
                  </label>
                  <div className="space-y-4">
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={holidayPolicyForm.assignedTo.allEmployees}
                        onChange={handleSelectAllEmployeesForHoliday}
                        className="rounded border-gray-300 text-redhalo-red focus:ring-redhalo-red/35"
                      />
                      <span>Assign to all employees</span>
                    </label>

                    {!holidayPolicyForm.assignedTo.allEmployees && (
                      <div className="space-y-4">
                        {/* Applicability UI (aligned with AttendancePoliciesSection style) */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-medium mb-1">Branch</label>
                            <select
                              value={holidayPolicyForm.assignedTo.branches[0] ?? ""}
                              onChange={(e) =>
                                setHolidayPolicyForm((prev) => ({
                                  ...prev,
                                  assignedTo: {
                                    ...prev.assignedTo,
                                    branches: e.target.value ? [e.target.value] : [],
                                  },
                                }))
                              }
                              className={`w-full p-2 text-xs border rounded ${
                                darkMode ? "bg-gray-700 border-gray-600" : "bg-white border-gray-300"
                              }`}
                            >
                              <option value="">All</option>
                              {branchesList.map((branch) => (
                                <option key={branch.id} value={branch.id}>{branch.name}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-medium mb-1">Department</label>
                            <select
                              value={holidayPolicyForm.assignedTo.departments[0] ?? ""}
                              onChange={(e) =>
                                setHolidayPolicyForm((prev) => ({
                                  ...prev,
                                  assignedTo: {
                                    ...prev.assignedTo,
                                    departments: e.target.value ? [e.target.value] : [],
                                  },
                                }))
                              }
                              className={`w-full p-2 text-xs border rounded ${
                                darkMode ? "bg-gray-700 border-gray-600" : "bg-white border-gray-300"
                              }`}
                            >
                              <option value="">All</option>
                              {departmentsList.map((dept) => (
                                <option key={dept.id} value={dept.id}>{dept.name}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-medium mb-1">Designation</label>
                            <select
                              value={holidayPolicyForm.assignedTo.designations[0] ?? ""}
                              onChange={(e) =>
                                setHolidayPolicyForm((prev) => ({
                                  ...prev,
                                  assignedTo: {
                                    ...prev.assignedTo,
                                    designations: e.target.value ? [e.target.value] : [],
                                  },
                                }))
                              }
                              className={`w-full p-2 text-xs border rounded ${
                                darkMode ? "bg-gray-700 border-gray-600" : "bg-white border-gray-300"
                              }`}
                            >
                              <option value="">All</option>
                              {designationsList.map((designation) => (
                                <option key={designation.id} value={designation.id}>{designation.name}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-medium mb-1">Grade</label>
                            <select
                              value={holidayPolicyForm.assignedTo.grades[0] ?? ""}
                              onChange={(e) =>
                                setHolidayPolicyForm((prev) => ({
                                  ...prev,
                                  assignedTo: {
                                    ...prev.assignedTo,
                                    grades: e.target.value ? [e.target.value] : [],
                                  },
                                }))
                              }
                              className={`w-full p-2 text-xs border rounded ${
                                darkMode ? "bg-gray-700 border-gray-600" : "bg-white border-gray-300"
                              }`}
                            >
                              <option value="">All</option>
                              {gradesList.map((grade) => (
                                <option key={grade.id} value={grade.id}>{grade.name}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-medium mb-1">Category</label>
                            <select
                              value={holidayPolicyForm.assignedTo.categories[0] ?? ""}
                              onChange={(e) =>
                                setHolidayPolicyForm((prev) => ({
                                  ...prev,
                                  assignedTo: {
                                    ...prev.assignedTo,
                                    categories: e.target.value ? [e.target.value] : [],
                                  },
                                }))
                              }
                              className={`w-full p-2 text-xs border rounded ${
                                darkMode ? "bg-gray-700 border-gray-600" : "bg-white border-gray-300"
                              }`}
                            >
                              <option value="">All</option>
                              {categoriesList.map((category) => (
                                <option key={category.id} value={category.id}>{category.name}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-medium mb-1">Sub-Department</label>
                            <select
                              value={holidayPolicyForm.assignedTo.subDepartments[0] ?? ""}
                              onChange={(e) =>
                                setHolidayPolicyForm((prev) => ({
                                  ...prev,
                                  assignedTo: {
                                    ...prev.assignedTo,
                                    subDepartments: e.target.value ? [e.target.value] : [],
                                  },
                                }))
                              }
                              className={`w-full p-2 text-xs border rounded ${
                                darkMode ? "bg-gray-700 border-gray-600" : "bg-white border-gray-300"
                              }`}
                            >
                              <option value="">All</option>
                              {subDepartmentsList.map((subDept) => (
                                <option key={subDept.id} value={subDept.id}>{subDept.name}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-medium mb-1">Employment Type</label>
                            <select
                              value={holidayPolicyForm.assignedTo.employmentTypes[0] ?? ""}
                              onChange={(e) =>
                                setHolidayPolicyForm((prev) => ({
                                  ...prev,
                                  assignedTo: {
                                    ...prev.assignedTo,
                                    employmentTypes: e.target.value ? [e.target.value] : [],
                                  },
                                }))
                              }
                              className={`w-full p-2 text-xs border rounded ${
                                darkMode ? "bg-gray-700 border-gray-600" : "bg-white border-gray-300"
                              }`}
                            >
                              <option value="">All</option>
                              {employmentTypes.map((empType) => (
                                <option key={empType.id} value={empType.id}>{empType.name}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-medium mb-1">Individual employees (override)</label>
                            <input
                              type="text"
                              placeholder="Comma separated employee IDs"
                              value={(holidayPolicyForm.assignedTo.employees || []).join(", ")}
                              onChange={(e) => {
                                const ids = e.target.value
                                  .split(",")
                                  .map((v) => v.trim())
                                  .filter(Boolean);
                                setHolidayPolicyForm((prev) => ({
                                  ...prev,
                                  assignedTo: {
                                    ...prev.assignedTo,
                                    employees: ids,
                                  },
                                }));
                              }}
                              className={`w-full p-2 text-xs border rounded ${
                                darkMode ? "bg-gray-700 border-gray-600" : "bg-white border-gray-300"
                              }`}
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Fixed Action Buttons */}
            <div className="flex justify-end space-x-2 p-6 border-t border-gray-300 flex-shrink-0">
              <button
                onClick={() => {
                  setShowHolidayModal(false);
                  setEditingHolidayPolicy(null);
                }}
                className={`px-4 py-2 rounded ${
                  darkMode
                    ? "bg-gray-700 text-white"
                    : "bg-gray-200 text-gray-800"
                }`}
              >
                Cancel
              </button>
              <button
                onClick={handleHolidayPolicySubmit}
                disabled={isSavingHolidayPolicy}
                className={`px-4 py-2 rh-btn-primary rounded hover:opacity-95 ${
                  isSavingHolidayPolicy ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                {isSavingHolidayPolicy
                  ? editingHolidayPolicy
                    ? "Updating..."
                    : "Creating..."
                  : editingHolidayPolicy
                  ? "Update Policy"
                  : "Create Policy"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Holiday Bank Modal */}
      {showHolidayBankModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div
            className={`p-6 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto ${
              darkMode ? "bg-gray-800 text-white" : "bg-white text-gray-800"
            }`}
          >
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Add Holiday to Bank</h2>
              <button
                onClick={() => setShowHolidayBankModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={24} />
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Holiday Date *
                  </label>
                  <DatePicker
                    label="Holiday Date"
                    required
                    value={newHolidayBankForm.date}
                    onChange={(e) =>
                      setNewHolidayBankForm((prev) => ({
                        ...prev,
                        date: e.target.value,
                      }))
                    }
                    className={`w-full p-2 border rounded ${
                      darkMode
                        ? "bg-gray-700 border-gray-600"
                        : "bg-white border-gray-300"
                    }`}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Holiday Name *
                  </label>
                  <input
                    type="text"
                    value={newHolidayBankForm.name}
                    onChange={(e) =>
                      setNewHolidayBankForm((prev) => ({
                        ...prev,
                        name: e.target.value,
                      }))
                    }
                    className={`w-full p-2 border rounded ${
                      darkMode
                        ? "bg-gray-700 border-gray-600"
                        : "bg-white border-gray-300"
                    }`}
                    placeholder="Enter holiday name"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  Holiday Type *
                </label>
                <select
                  value={newHolidayBankForm.type}
                  onChange={(e) =>
                    setNewHolidayBankForm((prev) => ({
                      ...prev,
                      type: e.target.value,
                    }))
                  }
                  className={`w-full p-2 border rounded ${
                    darkMode
                      ? "bg-gray-700 border-gray-600"
                      : "bg-white border-gray-300"
                  }`}
                >
                  <option value="national">National Holiday</option>
                  <option value="festival">Festival Holiday</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  Description
                </label>
                <textarea
                  value={newHolidayBankForm.description}
                  onChange={(e) =>
                    setNewHolidayBankForm((prev) => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                  className={`w-full p-2 border rounded ${
                    darkMode
                      ? "bg-gray-700 border-gray-600"
                      : "bg-white border-gray-300"
                  }`}
                  rows={3}
                  placeholder="Enter holiday description"
                />
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowHolidayBankModal(false)}
                  className={`px-4 py-2 rounded ${
                    darkMode
                      ? "bg-gray-700 text-[#E0E0E0]"
                      : "bg-gray-200 text-gray-900"
                  }`}
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddToHolidayBank}
                  className="px-4 py-2 rh-btn-primary rounded hover:opacity-95"
                >
                  Add to Bank
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Holiday Import Modal */}
      {showHolidayImportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div
            className={`p-6 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto ${
              darkMode ? "bg-gray-800 text-white" : "bg-white text-gray-800"
            }`}
          >
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Import Holidays from Excel</h2>
              <button
                onClick={() => setShowHolidayImportModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={24} />
              </button>
            </div>

            <div className="space-y-4">
              <div
                className={`p-4 rounded-lg ${
                  darkMode ? "bg-gray-700" : "bg-gray-50"
                }`}
              >
                <h3
                  className={`font-medium mb-2 ${
                    darkMode ? "text-white" : "text-gray-800"
                  }`}
                >
                  Instructions
                </h3>
                <ul
                  className={`text-sm space-y-1 ${
                    darkMode ? "text-gray-300" : "text-gray-600"
                  }`}
                >
                  <li>• Download the template to see the required format</li>
                  <li>
                    • Required columns: Holiday Name, Date, Type, Description
                  </li>
                  <li>• Date format: YYYY-MM-DD (e.g., 2024-01-01)</li>
                  <li>• Type: National or Festival</li>
                  <li>• Description is optional</li>
                </ul>
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={handleDownloadHolidayTemplate}
                  className="px-4 py-2 rh-btn-primary rounded hover:opacity-95"
                >
                  Download Template
                </button>
                <label className="px-4 py-2 rh-btn-primary rounded hover:opacity-95 cursor-pointer">
                  Select Excel File
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={(e) => {
                      if (e.target.files[0]) {
                        handleHolidayImport(e.target.files[0]);
                      }
                    }}
                    className="hidden"
                  />
                </label>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Leave Type Modal */}
      {showLeaveTypeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div
            className={`p-6 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto ${
              darkMode ? "bg-gray-800 text-white" : "bg-white text-gray-800"
            }`}
          >
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">
                {editingLeaveType ? "Edit Leave Type" : "Create Leave Type"}
              </h2>
              <button
                onClick={() => {
                  setShowLeaveTypeModal(false);
                  setEditingLeaveType(null);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={24} />
              </button>
            </div>

            <div className="space-y-6">
              {/* Basic Information */}
              <div>
                <h3 className="text-lg font-semibold mb-4">
                  Basic Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Leave Type Name *
                    </label>
                    <input
                      type="text"
                      value={leaveTypeForm.name}
                      onChange={(e) =>
                        setLeaveTypeForm((prev) => ({
                          ...prev,
                          name: e.target.value,
                        }))
                      }
                      className={`w-full p-2 border rounded ${
                        darkMode
                          ? "bg-gray-700 border-gray-600"
                          : "bg-white border-gray-300"
                      }`}
                      placeholder="e.g., Casual Leave"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Leave Code *
                    </label>
                    <input
                      type="text"
                      value={leaveTypeForm.code}
                      onChange={(e) => {
                        // ✅ Restrict to 2 uppercase letters only
                        let value = e.target.value.toUpperCase();
                        // Remove any non-letter characters
                        value = value.replace(/[^A-Z]/g, "");
                        // Check if user tried to enter more than 2 characters
                        if (
                          e.target.value.length > 2 &&
                          leaveTypeForm.code.length === 2
                        ) {
                          toast.error(
                            "Maximum 2 letters allowed for Leave Code"
                          );
                        }
                        // Limit to 2 characters
                        value = value.slice(0, 2);
                        setLeaveTypeForm((prev) => ({
                          ...prev,
                          code: value,
                        }));
                      }}
                      maxLength="2"
                      placeholder="e.g., CL"
                      className={`w-full p-2 border rounded ${
                        darkMode
                          ? "bg-gray-700 border-gray-600"
                          : "bg-white border-gray-300"
                      }`}
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium mb-1">
                      Description
                    </label>
                    <textarea
                      value={leaveTypeForm.description}
                      onChange={(e) =>
                        setLeaveTypeForm((prev) => ({
                          ...prev,
                          description: e.target.value,
                        }))
                      }
                      className={`w-full p-2 border rounded ${
                        darkMode
                          ? "bg-gray-700 border-gray-600"
                          : "bg-white border-gray-300"
                      }`}
                      placeholder="Enter leave type description"
                      rows={3}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Color
                    </label>
                    <input
                      type="color"
                      value={leaveTypeForm.color}
                      onChange={(e) =>
                        setLeaveTypeForm((prev) => ({
                          ...prev,
                          color: e.target.value,
                        }))
                      }
                      className="w-full h-10 border rounded"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Maximum Consecutive Days
                    </label>
                    <input
                      type="number"
                      value={leaveTypeForm.maxConsecutiveDays}
                      onChange={(e) =>
                        setLeaveTypeForm((prev) => ({
                          ...prev,
                          maxConsecutiveDays: e.target.value,
                        }))
                      }
                      className={`w-full p-2 border rounded ${
                        darkMode
                          ? "bg-gray-700 border-gray-600"
                          : "bg-white border-gray-300"
                      }`}
                      placeholder="e.g., 5"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Leave Category
                    </label>
                    <select
                      value={leaveTypeForm.category}
                      onChange={(e) =>
                        setLeaveTypeForm((prev) => ({
                          ...prev,
                          category: e.target.value,
                        }))
                      }
                      className={`w-full p-2 border rounded ${
                        darkMode
                          ? "bg-gray-700 border-gray-600"
                          : "bg-white border-gray-300"
                      }`}
                    >
                      <option value="general">General</option>
                      <option value="statutory">Statutory</option>
                      <option value="compensatory">Compensatory</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Gender Applicability
                    </label>
                    <select
                      value={leaveTypeForm.genderApplicability}
                      onChange={(e) =>
                        setLeaveTypeForm((prev) => ({
                          ...prev,
                          genderApplicability: e.target.value,
                        }))
                      }
                      className={`w-full p-2 border rounded ${
                        darkMode
                          ? "bg-gray-700 border-gray-600"
                          : "bg-white border-gray-300"
                      }`}
                    >
                      <option value="all">All Genders</option>
                      <option value="male">Male Only</option>
                      <option value="female">Female Only</option>
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={leaveTypeForm.isPaid}
                      onChange={(e) =>
                        setLeaveTypeForm((prev) => ({
                          ...prev,
                          isPaid: e.target.checked,
                        }))
                      }
                      className="rounded border-gray-300 text-redhalo-red focus:ring-redhalo-red/35"
                    />
                    <span className="text-sm">Paid Leave</span>
                  </label>
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={leaveTypeForm.requiresApproval}
                      onChange={(e) =>
                        setLeaveTypeForm((prev) => ({
                          ...prev,
                          requiresApproval: e.target.checked,
                        }))
                      }
                      className="rounded border-gray-300 text-redhalo-red focus:ring-redhalo-red/35"
                    />
                    <span className="text-sm">Requires Approval</span>
                  </label>
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={leaveTypeForm.isActive}
                      onChange={(e) =>
                        setLeaveTypeForm((prev) => ({
                          ...prev,
                          isActive: e.target.checked,
                        }))
                      }
                      className="rounded border-gray-300 text-redhalo-red focus:ring-redhalo-red/35"
                    />
                    <span className="text-sm">Active</span>
                  </label>
                </div>
              </div>

              {/* Statutory Leave Rules */}
              {leaveTypeForm.category === "statutory" && (
                <div>
                  <h3 className="text-lg font-semibold mb-4">
                    Statutory Leave Rules
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Lifetime Limit (days)
                      </label>
                      <input
                        type="number"
                        value={leaveTypeForm.statutoryRules.lifetimeLimit}
                        onChange={(e) =>
                          setLeaveTypeForm((prev) => ({
                            ...prev,
                            statutoryRules: {
                              ...prev.statutoryRules,
                              lifetimeLimit: e.target.value,
                            },
                          }))
                        }
                        className={`w-full p-2 border rounded ${
                          darkMode
                            ? "bg-gray-700 border-gray-600"
                            : "bg-white border-gray-300"
                        }`}
                        placeholder="e.g., 180"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Lifetime Limit Count
                      </label>
                      <input
                        type="number"
                        value={leaveTypeForm.statutoryRules.lifetimeLimitCount}
                        onChange={(e) =>
                          setLeaveTypeForm((prev) => ({
                            ...prev,
                            statutoryRules: {
                              ...prev.statutoryRules,
                              lifetimeLimitCount: e.target.value,
                            },
                          }))
                        }
                        className={`w-full p-2 border rounded ${
                          darkMode
                            ? "bg-gray-700 border-gray-600"
                            : "bg-white border-gray-300"
                        }`}
                        placeholder="e.g., 2"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Applicable Gender
                      </label>
                      <select
                        value={leaveTypeForm.statutoryRules.applicableGender}
                        onChange={(e) =>
                          setLeaveTypeForm((prev) => ({
                            ...prev,
                            statutoryRules: {
                              ...prev.statutoryRules,
                              applicableGender: e.target.value,
                            },
                          }))
                        }
                        className={`w-full p-2 border rounded ${
                          darkMode
                            ? "bg-gray-700 border-gray-600"
                            : "bg-white border-gray-300"
                        }`}
                      >
                        <option value="all">All Genders</option>
                        <option value="female">Female Only</option>
                        <option value="male">Male Only</option>
                      </select>
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium mb-1">
                        Special Conditions
                      </label>
                      <textarea
                        value={leaveTypeForm.statutoryRules.specialConditions}
                        onChange={(e) =>
                          setLeaveTypeForm((prev) => ({
                            ...prev,
                            statutoryRules: {
                              ...prev.statutoryRules,
                              specialConditions: e.target.value,
                            },
                          }))
                        }
                        className={`w-full p-2 border rounded ${
                          darkMode
                            ? "bg-gray-700 border-gray-600"
                            : "bg-white border-gray-300"
                        }`}
                        placeholder="e.g., For 3rd conception, eligible for 8 weeks only"
                        rows={3}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3 pt-4 border-t">
                <button
                  onClick={() => {
                    setShowLeaveTypeModal(false);
                    setEditingLeaveType(null);
                  }}
                  className={`px-4 py-2 rounded ${
                    darkMode
                      ? "bg-gray-600 text-white hover:bg-gray-700"
                      : "bg-gray-300 text-gray-700 hover:bg-gray-400"
                  }`}
                >
                  Cancel
                </button>
                <button
                  onClick={handleLeaveTypeSubmit}
                  disabled={isSavingLeaveType}
                  className={`px-4 py-2 rh-btn-primary rounded hover:opacity-95 ${
                    isSavingLeaveType ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                >
                  {isSavingLeaveType
                    ? editingLeaveType
                      ? "Updating..."
                      : "Creating..."
                    : editingLeaveType
                    ? "Update Leave Type"
                    : "Create Leave Type"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Leave Group Modal */}
      {showLeaveGroupModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div
            className={`rounded-lg shadow-xl w-[84vw] h-[84vh] flex flex-col ${
              darkMode ? "bg-gray-800 text-white" : "bg-white text-gray-800"
            }`}
          >
            {/* Fixed Header */}
            <div className="flex justify-between items-center p-6 border-b border-gray-300 flex-shrink-0">
              <h2 className="text-xl font-bold">
                {editingLeaveGroup ? "Edit Leave Group" : "Create Leave Group"}
              </h2>
              <button
                onClick={() => {
                  setShowLeaveGroupModal(false);
                  setEditingLeaveGroup(null);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={24} />
              </button>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-6">
                {/* Basic Information */}
                <div>
                  <h3 className="text-lg font-semibold mb-4">
                    Basic Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Leave Group Name *
                      </label>
                      <input
                        type="text"
                        value={leaveGroupForm.name}
                        onChange={(e) =>
                          setLeaveGroupForm((prev) => ({
                            ...prev,
                            name: e.target.value,
                          }))
                        }
                        className={`w-full p-2 border rounded ${
                          darkMode
                            ? "bg-gray-700 border-gray-600"
                            : "bg-white border-gray-300"
                        }`}
                        placeholder="e.g., Standard Leave Group"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium mb-1">
                        Description
                      </label>
                      <textarea
                        value={leaveGroupForm.description}
                        onChange={(e) =>
                          setLeaveGroupForm((prev) => ({
                            ...prev,
                            description: e.target.value,
                          }))
                        }
                        className={`w-full p-2 border rounded ${
                          darkMode
                            ? "bg-gray-700 border-gray-600"
                            : "bg-white border-gray-300"
                        }`}
                        placeholder="Enter leave group description"
                        rows={3}
                      />
                    </div>
                  </div>
                </div>

                {/* Leave Types Selection */}
                <div>
                  <h3 className="text-lg font-semibold mb-4">
                    Select Leave Types *
                  </h3>
                  <div
                    className={`border rounded p-4 ${
                      darkMode
                        ? "bg-gray-700 border-gray-600"
                        : "bg-white border-gray-300"
                    }`}
                  >
                    {leaveTypes.length === 0 ? (
                      <p
                        className={`text-center ${
                          darkMode ? "text-gray-400" : "text-gray-500"
                        }`}
                      >
                        No leave types available. Please create leave types
                        first.
                      </p>
                    ) : (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {leaveTypes.map((leaveType) => {
                          const isSelected = leaveGroupForm.leaveTypes.some((lt) => lt.id === leaveType.id);
                          return (
                            <div
                              key={leaveType.id}
                              className={`flex items-start justify-between gap-2 p-3 rounded border ${
                                isSelected
                                  ? darkMode
                                    ? "border-redhalo-red/50 bg-gray-600/50"
                                    : "border-redhalo-red/40 bg-red-50/30"
                                  : darkMode
                                    ? "border-gray-600 hover:bg-gray-600"
                                    : "border-gray-200 hover:bg-gray-50"
                              }`}
                            >
                              <label className="flex items-start space-x-2 cursor-pointer flex-1 min-w-0">
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setLeaveGroupForm((prev) => ({
                                        ...prev,
                                        leaveTypes: [...prev.leaveTypes, leaveType],
                                        leaveTypeConfigurations: {
                                          ...prev.leaveTypeConfigurations,
                                          [leaveType.id]: prev.leaveTypeConfigurations?.[leaveType.id] ?? getDefaultLeaveTypeConfig(leaveType),
                                        },
                                      }));
                                    } else {
                                      setLeaveGroupForm((prev) => {
                                        const next = { ...(prev.leaveTypeConfigurations || {}) };
                                        delete next[leaveType.id];
                                        return {
                                          ...prev,
                                          leaveTypes: prev.leaveTypes.filter(
                                            (lt) => lt.id !== leaveType.id
                                          ),
                                          leaveTypeConfigurations: next,
                                        };
                                      });
                                      if (configuringLeaveType?.id === leaveType.id) setConfiguringLeaveType(null);
                                    }
                                  }}
                                  className="rounded border-gray-300 text-redhalo-red focus:ring-redhalo-red/35 mt-0.5 flex-shrink-0"
                                />
                                <div className="flex flex-col min-w-0 flex-1">
                                  <div className="flex items-center space-x-2">
                                    <div
                                      className="w-3 h-3 rounded-full flex-shrink-0"
                                      style={{ backgroundColor: leaveType.color }}
                                    />
                                    <span className="text-sm font-medium truncate">{leaveType.name}</span>
                                  </div>
                                  <span className="text-xs text-gray-500 ml-5">({leaveType.code})</span>
                                </div>
                              </label>
                              <button
                                type="button"
                                disabled={!isSelected}
                                onClick={(e) => {
                                  e.preventDefault();
                                  if (!isSelected) return;
                                  setConfiguringLeaveType(leaveType);
                                  setExpandedLTCSection("credit");
                                }}
                                className={`flex-shrink-0 rounded p-1.5 transition-colors ${
                                  isSelected
                                    ? darkMode
                                      ? "bg-gray-500 hover:bg-gray-400 text-white cursor-pointer"
                                      : "bg-white border border-gray-300 hover:bg-gray-100 text-gray-700 cursor-pointer"
                                    : darkMode
                                      ? "bg-gray-700 text-gray-500 cursor-not-allowed opacity-50"
                                      : "bg-gray-100 text-gray-400 cursor-not-allowed opacity-60"
                                }`}
                                title={isSelected ? "Configure leave type settings" : "Select leave type first"}
                              >
                                <Settings size={16} />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>

                {/* Configure Leave Type modal (wizard with Next/Previous, opened via gear in Select Leave Types) */}
                {configuringLeaveType && (() => {
                  const lt = configuringLeaveType;
                  const cfg = (leaveGroupForm.leaveTypeConfigurations || {})[lt.id] || getDefaultLeaveTypeConfig(lt);
                  const inputCls = `w-full p-2 border rounded ${darkMode ? "bg-gray-700 border-gray-600" : "bg-white border-gray-300"}`;
                  const labelCls = "block text-sm font-medium mb-1";
                  const sectionCls = "mb-4";
                  const radioGrpCls = "flex items-center gap-4";
                  const radioLabelCls = "flex items-center gap-2 cursor-pointer";
                  const steps = [{ key: "credit", label: "Credit" }, { key: "accumulation", label: "Accumulation" }, { key: "usageRules", label: "Availment" }, { key: "approvalWorkflow", label: "Approval" }];
                  const stepIndex = steps.findIndex((s) => s.key === expandedLTCSection);
                  const currentStep = stepIndex >= 0 ? stepIndex : 0;
                  const goNext = () => { if (currentStep < steps.length - 1) setExpandedLTCSection(steps[currentStep + 1].key); };
                  const goPrev = () => { if (currentStep > 0) setExpandedLTCSection(steps[currentStep - 1].key); };
                  const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
                  const expiryEnabled = (cfg.accumulation?.expiryRuleEnabled ?? cfg.accumulation?.expiryRule ? "Yes" : "No") === "Yes";
                  const expiryHowRaw = cfg.accumulation?.expiryHow ?? cfg.accumulation?.expiryRule ?? "Lapse Immediately";
                                  const expiryHow = (expiryHowRaw === "Lapse after X months" || expiryHowRaw === "Never Lapse") ? "Lapse Immediately" : expiryHowRaw;
                  return (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]" onClick={() => { setConfiguringLeaveType(null); }}>
                      <div className={`rounded-lg shadow-xl w-[92vw] max-w-5xl max-h-[88vh] flex flex-col ${darkMode ? "bg-gray-800 text-white" : "bg-white text-gray-800"}`} onClick={(e) => e.stopPropagation()}>
                        <div className="flex justify-between items-center p-5 border-b border-gray-300 flex-shrink-0">
                          <h3 className="text-lg font-semibold">Configure {lt.name}</h3>
                          <button type="button" onClick={() => setConfiguringLeaveType(null)} className={`p-1 rounded ${darkMode ? "hover:bg-gray-600" : "hover:bg-gray-200"}`}>
                            <X size={20} />
                          </button>
                        </div>
                        <div className="flex-1 overflow-y-auto p-5 md:p-6">
                          {/* Effective From Date - above wizard */}
                          <div className="mb-5 max-w-xs">
                            <label className={labelCls}>Effective From Date</label>
                            <DatePicker value={cfg.basic?.effectiveFromDate ?? ""} onChange={(e) => updateLeaveTypeConfig(lt.id, "basic", { effectiveFromDate: e?.target?.value ?? e })} />
                          </div>
                          {/* Wizard step indicator */}
                          <div className={`text-sm mb-4 ${darkMode ? "text-gray-400" : "text-gray-500"}`}>
                            Step {currentStep + 1} of {steps.length}: {steps[currentStep].label}
                          </div>
                          {expandedLTCSection === "credit" && (
                            <div className={sectionCls}>
                              <h4 className="text-md font-medium mb-3">Credit</h4>
                              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                <div><label className={labelCls}>Credit Frequency</label><select value={cfg.credit?.creditFrequency ?? "Yearly"} onChange={(e) => updateLeaveTypeConfig(lt.id, "credit", { creditFrequency: e.target.value })} className={inputCls}><option value="Monthly">Monthly</option><option value="Quarterly">Quarterly</option><option value="Half-Yearly">Half-Yearly</option><option value="Yearly">Yearly</option><option value="One-time">One-time</option></select></div>
                                <div><label className={labelCls}>Credit Method</label><select value={cfg.credit?.creditMethod ?? "Fixed Days"} onChange={(e) => updateLeaveTypeConfig(lt.id, "credit", { creditMethod: e.target.value })} className={inputCls}><option value="Fixed Days">Fixed Days</option><option value="Based on Working Days">Based on Working Days</option><option value="Based on Attendance">Based on Attendance</option></select></div>
                                {cfg.credit?.creditMethod === "Fixed Days" && (
                                  <div><label className={labelCls}>No. of days to credit</label><input type="number" min={0} value={cfg.credit?.daysToCredit ?? ""} onChange={(e) => updateLeaveTypeConfig(lt.id, "credit", { daysToCredit: e.target.value })} className={inputCls} placeholder="Days" /></div>
                                )}
                                {(cfg.credit?.creditMethod === "Based on Attendance" || cfg.credit?.creditMethod === "Based on Working Days") && (
                                  <>
                                    <div><label className={labelCls}>{cfg.credit?.creditMethod === "Based on Attendance" ? "On how many days attendance" : "On how many working days"}</label><input type="number" min={0} value={cfg.credit?.attendanceDaysRequired ?? ""} onChange={(e) => updateLeaveTypeConfig(lt.id, "credit", { attendanceDaysRequired: e.target.value })} className={inputCls} placeholder="e.g. 22" /></div>
                                    <div><label className={labelCls}>How many leaves to credit</label><input type="number" min={0} value={cfg.credit?.leavesCreditedPerAttendanceDays ?? ""} onChange={(e) => updateLeaveTypeConfig(lt.id, "credit", { leavesCreditedPerAttendanceDays: e.target.value })} className={inputCls} placeholder="e.g. 1" /></div>
                                  </>
                                )}
                                <div><label className={labelCls}>Pro-rata for mid-joining</label><div className={radioGrpCls}><label className={radioLabelCls}><input type="radio" name={`proRata-${lt.id}`} checked={(cfg.credit?.proRataMidJoining ?? "No") === "Yes"} onChange={() => updateLeaveTypeConfig(lt.id, "credit", { proRataMidJoining: "Yes" })} /> Yes</label><label className={radioLabelCls}><input type="radio" name={`proRata-${lt.id}`} checked={(cfg.credit?.proRataMidJoining ?? "No") === "No"} onChange={() => updateLeaveTypeConfig(lt.id, "credit", { proRataMidJoining: "No" })} /> No</label></div></div>
                                <div><label className={labelCls}>Credit on</label><select value={cfg.credit?.creditOn ?? "Start of Period"} onChange={(e) => updateLeaveTypeConfig(lt.id, "credit", { creditOn: e.target.value })} className={inputCls}><option value="Start of Period">Start of Period</option><option value="End of Period">End of Period</option></select></div>
                              </div>
                            </div>
                          )}
                          {expandedLTCSection === "accumulation" && (
                            <div className={sectionCls}>
                              <h4 className="text-md font-medium mb-3">Accumulation & Carry Forward</h4>
                              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                <div><label className={labelCls}>Maximum Accumulation Limit</label><input type="number" value={cfg.accumulation?.maximumAccumulationLimit ?? ""} onChange={(e) => updateLeaveTypeConfig(lt.id, "accumulation", { maximumAccumulationLimit: e.target.value })} className={inputCls} placeholder="Days" /></div>
                                <div><label className={labelCls}>Carry Forward Limit</label><input type="number" value={cfg.accumulation?.carryForwardLimit ?? ""} onChange={(e) => updateLeaveTypeConfig(lt.id, "accumulation", { carryForwardLimit: e.target.value })} className={inputCls} placeholder="Days" /></div>
                                <div><label className={labelCls}>Expiry Rule</label><div className={radioGrpCls}><label className={radioLabelCls}><input type="radio" name={`expiryRule-${lt.id}`} checked={expiryEnabled} onChange={() => updateLeaveTypeConfig(lt.id, "accumulation", { expiryRuleEnabled: "Yes" })} /> Yes</label><label className={radioLabelCls}><input type="radio" name={`expiryRule-${lt.id}`} checked={!expiryEnabled} onChange={() => updateLeaveTypeConfig(lt.id, "accumulation", { expiryRuleEnabled: "No" })} /> No</label></div></div>
                                {expiryEnabled && (
                                  <>
                                    <div><label className={labelCls}>How</label><select value={expiryHow} onChange={(e) => updateLeaveTypeConfig(lt.id, "accumulation", { expiryHow: e.target.value })} className={inputCls}><option value="Lapse Immediately">Lapse Immediately</option><option value="Lapse in specific month">Lapse in specific month</option></select></div>
                                    {expiryHow === "Lapse in specific month" && (
                                      <div><label className={labelCls}>Which month to lapse</label><select value={cfg.accumulation?.lapseMonth ?? ""} onChange={(e) => updateLeaveTypeConfig(lt.id, "accumulation", { lapseMonth: e.target.value })} className={inputCls}><option value="">Select month</option>{MONTHS.map((m, i) => <option key={m} value={String(i + 1)}>{m}</option>)}</select></div>
                                    )}
                                  </>
                                )}
                                <div><label className={labelCls}>Encashment Allowed</label><div className={radioGrpCls}><label className={radioLabelCls}><input type="radio" name={`encash-${lt.id}`} checked={(cfg.accumulation?.encashmentAllowed ?? "No") === "Yes"} onChange={() => updateLeaveTypeConfig(lt.id, "accumulation", { encashmentAllowed: "Yes" })} /> Yes</label><label className={radioLabelCls}><input type="radio" name={`encash-${lt.id}`} checked={(cfg.accumulation?.encashmentAllowed ?? "No") === "No"} onChange={() => updateLeaveTypeConfig(lt.id, "accumulation", { encashmentAllowed: "No" })} /> No</label></div></div>
                                {(cfg.accumulation?.encashmentAllowed === "Yes") && (
                                  <div><label className={labelCls}>Encashment Limit</label><input type="number" value={cfg.accumulation?.encashmentLimit ?? ""} onChange={(e) => updateLeaveTypeConfig(lt.id, "accumulation", { encashmentLimit: e.target.value })} className={inputCls} placeholder="Days" /></div>
                                )}
                                {(cfg.accumulation?.encashmentAllowed === "Yes") && (
                                  <div><label className={labelCls}>Encashment Calculation Method</label><select value={cfg.accumulation?.encashmentCalculationMethod ?? "Basic"} onChange={(e) => updateLeaveTypeConfig(lt.id, "accumulation", { encashmentCalculationMethod: e.target.value })} className={inputCls}><option value="Basic">Basic</option><option value="Gross">Gross</option><option value="Custom formula">Custom formula</option></select></div>
                                )}
                              </div>
                            </div>
                          )}
                          {expandedLTCSection === "usageRules" && (
                            <div className={sectionCls}>
                              <h4 className="text-md font-medium mb-3">Availment</h4>
                              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                <div><label className={labelCls}>Minimum Leave Units (day)</label><select value={cfg.usageRules?.minimumLeaveUnits ?? "1"} onChange={(e) => updateLeaveTypeConfig(lt.id, "usageRules", { minimumLeaveUnits: e.target.value })} className={inputCls}><option value="0.5">0.5</option><option value="1">1</option></select></div>
                                <div><label className={labelCls}>Maximum Leave per Request</label><input type="number" value={cfg.usageRules?.maximumLeavePerRequest ?? ""} onChange={(e) => updateLeaveTypeConfig(lt.id, "usageRules", { maximumLeavePerRequest: e.target.value })} className={inputCls} placeholder="Days" /></div>
                                <div><label className={labelCls}>Consecutive Leave Limit</label><input type="number" value={cfg.usageRules?.consecutiveLeaveLimit ?? ""} onChange={(e) => updateLeaveTypeConfig(lt.id, "usageRules", { consecutiveLeaveLimit: e.target.value })} className={inputCls} placeholder="Days" /></div>
                                <div><label className={labelCls}>Sandwich Rule</label><div className={radioGrpCls}><label className={radioLabelCls}><input type="radio" name={`sandwich-${lt.id}`} checked={(cfg.usageRules?.sandwichRule ?? "Disable") === "Enable"} onChange={() => updateLeaveTypeConfig(lt.id, "usageRules", { sandwichRule: "Enable" })} /> Enable</label><label className={radioLabelCls}><input type="radio" name={`sandwich-${lt.id}`} checked={(cfg.usageRules?.sandwichRule ?? "Disable") === "Disable"} onChange={() => updateLeaveTypeConfig(lt.id, "usageRules", { sandwichRule: "Disable" })} /> Disable</label></div></div>
                                <div><label className={labelCls}>Clubbing Restrictions</label><input type="text" value={(cfg.usageRules?.clubbingRestrictions || []).join(", ")} onChange={(e) => updateLeaveTypeConfig(lt.id, "usageRules", { clubbingRestrictions: e.target.value ? e.target.value.split(",").map((s) => s.trim()).filter(Boolean) : [] })} className={inputCls} placeholder="Comma-separated" /></div>
                                <div><label className={labelCls}>Advance Leave Allowed</label><div className={radioGrpCls}><label className={radioLabelCls}><input type="radio" name={`advance-${lt.id}`} checked={(cfg.usageRules?.advanceLeaveAllowed ?? "No") === "Yes"} onChange={() => updateLeaveTypeConfig(lt.id, "usageRules", { advanceLeaveAllowed: "Yes" })} /> Yes</label><label className={radioLabelCls}><input type="radio" name={`advance-${lt.id}`} checked={(cfg.usageRules?.advanceLeaveAllowed ?? "No") === "No"} onChange={() => updateLeaveTypeConfig(lt.id, "usageRules", { advanceLeaveAllowed: "No" })} /> No</label></div></div>
                                {(cfg.usageRules?.advanceLeaveAllowed === "Yes") && (
                                  <div><label className={labelCls}>Limit of advance leave (days)</label><input type="number" min={0} value={cfg.usageRules?.advanceLeaveLimit ?? ""} onChange={(e) => updateLeaveTypeConfig(lt.id, "usageRules", { advanceLeaveLimit: e.target.value })} className={inputCls} placeholder="Days" /></div>
                                )}
                                <div><label className={labelCls}>Negative Balance Allowed</label><div className={radioGrpCls}><label className={radioLabelCls}><input type="radio" name={`negBal-${lt.id}`} checked={(cfg.usageRules?.negativeBalanceAllowed ?? "No") === "Yes"} onChange={() => updateLeaveTypeConfig(lt.id, "usageRules", { negativeBalanceAllowed: "Yes" })} /> Yes</label><label className={radioLabelCls}><input type="radio" name={`negBal-${lt.id}`} checked={(cfg.usageRules?.negativeBalanceAllowed ?? "No") === "No"} onChange={() => updateLeaveTypeConfig(lt.id, "usageRules", { negativeBalanceAllowed: "No" })} /> No</label></div></div>
                                {(cfg.usageRules?.negativeBalanceAllowed === "Yes") && (
                                  <div><label className={labelCls}>Negative Balance Limit</label><input type="number" value={cfg.usageRules?.negativeBalanceLimit ?? ""} onChange={(e) => updateLeaveTypeConfig(lt.id, "usageRules", { negativeBalanceLimit: e.target.value })} className={inputCls} placeholder="Days" /></div>
                                )}
                                <div className="md:col-span-2"><label className={labelCls}>Avail balance only from next year</label><div className={radioGrpCls}><label className={radioLabelCls}><input type="radio" name={`availNextYr-${lt.id}`} checked={(cfg.usageRules?.availOnlyFromNextYear ?? "No") === "Yes"} onChange={() => updateLeaveTypeConfig(lt.id, "usageRules", { availOnlyFromNextYear: "Yes" })} /> Yes</label><label className={radioLabelCls}><input type="radio" name={`availNextYr-${lt.id}`} checked={(cfg.usageRules?.availOnlyFromNextYear ?? "No") === "No"} onChange={() => updateLeaveTypeConfig(lt.id, "usageRules", { availOnlyFromNextYear: "No" })} /> No</label></div><span className={`text-xs ${darkMode ? "text-gray-500" : "text-gray-500"}`}> e.g. monthly credited balance can be availed only after next year</span></div>
                                <div><label className={labelCls}>Allow Leave in Notice Period</label><div className={radioGrpCls}><label className={radioLabelCls}><input type="radio" name={`notice-${lt.id}`} checked={(cfg.usageRules?.noticePeriodRestriction ?? "No") === "Yes"} onChange={() => updateLeaveTypeConfig(lt.id, "usageRules", { noticePeriodRestriction: "Yes" })} /> Yes</label><label className={radioLabelCls}><input type="radio" name={`notice-${lt.id}`} checked={(cfg.usageRules?.noticePeriodRestriction ?? "No") === "No"} onChange={() => updateLeaveTypeConfig(lt.id, "usageRules", { noticePeriodRestriction: "No" })} /> No</label></div></div>
                                {(cfg.usageRules?.noticePeriodRestriction === "Yes") && (
                                  <div><label className={labelCls}>Limit (days)</label><input type="number" min={0} value={cfg.usageRules?.noticePeriodDays ?? ""} onChange={(e) => updateLeaveTypeConfig(lt.id, "usageRules", { noticePeriodDays: e.target.value })} className={inputCls} placeholder="Days" /></div>
                                )}
                                <div><label className={labelCls}>Allow Leave in Probation</label><div className={radioGrpCls}><label className={radioLabelCls}><input type="radio" name={`probation-${lt.id}`} checked={(cfg.usageRules?.probationRestriction ?? "No") === "Yes"} onChange={() => updateLeaveTypeConfig(lt.id, "usageRules", { probationRestriction: "Yes" })} /> Yes</label><label className={radioLabelCls}><input type="radio" name={`probation-${lt.id}`} checked={(cfg.usageRules?.probationRestriction ?? "No") === "No"} onChange={() => updateLeaveTypeConfig(lt.id, "usageRules", { probationRestriction: "No" })} /> No</label></div></div>
                                {(cfg.usageRules?.probationRestriction === "Yes") && (
                                  <div><label className={labelCls}>Limit (days)</label><input type="number" min={0} value={cfg.usageRules?.probationDays ?? ""} onChange={(e) => updateLeaveTypeConfig(lt.id, "usageRules", { probationDays: e.target.value })} className={inputCls} placeholder="Days" /></div>
                                )}
                                <div><label className={labelCls}>Leave balance rounding</label><select value={cfg.usageRules?.balanceRounding ?? "full_day"} onChange={(e) => updateLeaveTypeConfig(lt.id, "usageRules", { balanceRounding: e.target.value })} className={inputCls}><option value="full_day">Nearest full day</option><option value="half_day">Half day</option><option value="quarter_day">Quarter day</option><option value="none">No rounding</option></select></div>
                              </div>
                            </div>
                          )}
                          {expandedLTCSection === "approvalWorkflow" && (
                            <LeaveTypeApprovalWorkflowSection
                              darkMode={darkMode}
                              cfg={cfg}
                              leaveTypeId={lt.id}
                              leaveTypeRequiresApproval={lt.requiresApproval !== false}
                              updateLeaveTypeConfig={updateLeaveTypeConfig}
                            />
                          )}
                        </div>
                        <div className={`flex justify-between gap-2 p-5 border-t ${darkMode ? "border-gray-600" : "border-gray-200"}`}>
                          <div className="flex gap-2">
                            <button type="button" onClick={() => setConfiguringLeaveType(null)} className={`px-4 py-2 rounded text-sm font-medium ${darkMode ? "bg-gray-600 hover:bg-gray-500" : "bg-gray-200 hover:bg-gray-300"}`}>Cancel</button>
                            <button type="button" onClick={goPrev} disabled={currentStep === 0} className={`px-4 py-2 rounded text-sm font-medium ${darkMode ? "bg-gray-600 hover:bg-gray-500" : "bg-gray-200 hover:bg-gray-300"} disabled:opacity-50 disabled:cursor-not-allowed`}>Previous</button>
                          </div>
                          <div className="flex gap-2">
                            {currentStep < steps.length - 1 ? (
                              <button type="button" onClick={goNext} className="px-4 py-2 rounded text-sm font-medium rh-btn-primary hover:opacity-95">Next</button>
                            ) : (
                              <button type="button" onClick={() => setConfiguringLeaveType(null)} className="px-4 py-2 rounded text-sm font-medium rh-btn-primary hover:opacity-95">Done</button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* Leave Group Workflow */}
                <div>
                  <h3 className="text-lg font-semibold mb-2">Leave Workflow</h3>
                  <LeaveGroupWorkflowSection
                    darkMode={darkMode}
                    workflow={normalizeLeaveGroupWorkflow(
                      leaveGroupForm.configurations?.leaveWorkflow
                    )}
                    onChange={updateLeaveGroupWorkflow}
                  />
                </div>

                {/* Assignment Settings - Similar to Weekly Off Policy */}
                <div>
                  <h3 className="text-lg font-semibold mb-4">
                    Assignment Settings
                  </h3>
                  <div className="space-y-4">
                    {/* All Employees Option */}
                    <div>
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={leaveGroupForm.assignedTo.allEmployees}
                          onChange={(e) =>
                            setLeaveGroupForm((prev) => ({
                              ...prev,
                              assignedTo: {
                                ...prev.assignedTo,
                                allEmployees: e.target.checked,
                              },
                            }))
                          }
                          className="rounded border-gray-300 text-redhalo-red focus:ring-redhalo-red/35"
                        />
                        <span className="text-sm font-medium">
                          Assign to All Employees
                        </span>
                      </label>
                    </div>

                    {!leaveGroupForm.assignedTo.allEmployees && (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {/* Branches */}
                        <div>
                          <label className="block text-sm font-medium mb-2">
                            Branches
                          </label>
                          <div
                            className={`border rounded p-2 max-h-32 overflow-y-auto ${
                              darkMode
                                ? "bg-gray-700 border-gray-600"
                                : "bg-white border-gray-300"
                            }`}
                          >
                            {branchesList.map((branch) => (
                              <label
                                key={branch.id}
                                className="flex items-center space-x-2 p-1 hover:bg-gray-100 text-sm"
                              >
                                <input
                                  type="checkbox"
                                  checked={leaveGroupForm.assignedTo.branches.includes(
                                    branch.id
                                  )}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setLeaveGroupForm((prev) => ({
                                        ...prev,
                                        assignedTo: {
                                          ...prev.assignedTo,
                                          branches: [
                                            ...prev.assignedTo.branches,
                                            branch.id,
                                          ],
                                        },
                                      }));
                                    } else {
                                      setLeaveGroupForm((prev) => ({
                                        ...prev,
                                        assignedTo: {
                                          ...prev.assignedTo,
                                          branches:
                                            prev.assignedTo.branches.filter(
                                              (id) => id !== branch.id
                                            ),
                                        },
                                      }));
                                    }
                                  }}
                                  className="rounded border-gray-300 text-redhalo-red focus:ring-redhalo-red/35"
                                />
                                <span>{branch.name}</span>
                              </label>
                            ))}
                          </div>
                        </div>

                        {/* Departments */}
                        <div>
                          <label className="block text-sm font-medium mb-2">
                            Departments
                          </label>
                          <div
                            className={`border rounded p-2 max-h-32 overflow-y-auto ${
                              darkMode
                                ? "bg-gray-700 border-gray-600"
                                : "bg-white border-gray-300"
                            }`}
                          >
                            {departmentsList.map((dept) => (
                              <label
                                key={dept.id}
                                className="flex items-center space-x-2 p-1 hover:bg-gray-100 text-sm"
                              >
                                <input
                                  type="checkbox"
                                  checked={leaveGroupForm.assignedTo.departments.includes(
                                    dept.id
                                  )}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setLeaveGroupForm((prev) => ({
                                        ...prev,
                                        assignedTo: {
                                          ...prev.assignedTo,
                                          departments: [
                                            ...prev.assignedTo.departments,
                                            dept.id,
                                          ],
                                        },
                                      }));
                                    } else {
                                      setLeaveGroupForm((prev) => ({
                                        ...prev,
                                        assignedTo: {
                                          ...prev.assignedTo,
                                          departments:
                                            prev.assignedTo.departments.filter(
                                              (id) => id !== dept.id
                                            ),
                                        },
                                      }));
                                    }
                                  }}
                                  className="rounded border-gray-300 text-redhalo-red focus:ring-redhalo-red/35"
                                />
                                <span>{dept.name}</span>
                              </label>
                            ))}
                          </div>
                        </div>

                        {/* Designations */}
                        <div>
                          <label className="block text-sm font-medium mb-2">
                            Designations
                          </label>
                          <div
                            className={`border rounded p-2 max-h-32 overflow-y-auto ${
                              darkMode
                                ? "bg-gray-700 border-gray-600"
                                : "bg-white border-gray-300"
                            }`}
                          >
                            {designationsList.map((designation) => (
                              <label
                                key={designation.id}
                                className="flex items-center space-x-2 p-1 hover:bg-gray-100 text-sm"
                              >
                                <input
                                  type="checkbox"
                                  checked={leaveGroupForm.assignedTo.designations.includes(
                                    designation.id
                                  )}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setLeaveGroupForm((prev) => ({
                                        ...prev,
                                        assignedTo: {
                                          ...prev.assignedTo,
                                          designations: [
                                            ...prev.assignedTo.designations,
                                            designation.id,
                                          ],
                                        },
                                      }));
                                    } else {
                                      setLeaveGroupForm((prev) => ({
                                        ...prev,
                                        assignedTo: {
                                          ...prev.assignedTo,
                                          designations:
                                            prev.assignedTo.designations.filter(
                                              (id) => id !== designation.id
                                            ),
                                        },
                                      }));
                                    }
                                  }}
                                  className="rounded border-gray-300 text-redhalo-red focus:ring-redhalo-red/35"
                                />
                                <span>{designation.name}</span>
                              </label>
                            ))}
                          </div>
                        </div>

                        {/* Individual Employees */}
                        <div className="md:col-span-2 lg:col-span-3">
                          <label className="block text-sm font-medium mb-2">
                            Individual Employees
                          </label>
                          <div className="relative">
                            <input
                              type="text"
                              placeholder="Search employees..."
                              value={employeeSearchTerm}
                              onChange={(e) =>
                                setEmployeeSearchTerm(e.target.value)
                              }
                              className={`w-full p-2 border rounded mb-2 ${
                                darkMode
                                  ? "bg-gray-700 border-gray-600"
                                  : "bg-white border-gray-300"
                              }`}
                            />
                            <div
                              className={`border rounded p-2 max-h-40 overflow-y-auto ${
                                darkMode
                                  ? "bg-gray-700 border-gray-600"
                                  : "bg-white border-gray-300"
                              }`}
                            >
                              {employees
                                .filter((emp) => {
                                  const searchTerm =
                                    employeeSearchTerm.toLowerCase();
                                  const name = (
                                    emp.user?.name ||
                                    emp.name ||
                                    ""
                                  ).toLowerCase();
                                  const code = (
                                    emp.empCode ||
                                    emp.employeeId ||
                                    ""
                                  ).toLowerCase();
                                  return (
                                    name.includes(searchTerm) ||
                                    code.includes(searchTerm)
                                  );
                                })
                                .map((employee) => (
                                  <label
                                    key={employee.id}
                                    className="flex items-center space-x-2 p-1 hover:bg-gray-100 text-sm"
                                  >
                                    <input
                                      type="checkbox"
                                      checked={leaveGroupForm.assignedTo.employees.includes(
                                        employee.id
                                      )}
                                      onChange={(e) => {
                                        if (e.target.checked) {
                                          setLeaveGroupForm((prev) => ({
                                            ...prev,
                                            assignedTo: {
                                              ...prev.assignedTo,
                                              employees: [
                                                ...prev.assignedTo.employees,
                                                employee.id,
                                              ],
                                            },
                                          }));
                                        } else {
                                          setLeaveGroupForm((prev) => ({
                                            ...prev,
                                            assignedTo: {
                                              ...prev.assignedTo,
                                              employees:
                                                prev.assignedTo.employees.filter(
                                                  (id) => id !== employee.id
                                                ),
                                            },
                                          }));
                                        }
                                      }}
                                      className="rounded border-gray-300 text-redhalo-red focus:ring-redhalo-red/35"
                                    />
                                    <span>
                                      {employee.user?.name || employee.name} (
                                      {employee.empCode || employee.employeeId})
                                    </span>
                                  </label>
                                ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Fixed Action Buttons */}
            <div className="flex justify-end space-x-3 p-6 border-t border-gray-300 flex-shrink-0">
              <button
                onClick={() => {
                  setShowLeaveGroupModal(false);
                  setEditingLeaveGroup(null);
                }}
                className={`px-4 py-2 rounded ${
                  darkMode
                    ? "bg-gray-600 text-white hover:bg-gray-700"
                    : "bg-gray-300 text-gray-700 hover:bg-gray-400"
                }`}
              >
                Cancel
              </button>
              <button
                onClick={handleLeaveGroupSubmit}
                disabled={isSavingLeaveGroup}
                className={`px-4 py-2 rh-btn-primary rounded hover:opacity-95 ${
                  isSavingLeaveGroup ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                {isSavingLeaveGroup
                  ? editingLeaveGroup
                    ? "Updating..."
                    : "Creating..."
                  : editingLeaveGroup
                  ? "Update Leave Group"
                  : "Create Leave Group"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmationModal.isOpen}
        onClose={closeConfirmation}
        onConfirm={confirmationModal.onConfirm}
        onCancel={confirmationModal.onCancel}
        title={confirmationModal.title}
        message={confirmationModal.message}
        confirmText={confirmationModal.confirmText}
        cancelText={confirmationModal.cancelText}
        darkMode={darkMode}
      />
    </>
  );
};

// AttendanceStructuresContent component ends here

export default function Attendance({
  darkMode,
  setCurrentPage,
  toggleDarkMode,
}) {
  const [searchTerm, setSearchTerm] = useState("");
  /** Blocks credential autofill until the user focuses the field (Chrome workaround). */
  const [toolbarSearchReadonlyBarrier, setToolbarSearchReadonlyBarrier] =
    useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(() => {
    // Always default to current month
    return new Date().getMonth();
  });
  const [selectedYear, setSelectedYear] = useState(() => {
    // Always default to current year
    return new Date().getFullYear();
  });
  // Minimum date for month selection (from company serviceCommencesOn)
  const [minDate, setMinDate] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [showMarkAllPopup, setShowMarkAllPopup] = useState(false);
  const [markAllEmployeesList, setMarkAllEmployeesList] = useState([]);
  const [hasMarkAllUnsavedChanges, setHasMarkAllUnsavedChanges] = useState(false);
  const [showUploadPopup, setShowUploadPopup] = useState(false);
  const [showUploadLeavePopup, setShowUploadLeavePopup] = useState(false);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [attendanceStatusesState, setAttendanceStatusesState] = useState([
    "PR",
    "LOP",
    "WO",
    "PH",
  ]);
  const [statusDefinitionsState, setStatusDefinitionsState] = useState({
    PR: "Present",
  });
  const [statusColorMapState, setStatusColorMapState] = useState({});
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportMonth, setExportMonth] = useState(() => new Date().getMonth());
  const [exportYear, setExportYear] = useState(() => new Date().getFullYear());
  const [isExporting, setIsExporting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [attendanceData, setAttendanceData] = useState({});
  const [attendanceDetailData, setAttendanceDetailData] = useState({});
  const [attendanceLOPReversalMap, setAttendanceLOPReversalMap] = useState({}); // { employeeId: { day: true } } for LOP-reversed cells
  const [remarksMap, setRemarksMap] = useState({});
  const [originalAttendance, setOriginalAttendance] = useState({});
  const [overrideMap, setOverrideMap] = useState({});
  const [appliedFilters, setAppliedFilters] = useState({});
  // Keep a separate copy of the *form* filters so reopening the modal shows the last applied selections
  // (appliedFilters above is API params used for fetching)
  const [appliedFiltersForm, setAppliedFiltersForm] = useState({});
  const { mapping: attendanceMapping, reverseMapping, exportMapping: attendanceExportMapping, importMapping: attendanceImportMapping, importReverseMapping, halfDayDivider, halfDayPrefix } =
    useAttendanceCodeConfig();
  const [isEditing, setIsEditing] = useState(false);

  const [isLocked, setIsLocked] = useState(false);
  const [attendanceLocks, setAttendanceLocks] = useState([]);
  const lockFetchRequestIdRef = useRef(0);
  const [lopReversalDaysMap, setLOPReversalDaysMap] = useState({}); // { employeeId: days } for selected payroll month
  const [showPasskeyModal, setShowPasskeyModal] = useState(false);
  const [unlockPasskeyInputKey, setUnlockPasskeyInputKey] = useState(0);
  const [unlockPasskey, setUnlockPasskey] = useState("");
  const [showPasskey, setShowPasskey] = useState(false);
  const [passkeyError, setPasskeyError] = useState("");
  const [lockingBusy, setLockingBusy] = useState(false);
  const [unlockingBusy, setUnlockingBusy] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [alert, setAlert] = useState(null);
  
  // Define showAlert early to avoid initialization order issues
  const showAlert = useCallback((message, type = "success") => {
    setAlert({ message, type });
    setTimeout(() => setAlert(null), 3000);
  }, []);
  
  // Confirmation Modal State for lock/unlock
  const [confirmationModal, setConfirmationModal] = useState({
    isOpen: false,
    title: "",
    message: "",
    onConfirm: null,
    onCancel: null,
    confirmText: "Yes",
    cancelText: "No",
  });

  // Helper function to show confirmation modal
  const showConfirmation = useCallback((
    title,
    message,
    onConfirm,
    confirmText = "Yes",
    cancelText = "No",
    onCancel = null
  ) => {
    setConfirmationModal({
      isOpen: true,
      title,
      message,
      onConfirm: () => {
        setConfirmationModal((prev) => ({ ...prev, isOpen: false }));
        onConfirm();
      },
      onCancel,
      confirmText,
      cancelText,
    });
  }, []);

  const closeConfirmation = useCallback(() => {
    setConfirmationModal((prev) => ({ ...prev, isOpen: false }));
  }, []);

  // Unsaved changes when changing month: industry-standard Save / Don't Save / Cancel
  const [unsavedChangesModal, setUnsavedChangesModal] = useState({
    isOpen: false,
    nextYear: null,
    nextMonth: null,
  });

  const applyMonthChange = useCallback((nextYear, nextMonth) => {
    try {
      if (!isLocked && changedRecordsRef?.current?.size > 0) {
        changedRecordsRef.current.clear();
      }
    } catch (e) {
      /* ignore */
    }
    setHasMarkAllUnsavedChanges(false);
    setIsSaved(false);
    setIsEditing(false);
    setMonthPickerValue(getMonthRangeValue(nextYear, nextMonth));
    setSelectedYear(nextYear);
    setSelectedMonth(Math.min(Math.max(nextMonth, 0), 11));
  }, [isLocked]);

  const saveButtonRef = useRef(null);
  const pendingMonthAfterSaveRef = useRef(null);

  // Debounce/cooldown for Lock & Request Unlock to prevent multiple API hits (same in employee flows if added)
  const LOCK_ACTION_COOLDOWN_MS = 1500;
  const lockCooldownRef = useRef(0);
  const requestUnlockCooldownRef = useRef(0);
  const unlockWithPasskeyCooldownRef = useRef(0);

  const [selectedDate, setSelectedDate] = useState(() => {
    return localStorage.getItem("selectedDate") || null;
  });
  const [selectedCell, setSelectedCell] = useState({
    employeeId: null,
    day: null,
  });
  const [showSummaryTable, setShowSummaryTable] = useState(true);

  // Pagination state
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10); // Default 50 for better performance with large datasets
  const [totalPages, setTotalPages] = useState(1);
  const [totalEmployees, setTotalEmployees] = useState(0);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [attendancePagination, setAttendancePagination] = useState(null);
  const [serverSidePagination, setServerSidePagination] = useState(false);
  // Server-driven infinite scroll metadata
  const [nextEmployeePage, setNextEmployeePage] = useState(null);
  const [hasMoreEmployees, setHasMoreEmployees] = useState(false);
  // For "all" mode: track how many employees to display (chunked loading)
  const [visibleEmployeeCountForAll, setVisibleEmployeeCountForAll] =
    useState(100);

  // Pagination state - similar to Employee.jsx
  // const [itemsPerPage, setItemsPerPage] = useState(10); // Removed duplicate

  // New state for structures
  const [showStructuresModal, setShowStructuresModal] = useState(false);
  const [activeStructureTab, setActiveStructureTab] = useState("generalSettings");
  const [scheduleRecalcTick, setScheduleRecalcTick] = useState(0);
  // Import preview/report state
  const [importReport, setImportReport] = useState(null);
  const [showImportPreview, setShowImportPreview] = useState(false);
  const [importPreviewValid, setImportPreviewValid] = useState([]);
  const [importPreviewInvalid, setImportPreviewInvalid] = useState([]);

  // New state for tabs
  const [activeTab, setActiveTab] = useState("attendance");
  const [reportsMenuOpen, setReportsMenuOpen] = useState(false);
  const [activeReportsSubTab, setActiveReportsSubTab] = useState("daily-attendance");
  const [reportsSubTabTouched, setReportsSubTabTouched] = useState(false);
  const { hasModuleAccess, hasScreenAccess, isAdmin } = usePermissions();
  const canAnyAttendanceScreen = useCallback(
    (screenKeys, action = "view") =>
      Array.isArray(screenKeys) &&
      screenKeys.some((screenKey) =>
        hasScreenAccess("attendance", screenKey, action)
      ),
    [hasScreenAccess]
  );
  const canAttendanceView =
    isAdmin ||
    hasModuleAccess("attendance", "view") ||
    canAnyAttendanceScreen(
      [
        "attendance_dashboard",
        "attendance_history",
        "mark_attendance",
        "leave_requests",
        "regularization",
        "attendance_import",
      ],
      "view"
    );
  const canAttendanceCreate =
    isAdmin ||
    hasModuleAccess("attendance", "create") ||
    canAnyAttendanceScreen(
      ["mark_attendance", "attendance_import", "leave_requests", "regularization"],
      "create"
    );
  const canAttendanceEdit =
    isAdmin ||
    hasModuleAccess("attendance", "edit") ||
    canAnyAttendanceScreen(
      ["mark_attendance", "leave_requests", "regularization"],
      "edit"
    );
  const canAttendanceExport =
    isAdmin ||
    hasModuleAccess("attendance", "export") ||
    canAnyAttendanceScreen(["attendance_history", "attendance_dashboard"], "export");
  const canAttendanceApprove =
    isAdmin ||
    hasModuleAccess("attendance", "approve") ||
    canAnyAttendanceScreen(["leave_requests", "regularization"], "approve");
  const canViewLeaves =
    isAdmin ||
    canAnyAttendanceScreen(["leave_requests", "regularization"], "view");
  const canViewReports =
    isAdmin || hasScreenAccess("attendance", "attendance_history", "view");
  const canAttendanceImport =
    isAdmin ||
    hasModuleAccess("attendance", "create") ||
    hasScreenAccess("attendance", "attendance_import", "create");
  const canDownloadAttendanceTemplate = canAttendanceImport;
  const canManageAttendanceSettings =
    isAdmin ||
    hasModuleAccess("attendance", "edit") ||
    hasModuleAccess("attendance", "create") ||
    hasScreenAccess("attendance", "mark_attendance", "edit") ||
    hasScreenAccess("attendance", "mark_attendance", "create");
  const canModifyAttendance = canAttendanceCreate || canAttendanceEdit;
  const canManageLeaveOps = canAttendanceCreate || canAttendanceApprove;
  const isAttendanceViewOnly =
    canAttendanceView &&
    !canAttendanceCreate &&
    !canAttendanceEdit &&
    !canAttendanceExport &&
    !canAttendanceApprove &&
    !canAttendanceImport &&
    !canManageAttendanceSettings;
  const allowedAttendanceTabs = useMemo(() => {
    const tabs = [];
    if (canAttendanceView) tabs.push("attendance");
    if (canViewLeaves) tabs.push("leaves");
    if (canViewReports) tabs.push("reports");
    return tabs;
  }, [canAttendanceView, canViewLeaves, canViewReports]);
  const [showAttendanceInsights, setShowAttendanceInsights] = useState(false);
  const [showAttendanceDashboard, setShowAttendanceDashboard] = useState(false);
  const [sidebarImportOpen, setSidebarImportOpen] = useState(false);
  const [sidebarBulkOpen, setSidebarBulkOpen] = useState(false);
  const [sidebarProcessOpen, setSidebarProcessOpen] = useState(false);
  const [attendanceActionLog, setAttendanceActionLog] = useState(() => loadAttendanceInsights());
  const [leaveInsightReview, setLeaveInsightReview] = useState(null);

  const refreshLeaveRequestInsights = useCallback(async () => {
    try {
      const res = await attendanceStructureService.getLeaveRequests({ limit: 200 });
      if (res?.success === false || !Array.isArray(res?.data)) return;
      const apiRows = res.data.map(mapLeaveRequestApiToInsight).filter(Boolean);
      setAttendanceActionLog((prev) => {
        const localOnly = prev.filter(
          (row) =>
            row.action !== LEAVE_REQUEST_ACTION ||
            (!row.requestId && !String(row.id || "").startsWith("leave_req_"))
        );
        return [...apiRows, ...localOnly];
      });
    } catch (error) {
      console.error("Failed to load leave requests:", error);
    }
  }, []);

  useEffect(() => {
    refreshLeaveRequestInsights();
  }, [refreshLeaveRequestInsights]);

  useEffect(() => {
    saveAttendanceInsights(attendanceActionLog);
  }, [attendanceActionLog]);

  const pushLeaveRequestInsight = useCallback((employee, formData, extra = {}) => {
    if (extra.apiRow) {
      const mapped = mapLeaveRequestApiToInsight(extra.apiRow);
      if (mapped) {
        setAttendanceActionLog((prev) => [mapped, ...prev.filter((r) => r.requestId !== mapped.requestId)]);
        return mapped;
      }
    }
    const entry = buildLeaveRequestInsight({
      employee,
      formData,
      pendingAt: extra.pendingAt || "Reporting Manager",
    });
    setAttendanceActionLog((prev) => [entry, ...prev]);
    return entry;
  }, []);

  const attendanceInsightItems = useMemo(() => attendanceActionLog, [attendanceActionLog]);

  const handleAttendanceInsightRowAction = useCallback(
    async (action, row) => {
      if (!row) return;
      if (action === "view") {
        setLeaveInsightReview({ row, mode: "review" });
        return;
      }

      const requestId = row.requestId || Number(String(row.id || "").replace(/^leave_req_/, ""));
      const hasBackendId = Number.isFinite(Number(requestId)) && Number(requestId) > 0;

      if (action === "approve" && hasBackendId) {
        try {
          const res = await attendanceStructureService.approveLeaveRequest(Number(requestId));
          if (res?.success === false) {
            toast.error(res?.message || "Failed to approve leave request");
            return;
          }
          setLeaveInsightReview(null);
          await refreshLeaveRequestInsights();
          notifyLeaveStatusChange(row, "approved");
          toast.success(
            res?.data?.status === "approved"
              ? "Leave request fully approved"
              : `Approved — pending at ${res?.data?.pendingAt || "next approver"}`
          );
          if (res?.data?.status === "approved") {
            setTimeout(async () => {
              try {
                await fetchData();
              } catch (refreshError) {
                console.error("Error refreshing attendance data:", refreshError);
              }
            }, 500);
          }
        } catch (err) {
          toast.error(err?.response?.data?.message || "Failed to approve leave request");
        }
        return;
      }

      if (action === "reject" && hasBackendId) {
        try {
          const res = await attendanceStructureService.rejectLeaveRequest(Number(requestId));
          if (res?.success === false) {
            toast.error(res?.message || "Failed to reject leave request");
            return;
          }
          setLeaveInsightReview(null);
          await refreshLeaveRequestInsights();
          notifyLeaveStatusChange(row, "rejected");
          toast.success("Leave request rejected");
        } catch (err) {
          toast.error(err?.response?.data?.message || "Failed to reject leave request");
        }
        return;
      }

      if (action === "cancel" && hasBackendId) {
        try {
          await attendanceStructureService.cancelLeaveRequest(Number(requestId));
          setLeaveInsightReview(null);
          await refreshLeaveRequestInsights();
          toast.success("Leave request cancelled");
        } catch (err) {
          toast.error(err?.response?.data?.message || "Failed to cancel leave request");
        }
        return;
      }

      const currentUser = JSON.parse(localStorage.getItem("currentUser") || "null");
      const actorName = currentUser?.name || "Approver";
      if (action === "approve") {
        setAttendanceActionLog((prev) =>
          prev.map((item) =>
            item.id === row.id
              ? { ...item, status: "Approved", pendingAt: "-", approvedBy: actorName }
              : item
          )
        );
        setLeaveInsightReview(null);
        notifyLeaveStatusChange(row, "approved");
        toast.success("Leave request approved");
        return;
      }
      if (action === "reject") {
        setAttendanceActionLog((prev) =>
          prev.map((item) =>
            item.id === row.id
              ? { ...item, status: "Rejected", pendingAt: "-", rejectedBy: actorName }
              : item
          )
        );
        setLeaveInsightReview(null);
        notifyLeaveStatusChange(row, "rejected");
        toast.success("Leave request rejected");
        return;
      }
      if (action === "cancel") {
        setAttendanceActionLog((prev) => prev.filter((item) => item.id !== row.id));
        setLeaveInsightReview(null);
        toast.success("Leave request cancelled");
      }
    },
    [refreshLeaveRequestInsights]
  );

  // New state for dynamic employee fetching
  const [allEmployees, setAllEmployees] = useState([]);
  const [employeesLoading, setEmployeesLoading] = useState(false);

  // New state for Apply Leave popup
  const [showApplyLeavePopup, setShowApplyLeavePopup] = useState(false);
  const [departmentsList, setDepartmentsList] = useState([]);
  const [branchesList, setBranchesList] = useState([]);
  const [designationsList, setDesignationsList] = useState([]);

  // New state for selected employee actions
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [singleEmployeeForLeave, setSingleEmployeeForLeave] = useState(null);
  const [showEmployeeAttendanceCalendar, setShowEmployeeAttendanceCalendar] =
    useState(false);
  const [employeeAttendanceCalendarTarget, setEmployeeAttendanceCalendarTarget] =
    useState(null);

  // LOP Reversal popup
  const [showLOPReversalPopup, setShowLOPReversalPopup] = useState(false);
  const [lopReversalEmployee, setLOPReversalEmployee] = useState(null);

  // Leave Adjustment & Leave Balance popups
  const [showLeaveAdjustmentPopup, setShowLeaveAdjustmentPopup] = useState(false);
  const [leaveAdjustmentEmployee, setLeaveAdjustmentEmployee] = useState(null);
  const [showLeaveBalancePopup, setShowLeaveBalancePopup] = useState(false);
  const [leaveBalanceEmployee, setLeaveBalanceEmployee] = useState(null);
  const [leaveBalanceRefreshKey, setLeaveBalanceRefreshKey] = useState(0);

  // Attendance structure data state
  const [weeklyOffPolicies, setWeeklyOffPolicies] = useState([]);
  const [holidayPolicies, setHolidayPolicies] = useState([]);
  const [holidaysData, setHolidaysData] = useState([]);
  const [employeeWeeklyOffMappings, setEmployeeWeeklyOffMappings] = useState(
    {}
  );
  const [employeeHolidayMappings, setEmployeeHolidayMappings] = useState({});
  // Deduplicate structure API calls (prevents repeated calls on every action/focus).
  const attendanceStructureLoadedOnceRef = useRef(false);
  const attendanceStructureInFlightRef = useRef(false);

  // Memoize daysInMonth for performance - avoid recalculating on every render
  const daysInMonth = useMemo(
    () => Math.min(31, new Date(selectedYear, selectedMonth + 1, 0).getDate()),
    [selectedYear, selectedMonth]
  );
  // Stable handler to open apply-leave popup for a single employee
  const handleApplyLeaveClick = useCallback(
    (employee) => {
      console.log("Opening apply leave popup for employee:", employee);
      setSingleEmployeeForLeave(employee);
      setShowApplyLeavePopup(true);
    },
    [setSingleEmployeeForLeave, setShowApplyLeavePopup]
  );

  const handleLOPReversalClick = useCallback((employee) => {
    setLOPReversalEmployee(employee);
    setShowLOPReversalPopup(true);
  }, []);

  const handleLeaveAdjustmentClick = useCallback((employee) => {
    setLeaveAdjustmentEmployee(employee);
    setShowLeaveAdjustmentPopup(true);
  }, []);

  const handleLeaveBalanceClick = useCallback((employee) => {
    setLeaveBalanceEmployee(employee);
    setShowLeaveBalancePopup(true);
  }, []);

  const handleViewAttendanceClick = useCallback((employee) => {
    setEmployeeAttendanceCalendarTarget(employee);
    setShowEmployeeAttendanceCalendar(true);
  }, []);

  useEffect(() => {
    const findTargetEmployee = (payload) => {
      const employeeId = Number(payload?.employeeId);
      const userId = Number(payload?.userId);
      return (
        allEmployees.find((emp) => emp?.id === employeeId) ||
        allEmployees.find((emp) => Number(emp?.user?.id) === userId) ||
        null
      );
    };

    const runQuickAction = (payload) => {
      const action = payload?.actionLabel;
      if (!action) return;
      const employee = findTargetEmployee(payload);
      if (!employee) return;

      if (action === "Leave") {
        handleApplyLeaveClick(employee);
        return;
      }
      if (action === "Regularise checks") {
        handleLOPReversalClick(employee);
        return;
      }
      if (action === "Change Shift") {
        handleLeaveBalanceClick(employee);
        return;
      }
      if (action === "Short Leave") {
        handleLeaveAdjustmentClick(employee);
        return;
      }
      if (action === "Ticket") {
        toast("Ticket form is not configured yet.");
      }
    };

    const handleQuickActionEvent = (event) => {
      runQuickAction(event?.detail || {});
    };

    const consumeStoredQuickAction = () => {
      try {
        const raw = localStorage.getItem("adminQuickActionRequest");
        if (!raw) return;
        const payload = JSON.parse(raw);
        if (Date.now() - Number(payload?.requestedAt || 0) > 3 * 60 * 1000) {
          localStorage.removeItem("adminQuickActionRequest");
          return;
        }
        const attendanceActions = new Set([
          "Regularise checks",
          "Leave",
          "Change Shift",
          "Short Leave",
          "Ticket",
        ]);
        if (attendanceActions.has(payload?.actionLabel)) {
          runQuickAction(payload);
        }
        localStorage.removeItem("adminQuickActionRequest");
      } catch {
        localStorage.removeItem("adminQuickActionRequest");
      }
    };

    window.addEventListener("admin:quick-action", handleQuickActionEvent);
    consumeStoredQuickAction();
    return () => {
      window.removeEventListener("admin:quick-action", handleQuickActionEvent);
    };
  }, [
    allEmployees,
    handleApplyLeaveClick,
    handleLOPReversalClick,
    handleLeaveBalanceClick,
    handleLeaveAdjustmentClick,
  ]);

  useEffect(() => {
    const resolveEmployeeForInsight = (request) => {
      const employeeId = Number(request?.employeeId);
      const userId = Number(request?.userId);
      return (
        allEmployees.find((emp) => emp?.id === employeeId) ||
        allEmployees.find((emp) => Number(emp?.user?.id) === userId) ||
        null
      );
    };

    try {
      const raw = localStorage.getItem("adminQuickActionRequests");
      const parsed = raw ? JSON.parse(raw) : [];
      if (Array.isArray(parsed) && parsed.length) {
        const merged = syncQuickActionLeaveRequestsToInsights(parsed, resolveEmployeeForInsight);
        setAttendanceActionLog(merged);
      }
    } catch {
      /* ignore */
    }

    const onQuickActionSubmitted = (event) => {
      const detail = event?.detail || {};
      if (mapActionLabelToInsightAction(detail.actionLabel) !== LEAVE_REQUEST_ACTION) return;
      const employee = resolveEmployeeForInsight(detail);
      const entry = buildLeaveRequestInsight({
        employee,
        employeeId: detail.employeeId,
        employeeName: detail.userName,
        formData: detail.formData || {},
        pendingAt: "Reporting Manager",
        requestId: detail.requestId,
      });
      setAttendanceActionLog((prev) => {
        if (entry.requestId && prev.some((r) => r.requestId === entry.requestId)) return prev;
        return [entry, ...prev];
      });
    };

    window.addEventListener("admin:quick-action-submitted", onQuickActionSubmitted);
    return () => {
      window.removeEventListener("admin:quick-action-submitted", onQuickActionSubmitted);
    };
  }, [allEmployees]);

  const handleCalendarAttendanceStatusUpdate = useCallback(
    (employee, day, newStatus, year, month) => {
      if (!employee?.id || !day || !newStatus) return;
      // Keep existing "as-is" behavior: update the main grid state only for the currently loaded month.
      // For other months, calendar keeps local state only.
      if (year !== selectedYear || month !== selectedMonth) return;
      const employeeId = String(employee.id);
      const dayKey = String(day);
      setAttendanceData((prev) => {
        const empPrev = (prev && (prev[employeeId] || prev[employee.id])) || {};
        return {
          ...prev,
          [employeeId]: {
            ...empPrev,
            [dayKey]: newStatus,
          },
        };
      });
      try {
        changedRecordsRef.current.set(`${employeeId}_${dayKey}`, {
          employeeId,
          day: Number(day),
          newStatus,
        });
      } catch (e) {
        console.debug("Failed to track calendar status change", e);
      }
      setIsEditing(true);
      setIsSaved(false);
    },
    [selectedYear, selectedMonth]
  );

  const handleCalendarApplyLeaveClick = useCallback(
    (employee) => {
      setShowEmployeeAttendanceCalendar(false);
      setEmployeeAttendanceCalendarTarget(null);
      handleApplyLeaveClick(employee);
    },
    [handleApplyLeaveClick]
  );

  const handleCalendarRegularizationTimesSave = useCallback(
    (employee, { day, year, month, checkIn, checkOut, totalHours }) => {
      if (!employee?.id || day == null) return;
      if (year !== selectedYear || month !== selectedMonth) return;
      const employeeId = String(employee.id);
      const dayKey = String(day);
      setAttendanceDetailData((prev) => {
        const empPrev = (prev && (prev[employeeId] || prev[employee.id])) || {};
        const dayPrev = empPrev[dayKey] || {};
        return {
          ...prev,
          [employeeId]: {
            ...empPrev,
            [dayKey]: {
              ...dayPrev,
              checkIn: checkIn ?? "--",
              checkOut: checkOut ?? "--",
              totalHours: totalHours ?? "--",
            },
          },
        };
      });
      setIsEditing(true);
      setIsSaved(false);
    },
    [selectedYear, selectedMonth]
  );

  const monthRangeValue = useMemo(
    () => getMonthRangeValue(selectedYear, selectedMonth),
    [selectedMonth, selectedYear]
  );

  // Minimum date for month selection from company serviceCommencesOn (localStorage companyData.serviceCommencesOn)
  useEffect(() => {
    try {
      const raw = localStorage.getItem("companyData");
      if (!raw) return;
      const companyData = typeof raw === "string" ? JSON.parse(raw) : raw;
      const commencesOn = companyData?.serviceCommencesOn;
      if (!commencesOn) return;
      const date = new Date(commencesOn);
      if (isNaN(date.getTime())) return;
      setMinDate(date);
    } catch (e) {
      console.warn("Attendance minDate from companyData.serviceCommencesOn:", e);
    }
  }, []);

  // When minDate is set, disallow selected month prior to company commencement
  useEffect(() => {
    if (!minDate) return;
    const minYear = minDate.getFullYear();
    const minMonth = minDate.getMonth();
    if (selectedYear < minYear || (selectedYear === minYear && selectedMonth < minMonth)) {
      setSelectedYear(minYear);
      setSelectedMonth(minMonth);
      setMonthPickerValue(getMonthRangeValue(minYear, minMonth));
    }
  }, [minDate]);
  const [monthPickerValue, setMonthPickerValue] = useState(monthRangeValue);
  useEffect(() => {
    setMonthPickerValue(monthRangeValue);
  }, [monthRangeValue]);

  // Debounced search - increased to 700ms for better performance
  const debouncedSearch = useMemo(
    () =>
      debounce((value) => {
        setDebouncedSearchTerm(value);
        setPage(1);
      }, 700),
    []
  );

  useEffect(() => {
    debouncedSearch(searchTerm);
    return () => debouncedSearch.cancel();
  }, [searchTerm, debouncedSearch]);

  // Consolidated data fetching
  const employeesCacheRef = useRef(new Map());
  const attendanceCacheRef = useRef(new Map());
  // Track changed records to avoid scanning entire attendanceData on save
  const changedRecordsRef = useRef(new Map()); // Key: "empId_day", Value: { employeeId, day, newStatus }
  const attendanceFetchInFlightRef = useRef(false);
  const attendanceFetchKeyRef = useRef("");

  const getCompanyCodeForAttendanceExcels = useCallback(() => {
    try {
      const raw = localStorage.getItem("companyData");
      if (!raw) return "COMPANY";
      const companyData = typeof raw === "string" ? JSON.parse(raw) : raw;
      const code = String(companyData?.code || "")
        .trim()
        .replace(/[^A-Za-z0-9]/g, "_")
        .replace(/_+/g, "_")
        .replace(/^_+|_+$/g, "")
        .toUpperCase();
      return code || "COMPANY";
    } catch (e) {
      return "COMPANY";
    }
  }, []);

  const buildAttendanceExcelFilename = useCallback(
    (excelName, monthIndex = null, yearValue = null) => {
      const code = getCompanyCodeForAttendanceExcels();
      const now = new Date();
      const y =
        Number.isFinite(Number(yearValue)) && Number(yearValue) > 0
          ? Number(yearValue)
          : now.getFullYear();
      const monthFromIndex =
        Number.isFinite(Number(monthIndex)) && Number(monthIndex) >= 0
          ? Number(monthIndex) + 1
          : null;
      const mm = String(
        monthFromIndex && monthFromIndex >= 1 && monthFromIndex <= 12
          ? monthFromIndex
          : now.getMonth() + 1
      ).padStart(2, "0");
      const safeName = String(excelName || "Excel")
        .trim()
        .replace(/[^A-Za-z0-9]+/g, "_")
        .replace(/_+/g, "_")
        .replace(/^_+|_+$/g, "");
      return `${code}_${safeName}_${mm}_${y}.xlsx`;
    },
    [getCompanyCodeForAttendanceExcels]
  );

  const fetchData = async () => {
    const requestKey = `${page}|${itemsPerPage}|${selectedMonth}|${selectedYear}|${debouncedSearchTerm}|${JSON.stringify(
      appliedFilters || {}
    )}`;
    // Avoid duplicate parallel fetches for the same inputs (common during rapid re-renders).
    if (
      attendanceFetchInFlightRef.current &&
      attendanceFetchKeyRef.current === requestKey
    ) {
      return;
    }
    attendanceFetchInFlightRef.current = true;
    attendanceFetchKeyRef.current = requestKey;

    const startTime = performance.now();
    let empStartTime = startTime;
    try {
      setEmployeesLoading(true);
      setLoading(true);

      // Prepare request parameters
      // IMPORTANT: Backend doesn't filter by month/year, so we need to fetch a larger batch
      // and filter client-side before pagination to ensure we get the correct employees for the selected month
      // Always fetch a large employee set so totals remain stable across 10/25/50/100 pagination.
      // Pagination should affect only visible rows, not the aggregate totals.
      const fetchLimit = 10000;
      
      const empParams = {
        page: 1, // Always fetch from page 1 when filtering by month
        search: debouncedSearchTerm,
        month: selectedMonth + 1, // Backend ignores this, but we keep it for consistency
        year: selectedYear, // Backend ignores this, but we keep it for consistency
        fields: "id,code,name,joinDate,exitDate,user",
        excludeEmploymentTypes: "External,external", // Exclude external employees from attendance grid
        sortBy: "dateOfJoin",
        sortOrder: "desc",
        ...appliedFilters,
      };
      // Always force a large fetch limit so filteredEmployees represents the full filtered dataset.
      empParams.limit = fetchLimit;

      const empCacheKey = `${itemsPerPage}|${page}|${selectedMonth}|${selectedYear}|${debouncedSearchTerm}|${JSON.stringify(
        appliedFilters
      )}`;

      const cached = employeesCacheRef.current.get(empCacheKey);

      let empResponse = null;
      let attResponse = null;

      if (cached) {
        empResponse = {
          success: true,
          data: cached.employees,
          pagination: cached.pagination || {},
          total: cached.totalEmployees || cached.employees.length,
        };

        const attCacheKey = `${selectedMonth}|${selectedYear}|${debouncedSearchTerm}|${JSON.stringify(
          appliedFilters
        )}`;
        const cachedAttendance = attendanceCacheRef.current.get(attCacheKey);
        if (cachedAttendance) {
          attResponse = cachedAttendance;
          console.log("⏱️ Using cached attendance for", attCacheKey);
        } else {
          // Don't fetch attendance yet - we'll fetch it after filtering and pagination
          // to ensure we only get attendance for employees active in the selected month
          attResponse = null; // Will be fetched after filtering
        }
      } else {
        empStartTime = performance.now();

        // COMMENTED OUT: Chunked API (getChunkedAttendance, getMultipleChunkedAttendance,
        // getMultipleChunkedAttendanceMax) - will use in future. When "all" is selected we use
        // employeeService.getEmployees without limit instead.
        // if (useChunkedLoading) {
        //   const shouldParallelLoad = ...
        //   chunkedResponse = await attendanceService.getChunkedAttendance(...) or getMultipleChunkedAttendance(Max)(...);
        //   ...
        // }

        empResponse = await employeeService.getEmployees(empParams);
        // Don't fetch attendance yet - we'll fetch it after filtering and pagination
        // to ensure we only get attendance for employees active in the selected month
        attResponse = null; // Will be fetched after filtering
      }

      const fetchEndTime = performance.now();
      console.log(
        `⏱️ Data fetch completed: ${(
          (fetchEndTime - empStartTime) /
          1000
        ).toFixed(2)}s`
      );

      let currentEmployees = [];
      if (empResponse.success) {
        // Support both { data: [...] } and { employees: [...] } backend response shapes
        currentEmployees = empResponse.data || empResponse.employees || [];

        // Deduplicate employees by ID (in case backend returns duplicates)
        const seenIds = new Set();
        const dedupedEmployees = [];
        currentEmployees.forEach((emp) => {
          const empId = String(emp.id);
          if (!seenIds.has(empId)) {
            dedupedEmployees.push(emp);
            seenIds.add(empId);
          }
        });

        // IMPORTANT: Filter employees to only include those who were active during the selected month
        // The backend doesn't filter by month/year, so we MUST filter client-side BEFORE pagination
        // to ensure we get the correct employees for the selected month.
        // 
        // When month changes (e.g., from Jan 2026 to Nov 2025), we fetch a larger batch of employees,
        // filter them by month, and then paginate the filtered results.
        // 
        // This filter ensures:
        // 1. Employees who joined after the selected month are excluded
        // 2. Employees who were relieved before the selected month are excluded
        const monthStart = new Date(selectedYear, selectedMonth, 1);
        const monthEnd = new Date(selectedYear, selectedMonth + 1, 0, 23, 59, 59, 999); // Last moment of the month
        
        const filteredEmployees = dedupedEmployees.filter((employee) => {
          // Parse join date
          const joinDateStr = employee.joinDate || employee.joiningDate;
          let joinDate = null;
          if (joinDateStr) {
            try {
              joinDate = new Date(joinDateStr);
              if (isNaN(joinDate.getTime())) {
                joinDate = null;
              }
            } catch (e) {
              // Invalid date, treat as null
            }
          }

          // Parse relieved date
          const relievedDateStr = 
            employee.relievedDate ||
            employee.exitDate ||
            employee.relievingDate ||
            employee.resignationDate;
          let relievedDate = null;
          if (relievedDateStr) {
            try {
              relievedDate = new Date(relievedDateStr);
              if (isNaN(relievedDate.getTime())) {
                relievedDate = null;
              }
            } catch (e) {
              // Invalid date, treat as null
            }
          }

          // If no join date, include the employee (let backend handle it)
          if (!joinDate) {
            // But exclude if they were relieved before the month started
            if (relievedDate && relievedDate < monthStart) {
              return false;
            }
            return true;
          }

          // Exclude if employee joined after the selected month ended
          if (joinDate > monthEnd) {
            return false;
          }

          // Exclude if employee was relieved before the selected month started
          if (relievedDate && relievedDate < monthStart) {
            return false;
          }

          // Include employee if they were active during the selected month
          // (joined on or before month end AND (not relieved OR relieved on or after month start))
          return true;
        });

        // Apply client-side pagination on filtered employees
        // Since backend doesn't filter by month, we filter first, then paginate
        // When "all" is selected: show all filtered employees (no slice)
        const perPage = parseInt(itemsPerPage, 10) || 50;
        const requestedPage = page || 1;
        const startIndex = itemsPerPage === "all" ? 0 : (requestedPage - 1) * perPage;
        const endIndex = itemsPerPage === "all" ? filteredEmployees.length : startIndex + perPage;
        const paginatedEmployees = filteredEmployees.slice(startIndex, endIndex);
        
        // Update pagination info based on filtered results
        const totalFiltered = filteredEmployees.length;
        const totalPages = itemsPerPage === "all" ? 1 : Math.ceil(totalFiltered / perPage);
        
        // IMPORTANT: Store ALL filtered employees in allEmployees (not just paginated)
        // The existing pagination logic (paginatedEmployees useMemo) will handle slicing
        // based on the current page. This ensures pagination works correctly.
        // The table uses paginatedEmployees which is computed from filteredEmployees,
        // which in turn is computed from allEmployees.
        setAllEmployees(filteredEmployees);
        
        // Use paginated employees for display
        currentEmployees = paginatedEmployees;
        
        // Update pagination metadata
        if (empResponse.pagination) {
          empResponse.pagination = {
            ...empResponse.pagination,
            currentPage: requestedPage,
            totalPages: totalPages,
            totalRecords: totalFiltered,
            limit: itemsPerPage === "all" ? totalFiltered : perPage,
            hasNext: endIndex < totalFiltered,
            hasPrev: startIndex > 0,
          };
        }
        
        // Fetch attendance for ALL filtered employees so totals are stable across page-size changes.
        if (!attResponse && empResponse.success) {
          const employeeIdsForAttendance = filteredEmployees
            .map((e) => Number(e.id))
            .filter((id) => Number.isFinite(id));
          try {
            console.log(
              "Attendance.fetchData - requesting attendance for all filtered employeeIds",
              {
                count: employeeIdsForAttendance.length,
                sample: employeeIdsForAttendance.slice(0, 10),
                month: selectedMonth + 1,
                year: selectedYear,
              }
            );
          } catch (e) {
            console.error(e);
          }
          const attStart = performance.now();
          try {
            // Use POST + chunking for large employee lists to avoid URL-length failures
            // and reduce DB pressure from single huge queries.
            const IDS_CHUNK_SIZE = 120;
            const ROWS_PAGE_SIZE = 3000;
            const idChunks = [];
            for (let i = 0; i < employeeIdsForAttendance.length; i += IDS_CHUNK_SIZE) {
              idChunks.push(employeeIdsForAttendance.slice(i, i + IDS_CHUNK_SIZE));
            }

            const mergedRows = [];
            for (let chunkIndex = 0; chunkIndex < idChunks.length; chunkIndex++) {
              const idsChunk = idChunks[chunkIndex];
              let pageNo = 1;
              let hasNext = true;
              let safety = 0;
              const MAX_PAGES_PER_CHUNK = 100;

              while (hasNext && safety < MAX_PAGES_PER_CHUNK) {
                safety += 1;
                const chunkResp = await attendanceService.getAttendanceHistoryBulk({
                  month: selectedMonth + 1,
                  year: selectedYear,
                  page: pageNo,
                  limit: ROWS_PAGE_SIZE,
                  search: debouncedSearchTerm,
                  employeeIds: idsChunk,
                  lightweight: true,
                  bulkMode: true,
                });

                if (chunkResp && chunkResp.success) {
                  const rows = Array.isArray(chunkResp.data) ? chunkResp.data : [];
                  mergedRows.push(...rows);
                  const pagination = chunkResp.pagination || {};
                  hasNext =
                    typeof pagination.hasNext === "boolean"
                      ? pagination.hasNext
                      : rows.length === ROWS_PAGE_SIZE;
                  pageNo += 1;
                } else {
                  hasNext = false;
                }
              }

              // Yield to UI thread between chunk calls on very large datasets.
              if (chunkIndex < idChunks.length - 1) {
                await new Promise((resolve) => setTimeout(resolve, 0));
              }
            }

            attResponse = {
              success: true,
              data: mergedRows,
              pagination: {
                currentPage: 1,
                totalPages: 1,
                totalRecords: mergedRows.length,
                limit: mergedRows.length,
                hasNext: false,
                hasPrev: false,
              },
            };
          } catch (bulkErr) {
            const isCanceled =
              bulkErr?.code === "ERR_CANCELED" ||
              String(bulkErr?.message || "")
                .toLowerCase()
                .includes("canceled");
            if (isCanceled) {
              console.debug(
                "Attendance.fetchData - bulk history request canceled; skipping fallback."
              );
              throw bulkErr;
            }
            console.warn(
              "Attendance.fetchData - bulk POST history failed, falling back to GET history:",
              bulkErr
            );
            // Safe fallback: chunked GET requests (avoid one giant query string).
            const IDS_GET_CHUNK_SIZE = 80;
            const GET_ROWS_PAGE_SIZE = 2000;
            const mergedRows = [];
            for (let i = 0; i < employeeIdsForAttendance.length; i += IDS_GET_CHUNK_SIZE) {
              const idsChunk = employeeIdsForAttendance.slice(i, i + IDS_GET_CHUNK_SIZE);
              let pageNo = 1;
              let hasNext = true;
              let safety = 0;
              const MAX_PAGES_PER_CHUNK = 100;

              while (hasNext && safety < MAX_PAGES_PER_CHUNK) {
                safety += 1;
                const resp = await attendanceService.getAttendanceHistory(
                  selectedMonth + 1,
                  selectedYear,
                  pageNo,
                  GET_ROWS_PAGE_SIZE,
                  debouncedSearchTerm,
                  idsChunk
                );
                const rows = Array.isArray(resp?.data) ? resp.data : [];
                mergedRows.push(...rows);
                const pagination = resp?.pagination || {};
                hasNext =
                  typeof pagination.hasNext === "boolean"
                    ? pagination.hasNext
                    : rows.length === GET_ROWS_PAGE_SIZE;
                pageNo += 1;
              }

              if (i + IDS_GET_CHUNK_SIZE < employeeIdsForAttendance.length) {
                await new Promise((resolve) => setTimeout(resolve, 0));
              }
            }

            attResponse = {
              success: true,
              data: mergedRows,
              pagination: {
                currentPage: 1,
                totalPages: 1,
                totalRecords: mergedRows.length,
                limit: mergedRows.length,
                hasNext: false,
                hasPrev: false,
              },
            };
          }
          const fetchEndTime = performance.now();
          console.log(
            `⏱️ Attendance fetch completed: ${(
              (fetchEndTime - attStart) /
              1000
            ).toFixed(2)}s`
          );
          
          // Cache the attendance response
          if (attResponse && attResponse.success) {
            try {
              const attCacheKey = `${selectedMonth}|${selectedYear}|${debouncedSearchTerm}|${JSON.stringify(
                appliedFilters
              )}`;
              attendanceCacheRef.current.set(attCacheKey, attResponse);
            } catch (e) {
              /* ignore cache errors */
            }
          }
        }

        if (ENABLE_DEBUG_LOGS) {
          try {
            console.debug("Attendance.fetchData: empParams:", empParams);
            console.debug("Attendance.fetchData: employees returned:", {
              requestedPage: empParams?.page,
              returnedCount: (currentEmployees || []).length,
              pagination: empResponse.pagination,
            });
            console.debug("Attendance.fetchData: attendance returned:", {
              returnedCount: (attResponse?.data || []).length,
              pagination: attResponse?.pagination,
            });
          } catch (dErr) {
            console.debug("Attendance.fetchData: debug log error", dErr);
          }
        }

        try {
          const cacheValue = {
            employees: currentEmployees,
            pagination: empResponse.pagination || {},
            totalEmployees:
              empResponse.pagination?.totalRecords ||
              empResponse.total ||
              currentEmployees.length,
          };
          employeesCacheRef.current.set(empCacheKey, cacheValue);
        } catch (e) {
          // ignore cache errors
        }

        const paginationData = empResponse.pagination || {};

        // Since we're doing client-side filtering and pagination (because backend doesn't filter by month),
        // we should use client-side pagination, not server-side pagination
        // This ensures paginatedEmployees correctly slices the filtered employees
        const hasServerPagination = false; // Force client-side pagination
        setServerSidePagination(hasServerPagination);

        const totalRecords =
          paginationData.totalRecords ||
          paginationData.total ||
          empResponse.total ||
          currentEmployees.length;
        setTotalEmployees(totalRecords);

        const perPageUsed =
          itemsPerPage === "all"
            ? totalRecords || currentEmployees.length || 1
            : parseInt(itemsPerPage, 10) || 50;

        const computedTotalPages = hasServerPagination
          ? paginationData.totalPages ||
            Math.max(1, Math.ceil(totalRecords / perPageUsed))
          : Math.max(1, Math.ceil(totalRecords / perPageUsed));
        setTotalPages(computedTotalPages);

        try {
          const nextPageFromServer =
            paginationData.nextPage ??
            (paginationData.currentPage
              ? paginationData.currentPage + 1
              : null);
          const hasNextFromServer = Boolean(
            paginationData.hasNextPage ??
              paginationData.hasNext ??
              (paginationData.totalPages && paginationData.currentPage
                ? paginationData.currentPage < paginationData.totalPages
                : false)
          );
          setNextEmployeePage(nextPageFromServer);
          setHasMoreEmployees(hasNextFromServer);
          lastLoadedPageRef.current = paginationData.currentPage || page;
        } catch (e) {
          setNextEmployeePage(null);
          setHasMoreEmployees(false);
        }
      } else {
        console.error("Failed to fetch employees:", empResponse.message);
        setAllEmployees([]);
      }

      try {
        console.log("Attendance.fetchData - request summary:", {
          requestedMonth: selectedMonth + 1,
          requestedYear: selectedYear,
          search: debouncedSearchTerm,
          appliedFilters,
          itemsPerPage,
          page,
        });
        console.log("Attendance.fetchData - attendance response:", {
          success: attResponse?.success,
          returnedRecords: Array.isArray(attResponse?.data)
            ? attResponse.data.length
            : undefined,
          pagination: attResponse?.pagination,
        });
      } catch (e) {
        console.debug("Attendance.fetchData - debug log failed", e);
      }

      const processStartTime = performance.now();
      if (attResponse.success) {
        let attendanceMap = {};
        let attendanceMetaMap = {};
        const lopReversalMap = {};

        if (attResponse.data && typeof attResponse.data === "object") {
          if (Array.isArray(attResponse.data)) {
            attResponse.data.forEach((record) => {
              const empId = String(record.employee_id || record.employeeId);
              const parsedDay = getDayOfMonthSafe(record.date);
              if (!parsedDay) return;
              const day = String(parsedDay);
              if (!attendanceMap[empId]) {
                attendanceMap[empId] = {};
              }
              if (!attendanceMetaMap[empId]) {
                attendanceMetaMap[empId] = {};
              }
              attendanceMap[empId][day] = record.status;
              attendanceMetaMap[empId][day] = {
                status: record.status || "-",
                checkIn:
                  record.checkIn ||
                  record.checkInTime ||
                  record.inTime ||
                  record.firstIn ||
                  record.firstPunchIn ||
                  "--",
                checkOut:
                  record.checkOut ||
                  record.checkOutTime ||
                  record.outTime ||
                  record.lastOut ||
                  record.lastPunchOut ||
                  "--",
                totalHours:
                  record.totalHours ||
                  record.workedHours ||
                  record.workingHours ||
                  record.totalWorkedHours ||
                  "--",
                shiftAssigned:
                  record.shiftAssigned ||
                  record.shiftName ||
                  record.shift ||
                  record.assignedShift ||
                  "--",
              };
              if ((record.remarks || "").toString().indexOf("LOP Reversal") !== -1) {
                if (!lopReversalMap[empId]) lopReversalMap[empId] = {};
                lopReversalMap[empId][day] = true;
              }
            });
            setAttendanceLOPReversalMap(lopReversalMap);
          } else {
            attendanceMap = attResponse.data;
            Object.entries(attendanceMap || {}).forEach(([empId, daysObj]) => {
              attendanceMetaMap[empId] = attendanceMetaMap[empId] || {};
              Object.entries(daysObj || {}).forEach(([day, value]) => {
                if (value && typeof value === "object") {
                  attendanceMetaMap[empId][day] = {
                    status: value.status || value.code || "-",
                    checkIn: value.checkIn || value.inTime || "--",
                    checkOut: value.checkOut || value.outTime || "--",
                    totalHours:
                      value.totalHours ||
                      value.workedHours ||
                      value.workingHours ||
                      "--",
                    shiftAssigned: value.shiftAssigned || value.shiftName || "--",
                  };
                } else {
                  attendanceMetaMap[empId][day] = {
                    status: value || "-",
                    checkIn: "--",
                    checkOut: "--",
                    totalHours: "--",
                    shiftAssigned: "--",
                  };
                }
              });
            });
            if (attResponse.attendanceLOPReversal && typeof attResponse.attendanceLOPReversal === "object") {
              setAttendanceLOPReversalMap(attResponse.attendanceLOPReversal);
            }
          }
        }

        const records = Array.isArray(attResponse.data) ? attResponse.data : [];
        const totalRecordCount = Array.isArray(attResponse.data)
          ? attResponse.data.length
          : Object.values(attendanceMap).reduce(
              (sum, emp) => sum + Object.keys(emp).length,
              0
            );

        try {
          const empSet = new Set(Object.keys(attendanceMap));
          let minDate = null;
          let maxDate = null;

          for (const r of records) {
            if (r && r.employeeId) empSet.add(String(r.employeeId));
            try {
              const d = new Date(r.date);
              if (!isNaN(d.getTime())) {
                if (!minDate || d < minDate) minDate = d;
                if (!maxDate || d > maxDate) maxDate = d;
              }
            } catch (e) {
              console.log("Attendance.fetchData - date parse error", e);
            }
          }

          console.log("Attendance.fetchData - diagnostics:", {
            totalRecords: totalRecordCount,
            uniqueEmployees: empSet.size,
            minDate: minDate ? minDate.toISOString().slice(0, 10) : null,
            maxDate: maxDate ? maxDate.toISOString().slice(0, 10) : null,
            sampleRecords: (records || []).slice(0, 6).map((r) => ({
              employeeId: r.employeeId,
              date: r.date,
              status: r.status,
              createdBy: r.createdBy,
            })),
          });
        } catch (diagErr) {
          console.debug("Attendance.fetchData - diagnostics failed", diagErr);
        }

        // Apply pending (unsaved) changes from changedRecordsRef so they are not lost when merging
        const applyPendingChanges = (next) => {
          const ref = changedRecordsRef.current;
          if (!ref || ref.size === 0) return;
          ref.forEach((change) => {
            const { employeeId, day, newStatus } = change;
            const empKey = String(employeeId);
            const dayKey = String(day);
            if (!next[empKey]) next[empKey] = {};
            else next[empKey] = { ...next[empKey] };
            next[empKey][dayKey] = newStatus;
          });
        };

        const BATCH_SIZE = 500;
        const empIds = Object.keys(attendanceMap);

        if (empIds.length > BATCH_SIZE) {
          let batchIndex = 0;
          const processBatch = () => {
            if (batchIndex * BATCH_SIZE < empIds.length) {
              const start = batchIndex * BATCH_SIZE;
              const end = Math.min(start + BATCH_SIZE, empIds.length);
              const batchData = {};
              for (let i = start; i < end; i++) {
                batchData[empIds[i]] = attendanceMap[empIds[i]];
              }

              if (batchIndex === 0) {
                setAttendanceData((prev) => {
                  const next = { ...prev, ...batchData };
                  applyPendingChanges(next);
                  return next;
                });
              } else {
                setAttendanceData((prev) => {
                  const next = { ...prev, ...batchData };
                  applyPendingChanges(next);
                  return next;
                });
              }

              batchIndex++;
              if (typeof requestIdleCallback !== "undefined") {
                requestIdleCallback(processBatch);
              } else {
                setTimeout(processBatch, 0);
              }
            }
          };
          processBatch();
        } else {
          setAttendanceData((prev) => {
            const next = { ...prev, ...attendanceMap };
            applyPendingChanges(next);
            return next;
          });
        }
        setAttendanceDetailData(attendanceMetaMap || {});

        const processEndTime = performance.now();
        console.log(
          `⏱️ Data processing: ${(
            (processEndTime - processStartTime) /
            1000
          ).toFixed(3)}s (${totalRecordCount} records)`
        );

        if (attResponse.pagination) {
          const paginationInfo = {
            currentPage: attResponse.pagination.currentPage,
            totalPages: attResponse.pagination.totalPages,
            totalRecords: attResponse.pagination.totalRecords,
            limit: attResponse.pagination.limit,
            hasNext: attResponse.pagination.hasNext,
            hasPrev: attResponse.pagination.hasPrev,
          };
          setAttendancePagination(paginationInfo);

          console.log(
            "Attendance - History pagination metadata:",
            paginationInfo
          );
          console.log("Attendance - Records count:", records.length);
        }
      }

      const endTime = performance.now();
      console.log(
        `✅ Total fetch time: ${((endTime - startTime) / 1000).toFixed(2)}s`
      );
    } catch (err) {
      const isCanceled =
        err?.code === "ERR_CANCELED" ||
        String(err?.message || "")
          .toLowerCase()
          .includes("canceled");
      if (isCanceled) {
        console.debug("Attendance - fetch canceled (ignored).");
        return;
      }
      console.error("Attendance - Error fetching data:", err);
      setError(err.message || "Failed to fetch data");
      toast.error("Error loading data");
    } finally {
      attendanceFetchInFlightRef.current = false;
      setEmployeesLoading(false);
      setLoading(false);
    }
  };

  // Effect to trigger data fetch
  useEffect(() => {
    fetchData();
  }, [
    page,
    itemsPerPage,
    debouncedSearchTerm,
    selectedMonth,
    selectedYear,
    JSON.stringify(appliedFilters), // Use JSON.stringify for stable object comparison
  ]);

  // Persist selected month and year to localStorage
  useEffect(() => {
    localStorage.setItem("selectedMonth", String(selectedMonth));
    localStorage.setItem("selectedYear", String(selectedYear));
  }, [selectedMonth, selectedYear]);

  // When Mark All popup opens, fetch ALL employees for the month so the list shows everyone
  useEffect(() => {
    if (!showMarkAllPopup) {
      setMarkAllEmployeesList([]);
      return;
    }
    let cancelled = false;
    const load = async () => {
      try {
        const empParams = {
          page: 1,
          limit: 10000,
          month: selectedMonth + 1,
          year: selectedYear,
          fields: "id,empCode,employeeId,name,user,joinDate,exitDate,relievedDate,joiningDate,resignationDate,relievingDate,email",
          excludeEmploymentTypes: "External,external",
          ...appliedFilters,
        };
        const empResponse = await employeeService.getEmployees(empParams);
        const raw = empResponse?.data || empResponse?.employees || [];
        const monthStart = new Date(selectedYear, selectedMonth, 1);
        const monthEnd = new Date(selectedYear, selectedMonth + 1, 0, 23, 59, 59, 999);
        const filtered = raw.filter((emp) => {
          const joinDateStr = emp.joinDate || emp.joiningDate;
          const joinDate = joinDateStr ? new Date(joinDateStr) : null;
          if (joinDate && !isNaN(joinDate.getTime()) && joinDate > monthEnd) return false;
          const relievedStr = emp.relievedDate || emp.exitDate || emp.relievingDate || emp.resignationDate;
          const relieved = relievedStr ? new Date(relievedStr) : null;
          if (relieved && !isNaN(relieved.getTime()) && relieved < monthStart) return false;
          return true;
        });
        if (!cancelled) setMarkAllEmployeesList(filtered);
      } catch (e) {
        console.error("Failed to load all employees for Mark All", e);
        if (!cancelled) setMarkAllEmployeesList([]);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [showMarkAllPopup, selectedMonth, selectedYear, appliedFilters]);

  // Fetch attendance locks and check if current month is locked
  useEffect(() => {
    let cancelled = false;
    const requestId = ++lockFetchRequestIdRef.current;
    const monthAtRequest = selectedMonth;
    const yearAtRequest = selectedYear;

    // Reset lock state immediately on month switch to avoid "locked UI leak"
    // while fresh lock status for the new month is loading.
    setIsLocked(false);

    const loadLocks = async () => {
      try {
        const response = await attendanceStructureService.getAttendanceLocks();
        if (
          cancelled ||
          requestId !== lockFetchRequestIdRef.current ||
          monthAtRequest !== selectedMonth ||
          yearAtRequest !== selectedYear
        ) {
          return;
        }
        if (response.success && Array.isArray(response.data)) {
          setAttendanceLocks(response.data || []);
          // Check if current month/year is locked
          const currentLock = response.data.find(
            (lock) =>
              lock.month === monthAtRequest + 1 && lock.year === yearAtRequest
          );
          const nextLocked = !!currentLock;
          setIsLocked(nextLocked);
          // Exit edit mode if month becomes locked
          if (nextLocked && isEditing) {
            setIsEditing(false);
            showAlert("Attendance is locked for this month. Editing disabled.", "info");
          }
        } else {
          setAttendanceLocks([]);
          setIsLocked(false);
        }
      } catch (err) {
        if (
          cancelled ||
          requestId !== lockFetchRequestIdRef.current
        ) {
          return;
        }
        console.error("Failed to load attendance locks:", err);
        setAttendanceLocks([]);
        setIsLocked(false);
      }
    };
    loadLocks();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMonth, selectedYear, isEditing]);

  // Fetch LOP reversal days per employee for the selected (payroll) month
  useEffect(() => {
    if (!attendanceService || typeof attendanceService.getLOPReversalSummary !== "function") {
      setLOPReversalDaysMap({});
      return;
    }
    let cancelled = false;
    attendanceService
      .getLOPReversalSummary(selectedMonth + 1, selectedYear)
      .then((res) => {
        if (cancelled) return;
        const data = res?.data ?? res;
        if (data && typeof data === "object" && !Array.isArray(data)) {
          setLOPReversalDaysMap(data);
        } else {
          setLOPReversalDaysMap({});
        }
      })
      .catch(() => {
        if (!cancelled) setLOPReversalDaysMap({});
      });
    return () => { cancelled = true; };
  }, [selectedMonth, selectedYear]);

  // Top-level WebSocket listeners for real-time attendance/leave updates
  useEffect(() => {
    const handleAttendanceUpdate = (payload) => {
      if (!payload) return;
      // Normalize payload values
      const empId = payload.employeeId || payload.id || payload.employeeId;
      const dateStr = payload.date;
      if (!empId || !dateStr) return;
      const day = new Date(dateStr).getDate();

      // Ensure we update string keys for consistency across the app
      const key = String(empId);
      const dayKey = String(day);

      setAttendanceData((prev) => {
        const next = { ...prev };
        next[key] = { ...(next[key] || {}) };
        next[key][dayKey] = payload.status;
        return next;
      });
      // Removed setOriginalAttendance - never read, only creates redundant state updates
    };

    socket.on("attendance-updated", handleAttendanceUpdate);
    socket.on("leave-updated", handleAttendanceUpdate);

    // Keep leave types in sync when they change elsewhere (Leave Management)
    const handleLeaveTypeChange = () => {
      try {
        fetchAttendanceStructureData({
          force: true,
          reason: "socket-leave-type-change",
        });
      } catch (e) {
        console.error("Error refreshing leave types:", e);
      }
    };

    socket.on("leave-type-created", handleLeaveTypeChange);
    socket.on("leave-type-updated", handleLeaveTypeChange);
    socket.on("leave-type-deleted", handleLeaveTypeChange);
    socket.on("leave-types-changed", handleLeaveTypeChange);

    return () => {
      socket.off("attendance-updated", handleAttendanceUpdate);
      socket.off("leave-updated", handleAttendanceUpdate);
      socket.off("leave-type-created", handleLeaveTypeChange);
      socket.off("leave-type-updated", handleLeaveTypeChange);
      socket.off("leave-type-deleted", handleLeaveTypeChange);
      socket.off("leave-types-changed", handleLeaveTypeChange);
    };
  }, []);

  // Initial load for static data
  useEffect(() => {
    fetchDepartments();
    fetchBranches();
    fetchDesignations();
    fetchAttendanceStructureData({ force: false, reason: "initial-load" });

    const handlePolicyUpdate = () => {
      fetchAttendanceStructureData({
        force: true,
        reason: "weekly-off-policy-event",
      });
    };

    const handleLeaveTypesUpdated = () => {
      fetchAttendanceStructureData({ force: true, reason: "leave-types-event" });
    };

    window.addEventListener("weeklyOffPolicyUpdated", handlePolicyUpdate);
    window.addEventListener("leaveTypesUpdated", handleLeaveTypesUpdated);
    return () => {
      window.removeEventListener("weeklyOffPolicyUpdated", handlePolicyUpdate);
      window.removeEventListener("leaveTypesUpdated", handleLeaveTypesUpdated);
    };
  }, []);

  const fetchDepartments = async () => {
    try {
      const response = await departmentService.getDepartments();
      if (response.success) {
        setDepartmentsList(response.data || []);
      }
    } catch (error) {
      console.error("Error fetching departments:", error);
    }
  };

  const fetchBranches = async () => {
    try {
      const response = await branchService.getBranches();
      if (response.success) {
        setBranchesList(response.data || []);
      }
    } catch (error) {
      console.error("Error fetching branches:", error);
    }
  };

  const fetchDesignations = async () => {
    try {
      const response = await designationService.getDesignations();
      if (response.success) {
        setDesignationsList(response.data || []);
      }
    } catch (error) {
      console.error("Error fetching designations:", error);
    }
  };

  // Function to fetch attendance structure data
  const fetchAttendanceStructureData = async ({
    force = false,
    reason = "unknown",
  } = {}) => {
    if (!force && attendanceStructureLoadedOnceRef.current) {
      return;
    }
    if (attendanceStructureInFlightRef.current) {
      return;
    }
    attendanceStructureInFlightRef.current = true;

    const coreStatuses = ["PR", "LOP", "WO", "PH"];
    const toUpper = (v) => String(v || "").toUpperCase();

    const updateStatusState = (nextDefinitions, nextStatuses, nextColorMap) => {
      statusDefinitions = nextDefinitions;
      attendanceStatuses = nextStatuses;
      statusColorMap = nextColorMap;
      setStatusDefinitionsState(nextDefinitions);
      setAttendanceStatusesState(nextStatuses);
      setStatusColorMapState(nextColorMap);
    };

    const fetchAndBuildLeaveTypeStatus = async (showFailureToast = false) => {
      try {
        const leaveTypesResponse = await attendanceStructureService.getLeaveTypes();
        if (!(leaveTypesResponse && leaveTypesResponse.success)) {
          throw new Error("Leave types API returned unsuccessful response");
        }

        const raw = leaveTypesResponse.data || [];
        const currentCompanyId = localStorage.getItem("companyId");
        const filteredLeaveTypes = raw.filter((lt) => {
          const ltCompanyId = lt.companyId || lt.company_id;
          const isGlobal = !ltCompanyId;
          const isForThisCompany =
            ltCompanyId === parseInt(currentCompanyId, 10) ||
            ltCompanyId === currentCompanyId;
          const isExplicitlyCreated = lt.id && lt.code && lt.name;
          return isGlobal || isForThisCompany || isExplicitlyCreated;
        });

        const nextStatusDefinitions = {
          PR: "Present",
          LOP: "Loss of Pay",
          WO: "Weekly Off",
          PH: "Public Holiday",
        };
        const nextStatusColorMap = {};

        filteredLeaveTypes.forEach((lt) => {
          const code = lt.code || lt.shortCode || lt.id;
          if (!code) return;
          const normalizedCode = toUpper(code);
          const label = lt.name || lt.label || lt.displayName || String(code);
          nextStatusDefinitions[normalizedCode] = label;
          if (lt.color) nextStatusColorMap[normalizedCode] = lt.color;
        });

        const configResp = await attendanceService.getConfigurations();
        if (!(configResp && Array.isArray(configResp))) {
          console.warn("Attendance code configuration is unavailable during leave type build.");
        }

        const activeLeaveTypeCodes = filteredLeaveTypes
          .filter((lt) => (lt.isActive !== undefined ? lt.isActive : true) === true)
          .map((lt) => toUpper(lt.code || lt.shortCode || lt.id))
          .filter(Boolean);

        const nextAttendanceStatuses = [...new Set([...coreStatuses, ...activeLeaveTypeCodes])];

        updateStatusState(
          nextStatusDefinitions,
          nextAttendanceStatuses,
          nextStatusColorMap
        );
        setLeaveTypes(filteredLeaveTypes);
        return true;
      } catch (leaveErr) {
        console.error("Failed to fetch leave types for attendance dropdown", leaveErr);
        updateStatusState(
          { PR: "Present", LOP: "Loss of Pay", WO: "Weekly Off", PH: "Public Holiday" },
          coreStatuses,
          {}
        );
        if (showFailureToast) {
          toast.error(
            "Attendance options are partially loaded. Retrying leave types in background."
          );
        }
        return false;
      }
    };

    try {
      const [
        weeklyOffResult,
        holidayPolicyResult,
        holidaysResult,
        employeeWeeklyOffResult,
        employeeHolidayResult,
      ] = await Promise.allSettled([
        attendanceStructureService.getWeeklyOffPolicies(),
        attendanceStructureService.getHolidayPolicies(),
        attendanceStructureService.getHolidays(),
        attendanceStructureService.getEmployeeWeeklyOffPolicies(),
        attendanceStructureService.getEmployeeHolidayPolicies(),
      ]);

      if (weeklyOffResult.status === "fulfilled" && weeklyOffResult.value?.success) {
        const raw = weeklyOffResult.value.data || [];
        const normalized = raw.map((p) => ({
          ...p,
          id: typeof p.id === "string" ? Number(p.id) : p.id,
          overridePolicy: p.overridePolicy === true || p.overridePolicy === "true",
          allowOverride: p.allowOverride === true || p.allowOverride === "true",
        }));
        setWeeklyOffPolicies(normalized);
      } else if (weeklyOffResult.status === "rejected") {
        console.error("Failed to fetch weekly off policies:", weeklyOffResult.reason);
      }

      if (
        holidayPolicyResult.status === "fulfilled" &&
        holidayPolicyResult.value?.success
      ) {
        setHolidayPolicies(holidayPolicyResult.value.data || []);
      } else if (holidayPolicyResult.status === "rejected") {
        console.error("Failed to fetch holiday policies:", holidayPolicyResult.reason);
      }

      if (holidaysResult.status === "fulfilled" && holidaysResult.value?.success) {
        setHolidaysData(holidaysResult.value.data || []);
      } else if (holidaysResult.status === "rejected") {
        console.error("Failed to fetch holidays:", holidaysResult.reason);
      }

      if (
        employeeWeeklyOffResult.status === "fulfilled" &&
        employeeWeeklyOffResult.value?.success
      ) {
        const normalizedMappings = normalizeEmployeeWeeklyOffMapping(
          employeeWeeklyOffResult.value.data || {}
        );
        setEmployeeWeeklyOffMappings(normalizedMappings);
      } else if (employeeWeeklyOffResult.status === "rejected") {
        console.error(
          "Failed to fetch employee weekly off mappings:",
          employeeWeeklyOffResult.reason
        );
      }

      if (
        employeeHolidayResult.status === "fulfilled" &&
        employeeHolidayResult.value?.success
      ) {
        setEmployeeHolidayMappings(employeeHolidayResult.value.data || {});
      } else if (employeeHolidayResult.status === "rejected") {
        console.error(
          "Failed to fetch employee holiday mappings:",
          employeeHolidayResult.reason
        );
      }

      const leaveLoaded = await fetchAndBuildLeaveTypeStatus(true);
      if (!leaveLoaded) {
        // lightweight retry to avoid requiring manual refresh
        setTimeout(() => {
          fetchAndBuildLeaveTypeStatus(false);
        }, 2000);
      }
      attendanceStructureLoadedOnceRef.current = true;
    } catch (error) {
      console.error(
        `Error fetching attendance structure data (reason: ${reason}):`,
        error
      );
    } finally {
      attendanceStructureInFlightRef.current = false;
    }
  };

  const handleMonthRangeChange = (eventOrValue) => {
    const rawValue =
      typeof eventOrValue === "string"
        ? eventOrValue
        : eventOrValue?.target?.value || "";

    const hasUnsavedChanges =
      (!!isEditing && !isLocked && (changedRecordsRef?.current?.size || 0) > 0) ||
      hasMarkAllUnsavedChanges;

    const confirmIfUnsaved = (nextYear, nextMonth) => {
      if (!hasUnsavedChanges) {
        applyMonthChange(nextYear, nextMonth);
        return;
      }

      setMonthPickerValue(monthRangeValue);
      setUnsavedChangesModal({ isOpen: true, nextYear, nextMonth });
    };

    if (!rawValue) {
      const now = new Date();
      // no-op if same month/year
      if (now.getFullYear() === selectedYear && now.getMonth() === selectedMonth) {
        return;
      }
      confirmIfUnsaved(now.getFullYear(), now.getMonth());
      return;
    }

    const [start] = rawValue.split("/");
    if (!start) return;

    const [yearString, monthString] = start.split("-");
    const parsedYear = parseInt(yearString, 10);
    const parsedMonth = parseInt(monthString, 10) - 1;

    if (Number.isNaN(parsedYear) || Number.isNaN(parsedMonth)) {
      return;
    }

    // no-op if same month/year
    if (parsedYear === selectedYear && parsedMonth === selectedMonth) {
      return;
    }

    confirmIfUnsaved(parsedYear, parsedMonth);
  };

  // Create employee lookup map for O(1) access instead of O(n) repeated finds
  const employeeMap = useMemo(() => {
    if (!allEmployees || !Array.isArray(allEmployees)) return new Map();
    const map = new Map();
    allEmployees.forEach((emp) => {
      map.set(String(emp.id), emp);
      if (emp.empCode) map.set(String(emp.empCode), emp);
      if (emp.employeeId) map.set(String(emp.employeeId), emp);
    });
    return map;
  }, [allEmployees]);

  // Build memoized sets of paid/unpaid leave codes so calculatePayDays can use them.
  // These must be defined before any function that references them to avoid TDZ errors.
  const unpaidLeaveCodes = useMemo(() => {
    const set = new Set();
    try {
      set.add("LOP");
      (leaveTypes || []).forEach((lt) => {
        if (!lt) return;
        const rawCode = lt.code || lt.shortCode || lt.id;
        if (!rawCode && rawCode !== 0) return;
        const codeStr = String(rawCode).trim().toUpperCase();
        const norm = normalizeStatus(codeStr).toUpperCase();
        if (lt.isUnpaid === true) set.add(norm);
      });
    } catch (e) {
      /* ignore */
    }
    return set;
  }, [leaveTypes]);

  const paidLeaveCodes = useMemo(() => {
    const set = new Set(["PR", "PH", "WO"]);
    try {
      (leaveTypes || []).forEach((lt) => {
        if (!lt) return;
        const rawCode = lt.code || lt.shortCode || lt.id;
        if (!rawCode && rawCode !== 0) return;
        const codeStr = String(rawCode).trim().toUpperCase();
        const norm = normalizeStatus(codeStr).toUpperCase();
        if (!lt.isUnpaid) set.add(norm);
      });
    } catch (e) {
      /* ignore */
    }
    return set;
  }, [leaveTypes]);

  // Print the runtime paid/unpaid leave code sets when they change (debug only)
  React.useEffect(() => {
    try {
      if (ENABLE_DEBUG_LOGS) {
        console.log("Attendance - leave code sets:", {
          paid: Array.from(paidLeaveCodes || []),
          unpaid: Array.from(unpaidLeaveCodes || []),
        });
      }
    } catch (e) {
      /* ignore */
    }
  }, [paidLeaveCodes, unpaidLeaveCodes]);

  const filteredEmployees = useMemo(() => {
    if (!allEmployees || !Array.isArray(allEmployees) || !allEmployees.length) {
      return [];
    }

    // Get the first day of the current month being viewed
    const firstDayOfMonth = new Date(selectedYear, selectedMonth, 1);
    // Get the last day of the current month being viewed
    const lastDayOfMonth = new Date(selectedYear, selectedMonth + 1, 0);

    // Backend already filters by date/status - just format the data
    // Remove heavy client-side filtering for performance
    const seen = new Set();
    return allEmployees
      .filter((employee) => {
        // Skip duplicate IDs
        const empId = String(employee.id);
        if (seen.has(empId)) {
          console.warn(
            `⚠️ Duplicate employee ID detected: ${empId} (${employee.name})`
          );
          return false;
        }
        seen.add(empId);

        // Filter out employees who haven't joined yet in this month
        const joinDate = employee.joiningDate || employee.joinDate;

        if (joinDate) {
          const jd = new Date(joinDate);
          // If employee joins after the last day of this month, exclude them
          if (jd > lastDayOfMonth) {
            console.log(
              `Filtering out employee ${
                employee.empCode
              } - joins on ${joinDate}, viewing month: ${selectedYear}-${
                selectedMonth + 1
              }`
            );
            return false;
          }
        }

        // Filter out employees who have been relieved before this month
        // If relieved date is before or on the last day of previous month, exclude them
        const relievedDate =
          employee.relievedDate ||
          employee.exitDate ||
          employee.resignationDate ||
          employee.relievingDate;

        if (relievedDate) {
          const rd = new Date(relievedDate);
          // If employee was relieved before the first day of this month, exclude them
          if (rd < firstDayOfMonth) {
            console.log(
              `Filtering out employee ${
                employee.empCode
              } - relieved on ${relievedDate}, viewing month: ${selectedYear}-${
                selectedMonth + 1
              }`
            );
            return false;
          }
        }
        return true;
      })
      .map((employee) => ({
        id: employee.id,
        code: employee.empCode || employee.employeeId || "",
        name: employee.user?.name || employee.name || "",
        joinDate: employee.joiningDate || employee.joinDate || "",
        exitDate: employee.exitDate || employee.relievingDate || "",
        relievedDate:
          employee.relievedDate ||
          employee.relievingDate ||
          employee.exitDate ||
          "",
        employmentStatus: employee.employmentStatus || "",
        // Keep user (and gender) so leave type popup can filter by gender applicability
        user: employee.user,
        gender: employee?.user?.gender ?? employee?.gender,
      }));
  }, [allEmployees, selectedMonth, selectedYear]);

  // Calculate totals for all employees
  // Pay Days = Total Paid Days (not subtracting unpaid days)
  // Pay Days represents the number of days for which the employee is paid
  const calculatePayDays = (employeeId) => {
    const counts = calculateStatusCounts(employeeId);

    let paidDays = 0;

    Object.entries(counts || {}).forEach(([code, val]) => {
      const c = Number(val) || 0;
      if (c === 0) return; // Skip zero counts
      
      const upper = String(code || "").toUpperCase();
      const normalized = normalizeStatus(upper).toUpperCase();
      
      // Only count paid days - don't subtract unpaid days
      // Check if it's explicitly unpaid - skip these
      if (unpaidLeaveCodes && unpaidLeaveCodes.has(normalized)) {
        // Don't count unpaid days (LOP, LWP, etc.)
        return;
      }
      // Check if it's a known unpaid code (LOP, LWP) - skip these
      if (normalized === "LOP" || normalized === "LWP") {
        // Don't count unpaid days
        return;
      }
      // All other statuses are counted as paid days
      // This includes: PR, PH, WO, and all leave types that are not explicitly unpaid
      if (paidLeaveCodes && paidLeaveCodes.has(normalized)) {
        paidDays += c;
      }
      // Default: if not explicitly unpaid, treat as paid
      // This handles custom leave types that might not be in leaveTypes yet
      else {
        paidDays += c;
      }
    });

    return paidDays;
  };

  // 🚀 MEMOIZE: Cache status counts calculation to avoid 6,200+ recalculations per render
  // When user scrolls and loads 200 employees, each has 31 days worth of calculations
  // Memoization reduces this from O(n*d) to O(1) lookups on re-render
  const statusCountsCache = useRef(new Map());

  // Create a stable reference for join dates to trigger cache clear only when dates change
  const joinDatesRef = useRef("");
  useEffect(() => {
    const joinDateString = (allEmployees || [])
      .map((e) => `${e.id}:${e.joinDate}`)
      .join("|");
    joinDatesRef.current = joinDateString;
  }, [allEmployees]);

  // Clear cache when dependencies change (attendance, month, etc)
  // IMPORTANT: Include joinDatesRef to clear when employee join dates change
  useEffect(() => {
    statusCountsCache.current.clear();
  }, [
    attendanceData,
    selectedMonth,
    selectedYear,
    weeklyOffPolicies,
    employeeWeeklyOffMappings,
    holidaysData,
    holidayPolicies,
    employeeHolidayMappings,
    statusDefinitions,
    joinDatesRef.current, // Only changes when join dates actually change
    scheduleRecalcTick,
  ]);

  useEffect(() => {
    return startAttendanceScheduleRunner();
  }, []);

  useEffect(() => {
    const bump = () => {
      statusCountsCache.current.clear();
      setScheduleRecalcTick((n) => n + 1);
    };
    window.addEventListener(ATTENDANCE_SCHEDULE_RECALC_EVENT, bump);
    window.addEventListener(ATTENDANCE_PUNCH_UPDATED_EVENT, bump);
    return () => {
      window.removeEventListener(ATTENDANCE_SCHEDULE_RECALC_EVENT, bump);
      window.removeEventListener(ATTENDANCE_PUNCH_UPDATED_EVENT, bump);
    };
  }, []);

  const calculateStatusCounts = (employeeId) => {
    // Check cache first - avoid recalculation if already computed
    const cacheKey = `${employeeId}:${selectedYear}:${selectedMonth}:${scheduleRecalcTick}`;
    if (statusCountsCache.current.has(cacheKey)) {
      return statusCountsCache.current.get(cacheKey);
    }

    // Locate the attendance map for this employee using multiple possible keys.
    // Use employeeMap for O(1) lookup instead of O(n) array.find
    const getAttendanceMapFor = (empId) => {
      if (!attendanceData) return null;

      if (attendanceData[empId]) return attendanceData[empId];
      if (attendanceData[String(empId)]) return attendanceData[String(empId)];

      const orig = employeeMap.get(String(empId));
      if (orig) {
        const candidates = [
          orig.employeeId,
          orig.empCode,
          orig.employeeId?.toString(),
          orig.empCode?.toString(),
        ];
        for (const key of candidates) {
          if (!key) continue;
          if (attendanceData[key]) return attendanceData[key];
          if (attendanceData[String(key)]) return attendanceData[String(key)];
        }
      }

      return null;
    };

    const attendanceMap = getAttendanceMapFor(employeeId);

    const getDetailMapFor = (empId) => {
      if (!attendanceDetailData) return {};
      if (attendanceDetailData[empId]) return attendanceDetailData[empId];
      if (attendanceDetailData[String(empId)]) return attendanceDetailData[String(empId)];
      const orig = employeeMap.get(String(empId));
      if (orig) {
        for (const key of [orig.employeeId, orig.empCode, orig.id]) {
          if (!key) continue;
          if (attendanceDetailData[key]) return attendanceDetailData[key];
          if (attendanceDetailData[String(key)]) return attendanceDetailData[String(key)];
        }
      }
      return {};
    };

    const generalSettings = loadAttendanceGeneralSettings();
    const useBatchPolicy = generalSettings.runAttendanceSchedule !== "realtime";
    const empObjForPolicy = employeeMap.get(String(employeeId)) || {};
    const worklogByDay = readEmployeeWorklogMap(employeeId);
    const hasWorklog =
      worklogByDay && Object.keys(worklogByDay).length > 0;
    let policyStatusMap = null;
    if (useBatchPolicy || hasWorklog) {
      policyStatusMap = buildMonthPolicyStatusMap({
        employee: empObjForPolicy,
        year: selectedYear,
        month: selectedMonth,
        statusMap: attendanceMap || {},
        detailMap: getDetailMapFor(employeeId),
        worklogByDay,
        daysInMonth: Math.min(
          31,
          new Date(selectedYear, selectedMonth + 1, 0).getDate()
        ),
        autoApplyForAllEmployees: useBatchPolicy,
      });
    }

    // Initialize counts with known status keys (including dynamic leave types)
    // Normalize keys to uppercase to be robust against backend casing differences
    const counts = {};
    try {
      Object.keys(statusDefinitions || {}).forEach((k) => {
        if (k && typeof k === "string") counts[k.toUpperCase()] = 0;
      });
    } catch (e) {
      // fallback to defaults if statusDefinitions is not available
      ["PR", "PH", "WO", "CL", "EL", "CO", "CW", "LOP"].forEach((k) => {
        counts[k] = 0;
      });
    }

    // Number of days in the selected month/year (clamped to 31)
    const daysInMonth = Math.min(
      31,
      new Date(selectedYear, selectedMonth + 1, 0).getDate()
    );

    // Get employee object once outside loop for better performance
    const empObj = employeeMap.get(String(employeeId)) || {};
    if (ENABLE_DEBUG_LOGS && !statusCountsCache.current.has(cacheKey)) {
      console.debug(
        `[CALC START] Employee ${employeeId} (${empObj.name}), joinDate=${empObj.joinDate}, daysInMonth=${daysInMonth}`
      );
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const currentDate = new Date(selectedYear, selectedMonth, day);

      // CRITICAL: Check date constraints FIRST before doing anything else
      // Skip days before employee joining date
      if (empObj && empObj.joinDate) {
        let joinDate;
        try {
          // Use the same parseDateComponents function for consistency
          const joinDateParts = parseDateComponents(empObj.joinDate);
          if (joinDateParts) {
            joinDate = new Date(
              joinDateParts.year,
              joinDateParts.month,
              joinDateParts.day
            );
          } else {
            // Fallback parsing
            joinDate = new Date(empObj.joinDate);
          }

          if (!isNaN(joinDate.getTime())) {
            // Compare dates properly
            const currentDateNum =
              selectedYear * 10000 + (selectedMonth + 1) * 100 + day;
            const joinDateNum =
              joinDate.getFullYear() * 10000 +
              (joinDate.getMonth() + 1) * 100 +
              joinDate.getDate();

            if (currentDateNum < joinDateNum) {
              if (ENABLE_DEBUG_LOGS)
                console.debug(
                  `Employee ${empObj.id} Day ${day}: Before join date (${joinDateNum}), SKIPPING`
                );
              continue;
            }
          }
        } catch (e) {
          // If date parsing fails, log and skip the check
          console.warn(
            `Failed to parse join date for employee ${empObj.id}: ${empObj.joinDate}`,
            e
          );
        }
      }

      // Skip days after employee relieving date
      const relievedDate =
        empObj?.relievedDate ||
        empObj?.exitDate ||
        empObj?.resignationDate ||
        empObj?.relievingDate;
      if (relievedDate) {
        try {
          // Use the same parseDateComponents function for consistency
          const relievedDateParts = parseDateComponents(relievedDate);
          let rd;
          if (relievedDateParts) {
            rd = new Date(
              relievedDateParts.year,
              relievedDateParts.month,
              relievedDateParts.day
            );
          } else {
            rd = new Date(relievedDate);
          }

          if (!isNaN(rd.getTime())) {
            // Compare dates properly
            const currentDateNum =
              selectedYear * 10000 + (selectedMonth + 1) * 100 + day;
            const relievedDateNum =
              rd.getFullYear() * 10000 +
              (rd.getMonth() + 1) * 100 +
              rd.getDate();

            if (currentDateNum > relievedDateNum) {
              if (ENABLE_DEBUG_LOGS)
                console.debug(
                  `Employee ${empObj.id} Day ${day}: After relieving date (${relievedDateNum}), SKIPPING`
                );
              continue;
            }
          }
        } catch (e) {
          // If date parsing fails, log and skip the check
          console.warn(
            `Failed to parse relieving date for employee ${empObj.id}: ${relievedDate}`,
            e
          );
        }
      }

      // NOW check and process the status
      // Prefer policy/worklog overlay when schedule or punches apply
      let status =
        policyStatusMap?.[String(day)] ??
        policyStatusMap?.[day] ??
        attendanceMap?.[day];

      // Normalize incoming status to uppercase string when present
      if (typeof status === "string") status = status.trim().toUpperCase();

      // Log raw status read from attendance data
      if (
        ENABLE_DEBUG_LOGS &&
        status &&
        (status === "PHS" || status === "PH")
      ) {
        console.debug(`[DAY ${day}] Raw status: ${status}`);
      }

      // IMPORTANT: Convert mapped status codes back to original codes
      // If backend returns "PHS" (mapped), convert to "PH" (original) for counting
      if (status && reverseMapping && reverseMapping[status]) {
        const originalStatus = reverseMapping[status].toUpperCase();
        status = originalStatus;
      }

      // Skip "-" status and days before joining/after relieving in calculations
      if (status === "-") {
        if (ENABLE_DEBUG_LOGS)
          console.debug(
            `Employee ${empObj.id} Day ${day}: Skipping "-" status`
          );
        continue;
      }

      if (typeof status === "string" && status.includes("/")) {
        const parts = status.split("/").map((p) => normalizeStatus(p).toUpperCase());
        if (parts.length === 2 && parts[0] && parts[1]) {
          parts.forEach((half) => {
            if (counts[half] === undefined) counts[half] = 0;
            counts[half] += 0.5;
          });
          continue;
        }
      }

      // Only calculate default status if no attendance data exists
      if (!status) {
        // Compute weekly-off for this employee & date
        const isWOForEmp = isWeeklyOffForEmployee(
          currentDate,
          empObj,
          weeklyOffPolicies,
          employeeWeeklyOffMappings
        );

        // Compute default status that respects WO
        status = isWOForEmp
          ? "WO"
          : getDefaultStatus(
              currentDate,
              empObj,
              weeklyOffPolicies,
              employeeWeeklyOffMappings,
              holidaysData,
              holidayPolicies,
              employeeHolidayMappings
            );
      }

      // Handle combined/half-day statuses (e.g., PRCL, LWPPR)
      // Ensure we compare with normalized attendanceStatuses (uppercase)
      const normAttendanceStatuses = (attendanceStatuses || []).map((s) =>
        typeof s === "string" ? s.toUpperCase() : s
      );
      if (
        status &&
        status.length > 3 &&
        !normAttendanceStatuses.includes(status)
      ) {
        let first, second;
        const potential3Letter = status.substring(0, 3);
        if (normAttendanceStatuses.includes(potential3Letter)) {
          first = potential3Letter;
          second = status.substring(3);
        } else {
          first = status.substring(0, 2);
          second = status.substring(2);
        }
        first = normalizeStatus(first).toUpperCase();
        second = normalizeStatus(second).toUpperCase();

        if (counts[first] === undefined) counts[first] = 0;
        if (counts[second] === undefined) counts[second] = 0;
        counts[first] += 0.5;
        counts[second] += 0.5;
      } else {
        status = normalizeStatus(status).toUpperCase();
        if (counts[status] === undefined) counts[status] = 0;
        counts[status] += 1;
        if (
          ENABLE_DEBUG_LOGS &&
          (status === "CO" || status === "PH" || status === "CL")
        ) {
          console.debug(
            `[DAY ${day}] Incremented ${status} to ${counts[status]}`
          );
        }
      }
    }

    // Debug logging for this employee's counts
    if (ENABLE_DEBUG_LOGS && Object.values(counts).some((v) => v > 0)) {
      console.debug(`calculateStatusCounts(${employeeId}):`, counts);
    }

    // 🚀 CACHE: Store result for future calls to avoid recalculation
    statusCountsCache.current.set(cacheKey, counts);
    return counts;
  };

  // Calculate totals for all employees
  const calculateTotals = () => {
    // Initialize totals dynamically from statusDefinitions to include custom leave types
    const totals = {};
    try {
      Object.keys(statusDefinitions || {}).forEach((k) => {
        if (k && typeof k === "string") totals[k.toUpperCase()] = 0;
      });
    } catch (e) {
      // fallback to common keys
      ["PR", "PH", "WO", "CL", "EL", "CO", "CW", "LOP"].forEach(
        (k) => (totals[k] = 0)
      );
    }
    totals.payDays = 0;
    totals.lopReversalDays = 0;

    // Calculate against all currently filtered employees (not just current page)
    // so totals represent the full dataset by default and filtered dataset when filters are applied.
    const employeesToCalculate = filteredEmployees || [];

    employeesToCalculate.forEach((emp) => {
      const counts = calculateStatusCounts(emp.id);
      totals.lopReversalDays += Number(lopReversalDaysMap[emp.id] ?? lopReversalDaysMap[String(emp.id)] ?? 0);

      // Add all status counts into totals (includes dynamic codes)
      Object.entries(counts).forEach(([k, v]) => {
        const key = (k || "").toString().toUpperCase();
        totals[key] = (totals[key] || 0) + (v || 0);
      });

      // Inlined pay day calculation (same logic as calculatePayDays)
      // Pay Days = Total Paid Days (not subtracting unpaid days)
      let paidDays = 0;
      Object.entries(counts || {}).forEach(([code, val]) => {
        const c = Number(val) || 0;
        if (c === 0) return; // Skip zero counts
        
        const upper = String(code || "").toUpperCase();
        const normalized = normalizeStatus(upper).toUpperCase();
        
        // Only count paid days - don't subtract unpaid days
        // Check if it's explicitly unpaid - skip these
        if (unpaidLeaveCodes && unpaidLeaveCodes.has(normalized)) {
          // Don't count unpaid days (LOP, LWP, etc.)
          return;
        }
        // Check if it's a known unpaid code (LOP, LWP) - skip these
        if (normalized === "LOP" || normalized === "LWP") {
          // Don't count unpaid days
          return;
        }
        // All other statuses are counted as paid days
        // This includes: PR, PH, WO, and all leave types that are not explicitly unpaid
        if (paidLeaveCodes && paidLeaveCodes.has(normalized)) {
          paidDays += c;
        }
        // Default: if not explicitly unpaid, treat as paid
        // This handles custom leave types that might not be in leaveTypes yet
        else {
          paidDays += c;
        }
      });
      const empPayDays = paidDays;

      // Per-employee concise equation log (always printed for current page)
      try {
        const paidExpr =
          Object.entries(counts || {})
            .filter(
              ([code, val]) =>
                paidLeaveCodes.has(String(code || "").toUpperCase()) && val > 0
            )
            .map(([code, val]) => `${code}:${val}`)
            .join(" + ") || "0";
        const unpaidExpr =
          Object.entries(counts || {})
            .filter(
              ([code, val]) =>
                unpaidLeaveCodes.has(String(code || "").toUpperCase()) &&
                val > 0
            )
            .map(([code, val]) => `${code}:${val}`)
            .join(", ") || "0";

        // console.log(
        //   `Emp ${emp.id} ${
        //     emp.name || ""
        //   }: PAID = ${paidDays} (${paidExpr}) ; UNPAID = ${unpaidDays} (${unpaidExpr}) => FINAL = ${empPayDays} ; calcPayDays = ${empPayDays}`
        // );
      } catch (e) {
        /* ignore logging errors */
      }

      // Use the calculated employee pay days (empPayDays) for totals aggregation.
      totals.payDays += empPayDays;
    });

    // Debug: final totals (enable by setting ENABLE_DEBUG_LOGS = true)
    try {
      if (ENABLE_DEBUG_LOGS) {
        console.log("Attendance totals computed:", totals);
      }
    } catch (e) {
      /* ignore */
    }

    return totals;
  };

  // (moved totals state to earlier near top-level states)

  // Refresh employees when month/year changes
  // Note: Don't reset itemsPerPage here - let user selection persist

  // Determine paginated employees for display. If backend provides pages (serverSidePagination)
  // we assume `filteredEmployees` already contains only the current page. Otherwise we slice
  // the full `filteredEmployees` client-side. When user selects "all" we show all employees
  // (fetched via getEmployees without limit). Chunked/infinite-scroll logic commented out for now.
  const paginatedEmployees = useMemo(() => {
    if (!filteredEmployees || !Array.isArray(filteredEmployees)) return [];
    if (serverSidePagination) return filteredEmployees;
    // When "all": show all filtered employees (no slice)
    if (itemsPerPage === "all") return filteredEmployees;
    const perPage = parseInt(itemsPerPage, 10) || 50;
    const start = (page - 1) * perPage;
    return filteredEmployees.slice(start, start + perPage);
  }, [filteredEmployees, page, itemsPerPage, serverSidePagination]);

  // Recalculate total pages whenever totalEmployees or itemsPerPage changes
  // Skip recalculation when server provides pagination metadata
  useEffect(() => {
    if (serverSidePagination) return;
    // When "all": single page with all employees
    if (itemsPerPage === "all") {
      setTotalPages(1);
      return;
    }
    const perPage = parseInt(itemsPerPage, 10) || 50;
    const total =
      totalEmployees || (filteredEmployees ? filteredEmployees.length : 0);
    setTotalPages(Math.max(1, Math.ceil(total / perPage)));
  }, [totalEmployees, itemsPerPage, filteredEmployees, serverSidePagination]);

  // -----------------------------
  // Infinite scroll (chunked): parent keeps track of loaded pages and provides
  // `loadMoreEmployees(nextPage)`; here we expose a small trigger wrapper that
  // AttendanceTable can call when an internal sentinel becomes visible.
  // For "all" mode: track how many employees are visible (chunked loading)
  // Initial chunk = 100, then auto-load next 100 when user scrolls to ~70 loaded
  // -----------------------------
  const [infiniteLoading, setInfiniteLoading] = useState(false);
  const lastLoadedPageRef = useRef(page);

  // Chunked API for infinite scroll commented out - will use in future.
  // When "all" is selected we use getEmployees without limit, so no load-more needed.
  // To restore: call attendanceService.getChunkedAttendance(month, year, pageToRequest, 100, search, filters),
  // then transform chunkedResp.employees, merge into setAllEmployees/setAttendanceData, update pagination.
  const loadMoreEmployees = useCallback(async () => {
    // No-op: chunked infinite scroll disabled
  }, []);

  // Reset visible employee count when itemsPerPage or filters change
  // For chunked modes (all, 100, 200), initialize visible count appropriately
  useEffect(() => {
    const useChunking =
      itemsPerPage === "all" ||
      itemsPerPage === 100 ||
      itemsPerPage === "100" ||
      itemsPerPage === 200 ||
      itemsPerPage === "200";
    if (useChunking) {
      // 🚀 Initialize visible count based on itemsPerPage selection
      // "all" → show 500 (5 chunks loaded)
      // "200" → show 200 (2 chunks loaded)
      // "100" → show 100 (1 chunk loaded)
      let visibleCount = 100; // default

      if (itemsPerPage === "all") {
        // Show all loaded employees (5 chunks = 500)
        visibleCount = Math.min(500, allEmployees.length);
      } else if (itemsPerPage === 200 || itemsPerPage === "200") {
        // Show 200 employees (2 chunks)
        visibleCount = Math.min(200, allEmployees.length);
      } else if (itemsPerPage === 100 || itemsPerPage === "100") {
        // Show 100 employees (1 chunk)
        visibleCount = Math.min(100, allEmployees.length);
      }

      setVisibleEmployeeCountForAll(visibleCount);
      // Reset auto-preload flag so next batch will be preloaded again
      autoPreloadedRef.current = false;
    }
  }, [
    itemsPerPage,
    debouncedSearchTerm,
    selectedMonth,
    selectedYear,
    JSON.stringify(appliedFilters),
    allEmployees.length,
  ]);

  // Trigger callable by child table when its sentinel becomes visible.
  const triggerLoadMore = useCallback(() => {
    // Only trigger infinite loads when chunked modes (all, 100, 200) are selected
    const useChunking =
      itemsPerPage === "all" ||
      itemsPerPage === 100 ||
      itemsPerPage === "100" ||
      itemsPerPage === 200 ||
      itemsPerPage === "200";
    if (!useChunking) return;
    if (infiniteLoading) return;
    if (!hasMoreEmployees) return;
    // Let loadMoreEmployees pick the correct next page (uses nextEmployeePage)
    loadMoreEmployees();
  }, [
    itemsPerPage,
    infiniteLoading,
    page,
    loadMoreEmployees,
    hasMoreEmployees,
  ]);

  // 🚀 AUTO-PRELOAD: Reference to track if we've auto-loaded next batch
  const autoPreloadedRef = useRef(false);

  // Auto-preload next batch in background after first batch is displayed
  // This ensures smooth infinite scroll - next batch loads BEFORE user scrolls to sentinel
  useEffect(() => {
    const useChunking =
      itemsPerPage === "all" ||
      itemsPerPage === 100 ||
      itemsPerPage === "100" ||
      itemsPerPage === 200 ||
      itemsPerPage === "200";

    if (!useChunking) {
      autoPreloadedRef.current = false;
      return;
    }

    // Determine the target visible count for this itemsPerPage setting
    let targetVisible = 100;
    if (itemsPerPage === "all") {
      targetVisible = 500;
    } else if (itemsPerPage === 200 || itemsPerPage === "200") {
      targetVisible = 200;
    }

    // Only auto-preload once when initial batch is fully displayed
    if (visibleEmployeeCountForAll !== targetVisible) return;
    if (infiniteLoading) return;
    if (!hasMoreEmployees) return;
    if (autoPreloadedRef.current) return; // Already triggered once

    autoPreloadedRef.current = true;

    // 🚀 AGGRESSIVE: Auto-load next batch with minimal delay (200ms instead of 500ms)
    // Reduces perceived loading time significantly
    const timer = setTimeout(() => {
      if (!infiniteLoading && hasMoreEmployees) {
        loadMoreEmployees();
      }
    }, 200);

    return () => clearTimeout(timer);
  }, [
    visibleEmployeeCountForAll,
    itemsPerPage,
    infiniteLoading,
    hasMoreEmployees,
    loadMoreEmployees,
  ]);

  // COMMENTED OUT: Aggressive pre-caching using getMultipleChunkedAttendance - will use in future.
  // const aggressivePreCacheRef = useRef(false);
  // useEffect(() => {
  //   if (itemsPerPage !== "all") { aggressivePreCacheRef.current = false; return; }
  //   if (allEmployees.length < 500) return;
  //   if (infiniteLoading) return;
  //   if (!hasMoreEmployees) return;
  //   if (aggressivePreCacheRef.current) return;
  //   aggressivePreCacheRef.current = true;
  //   (async () => {
  //     try {
  //       if (attendanceService.getMultipleChunkedAttendance) {
  //         await attendanceService.getMultipleChunkedAttendance(selectedMonth + 1, selectedYear, [4,5,6,7,8], 100, debouncedSearchTerm, appliedFilters);
  //       }
  //     } catch (err) { console.debug("Aggressive pre-cache error (non-blocking):", err); }
  //   })();
  // }, [allEmployees.length, itemsPerPage, infiniteLoading, hasMoreEmployees, selectedMonth, selectedYear, debouncedSearchTerm, appliedFilters]);
  const aggressivePreCacheRef = useRef(false);
  useEffect(() => {
    if (itemsPerPage !== "all") {
      aggressivePreCacheRef.current = false;
      return;
    }
    // Chunked pre-cache commented out - no-op for now
    aggressivePreCacheRef.current = false;
  }, [itemsPerPage]);

  // Compute totals ONLY when summary is requested - prevents automatic recalculation
  // For large datasets, this saves massive performance (50 employees × 31 days = 1550 calculations)
  const totals = useMemo(() => {
    if (!showSummaryTable) {
      // Return zeros if summary not requested - prevents expensive calculations
      return {
        PR: 0,
        PH: 0,
        WO: 0,
        CL: 0,
        EL: 0,
        CO: 0,
        CW: 0,
        LOP: 0,
        Absent: 0,
        payDays: 0,
        lopReversalDays: 0,
      };
    }
    // console.log(
    //   `Calculating totals for ${paginatedEmployees.length} employees on current page`
    // );
    return calculateTotals();
  }, [
    showSummaryTable, // Only recalculate when user clicks Calculate Summary
    filteredEmployees,
    attendanceData,
    selectedMonth,
    selectedYear,
    weeklyOffPolicies,
    employeeWeeklyOffMappings,
    holidaysData,
    holidayPolicies,
    employeeHolidayMappings,
    leaveTypes,
    lopReversalDaysMap,
  ]);

  // Build a memoized set of unpaid leave codes (normalized) from runtime `leaveTypes`.
  // Always treat LOP (and LWP via normalization) as unpaid.
  // NOTE: `paidLeaveCodes` and `unpaidLeaveCodes` are defined earlier in the
  // file (above `calculatePayDays`) to avoid Temporal Dead Zone errors.

  // Helper to calculate totals for lock email (even if summary not shown)
  const calculateTotalsForLock = useCallback(() => {
    const lockTotals = {};
    // Initialize with common codes
    ["PR", "PH", "WO", "CL", "EL", "CO", "CW", "BL", "ML", "KL", "MN", "LW", "LOP", "LWP"].forEach(
      (k) => (lockTotals[k] = 0)
    );
    lockTotals.payDays = 0;

    // Calculate for all paginated employees
    paginatedEmployees.forEach((emp) => {
      const empData = attendanceData[String(emp.id)] || {};
      const counts = {};
      
      // Count statuses for this employee
      Object.entries(empData).forEach(([day, status]) => {
        if (!status || status === "-") return;
        const normalized = String(status).toUpperCase();
        // Handle half-day statuses (e.g., "PR/LW")
        if (normalized.includes("/")) {
          const [first, second] = normalized.split("/");
          counts[first] = (counts[first] || 0) + 0.5;
          counts[second] = (counts[second] || 0) + 0.5;
        } else {
          counts[normalized] = (counts[normalized] || 0) + 1;
        }
      });

      // Aggregate into totals
      Object.entries(counts).forEach(([code, val]) => {
        const key = code.toUpperCase();
        lockTotals[key] = (lockTotals[key] || 0) + (val || 0);
        // Count pay days (exclude LOP, LWP)
        if (key !== "LOP" && key !== "LWP") {
          lockTotals.payDays = (lockTotals.payDays || 0) + (val || 0);
        }
      });
    });

    return lockTotals;
  }, [paginatedEmployees, attendanceData]);

  // Build an exact day-by-day snapshot from the UI state/default logic so
  // lock-email attachments can render exactly what users see in the grid.
  const buildAttendanceSnapshotForLock = useCallback(() => {
    const snapshot = {};
    const employeesList = Array.isArray(allEmployees) ? allEmployees : [];
    const daysInSelectedMonth = new Date(
      selectedYear,
      selectedMonth + 1,
      0
    ).getDate();

    employeesList.forEach((emp) => {
      if (!emp || emp.id === undefined || emp.id === null) return;
      const empId = String(emp.id);
      const manualMap =
        attendanceData[String(emp.id)] || attendanceData[emp.id] || {};
      const dayMap = {};

      for (let day = 1; day <= daysInSelectedMonth; day++) {
        let status = manualMap[String(day)] ?? manualMap[day];

        if (!status || typeof status !== "string") {
          const def = getDefaultStatusWithLock(
            new Date(selectedYear, selectedMonth, day),
            emp,
            weeklyOffPolicies,
            employeeWeeklyOffMappings,
            holidaysData,
            holidayPolicies,
            employeeHolidayMappings
          );
          status = def?.status || "";
        }

        const normalized = String(status || "").trim();
        if (!normalized || normalized === "-") continue;
        dayMap[String(day)] = normalized;
      }

      if (Object.keys(dayMap).length > 0) {
        snapshot[empId] = dayMap;
      }
    });

    return snapshot;
  }, [
    allEmployees,
    attendanceData,
    selectedYear,
    selectedMonth,
    weeklyOffPolicies,
    employeeWeeklyOffMappings,
    holidaysData,
    holidayPolicies,
    employeeHolidayMappings,
  ]);

  // Handle unlock with passkey
  const handleUnlockWithPasskey = useCallback(async () => {
    if (!unlockPasskey.trim()) {
      setPasskeyError("Please enter the passkey");
      showAlert("Please enter the passkey", "error");
      return;
    }
    const now = Date.now();
    if (now - unlockWithPasskeyCooldownRef.current < LOCK_ACTION_COOLDOWN_MS) return;
    unlockWithPasskeyCooldownRef.current = now;

    try {
      setUnlockingBusy(true);
      setPasskeyError("");
      const monthLabel = new Date(selectedYear, selectedMonth, 1).toLocaleDateString("en-US", {
        month: "long",
        year: "numeric",
      });

      await attendanceService.unlockAttendance(selectedMonth + 1, selectedYear, {
        passkey: unlockPasskey.trim(),
      });

      // Refresh locks
      const locksResponse = await attendanceStructureService.getAttendanceLocks();
      if (locksResponse.success && Array.isArray(locksResponse.data)) {
        setAttendanceLocks(locksResponse.data || []);
        const currentLock = locksResponse.data.find(
          (lock) =>
            lock.month === selectedMonth + 1 && lock.year === selectedYear
        );
        setIsLocked(!!currentLock);
      }

      setShowPasskeyModal(false);
      setUnlockPasskey("");
      setShowPasskey(false);
      setPasskeyError("");
      showAlert(`Attendance unlocked for ${monthLabel}`, "success");
    } catch (err) {
      console.error("Unlock failed:", err);
      const st = err.response?.status;
      const apiMsg = err.response?.data?.message;
      setPasskeyError(
        st === 429
          ? "Please wait before trying again"
          : apiMsg || "Wrong passkey"
      );
      showAlert(apiMsg || err.message || "Invalid passkey or unlock failed", "error");
    } finally {
      setUnlockingBusy(false);
    }
  }, [unlockPasskey, selectedMonth, selectedYear, showAlert]);

  const handleMarkAll = useCallback(
    (type, value, status, optionsParam) => {
      if (!isEditing) return;

      setHasMarkAllUnsavedChanges(true);

      const options = optionsParam || (typeof status === "object" && status && "overrideWeeklyOff" in status ? status : {});
      const overrideWeeklyOff = options.overrideWeeklyOff === true;
      const statusValue = typeof type === "number" && typeof value === "string" ? value : status;

      // Defer expensive operation to idle callback to keep UI responsive
      requestIdleCallback(
        () => {
          setAttendanceData((prev) => {
            const newData = { ...prev };
            let affectedCount = 0;

            if (
              type === "day" ||
              (typeof type === "number" && typeof value === "string")
            ) {
              // Mark all employees for selected day
              const day = typeof type === "number" ? type : value;

              filteredEmployees.forEach((employee) => {
                if (!newData[employee.id]) {
                  newData[employee.id] = {};
                }

                const currentDate = new Date(selectedYear, selectedMonth, day);

                const defaultStatusWithLock = getDefaultStatusWithLock(
                  currentDate,
                  employee.id,
                  weeklyOffPolicies,
                  employeeWeeklyOffMappings,
                  holidaysData,
                  holidayPolicies,
                  employeeHolidayMappings
                );
                if (defaultStatusWithLock && defaultStatusWithLock.locked)
                  return;
                if (!overrideWeeklyOff && (defaultStatusWithLock?.status === "WO" || defaultStatusWithLock?.status === "PH"))
                  return;

                newData[employee.id][day] = statusValue;
                try {
                  if (changedRecordsRef?.current)
                    changedRecordsRef.current.set(`${employee.id}_${day}`, {
                      employeeId: employee.id,
                      day,
                      newStatus: statusValue,
                      override: overrideWeeklyOff === true,
                    });
                } catch (e) {
                  console.debug("Mark All: failed to track change", e);
                }
                affectedCount++;
              });
              showAlert(
                `Marked ${statusValue} for day ${day} for ${affectedCount} employee(s)`
              );
            } else if (type === "employee") {
              // New functionality: mark selected employees for all days
              const employeeIds = Array.isArray(value)
                ? value
                : [parseInt(value)];
              const daysInMonth = Math.min(
                31,
                new Date(selectedYear, selectedMonth + 1, 0).getDate()
              );

              employeeIds.forEach((employeeId) => {
                if (!newData[employeeId]) {
                  newData[employeeId] = {};
                }

                for (let day = 1; day <= daysInMonth; day++) {
                  const currentDate = new Date(
                    selectedYear,
                    selectedMonth,
                    day
                  );
                  const empObj =
                    (allEmployees &&
                      allEmployees.find(
                        (a) => String(a.id) === String(employeeId)
                      )) ||
                    employeeId;

                  // Compute weekly-off for this employee & date
                  const isWOForEmp = isWeeklyOffForEmployee(
                    currentDate,
                    empObj,
                    weeklyOffPolicies,
                    employeeWeeklyOffMappings
                  );

                  // Compute default status that respects WO
                  const defaultStatus = isWOForEmp
                    ? "WO"
                    : getDefaultStatus(
                        currentDate,
                        empObj,
                        weeklyOffPolicies,
                        employeeWeeklyOffMappings,
                        holidaysData,
                        holidayPolicies,
                        employeeHolidayMappings
                      );

                  // Use lock-aware check
                  const defaultStatusWithLock = isWOForEmp
                    ? { status: "WO", locked: false }
                    : getDefaultStatusWithLock(
                        currentDate,
                        employeeId,
                        weeklyOffPolicies,
                        employeeWeeklyOffMappings,
                        holidaysData,
                        holidayPolicies,
                        employeeHolidayMappings
                      );
                  if (defaultStatusWithLock && defaultStatusWithLock.locked)
                    continue;
                  // When marking as Present, do not override WO or PH
                  const applyingPR = status === "PR" || String(status).toUpperCase() === "PR";
                  if (applyingPR && (isWOForEmp || defaultStatus === "PH"))
                    continue;
                  newData[employeeId][day] = status;
                  try {
                    if (changedRecordsRef?.current)
                      changedRecordsRef.current.set(`${employeeId}_${day}`, {
                        employeeId,
                        day,
                        newStatus: statusValue,
                        override: overrideWeeklyOff === true,
                      });
                  } catch (e) {
                    console.debug("Mark All: failed to track change", e);
                  }
                  affectedCount++;
                }
              });

              showAlert(
                `Marked ${statusValue} for all days for ${employeeIds.length} selected employee(s). Review and save when ready.`
              );
            } else if (type === "all") {
              // New functionality: mark all employees for all days
              const daysInMonth = Math.min(
                31,
                new Date(selectedYear, selectedMonth + 1, 0).getDate()
              );

              filteredEmployees.forEach((employee) => {
                if (!newData[employee.id]) {
                  newData[employee.id] = {};
                }

                for (let day = 1; day <= daysInMonth; day++) {
                  const currentDate = new Date(
                    selectedYear,
                    selectedMonth,
                    day
                  );

                  // Compute weekly-off for this employee & date
                  const isWOForEmp = isWeeklyOffForEmployee(
                    currentDate,
                    employee,
                    weeklyOffPolicies,
                    employeeWeeklyOffMappings
                  );

                  // Compute default status that respects WO
                  const defaultStatus = isWOForEmp
                    ? "WO"
                    : getDefaultStatus(
                        currentDate,
                        employee,
                        weeklyOffPolicies,
                        employeeWeeklyOffMappings,
                        holidaysData,
                        holidayPolicies,
                        employeeHolidayMappings
                      );

                  const defaultStatusWithLock = isWOForEmp
                    ? { status: "WO", locked: false }
                    : getDefaultStatusWithLock(
                        currentDate,
                        employee.id,
                        weeklyOffPolicies,
                        employeeWeeklyOffMappings,
                        holidaysData,
                        holidayPolicies,
                        employeeHolidayMappings
                      );
                  if (defaultStatusWithLock && defaultStatusWithLock.locked)
                    continue;
                  if (!overrideWeeklyOff && (isWOForEmp || defaultStatus === "PH"))
                    continue;
                  newData[employee.id][day] = statusValue;
                  try {
                    if (changedRecordsRef?.current)
                      changedRecordsRef.current.set(`${employee.id}_${day}`, {
                        employeeId: employee.id,
                        day,
                        newStatus: statusValue,
                        override: overrideWeeklyOff === true,
                      });
                  } catch (e) {
                    console.debug("Mark All: failed to track change", e);
                  }
                  affectedCount++;
                }
              });

              showAlert(
                `Marked ${statusValue} for all days for all ${filteredEmployees.length} employee(s). Review and save when ready.`
              );
            } else if (type === "clear") {
              // New functionality: clear all attendance data but preserve weekly off and holiday policies
              const daysInMonth = Math.min(
                31,
                new Date(selectedYear, selectedMonth + 1, 0).getDate()
              );

              filteredEmployees.forEach((employee) => {
                if (!newData[employee.id]) {
                  newData[employee.id] = {};
                }

                for (let day = 1; day <= daysInMonth; day++) {
                  const currentDate = new Date(
                    selectedYear,
                    selectedMonth,
                    day
                  );

                  // Compute weekly-off for this employee & date
                  const isWOForEmp = isWeeklyOffForEmployee(
                    currentDate,
                    employee,
                    weeklyOffPolicies,
                    employeeWeeklyOffMappings
                  );

                  // Compute default status that respects WO
                  const defaultStatus = isWOForEmp
                    ? "WO"
                    : getDefaultStatus(
                        currentDate,
                        employee,
                        weeklyOffPolicies,
                        employeeWeeklyOffMappings,
                        holidaysData,
                        holidayPolicies,
                        employeeHolidayMappings
                      );

                  // Only clear if it's not a policy-based status (WO, PH)
                  const finalStatus = defaultStatus === "PR" ? "-" : defaultStatus;
                  newData[employee.id][day] = finalStatus;
                  try {
                    if (changedRecordsRef?.current)
                      changedRecordsRef.current.set(`${employee.id}_${day}`, {
                        employeeId: employee.id,
                        day,
                        newStatus: finalStatus,
                        override: false,
                      });
                  } catch (e) {
                    console.debug("Mark All: failed to track change", e);
                  }
                  affectedCount++;
                }
              });

              showAlert(
                `Cleared all attendance data for ${filteredEmployees.length} employee(s). Weekly off and holiday policies preserved.`
              );
            }

            return newData;
          });
          setTimeout(() => {
            try {
              if (statusCountsCache?.current) statusCountsCache.current.clear();
            } catch (e) {
              console.debug("Mark All: cache clear", e);
            }
          }, 150);
        },
        { timeout: 1000 }
      );
    },
    [
      isEditing,
      filteredEmployees,
      selectedYear,
      selectedMonth,
      getDefaultStatusWithLock,
      weeklyOffPolicies,
      employeeWeeklyOffMappings,
      holidaysData,
      holidayPolicies,
      employeeHolidayMappings,
      isWeeklyOffForEmployee,
      getDefaultStatus,
      allEmployees,
      showAlert,
    ]
  );

  const handleApplyFilters = useCallback((filters) => {
    setAppliedFilters(filters);
  }, []);

  const handleUploadAttendance = async (
    file,
    importYear,
    importMonth,
    options = {}
  ) => {
    try {
      const onProgress = options.onProgress || (() => {});

      onProgress(10, "Reading file...");
      const workbook = XLSX.read(await file.arrayBuffer(), { type: "array" });
      const rows = XLSX.utils.sheet_to_json(
        workbook.Sheets[workbook.SheetNames[0]],
        { defval: "" }
      );
      if (!rows || rows.length === 0) {
        throw new Error("No attendance records found in the file");
      }
      const headers = Object.keys(rows[0]);
      const monthCheck = validateAttendanceTemplateMonthColumn(
        rows,
        headers,
        importYear,
        importMonth
      );
      if (!monthCheck.ok) {
        throw new Error(monthCheck.message);
      }
      const parsedRecords = parseAttendanceFileFromRows(
        rows,
        importYear,
        importMonth
      );

      if (!parsedRecords || parsedRecords.length === 0) {
        throw new Error("No attendance records found in the file");
      }

      onProgress(30, `Parsed ${parsedRecords.length} records from file...`);

      // Convert parsed records to bulkMarkAttendance format
      const employeeAttendance = parsedRecords.map((record) => ({
        employeeCode: record.employeeCode,
        employeeName: record.employeeName,
        date: record.date,
        status: record.status,
        remarks: record.remarks || "",
      }));

      onProgress(
        50,
        `Sending ${employeeAttendance.length} records to server...`
      );

      // Send to backend - let backend handle validation and business logic
      const bulkPayload = {
        employeeAttendance: employeeAttendance,
      };

      const response = await attendanceService.bulkMarkAttendance(bulkPayload);

      onProgress(70, "Processing server response...");

      // Handle response from backend
      const successCount =
        response.count || (response.data ? response.data.length : 0);
      const skippedCount = response.skipped ? response.skipped.length : 0;

      let successMessage = `Successfully imported ${successCount} attendance records.`;

      if (skippedCount > 0) {
        const reasons = response.skipped
          .slice(0, 3)
          .map((s) => s.reason)
          .join("; ");
        successMessage += ` ${skippedCount} records were skipped (${reasons}${
          response.skipped.length > 3 ? "..." : ""
        })`;
      }

      setShowUploadPopup(false);
      toast.success(successMessage);

      onProgress(80, "Fetching updated attendance records...");
      // Fetch and refresh attendance data
      const historyResponse = await attendanceService.getAttendanceHistory(
        importMonth + 1,
        importYear
      );

      if (historyResponse && historyResponse.success) {
        const formattedData = {};
        const fetchDaysInMonth = Math.min(
          31,
          new Date(importYear, importMonth + 1, 0).getDate()
        );
        historyResponse.data.forEach((record) => {
          try {
            const recordDate = new Date(record.date);
            if (
              recordDate.getFullYear() === importYear &&
              recordDate.getMonth() === importMonth
            ) {
              const dateNum = recordDate.getDate();
              if (dateNum >= 1 && dateNum <= fetchDaysInMonth) {
                const empKey = String(record.employeeId);
                const dayKey = String(dateNum);
                if (!formattedData[empKey]) formattedData[empKey] = {};
                formattedData[empKey][dayKey] = record.status;
              }
            }
          } catch (err) {
            console.warn("Skipping malformed attendance record:", record);
          }
        });

        setAttendanceData(formattedData);
      } else {
        setAttendanceData({});
      }

      onProgress(100, "Complete!");
    } catch (err) {
      setError(err.message || "Failed to import attendance data");
      showAlert(err.message || "Failed to import attendance data", "error");
    }
  };

  const handleDownloadTemplate = async (month = null, year = null) => {
    if (!canDownloadAttendanceTemplate) {
      toast.error("You do not have permission to download attendance templates.");
      return;
    }
    try {
      const targetMonth =
        month !== null && month !== undefined ? Number(month) : selectedMonth; // 0-based
      const targetYear =
        year !== null && year !== undefined ? Number(year) : selectedYear;

      // Ensure template employee list matches Attendance module list for the selected month.
      // If popup month/year equals current grid month/year, use the exact filtered UI dataset.
      // Otherwise refetch and apply the same "active in month" logic used in grid fetch.
      let employeeIdsForTemplate = [];
      if (targetMonth === selectedMonth && targetYear === selectedYear) {
        employeeIdsForTemplate = (filteredEmployees || [])
          .map((e) => Number(e?.id))
          .filter((id) => Number.isFinite(id));
      } else {
        try {
          const empParams = {
            page: 1,
            limit: 10000,
            search: debouncedSearchTerm,
            fields: "id,joinDate,joiningDate,relievedDate,exitDate,relievingDate,resignationDate",
            excludeEmploymentTypes: "External,external",
            ...appliedFilters,
          };
          const empResp = await employeeService.getEmployees(empParams);
          const raw = empResp?.data || empResp?.employees || [];
          const monthStart = new Date(targetYear, targetMonth, 1);
          const monthEnd = new Date(targetYear, targetMonth + 1, 0, 23, 59, 59, 999);
          const monthFiltered = raw.filter((employee) => {
            const joinDateStr = employee.joinDate || employee.joiningDate;
            const relievedDateStr =
              employee.relievedDate ||
              employee.exitDate ||
              employee.relievingDate ||
              employee.resignationDate;

            const joinDate = joinDateStr ? new Date(joinDateStr) : null;
            const relievedDate = relievedDateStr ? new Date(relievedDateStr) : null;

            if (joinDate && !Number.isNaN(joinDate.getTime()) && joinDate > monthEnd) return false;
            if (relievedDate && !Number.isNaN(relievedDate.getTime()) && relievedDate < monthStart) return false;
            return true;
          });

          employeeIdsForTemplate = monthFiltered
            .map((e) => Number(e?.id))
            .filter((id) => Number.isFinite(id));
        } catch (e) {
          console.error("Failed to refetch employees for template month filter:", e);
        }
      }

      if (!employeeIdsForTemplate.length) {
        showAlert("No employees found for the selected month.", "info");
        return;
      }

      // Use backend-generated template but constrain with exact employee IDs.
      const response = await attendanceService.downloadTemplate(
        targetMonth,
        targetYear,
        employeeIdsForTemplate
      );
      // Post-process the template to blank out Relieved Date unless it falls within the selected month/year.
      // This preserves template structure (validations/dropdowns) by editing cells in-place.
      const tryGetArrayBuffer = async (maybe) => {
        if (!maybe) return null;
        if (maybe instanceof ArrayBuffer) return maybe;
        if (typeof Blob !== "undefined" && maybe instanceof Blob) return await maybe.arrayBuffer();
        // Axios may return Uint8Array or similar
        if (ArrayBuffer.isView(maybe) && maybe.buffer) return maybe.buffer;
        return null;
      };

      const parseExcelDateParts = (value) => {
        if (value == null || value === "") return null;
        // Excel serial number
        if (typeof value === "number" && Number.isFinite(value)) {
          try {
            const o = XLSX.SSF.parse_date_code(value);
            if (o && o.y && o.m && o.d) {
              return { year: o.y, month: o.m - 1, day: o.d };
            }
          } catch (e) {
            /* ignore */
          }
        }
        // JS Date
        if (value instanceof Date && !Number.isNaN(value.getTime())) {
          return { year: value.getFullYear(), month: value.getMonth(), day: value.getDate() };
        }
        // String date
        return parseDateComponents(String(value));
      };

      const ab = await tryGetArrayBuffer(response?.data);
      let downloadData = response?.data;
      try {
        if (ab) {
          const wb = XLSX.read(ab, { type: "array" });
          const firstSheetName = wb.SheetNames && wb.SheetNames[0];
          const ws = firstSheetName ? wb.Sheets[firstSheetName] : null;

          if (ws && ws["!ref"]) {
            const range = XLSX.utils.decode_range(ws["!ref"]);

            // Find header row + Relieved Date column by scanning the top few rows.
            let headerRow = null;
            let relievedCol = null;
            const maxScanRows = Math.min(range.e.r, 30);
            const maxScanCols = Math.min(range.e.c, 30);

            for (let r = range.s.r; r <= maxScanRows && headerRow == null; r++) {
              for (let c = range.s.c; c <= maxScanCols; c++) {
                const addr = XLSX.utils.encode_cell({ r, c });
                const cell = ws[addr];
                const v = cell ? (cell.w != null ? cell.w : cell.v) : "";
                const key = String(v || "")
                  .replace(/\s+/g, "")
                  .replace(/_/g, "")
                  .toLowerCase();
                if (key === "relieveddate" || key === "reliveddate" || key === "dateofrelieving") {
                  headerRow = r;
                  relievedCol = c;
                  break;
                }
              }
            }

            if (headerRow != null && relievedCol != null) {
              for (let r = headerRow + 1; r <= range.e.r; r++) {
                const addr = XLSX.utils.encode_cell({ r, c: relievedCol });
                const cell = ws[addr];
                if (!cell) continue;
                const parts = parseExcelDateParts(cell.v != null ? cell.v : cell.w);
                const isInMonth =
                  parts &&
                  Number.isFinite(parts.month) &&
                  Number.isFinite(parts.year) &&
                  parts.month === targetMonth &&
                  parts.year === targetYear;
                if (!isInMonth) {
                  // blank cell while keeping other worksheet features intact
                  delete ws[addr];
                }
              }
            }
          }

          const out = XLSX.write(wb, { bookType: "xlsx", type: "array" });
          downloadData = out;
        }
      } catch (e) {
        // If post-processing fails, fall back to original template.
        console.warn("Template post-process failed; downloading original template.", e);
        downloadData = response?.data;
      }

      const blob = new Blob([downloadData], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = buildAttendanceExcelFilename(
        "Attendance_Template",
        targetMonth,
        targetYear
      );
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      showAlert("Template has been downloaded.");
    } catch (err) {
      setError(err.message || "Failed to download template");
      showAlert(err.message || "Failed to download template", "error");
    }
  };

  const handleExportAttendance = async () => {
    // Kept as a utility but actual export is performed by handleConfirmExport
    return;
  };

  const handleExportMonthChange = (eventOrValue) => {
    const rawValue =
      typeof eventOrValue === "string"
        ? eventOrValue
        : eventOrValue?.target?.value || "";

    if (!rawValue) return;

    const [start] = rawValue.split("/");
    if (!start) return;
    const [yearString, monthString] = start.split("-");
    const parsedYear = parseInt(yearString, 10);
    const parsedMonth = parseInt(monthString, 10) - 1;
    if (Number.isNaN(parsedYear) || Number.isNaN(parsedMonth)) return;
    setExportYear(parsedYear);
    setExportMonth(Math.min(Math.max(parsedMonth, 0), 11));
  };

  // Helper function to generate attendance export buffer (used for both download and email attachment)
  const generateAttendanceExportBuffer = async (month, year, returnBuffer = false) => {
    try {
      // Client-side export: build a workbook with full month data per employee.
      // month is 0-based (0 = January, 11 = December)
      const daysInMonth = new Date(year, month + 1, 0).getDate();

      // Build header row: Employee Code, Employee Name, Designation, Department, Branch, Joining Date, Relieved Date, Day 1..N, Summary (PR, PH, WO, CL, EL, CO, CW, TL, LO, Pay Days)

      // Get summary column headers from statusDefinitions and include Pay Days
      const summaryColumns = [];
      try {
        Object.keys(statusDefinitions || {}).forEach((k) => {
          if (k && typeof k === "string") {
            // Use mapped display name if available
            const displayName =
              (attendanceMapping && attendanceMapping[k]) || k;
            summaryColumns.push(displayName);
          }
        });
      } catch (e) {
        // Fallback to standard columns
        ["PR", "PH", "WO", "CL", "EL", "CO", "CW", "LOP"]
          .map((k) => (attendanceMapping && attendanceMapping[k]) || k)
          .forEach((col) => summaryColumns.push(col));
      }
      summaryColumns.push("LOP Rev");
      summaryColumns.push("Pay Days");

      const header = [
        "Sl No",
        "Employee Code",
        "Employee Name",
        "Designation",
        "Department",
        "Branch",
        "Joining Date",
        "Relieved Date",
        ...Array.from({ length: daysInMonth }, (_, i) => String(i + 1)),
        ...summaryColumns,
      ];

      // Fetch ALL employees for the export (not just the paginated ones currently loaded)
      let allExportEmployees = [];
      let attendanceDataForExport = {};

      // Fetch employees in pages efficiently
      try {
        let page = 1;
        let hasMore = true;
        const pageSize = 500; // Fetch 500 at a time for efficiency

        while (hasMore) {
          const empResp = await employeeService.getEmployees({
            page,
            limit: pageSize,
            fields:
              "id,empCode,employeeId,code,name,joinDate,exitDate,relievedDate,resignationDate,firstName,lastName,user,fullName,employeeName,designation,designationName,department,departmentName,branch,branchName",
            excludeEmploymentTypes: "External,external",
          });

          if (
            empResp &&
            empResp.success &&
            Array.isArray(empResp.data) &&
            empResp.data.length > 0
          ) {
            allExportEmployees = [...allExportEmployees, ...empResp.data];
            page++;
            // Check if there might be more pages
            hasMore = empResp.data.length === pageSize;
          } else {
            hasMore = false;
          }

          // Yield to browser to show loading progress
          await new Promise((resolve) => setTimeout(resolve, 10));
        }
      } catch (e) {
        console.error("Error fetching employees for export:", e);
        // Fall back to currently loaded employees
        allExportEmployees = allEmployees || [];
      }

      // Fetch attendance data for the selected month for all employees
      let lopRevCountByEmp = {};
      try {
        const attResp = await attendanceService.getAttendanceHistory(
          month + 1,
          year,
          1,
          50000,
          null,
          null
        );
        if (attResp && attResp.success && Array.isArray(attResp.data)) {
          const fm = new Map();
          lopRevCountByEmp = {};
          for (let i = 0; i < attResp.data.length; i++) {
            const record = attResp.data[i];
            const empId = String(
              record.employeeId ?? record.employee_id ?? ""
            );
            const parsedDay = getDayOfMonthSafe(record.date);
            if (!parsedDay) continue;
            const day = String(parsedDay);
            if (!fm.has(empId)) fm.set(empId, {});
            fm.get(empId)[day] = record.status;
            if (
              String(record.remarks || "")
                .toLowerCase()
                .indexOf("lop reversal") !== -1
            ) {
              lopRevCountByEmp[empId] = (lopRevCountByEmp[empId] || 0) + 1;
            }
          }
          attendanceDataForExport = Object.fromEntries(fm);
        }
      } catch (e) {
        console.error("Error fetching attendance for export:", e);
        attendanceDataForExport = attendanceData || {};
      }

      const sourceEmployees = allExportEmployees;
      const exportAttendanceMap = attendanceDataForExport;

      // Build a lookup map from available sourceEmployees for quick resolution
      const empLookup = {};
      (sourceEmployees || []).forEach((e) => {
        try {
          if (e == null) return;
          const keys = [e.id, e.employeeId, e.empId, e.empCode, e.code];
          keys.forEach((k) => {
            if (k !== undefined && k !== null) empLookup[String(k)] = e;
          });
        } catch (err) {
          /* ignore */
        }
      });

      // Choose employees to export: use all fetched employees
      let employeesToExport =
        sourceEmployees && sourceEmployees.length > 0
          ? sourceEmployees
          : Object.keys(exportAttendanceMap || {}).map(
              (id) => empLookup[String(id)] || { id }
            );

      // Filter out employees based on join date and relieved date
      // Exclude employees who:
      // 1. Joined after the selected month (e.g., if exporting November, exclude employees who joined in December or later)
      // 2. Were relieved before the selected month (e.g., if exporting January, exclude employees relieved in December of previous year or earlier)
      
      const monthStart = new Date(year, month, 1);
      const monthEnd = new Date(year, month + 1, 0); // Last day of the month
      const monthStartNum = parseInt(
        year.toString() + String(month + 1).padStart(2, "0") + "01"
      );
      const monthEndNum = parseInt(
        year.toString() + String(month + 1).padStart(2, "0") + String(monthEnd.getDate()).padStart(2, "0")
      );

      // Helper function to parse date string to numeric format (YYYYMMDD)
      const parseDateToNum = (dateStr) => {
        if (!dateStr) return null;

        // Try ISO format first: YYYY-MM-DD
        let match = String(dateStr).match(/(\d{4})-(\d{2})-(\d{2})/);
        if (match) {
          return parseInt(match[1] + match[2] + match[3]);
        }

          // Try DD-MM-YYYY format
        match = String(dateStr).match(/(\d{2})-(\d{2})-(\d{4})/);
          if (match) {
          return parseInt(match[3] + match[2] + match[1]); // YYYY + MM + DD
        }

            // Try DD/MM/YYYY format
        match = String(dateStr).match(/(\d{2})\/(\d{2})\/(\d{4})/);
            if (match) {
          return parseInt(match[3] + match[2] + match[1]); // YYYY + MM + DD
        }

        // Try parsing as Date object
        try {
          const dateObj = new Date(dateStr);
                if (!isNaN(dateObj.getTime())) {
            return parseInt(
                    dateObj.getFullYear().toString() +
                      String(dateObj.getMonth() + 1).padStart(2, "0") +
                      String(dateObj.getDate()).padStart(2, "0")
                  );
                }
              } catch (e) {
                /* ignore */
              }

        return null;
      };

      employeesToExport = employeesToExport.filter((emp) => {
        if (!emp) return false; // exclude if no employee data

        // Check join date - exclude if employee joined after the selected month
        const joinDate =
          emp.joinDate ||
          emp.joiningDate;
        
        if (joinDate) {
          const joinNum = parseDateToNum(joinDate);
          // If join date is after the end of the selected month, exclude this employee
          if (joinNum && joinNum > monthEndNum) {
            return false; // exclude - joined after this month
          }
        }

        // Check relieved date - exclude if employee was relieved before the selected month
        const relievedDate =
          emp.relievedDate ||
          emp.exitDate ||
          emp.resignationDate ||
          emp.relievingDate;

        if (relievedDate) {
          const relievedNum = parseDateToNum(relievedDate);
          // If relieved date is before the start of the selected month, exclude this employee
        if (relievedNum && relievedNum < monthStartNum) {
          return false; // exclude - relieved before this month
        }
        }

        // Include employee if they were active during the selected month
        // (joined on or before the end of the month AND (not relieved OR relieved on or after the start of the month))
        return true;
      });

      // If the export is large, prefer server-side streaming export to avoid client CPU/memory bottlenecks.
      const SERVER_EXPORT_EMPLOYEE_THRESHOLD = 5000; // tuneable
      if (
        employeesToExport &&
        employeesToExport.length > SERVER_EXPORT_EMPLOYEE_THRESHOLD
      ) {
        try {
          const resp = await attendanceService.exportAttendance(
            month + 1,
            year
          );
          const blob = resp && resp.data ? resp.data : resp;
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = buildAttendanceExcelFilename(
            "Attendance_Export",
            month,
            year
          );
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
          showAlert("Attendance exported via server successfully.");
          setShowExportModal(false);
          setIsExporting(false);
          return;
        } catch (err) {
          // If server export fails, fall back to client-side generation
          console.error(
            "Server export failed, falling back to client export",
            err
          );
        }
      }

      const rows = [header];

      // Pre-build status mapping cache for performance (avoid repeated lookups)
      // Use exportMapping from component level for export codes
      // Half-day export: use halfDayDivider and halfDayPrefix from Attendance Configuration (e.g. "1/2EL+1/2ML")
      const divider =
        halfDayDivider != null && String(halfDayDivider).trim() !== ""
          ? String(halfDayDivider).trim()
          : "";
      const prefix =
        halfDayPrefix != null && String(halfDayPrefix).trim() !== ""
          ? String(halfDayPrefix).trim()
          : "";
      const statusMapCache = new Map();
      const mapStatus = (status) => {
        if (!status) return "";
        const cacheKey = String(status).toUpperCase();
        if (statusMapCache.has(cacheKey)) {
          return statusMapCache.get(cacheKey);
        }

        let exportStatus = status || "";
        if (exportStatus && typeof exportStatus === "string") {
          const lookup = String(exportStatus).toUpperCase().trim();
          // Use export mapping instead of display mapping for exports
          if (attendanceExportMapping && attendanceExportMapping[lookup]) {
            exportStatus = attendanceExportMapping[lookup];
          } else {
            try {
              const parts = splitIntoTwoCodes(exportStatus);
              if (parts && parts.length === 2) {
                const p0 =
                  (attendanceExportMapping &&
                    attendanceExportMapping[String(parts[0] || "").toUpperCase()]) ||
                  parts[0];
                const p1 =
                  (attendanceExportMapping &&
                    attendanceExportMapping[String(parts[1] || "").toUpperCase()]) ||
                  parts[1];
                const first = (p0 || "").trim();
                const second = (p1 || "").trim();
                // Format half-day using divider and optional prefix: e.g. "1/2EL+1/2ML" or "EL+ML"
                if (prefix.trim() !== "") {
                  exportStatus = `${prefix}${first}${divider}${prefix}${second}`;
                } else {
                  exportStatus = `${first}${divider}${second}`;
                }
              }
            } catch (e) {
              // ignore and keep original
            }
          }
        }
        statusMapCache.set(cacheKey, exportStatus || "");
        return exportStatus || "";
      };

      // Cache default statuses to avoid recalculation for employees with same policies
      const getDefaultStatusCached = (day, emp) => {
        const joinDate = emp.joinDate || emp.joiningDate;
        const relievedDate =
          emp.relievedDate ||
          emp.exitDate ||
          emp.resignationDate ||
          emp.relievingDate;

        // Quick check: if outside active period, return "-"
        const currentDate = new Date(year, month, day);

        // Helper to parse date string in multiple formats
        const parseDateNum = (dateStr) => {
          if (!dateStr) return null;

          // Try ISO format first: YYYY-MM-DD
          let match = String(dateStr).match(/(\d{4})-(\d{2})-(\d{2})/);
          if (match) {
            return parseInt(match[1] + match[2] + match[3]);
          }

          // Try DD-MMM-YY or DD/MM/YYYY or similar formats
          try {
            const dateObj = new Date(dateStr);
            if (!isNaN(dateObj.getTime())) {
              return parseInt(
                dateObj.getFullYear().toString() +
                  String(dateObj.getMonth() + 1).padStart(2, "0") +
                  String(dateObj.getDate()).padStart(2, "0")
              );
            }
          } catch (e) {
            /* ignore */
          }
          return null;
        };

        // Use numeric comparison to avoid timezone issues
        if (joinDate) {
          const joinNum = parseDateNum(joinDate);
          const currentNum = parseInt(
            currentDate.getFullYear().toString() +
              String(currentDate.getMonth() + 1).padStart(2, "0") +
              String(currentDate.getDate()).padStart(2, "0")
          );
          if (joinNum && currentNum < joinNum) return "-";
        }

        if (relievedDate) {
          const relievedNum = parseDateNum(relievedDate);
          const currentNum = parseInt(
            currentDate.getFullYear().toString() +
              String(currentDate.getMonth() + 1).padStart(2, "0") +
              String(currentDate.getDate()).padStart(2, "0")
          );
          if (relievedNum && currentNum > relievedNum) return "-";
        }

        // Use default status logic for active period
        try {
          const defaultStatusResult = getDefaultStatusWithLock(
            currentDate,
            emp,
            weeklyOffPolicies,
            employeeWeeklyOffMappings,
            holidaysData,
            holidayPolicies,
            employeeHolidayMappings
          );
          return defaultStatusResult.status;
        } catch (e) {
          return "";
        }
      };

      // Process employees in batches to avoid UI freeze
      const BATCH_SIZE = 50;
      const employeeCount = employeesToExport.length;

      for (
        let batchStart = 0;
        batchStart < employeeCount;
        batchStart += BATCH_SIZE
      ) {
        const batchEnd = Math.min(batchStart + BATCH_SIZE, employeeCount);

        for (let empIdx = batchStart; empIdx < batchEnd; empIdx++) {
          const emp = employeesToExport[empIdx];
          const rawId =
            emp &&
            (emp.id || emp.employeeId || emp.empId || emp.empCode || emp.code);
          const empId =
            rawId !== undefined && rawId !== null
              ? rawId
              : (emp && emp.id) || "";
          const empObj = empLookup[String(empId)] || emp || {};

          // Derive employee code and name from multiple possible fields
          const empCode =
            empObj.empCode ||
            empObj.employeeCode ||
            empObj.code ||
            empObj.employeeId ||
            "";
          const empName =
            empObj.name ||
            (empObj.user && empObj.user.name) ||
            empObj.employeeName ||
            empObj.fullName ||
            `${empObj.firstName || ""} ${empObj.lastName || ""}`.trim() ||
            "";

          // Extract additional employee details
          const designation =
            empObj.designation?.name || empObj.designationName || "";
          const department =
            empObj.department?.name || empObj.departmentName || "";
          const branch = empObj.branch?.name || empObj.branchName || "";
          
          // Convert dates to DD/MM/YYYY strings for strict formatting in export
          const parseDateForExport = (dateStr) => {
            if (!dateStr) return "";

            // Try parsing various date formats
            try {
              // Try ISO format first: YYYY-MM-DD
              let match = String(dateStr).match(/(\d{4})-(\d{2})-(\d{2})/);
              if (match) {
                const dt = new Date(parseInt(match[1]), parseInt(match[2]) - 1, parseInt(match[3]));
                return formatDate(dt);
              }
              
              // Try DD-MM-YYYY format
              match = String(dateStr).match(/(\d{2})-(\d{2})-(\d{4})/);
              if (match) {
                const dt = new Date(parseInt(match[3]), parseInt(match[2]) - 1, parseInt(match[1]));
                return formatDate(dt);
              }
              
              // Try DD/MM/YYYY format
              match = String(dateStr).match(/(\d{2})\/(\d{2})\/(\d{4})/);
              if (match) {
                const dt = new Date(parseInt(match[3]), parseInt(match[2]) - 1, parseInt(match[1]));
                return formatDate(dt);
              }
              
              // Try parsing as Date object
              const dateObj = new Date(dateStr);
              if (!isNaN(dateObj.getTime())) {
                return formatDate(dateObj);
              }
            } catch (e) {
              // If parsing fails, return null
            }
            
            return "";
          };
          
          const joiningDateRaw = empObj.joiningDate || empObj.joinDate || "";
          const joiningDate = parseDateForExport(joiningDateRaw);
          
          const relievedDateRaw =
            empObj.relievedDate ||
            empObj.exitDate ||
            empObj.relievingDate ||
            "";
          const relievedDateParsed = parseDateForExport(relievedDateRaw);
          // Only include relieved date in export if it falls in the export month; otherwise leave blank
          const relievedDate =
            relievedDateParsed &&
            (() => {
              const parts = relievedDateParsed.match(/(\d{2})\/(\d{2})\/(\d{4})/);
              if (!parts) return false;
              const m = parseInt(parts[2], 10) - 1;
              const y = parseInt(parts[3], 10);
              return m === month && y === year;
            })()
              ? relievedDateParsed
              : "";

          // Serial number starts from 1 for the first employee
          const serialNo = empIdx + 1;

          const row = [
            serialNo,
            empCode,
            empName,
            designation,
            department,
            branch,
            joiningDate, // DD/MM/YYYY string
            relievedDate, // DD/MM/YYYY only when relieved in this month; else ""
          ];

          // Get attendance data for this employee once
          const empAttendance =
            (attendanceData &&
              (attendanceData[empId] || attendanceData[String(empId)])) ||
            {};

          // Calculate status counts for this employee for the export summary
          const statusCountsForExport = {};
          try {
            Object.keys(statusDefinitions || {}).forEach((k) => {
              if (k && typeof k === "string")
                statusCountsForExport[k.toUpperCase()] = 0;
            });
          } catch (e) {
            ["PR", "PH", "WO", "CL", "EL", "CO", "CW", "LOP"].forEach((k) => {
              statusCountsForExport[k] = 0;
            });
          }

          let paidDaysForExport = 0;
          let unpaidDaysForExport = 0;

          for (let day = 1; day <= daysInMonth; day++) {
            // Check if we have manual attendance first (fastest path)
            let status = empAttendance[day];

            // Only calculate default if no manual entry
            if (!status || typeof status !== "string") {
              status = getDefaultStatusCached(day, empObj);
            }

            // Skip days before joining or after relieving for counting/export cell value.
            // Export requirement: keep these cells BLANK (not "-") for pre-DOJ / post-DOL days.
            const currentDateNum = year * 10000 + (month + 1) * 100 + day;
            const exportJoinDate =
              empObj.joinDate ||
              empObj.joiningDate ||
              empObj.dateOfJoin;
            if (exportJoinDate) {
              const joinParts = parseDateComponents(String(exportJoinDate));
              if (joinParts) {
                const joinDateNum =
                  joinParts.year * 10000 + (joinParts.month + 1) * 100 + joinParts.day;
                if (currentDateNum < joinDateNum) {
                  row.push("");
                  continue;
                }
              }
            }
            const exportRelievedDate =
              empObj.relievedDate ||
              empObj.exitDate ||
              empObj.resignationDate ||
              empObj.relievingDate ||
              empObj.dateOfRelieving ||
              "";
            if (exportRelievedDate) {
              const relievedParts = parseDateComponents(String(exportRelievedDate));
              if (relievedParts) {
                const relievedDateNum =
                  relievedParts.year * 10000 +
                  (relievedParts.month + 1) * 100 +
                  relievedParts.day;
                if (currentDateNum > relievedDateNum) {
                  row.push("");
                  continue;
                }
              }
            }

            // Normalize status and count it (same as UI: LWP -> LOP so export summary matches UI)
            let rawStatus = String(status || "").toUpperCase();
            if (rawStatus && reverseMapping && reverseMapping[rawStatus]) {
              rawStatus = reverseMapping[rawStatus].toUpperCase();
            }

            // Handle half-day: split and add 0.5 each so export counts match UI (e.g. PR+LOP -> PR 0.5, LOP 0.5)
            const twoParts = splitIntoTwoCodes(rawStatus);
            if (twoParts && twoParts.length === 2) {
              const first = normalizeStatus(twoParts[0].trim()).toUpperCase();
              const second = normalizeStatus(twoParts[1].trim()).toUpperCase();
              const weight = 0.5;
              for (const norm of [first, second]) {
                if (norm && norm !== "-" && statusCountsForExport[norm] !== undefined) {
                  statusCountsForExport[norm] += weight;
                  if (paidLeaveCodes && paidLeaveCodes.has(norm)) {
                    paidDaysForExport += weight;
                  }
                  if (unpaidLeaveCodes && unpaidLeaveCodes.has(norm)) {
                    unpaidDaysForExport += weight;
                  }
                }
              }
            } else {
              const normalizedForCount = normalizeStatus(rawStatus).toUpperCase();
              if (
                normalizedForCount &&
                normalizedForCount !== "-" &&
                statusCountsForExport[normalizedForCount] !== undefined
              ) {
                statusCountsForExport[normalizedForCount] += 1;
                if (paidLeaveCodes && paidLeaveCodes.has(normalizedForCount)) {
                  paidDaysForExport += 1;
                }
                if (unpaidLeaveCodes && unpaidLeaveCodes.has(normalizedForCount)) {
                  unpaidDaysForExport += 1;
                }
              }
            }

            // Map status and push
            row.push(mapStatus(status));
          }

          // Add summary columns for this employee
          try {
            Object.keys(statusDefinitions || {}).forEach((k) => {
              const upper = String(k || "").toUpperCase();
              row.push(statusCountsForExport[upper] || 0);
            });
          } catch (e) {
            // Fallback
            ["PR", "PH", "WO", "CL", "EL", "CO", "CW", "LOP"].forEach((k) => {
              row.push(statusCountsForExport[k] || 0);
            });
          }

          const lopRevVal =
            lopRevCountByEmp[String(empId)] ??
            lopRevCountByEmp[String(rawId)] ??
            0;
          row.push(lopRevVal);

          // Pay Days = sum of paid days only (match UI: do not subtract unpaid)
          row.push(paidDaysForExport);

          rows.push(row);
        }

        // Yield to browser to prevent UI freeze on large datasets
        if (batchEnd < employeeCount) {
          await new Promise((resolve) => setTimeout(resolve, 0));
        }
      }

      // Totals row removed as per user requirement

      // Always export as XLSX format (CSV generation removed as per user requirement)
      // Generate the Excel buffer
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.aoa_to_sheet(rows);

      // Set reasonable column widths for header columns + day columns
      const cols = [
        { wch: 8 },  // S.No
        { wch: 8 }, // Employee Code
        { wch: 25 }, // Employee Name
        { wch: 18 }, // Designation
        { wch: 18 }, // Department
        { wch: 15 }, // Branch
        { wch: 14 }, // Joining Date
        { wch: 14 }, // Relieved Date
      ];
      for (let i = 0; i < daysInMonth; i++) cols.push({ wch: 8 });
      worksheet["!cols"] = cols;

      // Set date format for Joining Date (column G, index 6) and Relieved Date (column H, index 7)
      const range = XLSX.utils.decode_range(worksheet["!ref"]);
      if (range) {
        // Joining Date column (index 6)
        const joiningDateCol = 6;
        // Relieved Date column (index 7)
        const relievedDateCol = 7;
        
        // Date format: "dd/mm/yyyy" or "mm/dd/yyyy" - using "dd/mm/yyyy" as standard
        const dateFormat = "dd/mm/yyyy";
        
        // Format all data rows (skip header row 0)
        for (let row = 1; row <= range.e.r; row++) {
          // Format Joining Date column
          const joiningDateCell = XLSX.utils.encode_cell({ r: row, c: joiningDateCol });
          if (worksheet[joiningDateCell] && worksheet[joiningDateCell].v instanceof Date) {
            worksheet[joiningDateCell].z = dateFormat;
          }
          
          // Format Relieved Date column
          const relievedDateCell = XLSX.utils.encode_cell({ r: row, c: relievedDateCol });
          if (worksheet[relievedDateCell] && worksheet[relievedDateCell].v instanceof Date) {
            worksheet[relievedDateCell].z = dateFormat;
          }
        }
      }

      XLSX.utils.book_append_sheet(workbook, worksheet, "Attendance");
      const excelBuffer = XLSX.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });

      // If returnBuffer is true, return the buffer as base64 for email attachment
      if (returnBuffer) {
        const base64 = btoa(
          new Uint8Array(excelBuffer).reduce(
            (data, byte) => data + String.fromCharCode(byte),
            ""
          )
        );
        return {
          buffer: base64,
          filename: buildAttendanceExcelFilenameStatic(
            "Attendance_Export",
            month,
            year
          ),
        };
      }

      // Otherwise, download the file
      const blob = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = buildAttendanceExcelFilenameStatic(
        "Attendance_Export",
        month,
        year
      );
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      return null;
    } catch (err) {
      console.error("Export buffer generation error:", err);
      throw err;
    }
  };

  const handleConfirmExport = async () => {
    try {
      setIsExporting(true);
      showAlert("Fetching complete employee data for export...", "info");
      await generateAttendanceExportBuffer(exportMonth, exportYear, false);
      showAlert("Attendance exported successfully.");
      setShowExportModal(false);
    } catch (err) {
      console.error("Export error:", err);
      setError(err.message || "Failed to export attendance");
      showAlert(err.message || "Failed to export attendance", "error");
    } finally {
      setIsExporting(false);
    }
  };

  const handleUploadLeaveDetails = async (
    file,
    importMonth,
    importYear,
    options = {}
  ) => {
    console.log("🚀 [handleUploadLeaveDetails] Starting bulk leave upload");
    console.log("📅 [handleUploadLeaveDetails] Import month/year:", importMonth + 1, importYear);
    
    try {
      const onProgress = options.onProgress || (() => {});

      onProgress(10, "Reading file...");
      console.log("📖 [handleUploadLeaveDetails] Step 1: Reading Excel file...");
      
      // keep exporting until async generation completes
      const workbook = XLSX.read(await file.arrayBuffer());
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const data = XLSX.utils.sheet_to_json(worksheet);

      console.log("✅ [handleUploadLeaveDetails] Step 1 Complete: File read successfully");
      console.log("📊 [handleUploadLeaveDetails] Total rows in file:", data.length);
      console.log("📋 [handleUploadLeaveDetails] Leave details data:", data);
      // importMonth is 0-based (0 = January, 11 = December), convert to 1-based for display
      console.log("📅 [handleUploadLeaveDetails] Import month/year:", importMonth + 1, importYear);

      onProgress(25, "Validating data...");
      console.log("🔍 [handleUploadLeaveDetails] Step 2: Validating and processing data...");
      
      // Process the leave details
      const newAttendanceData = { ...attendanceData };
      let processedCount = 0;
      let errorCount = 0;
      const errors = [];
      const totalRows = data.length;

      console.log("📝 [handleUploadLeaveDetails] Processing", totalRows, "rows...");
      console.log("📋 [handleUploadLeaveDetails] Data array:", data);
      console.log("📋 [handleUploadLeaveDetails] Data array length:", data.length);
      console.log("📋 [handleUploadLeaveDetails] Data array type:", Array.isArray(data));
      
      if (!Array.isArray(data) || data.length === 0) {
        console.error("❌ [handleUploadLeaveDetails] Data is not a valid array or is empty!");
        throw new Error("No data found in the uploaded file");
      }
      
      data.forEach((row, index) => {
        console.log(`🔄 [handleUploadLeaveDetails] Starting to process row ${index + 1} of ${totalRows}`);
        
        // Update progress every 5 rows
        if (index % 5 === 0) {
          const progress = Math.round(25 + (index / totalRows) * 70);
          onProgress(
            progress,
            `Processing row ${index + 1} of ${totalRows}...`
          );
        }
        // Convert to string first in case Excel returns numbers
        const employeeCode = String(
          row["Employee Code"] ||
          row["employeeCode"] ||
          ""
        ).trim();
        const employeeName = String(
          row["Employee Name"] ||
          row["employeeName"] ||
          ""
        ).trim();
        
        console.log(`\n📄 [handleUploadLeaveDetails] Processing Row ${index + 2}:`, {
          rowIndex: index + 2,
          rawRow: row,
          employeeCode,
          employeeName,
        });
        
        // Convert to string in case Excel returns numbers
        const dateFromStr = row["Date From"] || row["dateFrom"];
        const dateToStr = row["Date To"] || row["dateTo"];
        const leaveType = String(row["Leave Type"] || row["leaveType"] || "").trim();
        const leavePeriod = String(row["Leave Period"] || row["leavePeriod"] || "").trim();

        console.log(`📋 [handleUploadLeaveDetails] Row ${index + 2} extracted values:`, {
          employeeCode,
          employeeName: employeeName || "(not provided)",
          dateFromStr,
          dateToStr,
          leaveType,
          leavePeriod,
        });
        if (
          employeeCode &&
          dateFromStr &&
          dateToStr &&
          leaveType &&
          leavePeriod
        ) {
          // Find employee by code
          console.log(`🔍 [handleUploadLeaveDetails] Row ${index + 2}: Searching for employee with code:`, employeeCode);
          console.log(`📋 [handleUploadLeaveDetails] Row ${index + 2}: Total employees in system:`, allEmployees.length);
          
          const employee = allEmployees.find(
            (emp) => (emp.empCode || emp.employeeId || emp.code) === employeeCode
          );
          
          console.log(`👤 [handleUploadLeaveDetails] Row ${index + 2}: Employee found:`, employee ? {
            id: employee.id,
            code: employee.empCode || employee.employeeId || employee.code,
            name: employee.name || employee.user?.name,
          } : "NOT FOUND");

          if (employee) {
            // Name validation removed - not mandatory as per user requirement
            console.log(`✅ [handleUploadLeaveDetails] Row ${index + 2}: Employee found, proceeding with leave processing`);
            
            try {
              // Parse dates in DD/MM/YYYY or DD-MM-YYYY format (Indian format)
              // IMPORTANT: For Excel, please format date cells as TEXT and enter dates as DD/MM/YYYY or DD-MM-YYYY
              // to avoid Excel's automatic date format conversion issues
              const parseDate = (dateStr, dateLabel) => {
                  // If it's already a Date object, return it
                  if (dateStr instanceof Date) {
                    return dateStr;
                  }

                  // If it's a number (Excel date serial), convert it
                  // NOTE: Excel date serials can be ambiguous due to Excel's date format interpretation
                  // Recommend using TEXT format in Excel with DD/MM/YYYY or DD-MM-YYYY
                  if (typeof dateStr === "number") {
                    console.log(`  🔢 [handleUploadLeaveDetails] Row ${index + 2}: Detected Excel date number:`, dateStr);
                    console.warn(`  ⚠️ [handleUploadLeaveDetails] Row ${index + 2}: Excel date numbers can be unreliable. Consider using TEXT format (DD/MM/YYYY or DD-MM-YYYY) in Excel for accurate dates.`);
                    // Excel date serial number conversion
                    // Excel date 1 = January 1, 1900
                    // Excel incorrectly treats 1900 as a leap year (it wasn't)
                    // The standard conversion formula: (excelDate - 25569) * 86400000
                    // Where 25569 = days from Jan 1, 1900 to Jan 1, 1970 (JavaScript epoch)
                    // This accounts for Excel's leap year bug
                    const millisecondsSinceEpoch = (dateStr - 25569) * 86400000;
                    const date = new Date(millisecondsSinceEpoch);
                    console.log(`  ✅ [handleUploadLeaveDetails] Row ${index + 2}: Converted Excel date ${dateStr} to:`, date.toISOString(), `(${date.toLocaleDateString('en-IN')})`);
                    return date;
                  }

                  // Convert to string if it's not already
                  dateStr = String(dateStr).trim();

                  // Support both "/" and "-" delimiters for Indian date format (DD/MM/YYYY or DD-MM-YYYY)
                  let parts;
                  if (dateStr.includes("/")) {
                    parts = dateStr.split("/");
                  } else if (dateStr.includes("-")) {
                    parts = dateStr.split("-");
                  } else {
                    throw new Error(
                      `Invalid date format for ${dateLabel}. Expected DD/MM/YYYY or DD-MM-YYYY but got: ${dateStr}`
                    );
                  }

                  if (parts.length === 3) {
                    const day = parseInt(parts[0], 10);
                    const month = parseInt(parts[1], 10) - 1; // JavaScript months are 0-based
                    const year = parseInt(parts[2], 10);

                    // Validate day, month, year values
                    if (isNaN(day) || isNaN(month) || isNaN(year)) {
                      throw new Error(
                        `Invalid date values for ${dateLabel}. Expected DD/MM/YYYY or DD-MM-YYYY but got: ${dateStr}`
                      );
                    }

                    // Validate reasonable ranges (Indian format: DD/MM/YYYY)
                    if (day < 1 || day > 31) {
                      throw new Error(
                        `Invalid day (${day}) in ${dateLabel}. Expected day between 1-31. Format should be DD/MM/YYYY or DD-MM-YYYY. Got: ${dateStr}`
                      );
                    }
                    if (month < 0 || month > 11) {
                      throw new Error(
                        `Invalid month (${month + 1}) in ${dateLabel}. Expected month between 1-12. Format should be DD/MM/YYYY or DD-MM-YYYY. Got: ${dateStr}`
                      );
                    }
                    if (year < 1900 || year > 2100) {
                      throw new Error(
                        `Invalid year (${year}) in ${dateLabel}. Expected 4-digit year between 1900-2100. Format should be DD/MM/YYYY or DD-MM-YYYY. Got: ${dateStr}`
                      );
                    }

                    const parsedDate = new Date(year, month, day);
                    console.log(`  ✅ [handleUploadLeaveDetails] Row ${index + 2}: Parsed ${dateLabel} string "${dateStr}" to:`, parsedDate.toISOString(), `(${parsedDate.toLocaleDateString('en-IN')})`);
                    return parsedDate;
                  }
                  throw new Error(
                    `Invalid date format for ${dateLabel}. Expected DD/MM/YYYY or DD-MM-YYYY but got: ${dateStr}`
                  );
                };

                const fromDate = parseDate(dateFromStr, "Date From");
                const toDate = parseDate(dateToStr, "Date To");
                
                console.log(`📅 [handleUploadLeaveDetails] Row ${index + 2}: Parsed dates:`, {
                  fromDate: fromDate.toISOString(),
                  toDate: toDate.toISOString(),
                });

                if (isNaN(fromDate.getTime()) || isNaN(toDate.getTime())) {
                  console.error(`❌ [handleUploadLeaveDetails] Row ${index + 2}: Invalid date parsed:`, {
                    fromDate: fromDate.toString(),
                    toDate: toDate.toString(),
                  });
                  throw new Error("Invalid date");
                }
                
                console.log(`✅ [handleUploadLeaveDetails] Row ${index + 2}: Dates validated successfully`);

                // Validate employee's join/relieved dates
                let empJoinDate =
                  employee.joinDate ||
                  employee.joiningDate ||
                  employee.dateOfJoining ||
                  employee.doj;
                let empReliefDate =
                  employee.exitDate ||
                  employee.relievingDate ||
                  employee.dateOfExit;

                // Parse joining date if it's a string
                if (empJoinDate) {
                  if (typeof empJoinDate === "string") {
                    empJoinDate = new Date(empJoinDate);
                  } else if (typeof empJoinDate === "number") {
                    // Handle Excel date numbers
                    const excelEpoch = new Date(1900, 0, 1);
                    empJoinDate = new Date(
                      excelEpoch.getTime() +
                        (empJoinDate - 1) * 24 * 60 * 60 * 1000
                    );
                  }
                }

                // Parse relief date if it's a string
                if (empReliefDate) {
                  if (typeof empReliefDate === "string") {
                    empReliefDate = new Date(empReliefDate);
                  } else if (typeof empReliefDate === "number") {
                    // Handle Excel date numbers
                    const excelEpoch = new Date(1900, 0, 1);
                    empReliefDate = new Date(
                      excelEpoch.getTime() +
                        (empReliefDate - 1) * 24 * 60 * 60 * 1000
                    );
                  }
                }

                // Check if leave start date is AFTER joining date
                if (empJoinDate && !isNaN(empJoinDate.getTime())) {
                  if (fromDate < empJoinDate) {
                    throw new Error(
                      `Bulk leave upload failed: Cannot apply leave before joining date. Employee ${employeeName || employeeCode} (${employeeCode}) has joining date ${empJoinDate.toLocaleDateString(
                        "en-IN"
                      )}, but leave start date is ${fromDate.toLocaleDateString("en-IN")}. Please ensure leave dates are within the employee's active period.`
                    );
                  }
                }

                // Check if leave end date is AFTER relieving/resignation date (if employee has been relieved)
                if (empReliefDate && !isNaN(empReliefDate.getTime())) {
                  if (toDate > empReliefDate) {
                    throw new Error(
                      `Bulk leave upload failed: Cannot apply leave beyond resignation/relieving date. Employee ${employeeName || employeeCode} (${employeeCode}) has resignation date ${empReliefDate.toLocaleDateString(
                        "en-IN"
                      )}, but leave end date is ${toDate.toLocaleDateString("en-IN")}. Please ensure leave dates are within the employee's active period.`
                    );
                  }
                  // Also check if fromDate is after relieving date
                  if (fromDate > empReliefDate) {
                    throw new Error(
                      `Bulk leave upload failed: Cannot apply leave after resignation/relieving date. Employee ${employeeName || employeeCode} (${employeeCode}) has resignation date ${empReliefDate.toLocaleDateString(
                        "en-IN"
                      )}, but leave start date is ${fromDate.toLocaleDateString("en-IN")}. Please ensure leave dates are within the employee's active period.`
                    );
                  }
                }

                console.log(`🔄 [handleUploadLeaveDetails] Row ${index + 2}: Processing date range...`);
                const currentDate = new Date(fromDate);
                let daysProcessed = 0;
                
                while (currentDate <= toDate) {
                  const day = currentDate.getDate();
                  const month = currentDate.getMonth();
                  const year = currentDate.getFullYear();

                  console.log(`  📅 [handleUploadLeaveDetails] Row ${index + 2}: Checking date ${day}/${month + 1}/${year} (importMonth: ${importMonth}, importYear: ${importYear})`);

                  if (month === importMonth && year === importYear) {
                    console.log(`  ✅ [handleUploadLeaveDetails] Row ${index + 2}: Date ${day}/${month + 1}/${year} matches import month, applying leave`);
                    if (!newAttendanceData[employee.id]) {
                      newAttendanceData[employee.id] = {};
                    }

                    // Handle leave period logic
                    if (leavePeriod === "FD") {
                      // Full day leave
                      newAttendanceData[employee.id][day] = leaveType;
                    } else if (leavePeriod === "FH") {
                      // First half leave
                      const existingStatus =
                        newAttendanceData[employee.id][day] || "PR";

                      if (
                        existingStatus.length > 2 &&
                        !attendanceStatuses.includes(existingStatus)
                      ) {
                        // Already has half day status, update the first half
                        const secondHalf = existingStatus.substring(2, 4);
                        newAttendanceData[employee.id][
                          day
                        ] = `${leaveType}${secondHalf}`;
                      } else {
                        // Set first half status
                        newAttendanceData[employee.id][day] = `${leaveType}PR`;
                      }
                    } else if (leavePeriod === "SH") {
                      // Second half leave
                      const existingStatus =
                        newAttendanceData[employee.id][day] || "PR";

                      if (
                        existingStatus.length > 2 &&
                        !attendanceStatuses.includes(existingStatus)
                      ) {
                        // Already has half day status, update the second half
                        const firstHalf = existingStatus.substring(0, 2);
                        newAttendanceData[employee.id][
                          day
                        ] = `${firstHalf}${leaveType}`;
                      } else {
                        // Set second half status
                        newAttendanceData[employee.id][day] = `PR${leaveType}`;
                      }
                    }

                    daysProcessed++;
                    processedCount++;
                    console.log(`  ✅ [handleUploadLeaveDetails] Row ${index + 2}: Applied ${leaveType} (${leavePeriod}) for day ${day}`);
                  } else {
                    console.log(`  ⏭️ [handleUploadLeaveDetails] Row ${index + 2}: Date ${day}/${month + 1}/${year} is outside import month, skipping`);
                  }

                  currentDate.setDate(currentDate.getDate() + 1);
                }
                
                console.log(`✅ [handleUploadLeaveDetails] Row ${index + 2}: Processed ${daysProcessed} days for employee ${employeeCode}`);
              } catch (dateError) {
                console.error(`❌ [handleUploadLeaveDetails] Row ${index + 2}: Date processing error:`, dateError);
                errorCount++;
                errors.push(
                  `Row ${
                    index + 2
                  }: ❌ Date Error for ${employeeName || employeeCode} (${employeeCode}) - ${
                    dateError.message
                  }`
                );
              }
          } else {
            console.error(`❌ [handleUploadLeaveDetails] Row ${index + 2}: Employee not found - Code:`, employeeCode);
            errorCount++;
            errors.push(
              `Row ${
                index + 2
              }: ❌ Employee Not Found - Code "${employeeCode}" does not exist in system. Check the employee code and try again.`
            );
          }
        } else {
          const missingFields = [];
          if (!employeeCode) missingFields.push("Employee Code");
          if (!dateFromStr) missingFields.push("Date From");
          if (!dateToStr) missingFields.push("Date To");
          if (!leaveType) missingFields.push("Leave Type");
          if (!leavePeriod) missingFields.push("Leave Period");

          console.error(`❌ [handleUploadLeaveDetails] Row ${index + 2}: Missing required fields:`, missingFields);
          errorCount++;
          errors.push(
            `Row ${
              index + 2
            }: ⚠️ Missing Required Fields - ${missingFields.join(", ")} (Emp: ${
              employeeCode || "N/A"
            })`
          );
        }
      });

      console.log(`\n📊 [handleUploadLeaveDetails] Processing complete:`, {
        totalRows,
        processedCount,
        errorCount,
        errors: errors.length,
      });

      onProgress(95, "Saving data to backend...");
      console.log("💾 [handleUploadLeaveDetails] Step 3: Saving data to backend...");

      // Transform and save leave data to backend
      try {
        console.log("🔄 [handleUploadLeaveDetails] Transforming attendance data...");
        const yearMonthPrefix = `${importYear}-${String(
          importMonth + 1
        ).padStart(2, "0")}-`;
        
        console.log("📅 [handleUploadLeaveDetails] Year-month prefix:", yearMonthPrefix);
        console.log("📊 [handleUploadLeaveDetails] Attendance data entries:", Object.keys(newAttendanceData).length);
        
        const employeeAttendance = Object.entries(newAttendanceData).flatMap(
          ([empId, days]) =>
            Object.entries(days).map(([day, status]) => ({
              employeeId: parseInt(empId, 10),
              date: `${yearMonthPrefix}${String(day).padStart(2, "0")}`,
              status,
              remarks: "Leave uploaded via bulk import",
            }))
        );

        console.log("✅ [handleUploadLeaveDetails] Transformed attendance records:", employeeAttendance.length);
        console.log("📋 [handleUploadLeaveDetails] Sample records:", employeeAttendance.slice(0, 3));

        if (employeeAttendance.length > 0) {
          // Build correct date string: importMonth is 0-indexed (0=Jan, 11=Dec)
          const dateStr = `${importYear}-${String(importMonth + 1).padStart(
            2,
            "0"
          )}-01`;
          const savePayload = {
            date: dateStr,
            employeeAttendance,
          };

          // Debug log to verify payload structure
          console.log("📤 Sending payload to backend:", {
            date: savePayload.date,
            attendanceCount: savePayload.employeeAttendance.length,
            sampleRecord: savePayload.employeeAttendance[0],
          });

          // Validate payload before sending
          if (
            !savePayload.date ||
            !Array.isArray(savePayload.employeeAttendance)
          ) {
            throw new Error(
              "Invalid payload structure: missing date or employeeAttendance array"
            );
          }

          if (
            savePayload.employeeAttendance.length === 0 ||
            !savePayload.employeeAttendance[0].employeeId ||
            !savePayload.employeeAttendance[0].date
          ) {
            throw new Error(
              "Invalid attendance records: missing required fields (employeeId, date)"
            );
          }

          // Save to backend using bulkMarkAttendance API
          console.log("📤 [handleUploadLeaveDetails] Sending payload to backend API...");
          console.log("📦 [handleUploadLeaveDetails] Payload structure:", {
            date: savePayload.date,
            recordCount: savePayload.employeeAttendance.length,
            sampleRecord: savePayload.employeeAttendance[0],
          });
          
          const response = await attendanceService.bulkMarkAttendance(
            savePayload,
            dateStr
          );

          console.log("📥 [handleUploadLeaveDetails] Backend response received:", response);

          // Handle the response according to the expected format
          if (response && response.success) {
            console.log(
              `✅ [handleUploadLeaveDetails] Leave data saved to backend successfully. Processed: ${response.count || response.data?.count || 'unknown'} records`
            );

            // Update frontend state
            console.log("🔄 [handleUploadLeaveDetails] Updating frontend attendance data...");
            setAttendanceData(newAttendanceData);

            // If there were skipped records from backend, log them
            if (response.skipped && response.skipped.length > 0) {
              console.warn(
                `⚠️ [handleUploadLeaveDetails] ${response.skipped.length} records were skipped by backend:`,
                response.skipped
              );
            }
          } else {
            console.error(
              "❌ [handleUploadLeaveDetails] Backend response indicates failure:",
              response
            );
            setAttendanceData(newAttendanceData);
            throw new Error(response?.message || "Backend save failed");
          }
        } else {
          console.warn("⚠️ [handleUploadLeaveDetails] No attendance records to save");
        }
      } catch (saveError) {
        console.error("❌ [handleUploadLeaveDetails] Error saving to backend:", saveError);
        console.error("❌ [handleUploadLeaveDetails] Error details:", {
          message: saveError.message,
          stack: saveError.stack,
          name: saveError.name,
        });
        // Still show success if frontend save worked, but warn about backend
        showAlert(
          `Leave data processed but there was an issue saving to backend: ${saveError.message}`,
          "warning"
        );
        throw saveError; // Re-throw to be caught by outer try-catch
      }

      if (errorCount > 0) {
        onProgress(100, "Completed with errors");
        // Return errors for display in modal
        return { errors, processedCount, errorCount, totalRows: data.length };
      } else {
        onProgress(100, "Complete!");
        return {
          errors: [],
          processedCount,
          errorCount: 0,
          totalRows: data.length,
        };
      }
    } catch (err) {
      console.error("❌ [handleUploadLeaveDetails] Fatal error caught:", err);
      console.error("❌ [handleUploadLeaveDetails] Error name:", err.name);
      console.error("❌ [handleUploadLeaveDetails] Error message:", err.message);
      console.error("❌ [handleUploadLeaveDetails] Error stack:", err.stack);
      setError(err.message || "Failed to process leave details");
      showAlert(err.message || "Failed to process leave details", "error");
      return { errors: [], processedCount: 0, errorCount: 0, totalRows: 0 };
    }
  };

  const handleDownloadLeaveTemplate = async () => {
    try {
      // Create sample leave data with new format
      const sampleData = [
        {
          "Sl No": 1,
          "Employee Code": "EMP001",
          "Employee Name": "John Doe",
          "Date From": "15/01/2024",
          "Date To": "17/01/2024",
          "Leave Type": "CL",
          "Leave Period": "FD",
        },
        {
          "Sl No": 2,
          "Employee Code": "EMP002",
          "Employee Name": "Jane Smith",
          "Date From": "20/01/2024",
          "Date To": "20/01/2024",
          "Leave Type": "EL",
          "Leave Period": "FH",
        },
      ];

      // Create workbook
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(sampleData);

      // Set column widths
      worksheet["!cols"] = [
        { wch: 8 }, // Sl No
        { wch: 15 }, // Employee Code
        { wch: 20 }, // Employee Name
        { wch: 12 }, // Date From
        { wch: 12 }, // Date To
        { wch: 12 }, // Leave Type
        { wch: 15 }, // Leave Period
      ];

      // Add data validation for Leave Type column (column E)
      const leaveTypeValidation = {
        type: "list",
        allowBlank: false,
        formula1: '"PL,CL,EL,CO,CW,LOP"',
        showErrorMessage: true,
        errorTitle: "Invalid Leave Type",
        error: "Please select a valid leave type from the dropdown",
      };

      // Add data validation for Leave Period column (column F)
      const leavePeriodValidation = {
        type: "list",
        allowBlank: false,
        formula1: '"FD,FH,SH"',
        showErrorMessage: true,
        errorTitle: "Invalid Leave Period",
        error:
          "Please select FD (Full Day), FH (First Half), or SH (Second Half)",
      };

      // Apply validations to the range (excluding header row)
      const range = XLSX.utils.decode_range(worksheet["!ref"]);
      const leaveTypeRange = `E2:E${range.e.r + 1}`;
      const leavePeriodRange = `F2:F${range.e.r + 1}`;

      // Add validation ranges
      if (!worksheet["!dataValidation"]) {
        worksheet["!dataValidation"] = [];
      }

      worksheet["!dataValidation"].push({
        ref: leaveTypeRange,
        ...leaveTypeValidation,
      });

      worksheet["!dataValidation"].push({
        ref: leavePeriodRange,
        ...leavePeriodValidation,
      });

      XLSX.utils.book_append_sheet(workbook, worksheet, "Leave Details");

      // Generate and download file
      const excelBuffer = XLSX.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });
      const blob = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = buildAttendanceExcelFilename(
        "Leave_Details_Template",
        selectedMonth,
        selectedYear
      );
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      showAlert(
        "Leave template has been downloaded with dropdown validations."
      );
    } catch (err) {
      setError(err.message || "Failed to download leave template");
      showAlert(err.message || "Failed to download leave template", "error");
    }
  };

  // Parser for Format A: one row per employee with day columns (1..N)
  const parseAttendanceFileFromRows = (rows, targetYear, targetMonth) => {
    if (!rows || rows.length === 0) return [];

    // Parse template format: EmployeeCode, Name, JoiningDate, RelievedDate, Month, 1-31
    const headers = Object.keys(rows[0]);

    // Find column mappings for your template
    const idCol =
      headers.find((h) => /employee[\s_]*id|emp[\s_]*id/i.test(h)) ||
      headers.find((h) =>
        /employee[\s_]*code|emp[\s_]*code|employeecode|empcode|code/i.test(h)
      ) ||
      headers.find((h) => !/sl[\s_.-]*no|serial/i.test(h)) ||
      headers[0];
    const nameCol = headers.find((h) =>
      /employeename|empname|name|employee_name|emp_name/i.test(h)
    );
    const monthCol =
      headers.find((h) => String(h).trim().toLowerCase() === "month") ||
      headers.find((h) => /month|month_year|period/i.test(h));

    // Find day columns (1-31) - these are numeric
    const dayCols = headers
      .filter((h) => {
        const num = Number(h);
        return !Number.isNaN(num) && num >= 1 && num <= 31;
      })
      .sort((a, b) => Number(a) - Number(b));

    console.debug("Parse template - headers:", headers);
    console.debug("Parse template - day columns:", dayCols);

    const entries = [];
    rows.forEach((r, rowIdx) => {
      const rawId = r[idCol];
      if (
        rawId === undefined ||
        rawId === null ||
        String(rawId).trim() === ""
      ) {
        return; // Skip rows without employee code
      }

      const employeeCode = String(rawId).trim();
      const employeeName = nameCol ? String(r[nameCol] || "").trim() : "";

      // Month/year: prefer "Month" column (MM/YYYY etc.); validated upstream for imports
      let rowYear = targetYear;
      let rowMonth = targetMonth;

      if (monthCol && r[monthCol] !== undefined && r[monthCol] !== null) {
        const trimmed = String(r[monthCol]).trim();
        if (trimmed !== "") {
          const parsed = parseAttendanceTemplateMonthValue(r[monthCol]);
          if (parsed) {
            rowYear = parsed.year;
            rowMonth = parsed.month;
          }
        }
      }

      // Extract all day cells: non-blank as given, blank/empty as "-" (clear that day)
      dayCols.forEach((dayCol) => {
        const statusRaw = String(r[dayCol] ?? "").trim();

        // Blank, "-", or "no data" → treat as "clear this day" (upload as "-")
        const statusToSend =
          !statusRaw || statusRaw === "-" || statusRaw.toLowerCase() === "no data"
            ? "-"
            : statusRaw;

        const dayNum = Number(dayCol);
        if (Number.isNaN(dayNum) || dayNum < 1 || dayNum > 31) {
          return;
        }

        // Build the date in YYYY-MM-DD format
        const date = `${rowYear}-${String(rowMonth + 1).padStart(
          2,
          "0"
        )}-${String(dayNum).padStart(2, "0")}`;

        entries.push({
          employeeCode,
          employeeName,
          date,
          status: statusToSend,
          remarks: "",
        });

        if (ENABLE_DEBUG_LOGS) {
          console.debug(
            `Parse: Row ${
              rowIdx + 2
            } - ${employeeCode} Day ${dayNum}: ${statusRaw}`
          );
        }
      });
    });

    console.debug(
      `Parse complete: ${entries.length} changed attendance records extracted from ${rows.length} employee rows`
    );
    return entries;
  };

  const parseAttendanceFile = async (file, targetYear, targetMonth) => {
    const workbook = XLSX.read(await file.arrayBuffer(), { type: "array" });
    const sheetName = workbook.SheetNames[0];
    const worksheet = workbook.Sheets[sheetName];
    const rows = XLSX.utils.sheet_to_json(worksheet, { defval: "" });
    return parseAttendanceFileFromRows(rows, targetYear, targetMonth);
  };

  // Handler to import file and call bulkMarkAttendance
  const handleImportFile = async (
    file,
    year = exportYear,
    month = exportMonth,
    options = {}
  ) => {
    try {
      if (!file) return;
      const onProgress = options?.onProgress || (() => {});

      // Prefer backend import so data persists in DB and survives refresh.
      // Fallback to client-side parsing + /attendance/bulk-mark if backend import isn't available.
      try {
        onProgress(5, "Uploading file to server...");
        const formData = new FormData();
        formData.append("file", file);
        formData.append("year", String(year));
        // backend commonly expects month as 1-12
        formData.append("month", String(Number(month) + 1));

        const importResp = await attendanceService.importAttendance(formData);
        // If backend returns a structured error, throw so we can fall back / show message
        if (importResp && importResp.success === false) {
          throw new Error(importResp.message || "Import failed");
        }

        onProgress(70, "Refreshing attendance from server...");

        // Invalidate all frontend caches so subsequent searches/refreshes hit fresh DB data
        try {
          attendanceCacheRef.current.clear();
          employeesCacheRef.current.clear();
          if (statusCountsCache?.current) statusCountsCache.current.clear();
        } catch (_e) { /* ignore */ }

        // Instead of doing a partial fetch here (which would only get 20 rows and wipe
        // the rest), trigger a full re-fetch via fetchData which handles chunking,
        // pagination and proper attendanceData merging for ALL employees.
        try {
          attendanceFetchInFlightRef.current = false;
          attendanceFetchKeyRef.current = "";
          await fetchData();
        } catch (refreshErr) {
          console.warn("Post-import fetchData refresh failed:", refreshErr);
        }

        onProgress(100, "Complete!");
        showAlert("Attendance imported successfully.", "success");
        setShowUploadPopup(false);
        return;
      } catch (serverImportErr) {
        // Backend import not available / failed. Fall back to legacy client parsing route below.
        console.warn(
          "Backend import failed; falling back to client-side import:",
          serverImportErr
        );
      }

      setIsExporting(true);
      setIsImporting(true);
      const workbook = XLSX.read(await file.arrayBuffer(), { type: "array" });
      const rows = XLSX.utils.sheet_to_json(
        workbook.Sheets[workbook.SheetNames[0]],
        { defval: "" }
      );
      if (!rows || rows.length === 0) {
        showAlert(
          "No attendance entries found in the uploaded file.",
          "warning"
        );
        setIsImporting(false);
        setIsExporting(false);
        return;
      }
      const headers = Object.keys(rows[0]);
      const monthCheck = validateAttendanceTemplateMonthColumn(
        rows,
        headers,
        year,
        month
      );
      if (!monthCheck.ok) {
        showAlert(monthCheck.message, "error");
        setIsImporting(false);
        setIsExporting(false);
        return;
      }
      const items = parseAttendanceFileFromRows(rows, year, month);
      if (!items || items.length === 0) {
        showAlert(
          "No attendance entries found in the uploaded file.",
          "warning"
        );
        setIsImporting(false);
        setIsExporting(false);
        return;
      }

      // Validate parsed items before sending
      const daysInMonth = new Date(year, month + 1, 0).getDate();
      const normAttendanceStatuses = (attendanceStatuses || []).map((s) =>
        String(s).toUpperCase()
      );
      
      // Build list of valid import codes for validation
      const validImportCodes = new Set();
      if (importReverseMapping) {
        // Add all import codes (keys of importReverseMapping)
        Object.keys(importReverseMapping).forEach(code => validImportCodes.add(code.toUpperCase()));
      }
      // Also include original codes as fallback (in case import codes match original)
      normAttendanceStatuses.forEach(code => validImportCodes.add(code));

      const valid = [];
      const invalid = [];

      const makeVariants = (raw) => {
        const s0 = raw === undefined || raw === null ? "" : String(raw);
        const sTrim = s0.trim();
        const up = sTrim.toUpperCase();
        const alnum = up.replace(/[^A-Z0-9]/g, "");
        const noLeadingZeros = alnum.replace(/^0+/, "");
        const noZerosAfterLetters = alnum.replace(/([A-Z]+)0+/, "$1");
        return Array.from(
          new Set([
            sTrim,
            up,
            alnum,
            noLeadingZeros,
            noZerosAfterLetters,
          ]).values()
        ).filter(Boolean);
      };

      const parseImportDateParts = (dateStr) => {
        const isoMatch = String(dateStr || "")
          .trim()
          .match(/^(\d{4})-(\d{2})-(\d{2})$/);
        if (!isoMatch) return null;
        return {
          year: Number(isoMatch[1]),
          month: Number(isoMatch[2]) - 1,
          day: Number(isoMatch[3]),
        };
      };

      const employeesList = Array.isArray(allEmployees) ? allEmployees : [];
      const employeeLookup = new Map();
      employeesList.forEach((emp) => {
        const keys = [
          emp?.id,
          emp?.employeeId,
          emp?.empId,
          emp?.code,
          emp?.empCode,
          emp?.employeeCode,
          emp?.user?.employeeCode,
        ];
        keys.forEach((key) => {
          makeVariants(key).forEach((variant) => {
            if (!employeeLookup.has(variant)) {
              employeeLookup.set(variant, emp);
            }
          });
        });
      });

      const resolveEmployeeByIdentifier = (identifier) => {
        const variants = makeVariants(identifier);
        for (const variant of variants) {
          const match = employeeLookup.get(variant);
          if (match) return match;
        }
        return null;
      };

      // Use a local copy of allEmployees that we can augment synchronously
      // when we auto-fetch missing employee records. React state updates are
      // asynchronous, so relying on `setAllEmployees` alone would not make the
      // newly fetched employees available to the validation loop running in
      // this same function call. We therefore maintain `localAllEmployees`
      // and have `findEmployee` consult it.
      // NOTE: Frontend validation removed - backend handles all validation

      // Helper to map incoming raw status to canonical status string
      // For imports, prioritize import code mapping, then fall back to custom/display mapping
      const mapToCanonical = (rawStatus) => {
        const up = String(rawStatus || "")
          .toUpperCase()
          .trim();
        if (!up) return "-";
        if (up === "-") return "-";
        
        // First check import reverse mapping (import code -> original code)
        if (importReverseMapping && importReverseMapping[up]) {
          return importReverseMapping[up];
        }
        
        // Fall back to custom code reverse mapping
        if (reverseMapping && reverseMapping[up]) return reverseMapping[up];
        
        // Try splitting into two codes (half-day) and map each part
        const parts = splitIntoTwoCodes(up);
        if (parts && parts.length === 2) {
          const mapPart = (s) => {
            const u = String(s || "")
              .toUpperCase()
              .trim();
            if (!u) return "";
            // Check import mapping first, then custom mapping
            return (
              (importReverseMapping && importReverseMapping[u]) ||
              (reverseMapping && reverseMapping[u]) ||
              normalizeStatus(u).toUpperCase()
            );
          };
          return `${mapPart(parts[0])}${mapPart(parts[1])}`;
        }
        return normalizeStatus(up).toUpperCase();
      };

      // Pre-filter: Only check rows that actually changed from current attendance data
      // This avoids re-validating unchanged rows on subsequent imports
      const rowsToCheck = items
        .map((it) => ({
          ...it,
          _matchedEmployee: resolveEmployeeByIdentifier(it.employeeCode),
        }))
        .filter((it) => {
          const dateParts = parseImportDateParts(it.date);
          const dayOfMonth = dateParts ? String(dateParts.day) : "";
          const employeeKeys = [
            it?._matchedEmployee?.id,
            it?._matchedEmployee?.employeeId,
            it?._matchedEmployee?.code,
            it?._matchedEmployee?.empCode,
            it?._matchedEmployee?.employeeCode,
            it?.employeeCode,
          ]
            .map((x) => (x === undefined || x === null ? "" : String(x).trim()))
            .filter(Boolean);

          const existingRecord = employeeKeys.reduce((found, empKey) => {
            if (found !== undefined) return found;
            return attendanceData?.[empKey]?.[dayOfMonth];
          }, undefined);

          // If no existing record, it's new/changed
          if (existingRecord === undefined || existingRecord === null) return true;

          // Compare canonicalized status (supports import aliases)
          const incomingStatus = mapToCanonical(it.status);
          const existingStatus = String(existingRecord || "").toUpperCase();
          if (incomingStatus !== existingStatus) return true;

          // Otherwise no change - skip validation
          return false;
        });

      console.log(
        `Import: ${rowsToCheck.length} changed rows out of ${items.length} total`
      );

      // Validate only the changed rows and separate into valid/invalid
      rowsToCheck.forEach((it, idx) => {
        const rowErrors = [];

        // 0. Validate date belongs to selected month
        if (it.date) {
          try {
            const dateParts = parseImportDateParts(it.date);
            if (!dateParts) {
              throw new Error("Invalid date format");
            }
            const importedMonth = dateParts.month; // 0-11
            const importedYear = dateParts.year;

            if (importedMonth !== month || importedYear !== year) {
              const monthNames = [
                "Jan",
                "Feb",
                "Mar",
                "Apr",
                "May",
                "Jun",
                "Jul",
                "Aug",
                "Sep",
                "Oct",
                "Nov",
                "Dec",
              ];
              const selectedMonth = monthNames[month];
              const importedMonthName = monthNames[importedMonth];
              rowErrors.push(
                `Date mismatch: Row contains ${importedMonthName} ${importedYear}, but selected month is ${selectedMonth} ${year}`
              );
            }
          } catch (e) {
            rowErrors.push(`Invalid date format: ${it.date}`);
          }
        }

        // 1. Validate employee code exists
        if (!it.employeeCode || String(it.employeeCode).trim() === "") {
          rowErrors.push("Missing employee identifier (Employee ID/Code)");
        }

        const matchedEmployee =
          it._matchedEmployee || resolveEmployeeByIdentifier(it.employeeCode);
        if (!matchedEmployee) {
          rowErrors.push(
            `Employee not found for identifier "${String(
              it.employeeCode || ""
            ).trim()}"`
          );
        }

        // 2. Allow "-" or empty as valid (means clear/blank this day)
        const statusTrimmed = String(it.status ?? "").trim();
        const isClearDay = !statusTrimmed || statusTrimmed === "-";
        if (!isClearDay) {
          // 3. Validate status is a known attendance code (check against import codes)
          const rawStatus = statusTrimmed.toUpperCase();

          // Check if it's a valid import code
          const isValidImportCode = validImportCodes.has(rawStatus);

          // Check if it's in import reverse mapping (import code -> original)
          const isInImportReverseMapping =
            importReverseMapping && importReverseMapping[rawStatus];

          // Also check original codes and custom codes as fallback
          const isValidStatus = normAttendanceStatuses.includes(rawStatus);
          const isInReverseMapping =
            reverseMapping && reverseMapping[rawStatus];

          // Try splitting into two codes (half-day) - check both parts against import codes
          let isValidHalfDay = false;
          const parts = splitIntoTwoCodes(rawStatus);
          if (parts && parts.length === 2) {
            const p1 = String(parts[0]).toUpperCase();
            const p2 = String(parts[1]).toUpperCase();
            isValidHalfDay =
              (validImportCodes.has(p1) || importReverseMapping?.[p1] || normAttendanceStatuses.includes(p1) || reverseMapping?.[p1]) &&
              (validImportCodes.has(p2) || importReverseMapping?.[p2] || normAttendanceStatuses.includes(p2) || reverseMapping?.[p2]);
          }

          if (!isValidImportCode && !isInImportReverseMapping && !isValidStatus && !isInReverseMapping && !isValidHalfDay) {
            // Build list of valid codes for error message (include import codes)
            const validCodesList = Array.from(validImportCodes).sort();
            rowErrors.push(
              `Invalid status: "${
                it.status
              }" (must be one of the configured import codes: ${validCodesList.join(", ")})`
            );
          }
        }

        // 4. Validate date format and day is valid for month
        if (it.date) {
          try {
            const dateParts = parseImportDateParts(it.date);
            if (!dateParts) {
              throw new Error("Invalid date format");
            }
            const dayNum = dateParts.day;
            if (!Number.isFinite(dayNum) || dayNum < 1 || dayNum > daysInMonth) {
              rowErrors.push(
                `Invalid date: day ${dayNum} is outside valid range for this month`
              );
            }
          } catch (e) {
            rowErrors.push(`Invalid date format: ${it.date}`);
          }
        }

        // If there are errors, add to invalid array
        if (rowErrors.length > 0) {
          invalid.push({
            ...it,
            employeeName: it.employeeName || "-",
            errors: rowErrors,
          });
        } else {
          // Valid record
          try {
            const canonicalStatus = mapToCanonical(it.status);
            valid.push({
              ...it,
              employeeName: it.employeeName || "-",
              _resolvedEmployeeId:
                matchedEmployee?.id ?? matchedEmployee?.employeeId ?? null,
              _resolvedEmployeeCode:
                matchedEmployee?.code ||
                matchedEmployee?.empCode ||
                matchedEmployee?.employeeCode ||
                it.employeeCode,
              _canonicalStatus: canonicalStatus,
            });
          } catch (e) {
            invalid.push({
              ...it,
              employeeName: it.employeeName || "-",
              errors: ["Error processing row: " + e.message],
            });
          }
        }
      });

      console.log(
        `Import: ${valid.length} valid records, ${invalid.length} invalid records`
      );

      const report = {
        total: items.length,
        validCount: valid.length,
        invalidCount: invalid.length,
        invalidRows: invalid,
      };

      setImportReport({ ...report, year, month });
      setImportPreviewValid(valid);
      setImportPreviewInvalid(invalid);

      // If there are invalid rows, show preview modal and let user decide
      if (invalid.length > 0) {
        setShowImportPreview(true);
        setIsImporting(false);
        setIsExporting(false);
        return;
      }

      // If all valid, filter to only changed records
      // Send all valid records directly to backend
      const changedRecords = valid;

      // If all valid, prepare payload with only changed records
      const payload = {
        date: `${year}-${String(month + 1).padStart(2, "0")}-01`,
        employeeAttendance: changedRecords.map((it) => ({
          ...(Number.isFinite(Number(it._resolvedEmployeeId))
            ? { employeeId: Number(it._resolvedEmployeeId) }
            : {}),
          employeeCode: it._resolvedEmployeeCode || it.employeeCode,
          date: it.date,
          status:
            it._canonicalStatus ||
            (importReverseMapping &&
              importReverseMapping[String(it.status || "").toUpperCase()]) ||
            (reverseMapping &&
              reverseMapping[String(it.status || "").toUpperCase()]) ||
            it.status,
          remarks: it.remarks || "",
        })),
      };

      if (
        !payload.employeeAttendance ||
        payload.employeeAttendance.length === 0
      ) {
        console.warn(
          "Import handler: payload.employeeAttendance is empty — no rows to send to /attendance/bulk-mark",
          payload
        );
      }

      // Optimistic: merge into local attendanceData so UI shows changes immediately
      try {
        const cloned = { ...(attendanceData || {}) };

        changedRecords.forEach((it) => {
          const empKey = String(
            Number.isFinite(Number(it._resolvedEmployeeId))
              ? Number(it._resolvedEmployeeId)
              : it._resolvedEmployeeCode || it.employeeCode
          );
          const day = Number(it.date.split("-")[2]);
          if (!cloned[empKey]) cloned[empKey] = {};
          // Use canonical status if available (ensures payload/UI match)
          cloned[empKey][day] = it._canonicalStatus || it.status;
        });
        setAttendanceData(cloned);
      } catch (mergeErr) {
        console.warn("Failed optimistic merge after import", mergeErr);
      }

      // Send to backend
      const resp = await attendanceService.bulkMarkAttendance(payload);
      if (resp && resp.success) {
        toast.success("Attendance imported successfully.");
        // Invalidate ALL frontend caches so searches/refreshes hit fresh DB data
        try {
          attendanceCacheRef.current.clear();
          employeesCacheRef.current.clear();
          if (statusCountsCache?.current) statusCountsCache.current.clear();
        } catch (e) {
          /* ignore cache clear errors */
        }

        // Re-fetch all attendance via the main fetchData pipeline which handles
        // chunking, pagination, and proper attendanceData merging for ALL employees.
        try {
          attendanceFetchInFlightRef.current = false;
          attendanceFetchKeyRef.current = "";
          await fetchData();
        } catch (refreshErr) {
          console.warn("Post-import fetchData refresh failed:", refreshErr);
        }
      } else {
        console.error("Import response:", resp);
        const errorMessage = resp?.message || "Import failed. Check server response.";
        toast.error(errorMessage);
      }
    } catch (err) {
      console.error("Import failed:", err);
      const errorMessage = err?.response?.data?.message || err?.message || "Failed to parse/import file.";
      toast.error(errorMessage);
    } finally {
      setIsExporting(false);
      setIsImporting(false);
    }
  };

  const handleApplyLeave = useCallback(
    async (leaveData) => {
      const { employeeIds, leaveStatus, fromDate, toDate, leaveReason, leaveTypeId } =
        leaveData;

      const from = new Date(fromDate);
      const to = new Date(toDate);

      try {
        let autoApprovedCount = 0;
        let pendingCount = 0;

        for (const employeeId of employeeIds) {
          const response = await attendanceStructureService.createLeaveRequest({
            employeeId: parseInt(employeeId, 10),
            leaveTypeId: leaveTypeId || undefined,
            leaveStatusCode: leaveStatus,
            fromDate,
            toDate,
            reason: leaveReason,
            leaveDuration: "full_day",
          });

          const employee = allEmployees.find(
            (emp) => String(emp.id) === String(employeeId)
          );

          if (response?.autoApproved) {
            autoApprovedCount += 1;
            const newAttendanceData = { ...attendanceData };
            if (!newAttendanceData[employeeId]) newAttendanceData[employeeId] = {};
            const currentDate = new Date(from);
            while (currentDate <= to) {
              const day = currentDate.getDate();
              const month = currentDate.getMonth();
              const year = currentDate.getFullYear();
              if (month === selectedMonth && year === selectedYear) {
                newAttendanceData[employeeId][day] = leaveStatus;
              }
              currentDate.setDate(currentDate.getDate() + 1);
            }
            setAttendanceData(newAttendanceData);
          } else if (response?.data) {
            pendingCount += 1;
            if (employee) {
              pushLeaveRequestInsight(
                employee,
                {
                  leaveType: leaveStatus,
                  leaveDuration: "full_day",
                  fromDate,
                  toDate,
                  reason: leaveReason,
                },
                { apiRow: response.data }
              );
            }
          }
        }

        await refreshLeaveRequestInsights();

        if (pendingCount > 0 && autoApprovedCount > 0) {
          toast.success(
            `${autoApprovedCount} leave(s) applied; ${pendingCount} sent for approval`
          );
        } else if (pendingCount > 0) {
          toast.success(`${pendingCount} leave request(s) submitted for approval`);
        } else {
          toast.success(`Leave applied successfully for ${employeeIds.length} employee(s)`);
        }

        if (autoApprovedCount > 0) {
          setTimeout(async () => {
            try {
              await fetchData();
            } catch (refreshError) {
              console.error("Error refreshing attendance data:", refreshError);
            }
          }, 500);
        }
      } catch (error) {
        console.error("Error applying leave:", error);
        toast.error(
          error?.response?.data?.message ||
            error?.message ||
            "Failed to apply leave. Please try again."
        );
      }
    },
    [
      attendanceData,
      selectedMonth,
      selectedYear,
      allEmployees,
      pushLeaveRequestInsight,
      refreshLeaveRequestInsights,
    ]
  );

  // Memoized tab click handlers to prevent unnecessary re-renders
  const handleTabChange = useCallback(
    (tab) => {
      if (!allowedAttendanceTabs.includes(tab)) {
        toast.error("You do not have permission to open this section.");
        return;
      }
      setActiveTab(tab);
    if (tab !== "reports") {
      setReportsMenuOpen(false);
    }
    },
    [allowedAttendanceTabs]
  );

  const handleReportsClick = useCallback(() => {
    setActiveTab("reports");
    setReportsMenuOpen((prev) => (activeTab === "reports" ? !prev : true));
  }, [activeTab]);

  // Memoized button handlers
  const handleUploadClick = useCallback(() => {
    if (isLocked) {
      toast.error("Attendance is locked for this month.");
      return;
    }
    if (!canAttendanceImport) {
      toast.error("You do not have permission to import attendance.");
      return;
    }
    setShowUploadPopup(true);
  }, [canAttendanceImport, isLocked]);
  const handleUploadLeaveClick = useCallback(
    () => {
      if (!canManageLeaveOps) {
        toast.error("You do not have permission to upload leave details.");
        return;
      }
      if (isLocked) {
        toast.error("Attendance is locked for this month.");
        return;
      }
      setShowUploadLeavePopup(true);
    },
    [canManageLeaveOps, isLocked]
  );
  const handleExportClick = useCallback(() => {
    if (!canAttendanceExport) {
      toast.error("You do not have permission to export attendance.");
      return;
    }
    setExportMonth(selectedMonth);
    setExportYear(selectedYear);
    setShowExportModal(true);
  }, [selectedMonth, selectedYear, canAttendanceExport]);
  const handleBulkApplyLeave = useCallback(
    () => {
      if (!canManageLeaveOps) {
        toast.error("You do not have permission to apply leave.");
        return;
      }
      if (isLocked) {
        toast.error("Attendance is locked for this month.");
        return;
      }
      setShowApplyLeavePopup(true);
    },
    [canManageLeaveOps, isLocked]
  );
  const handleMarkAllClick = useCallback(() => {
    if (!isEditing) {
      toast.error("Enable Edit to use Mark All.");
      return;
    }
    if (isLocked) {
      toast.error("Attendance is locked for this month.");
      return;
    }
    if (!canModifyAttendance) {
      toast.error("You do not have permission to mark attendance.");
      return;
    }
    setShowMarkAllPopup(true);
  }, [canModifyAttendance, isEditing, isLocked]);
  const handleStructuresClick = useCallback(
    () => {
      if (!canManageAttendanceSettings) {
        toast.error("You do not have permission to open attendance settings.");
        return;
      }
      setShowStructuresModal(true);
    },
    [canManageAttendanceSettings]
  );
  const handleShowFilters = useCallback(() => {
    setShowFilters(true);
    setSidebarImportOpen(false);
    setSidebarBulkOpen(false);
    setSidebarProcessOpen(false);
  }, []);

  const handleEnableEdit = useCallback(() => {
    if (isLocked) {
      toast.error("Attendance is locked for this month.");
      return;
    }
    if (!canModifyAttendance) {
      toast.error("You do not have permission to edit attendance.");
      return;
    }
    setIsEditing(true);
    setSidebarBulkOpen(true);
  }, [canModifyAttendance, isLocked]);

  const handleDashboardPeriodChange = useCallback(
    ({ periodMode, month, year }) => {
      if (periodMode === "month" && Number.isFinite(month) && Number.isFinite(year)) {
        applyMonthChange(year, month - 1);
      }
    },
    [applyMonthChange]
  );
  const handleSearchChange = useCallback((e) => {
    setSearchTerm(e.target.value);
  }, []);

  const handleToggleSummary = useCallback(() => {
    setShowSummaryTable((prev) => {
      const next = !prev;
      if (next) showAlert("Summary calculated successfully");
      return next;
    });
  }, [showAlert]);

  const handleRequestUnlock = useCallback(async () => {
    const now = Date.now();
    if (now - requestUnlockCooldownRef.current < LOCK_ACTION_COOLDOWN_MS) return;
    requestUnlockCooldownRef.current = now;

    const monthLabel = new Date(selectedYear, selectedMonth, 1).toLocaleDateString(
      "en-US",
      { month: "long", year: "numeric" }
    );

    try {
      setUnlockingBusy(true);
      await attendanceService.requestUnlockAttendance(
        selectedMonth + 1,
        selectedYear
      );
      showAlert(`Unlock passkey sent to admin email for ${monthLabel}`, "success");
      setUnlockPasskey("");
      setPasskeyError("");
      setShowPasskey(false);
      setUnlockPasskeyInputKey((k) => k + 1);
      setShowPasskeyModal(true);
    } catch (err) {
      console.error("Unlock request failed:", err);
      showAlert(
        err.response?.data?.message || err.message || "Failed to request unlock",
        "error"
      );
    } finally {
      setUnlockingBusy(false);
    }
  }, [selectedMonth, selectedYear, showAlert]);

  const handleLockAttendanceMonth = useCallback(() => {
    const now = Date.now();
    if (now - lockCooldownRef.current < LOCK_ACTION_COOLDOWN_MS) return;
    lockCooldownRef.current = now;

    const monthLabel = new Date(selectedYear, selectedMonth, 1).toLocaleDateString(
      "en-US",
      { month: "long", year: "numeric" }
    );

    showConfirmation(
      "Lock Attendance",
      `Are you sure you want to finalise and lock attendance for the month of (${monthLabel})?`,
      async () => {
        try {
          setLockingBusy(true);
          const totalsToSend = calculateTotalsForLock();
          const payDays = totalsToSend.payDays ?? 0;
          const labelFor = (code) =>
            (attendanceMapping &&
              attendanceMapping[String(code || "").toUpperCase()]) ||
            code;
          const fixedCodes = new Set(["PR", "PH", "WO", "CL", "EL", "CO", "CW", "LOP"]);
          const totalsDisplay = Object.entries(totalsToSend || {})
            .filter(([k]) => k.toLowerCase() !== "paydays")
            .map(([k, v]) => ({
              label: labelFor(k),
              value: Number(v || 0),
              code: k.toUpperCase(),
            }))
            .filter((x) => x.value > 0 || fixedCodes.has(x.code));

          const lockRes = await attendanceService.lockAttendance(
            selectedMonth + 1,
            selectedYear,
            {
              totals: totalsToSend,
              totalsDisplay,
              payDays,
              employeeIds: Array.from(
                new Set(
                  (Array.isArray(allEmployees) ? allEmployees : [])
                    .map((e) => e?.id)
                    .filter((id) => id !== undefined && id !== null)
                )
              ),
              attendanceSnapshot: buildAttendanceSnapshotForLock(),
            }
          );

          const locksResponse = await attendanceStructureService.getAttendanceLocks();
          if (locksResponse.success && Array.isArray(locksResponse.data)) {
            setAttendanceLocks(locksResponse.data || []);
            const currentLock = locksResponse.data.find(
              (lock) =>
                lock.month === selectedMonth + 1 && lock.year === selectedYear
            );
            setIsLocked(!!currentLock);
          }

          const baseMsg = `Attendance locked for ${monthLabel}`;
          const le = lockRes?.lockEmail;
          if (!le || le.overall === "all_sent") {
            showAlert(baseMsg, "success");
          } else if (le.overall === "none_configured") {
            showAlert(
              `${baseMsg} No notification email was sent (company email and/or super-admin recipient not configured).`,
              "warning"
            );
          } else if (le.overall === "partial") {
            const a = le.recipients?.companyAdmin;
            const s = le.recipients?.superAdmin;
            const errs = [];
            if (a?.attempted && a?.ok === false && a?.error) {
              errs.push(`Admin mail: ${a.error}`);
            }
            if (s?.attempted && s?.ok === false && s?.error) {
              errs.push(`Super admin mail: ${s.error}`);
            }
            showAlert(
              errs.length
                ? `${baseMsg} Some notifications failed: ${errs.join(" ")}`
                : `${baseMsg} Some notification emails may have failed.`,
              "warning"
            );
          } else if (le.overall === "all_failed") {
            const detail = [
              le.recipients?.companyAdmin?.error,
              le.recipients?.superAdmin?.error,
            ]
              .filter(Boolean)
              .join(" ");
            showAlert(
              detail
                ? `${baseMsg} Notification emails failed: ${detail}`
                : `${baseMsg} Notification emails failed to send.`,
              "error"
            );
          } else if (le.overall === "error") {
            showAlert(
              `${baseMsg} Email step error: ${le.error || "unknown"}`,
              "warning"
            );
          } else {
            showAlert(baseMsg, "success");
          }
        } catch (err) {
          console.error("Lock failed:", err);
          showAlert(
            err.response?.data?.message || err.message || "Failed to lock attendance",
            "error"
          );
        } finally {
          setLockingBusy(false);
        }
      },
      "Lock",
      "Cancel"
    );
  }, [
    selectedMonth,
    selectedYear,
    showConfirmation,
    calculateTotalsForLock,
    attendanceMapping,
    allEmployees,
    buildAttendanceSnapshotForLock,
    showAlert,
  ]);

  const leaveTypeLabels = useMemo(() => {
    const map = {};
    (leaveTypes || []).forEach((lt) => {
      const code = String(lt?.code || lt?.leaveCode || "").trim().toUpperCase();
      if (code) map[code] = lt?.name || lt?.label || code;
    });
    return map;
  }, [leaveTypes]);

  useEffect(() => {
    if (allowedAttendanceTabs.length === 0) return;
    if (!allowedAttendanceTabs.includes(activeTab)) {
      setActiveTab(allowedAttendanceTabs[0]);
    }
  }, [allowedAttendanceTabs, activeTab]);

  // Check if any non-default filters are currently active (match Employee behavior)
  const hasActiveFilters = useMemo(() => {
    const hasSearch = Boolean(
      typeof debouncedSearchTerm === "string"
        ? debouncedSearchTerm.trim()
        : debouncedSearchTerm
    );

    const obj = appliedFilters || {};
    const hasFilters = Object.values(obj).some((v) => {
      if (v === undefined || v === null) return false;
      if (Array.isArray(v)) return v.length > 0;
      if (typeof v === "string") {
        const normalized = v.trim();
        if (!normalized) return false;
        if (normalized === "all") return false;
        return true;
      }
      return true;
    });

    return hasSearch || hasFilters;
  }, [debouncedSearchTerm, appliedFilters]);

  return (
    <div className="flex-1 h-[calc(100dvh-5rem)] overflow-hidden">
      <div
        className={`flex h-full flex-col ${
          darkMode ? "bg-[#1C1C1C] text-[#E0E0E0]" : "bg-redhalo-gray/30 text-gray-900"
        }`}
      >
        <div className="flex flex-1 min-h-0">
          {/* Sub Sidebar — same frame as Employee.jsx */}
          <aside className="sticky top-0 z-20 flex h-full min-h-0 w-56 shrink-0 flex-col overflow-hidden bg-white shadow-sm dark:bg-[#121212]">
            <div className="rh-subnav-aside-head">
              <h4 className="invisible text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-400" aria-hidden="true">
                Attendance
              </h4>
            </div>
            <div className="min-h-0 flex-1 space-y-1 overflow-y-auto border-t border-gray-200 p-2 dark:border-[#3C3C4C]">
              {canAttendanceView && (
                <button
                  type="button"
                  onClick={() => handleTabChange("attendance")}
                  className={`block w-full text-left px-3 py-2 text-sm rounded-none transition-colors ${
                    activeTab === "attendance"
                      ? "rh-subnav-active"
                      : darkMode
                      ? "border-l-[3px] border-l-transparent text-gray-300 hover:bg-gray-800/80"
                      : "border-l-[3px] border-l-transparent text-gray-900 hover:bg-gray-100"
                  }`}
                >
                  Attendance
                </button>
              )}
              {canViewLeaves && (
                <button
                  type="button"
                  onClick={() => handleTabChange("leaves")}
                  className={`block w-full text-left px-3 py-2 text-sm rounded-none transition-colors ${
                    activeTab === "leaves"
                      ? "rh-subnav-active"
                      : darkMode
                      ? "border-l-[3px] border-l-transparent text-gray-300 hover:bg-gray-800/80"
                      : "border-l-[3px] border-l-transparent text-gray-900 hover:bg-gray-100"
                  }`}
                >
                  Leave Management
                </button>
              )}
              {canViewReports && (
                <>
                  <button
                    type="button"
                    onClick={handleReportsClick}
                    className={`block w-full text-left px-3 py-2 text-sm rounded-none transition-colors ${
                      activeTab === "reports"
                        ? "rh-subnav-active"
                        : darkMode
                        ? "border-l-[3px] border-l-transparent text-gray-300 hover:bg-gray-800/80"
                        : "border-l-[3px] border-l-transparent text-gray-900 hover:bg-gray-100"
                    }`}
                  >
                    Reports
                  </button>
                  {reportsMenuOpen && (
                    <div className="ml-3 border-l border-gray-200 pl-2 dark:border-[#3C3C4C]">
                      {[
                        { id: "daily-attendance", label: "Daily Attendance Report" },
                        { id: "monthly-attendance", label: "Monthly Attendance Report" },
                        { id: "leave-report", label: "Leave Report" },
                        { id: "overtime-report", label: "Overtime Report" },
                        { id: "lop-reversal-report", label: "LOP Reversal Report" },
                      ].map((report) => (
                        <button
                          key={report.id}
                          type="button"
                          onClick={() => {
                            setActiveTab("reports");
                            setReportsSubTabTouched(true);
                            setActiveReportsSubTab(report.id);
                          }}
                          className={`block w-full px-3 py-2 text-left text-xs transition-colors ${
                            activeTab === "reports" &&
                            reportsSubTabTouched &&
                            activeReportsSubTab === report.id
                              ? "border-l-[3px] border-l-redhalo-red font-semibold text-redhalo-navy dark:text-redhalo-mint"
                              : darkMode
                              ? "border-l-[3px] border-l-transparent text-gray-300 hover:bg-gray-800/80"
                              : "border-l-[3px] border-l-transparent text-gray-700 hover:bg-gray-100"
                          }`}
                        >
                          {report.label}
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}

              <button
                type="button"
                onClick={() => {
                  setSidebarImportOpen((p) => !p);
                  setSidebarBulkOpen(false);
                  setSidebarProcessOpen(false);
                }}
                className={`block w-full text-left px-3 py-2 text-sm rounded-none transition-colors ${
                  sidebarImportOpen || showUploadPopup || showUploadLeavePopup
                    ? "rh-subnav-active"
                    : darkMode
                    ? "border-l-[3px] border-l-transparent text-gray-300 hover:bg-gray-800/80"
                    : "border-l-[3px] border-l-transparent text-gray-900 hover:bg-gray-100"
                }`}
              >
                Import
              </button>
              {sidebarImportOpen && (
                <div className="ml-3 border-l border-gray-200 pl-2 dark:border-[#3C3C4C]">
                  <button
                    type="button"
                    onClick={handleUploadClick}
                    disabled={isLocked || !canAttendanceImport}
                    className={`block w-full px-3 py-2 text-left text-xs transition-colors disabled:cursor-not-allowed disabled:opacity-45 ${
                      showUploadPopup
                        ? "border-l-[3px] border-l-redhalo-red font-semibold text-redhalo-navy dark:text-redhalo-mint"
                        : darkMode
                        ? "border-l-[3px] border-l-transparent text-gray-300 hover:bg-gray-800/80"
                        : "border-l-[3px] border-l-transparent text-gray-700 hover:bg-gray-100"
                    }`}
                  >
                    Bulk Import Attendance
                  </button>
                  <button
                    type="button"
                    onClick={handleUploadLeaveClick}
                    disabled={isLocked || !canManageLeaveOps}
                    className={`block w-full px-3 py-2 text-left text-xs transition-colors disabled:cursor-not-allowed disabled:opacity-45 ${
                      showUploadLeavePopup
                        ? "border-l-[3px] border-l-redhalo-red font-semibold text-redhalo-navy dark:text-redhalo-mint"
                        : darkMode
                        ? "border-l-[3px] border-l-transparent text-gray-300 hover:bg-gray-800/80"
                        : "border-l-[3px] border-l-transparent text-gray-700 hover:bg-gray-100"
                    }`}
                  >
                    Bulk Leave Upload
                  </button>
                </div>
              )}

              <button
                type="button"
                onClick={() => {
                  setSidebarBulkOpen((p) => !p);
                  setSidebarImportOpen(false);
                  setSidebarProcessOpen(false);
                }}
                className={`block w-full text-left px-3 py-2 text-sm rounded-none transition-colors ${
                  sidebarBulkOpen ||
                  showApplyLeavePopup ||
                  showMarkAllPopup ||
                  isEditing
                    ? "rh-subnav-active"
                    : darkMode
                    ? "border-l-[3px] border-l-transparent text-gray-300 hover:bg-gray-800/80"
                    : "border-l-[3px] border-l-transparent text-gray-900 hover:bg-gray-100"
                }`}
              >
                Bulk Actions
              </button>
              {sidebarBulkOpen && (
                <div className="ml-3 border-l border-gray-200 pl-2 dark:border-[#3C3C4C]">
                  {canModifyAttendance && (
                    <button
                      type="button"
                      onClick={handleEnableEdit}
                      disabled={isLocked || isEditing}
                      className={`block w-full px-3 py-2 text-left text-xs transition-colors disabled:cursor-not-allowed disabled:opacity-45 ${
                        isEditing
                          ? "border-l-[3px] border-l-redhalo-red font-semibold text-redhalo-navy dark:text-redhalo-mint"
                          : darkMode
                          ? "border-l-[3px] border-l-transparent text-gray-300 hover:bg-gray-800/80"
                          : "border-l-[3px] border-l-transparent text-gray-700 hover:bg-gray-100"
                      }`}
                    >
                      Edit
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={handleBulkApplyLeave}
                    disabled={isLocked || !canManageLeaveOps}
                    className={`block w-full px-3 py-2 text-left text-xs transition-colors disabled:cursor-not-allowed disabled:opacity-45 ${
                      showApplyLeavePopup
                        ? "border-l-[3px] border-l-redhalo-red font-semibold text-redhalo-navy dark:text-redhalo-mint"
                        : darkMode
                        ? "border-l-[3px] border-l-transparent text-gray-300 hover:bg-gray-800/80"
                        : "border-l-[3px] border-l-transparent text-gray-700 hover:bg-gray-100"
                    }`}
                  >
                    Bulk Leave Apply
                  </button>
                  <button
                    type="button"
                    onClick={handleMarkAllClick}
                    disabled={!isEditing || !canModifyAttendance}
                    className={`block w-full px-3 py-2 text-left text-xs transition-colors disabled:cursor-not-allowed disabled:opacity-45 ${
                      showMarkAllPopup
                        ? "border-l-[3px] border-l-redhalo-red font-semibold text-redhalo-navy dark:text-redhalo-mint"
                        : darkMode
                        ? "border-l-[3px] border-l-transparent text-gray-300 hover:bg-gray-800/80"
                        : "border-l-[3px] border-l-transparent text-gray-700 hover:bg-gray-100"
                    }`}
                  >
                    Mark All
                  </button>
                </div>
              )}

              {canManageAttendanceSettings && (
                <button
                  type="button"
                  onClick={handleStructuresClick}
                  className={`block w-full text-left px-3 py-2 text-sm rounded-none transition-colors ${
                    showStructuresModal
                      ? "rh-subnav-active"
                      : darkMode
                      ? "border-l-[3px] border-l-transparent text-gray-300 hover:bg-gray-800/80"
                      : "border-l-[3px] border-l-transparent text-gray-900 hover:bg-gray-100"
                  }`}
                >
                  Settings
                </button>
              )}

              <button
                type="button"
                onClick={() => {
                  setSidebarProcessOpen((p) => !p);
                  setSidebarImportOpen(false);
                  setSidebarBulkOpen(false);
                }}
                className={`block w-full text-left px-3 py-2 text-sm rounded-none transition-colors ${
                  sidebarProcessOpen || showSummaryTable
                    ? "rh-subnav-active"
                    : darkMode
                    ? "border-l-[3px] border-l-transparent text-gray-300 hover:bg-gray-800/80"
                    : "border-l-[3px] border-l-transparent text-gray-900 hover:bg-gray-100"
                }`}
              >
                Process Attendance
              </button>
              {sidebarProcessOpen && (
                <div className="ml-3 border-l border-gray-200 pl-2 dark:border-[#3C3C4C]">
                  <button
                    type="button"
                    onClick={handleToggleSummary}
                    className={`block w-full px-3 py-2 text-left text-xs transition-colors ${
                      showSummaryTable
                        ? "border-l-[3px] border-l-redhalo-red font-semibold text-redhalo-navy dark:text-redhalo-mint"
                        : darkMode
                        ? "border-l-[3px] border-l-transparent text-gray-300 hover:bg-gray-800/80"
                        : "border-l-[3px] border-l-transparent text-gray-700 hover:bg-gray-100"
                    }`}
                  >
                    {showSummaryTable ? "Hide Summary" : "Calculate and Show Summary"}
                  </button>
                  {canAttendanceApprove &&
                    (isLocked ? (
                      <button
                        type="button"
                        onClick={handleRequestUnlock}
                        disabled={unlockingBusy}
                        className="block w-full px-3 py-2 text-left text-xs transition-colors disabled:cursor-not-allowed disabled:opacity-45 border-l-[3px] border-l-transparent text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800/80"
                      >
                        {unlockingBusy ? "Requesting unlock…" : "Request Unlock"}
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={handleLockAttendanceMonth}
                        disabled={lockingBusy || isEditing}
                        className="block w-full px-3 py-2 text-left text-xs transition-colors disabled:cursor-not-allowed disabled:opacity-45 border-l-[3px] border-l-transparent text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800/80"
                      >
                        {lockingBusy ? "Locking…" : "Lock Attendance"}
                      </button>
                    ))}
                </div>
              )}

              <button
                type="button"
                onClick={handleExportClick}
                disabled={isExporting || !canAttendanceExport}
                className={`block w-full text-left px-3 py-2 text-sm rounded-none transition-colors disabled:cursor-not-allowed disabled:opacity-45 ${
                  showExportModal
                    ? "rh-subnav-active"
                    : darkMode
                    ? "border-l-[3px] border-l-transparent text-gray-300 hover:bg-gray-800/80"
                    : "border-l-[3px] border-l-transparent text-gray-900 hover:bg-gray-100"
                }`}
              >
                Export
              </button>
            </div>
          </aside>
          {/* Tab content — flex sibling; aside width reserved in layout */}
          <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-white p-4 dark:bg-[#121212]">

        {allowedAttendanceTabs.length === 0 && (
          <div className="flex h-full items-center justify-center p-6">
            <div
              className={`max-w-md rounded-xl border p-6 text-center ${
                darkMode
                  ? "border-[#3C3C4C] bg-[#1f1f1f] text-gray-300"
                  : "border-gray-200 bg-white text-gray-700"
              }`}
            >
              <h4 className="mb-1 text-sm font-semibold uppercase tracking-wide">
                No attendance access
              </h4>
              <p className="text-xs">
                You do not currently have permission to view Attendance sections.
                Please contact your administrator.
              </p>
            </div>
          </div>
        )}

        {activeTab === "attendance" && (
          <div className="flex min-h-0 w-full flex-1 flex-col overflow-hidden">
            <style>{`
              @keyframes blink-border {
                0%, 100% {
                  border-color: rgb(59 130 246);
                  box-shadow: 0 0 0 0 rgba(209, 22, 22, 0.7);
                }
                50% {
                  border-color: rgb(37 99 235);
                  box-shadow: 0 0 0 4px rgba(236, 9, 9, 0);
                }
              }
            `}</style>
            <div className="mb-3 flex shrink-0 justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setShowAttendanceInsights((v) => !v);
                  setShowAttendanceDashboard(false);
                }}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-full border backdrop-blur-sm transition focus:outline-none focus-visible:ring-2 focus-visible:ring-redhalo-red/50 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-[#121212] ${
                  darkMode
                    ? "border-redhalo-red/70 bg-redhalo-navy/40 text-redhalo-mint hover:bg-redhalo-navy/60"
                    : "border-redhalo-red bg-redhalo-mint/40 text-redhalo-navy-dark hover:bg-redhalo-mint/60"
                }`}
                title={
                  showAttendanceInsights
                    ? "Back to attendance list"
                    : "Flip to insights view"
                }
              >
                <RefreshCw className="w-3.5 h-3.5" />
                {showAttendanceInsights ? "Back" : "Flip Insights"}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowAttendanceDashboard((v) => !v);
                  setShowAttendanceInsights(false);
                }}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-full border backdrop-blur-sm transition focus:outline-none focus-visible:ring-2 focus-visible:ring-redhalo-red/50 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-[#121212] ${
                  darkMode
                    ? "border-redhalo-red/70 bg-redhalo-navy/40 text-redhalo-mint hover:bg-redhalo-navy/60"
                    : "border-redhalo-red bg-redhalo-mint/40 text-redhalo-navy-dark hover:bg-redhalo-mint/60"
                }`}
                title={
                  showAttendanceDashboard
                    ? "Back to attendance list"
                    : "Flip to attendance analytics dashboard"
                }
              >
                <LayoutDashboard className="w-3.5 h-3.5" />
                {showAttendanceDashboard ? "Back" : "Flip Dashboard"}
              </button>
            </div>
            {showAttendanceInsights && (
              <ActionInsightsPanel
                darkMode={darkMode}
                title="Attendance Action Insights"
                items={attendanceInsightItems}
                isFlipped
                showFlipButton={false}
                onFlip={() => setShowAttendanceInsights(false)}
                overlayMode="panel"
                onRowAction={handleAttendanceInsightRowAction}
              />
            )}
            {showAttendanceDashboard && (
              <AttendanceDashboardPanel
                darkMode={darkMode}
                isFlipped
                showFlipButton={false}
                onFlip={() => setShowAttendanceDashboard(false)}
                employees={allEmployees}
                attendanceData={attendanceData}
                leaveTypes={leaveTypes}
                insightItems={attendanceInsightItems}
                attendanceDetailData={attendanceDetailData}
                selectedMonth={selectedMonth}
                selectedYear={selectedYear}
                reverseMapping={reverseMapping}
                loading={employeesLoading}
                leaveTypeLabels={leaveTypeLabels}
                onPeriodChange={handleDashboardPeriodChange}
              />
            )}
            {!showAttendanceInsights && !showAttendanceDashboard && (
            <div
              className={`flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden p-3 ${
                darkMode ? "bg-[#121212]" : "bg-white"
              }`}
            >
            {employeesLoading && (
              <div className="fixed top-0 left-0 right-0 h-1 bg-gradient-to-r from-redhalo-orange via-redhalo-red to-redhalo-navy z-[9998] animate-pulse" />
            )}

            {/* Search and Filter Section — toolbar style (Employee list) */}
            <div
              className={`shrink-0 px-3 pt-2.5 pb-3 ${
                darkMode
                  ? "bg-[#1C1C1C] border-[#3C3C3C] rounded-t-2xl"
                  : "bg-white border-[#165b5d]/30 rounded-t-2xl"
              } border z-50 shadow-sm`}
            >
              <div className="mt-2.5 flex flex-nowrap items-center gap-2.5 overflow-x-auto">
                <div className="relative w-full min-w-[240px] max-w-md shrink-0">
                  <div className="relative">
                    <Search
                      className={`absolute left-2.5 top-1/2 transform -translate-y-1/2 ${
                        darkMode ? "text-[#A6A9C8]" : "text-gray-500"
                      } w-3.5 h-3.5`}
                    />
                    <input
                      type="search"
                      name="att_employee_filter_q"
                      id="admin-attendance-toolbar-filter"
                      role="searchbox"
                      autoComplete="off"
                      autoCorrect="off"
                      spellCheck={false}
                      inputMode="search"
                      data-lpignore="true"
                      data-1p-ignore="true"
                      data-form-type="other"
                      readOnly={toolbarSearchReadonlyBarrier}
                      onFocus={() => setToolbarSearchReadonlyBarrier(false)}
                      onChange={handleSearchChange}
                      placeholder="Search by employee code, name, or mobile number"
                      value={searchTerm}
                      className={`w-full rounded-full border py-2 pl-8 pr-8 text-xs transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-redhalo-red/30 ${
                        darkMode
                          ? "border-[#3C3C3C] bg-[#2C2C2C] text-[#E0E0E0] focus:border-redhalo-red"
                          : "border-[#f3d6bf] bg-white text-gray-800 focus:border-[#ef6f2e] hover:border-[#ef6f2e]"
                      } shadow-sm`}
                    />
                  </div>
                </div>

                <div className="ml-auto flex flex-nowrap items-center gap-2.5 shrink-0">
                  <div className="flex items-center space-x-1.5 shrink-0">
                    <label
                      className={`text-xs font-medium whitespace-nowrap ${
                        darkMode ? "text-[#E0E0E0]" : "text-gray-700"
                      }`}
                    >
                      Month:
                    </label>
                    <DatePicker
                      compact
                      darkMode={darkMode}
                      monthYearOnly
                      showMonthName
                      key={monthPickerValue}
                      value={monthPickerValue}
                      onChange={handleMonthRangeChange}
                      min={minDate}
                      max={new Date()}
                    />
                  </div>

                  {/* Editing Controls */}
                  <div className="flex items-center space-x-2">
                    {isEditing && !isLocked && canModifyAttendance && (
                      <button
                        ref={saveButtonRef}
                        onClick={async () => {
                          console.time("⏱️ SAVE_TOTAL");
                          console.time("⏱️ SAVE_PREP_PAYLOAD");
                          try {
                            // ✅ Generate proper ISO date string (like 2025-11-05)
                            // ✅ Manually build the date string to avoid timezone bugs
                            const year = selectedYear;
                            // (selectedMonth + 1) converts 0-indexed month (10) to 1-indexed (11)
                            const month = (selectedMonth + 1)
                              .toString()
                              .padStart(2, "0");
                            const selectedDate = `${year}-${month}-01`;

                            console.log(
                              "📅 Sending selectedDate to bulkMark:",
                              selectedDate
                            );
                            // compute month prefix (existing selectedDate like "2025-11-01")
                            const yearMonthPrefix = selectedDate.substring(
                              0,
                              8
                            ); // "2025-11-"

                            // helper to validate date string 'YYYY-MM-DD'
                            const isValidYMD = (ymd) => {
                              const d = new Date(ymd);
                              if (isNaN(d.getTime())) return false;
                              const [y, m, day] = ymd
                                .split("-")
                                .map((s) => parseInt(s, 10));
                              return (
                                d.getFullYear() === y &&
                                d.getMonth() + 1 === m &&
                                d.getDate() === day
                              );
                            };

                            // ✅ OPTIMIZED: Use changedRecordsRef instead of scanning all attendanceData
                            console.log(
                              "📋 Building save payload from tracked changes:",
                              `${changedRecordsRef.current.size} changes tracked`,
                              "Keys:",
                              Array.from(changedRecordsRef.current.keys())
                            );

                            const employeeAttendance = Array.from(
                              changedRecordsRef.current.values()
                            )
                              .map((change) => {
                                const { employeeId, day, newStatus, override } = change;
                                const actualEmployeeId = parseInt(
                                  employeeId,
                                  10
                                );

                                if (isNaN(actualEmployeeId)) {
                                  console.warn(
                                    "⚠️ Invalid employee ID in changedRecords:",
                                    employeeId
                                  );
                                  return null;
                                }

                                const recordDate = `${yearMonthPrefix}${String(
                                  day
                                ).padStart(2, "0")}`;

                                // validate date (safety)
                                const currentDate = new Date(recordDate);
                                if (isNaN(currentDate.getTime())) {
                                  console.warn(
                                    "Skipping invalid date:",
                                    recordDate
                                  );
                                  return null;
                                }

                                // Convert UI/custom status to backend canonical code
                                const backendStatus =
                                  (reverseMapping &&
                                    reverseMapping[
                                      String(newStatus || "").toUpperCase()
                                    ]) ||
                                  newStatus;

                                const remark =
                                  remarksMap?.[employeeId]?.[day] || "";

                                const recordToSave = {
                                  employeeId: actualEmployeeId,
                                  date: recordDate,
                                  status: backendStatus,
                                  remarks: String(remark || ""),
                                };

                                // If user explicitly confirmed override for this cell, include it
                                try {
                                  const overrideKey = `${actualEmployeeId}_${day}`;
                                  if (override === true || (overrideMap && overrideMap[overrideKey])) {
                                    recordToSave.override = true;
                                  }
                                } catch (e) {
                                  console.debug(
                                    "Failed to attach override flag",
                                    e
                                  );
                                }

                                console.log(
                                  `✅ Adding to save: empId=${employeeId}, day=${day}, date=${recordDate}, status=${newStatus}`
                                );

                                return recordToSave;
                              })
                              .filter(Boolean);

                            if (employeeAttendance.length === 0) {
                              showAlert("No changes to save", "info");
                              setIsEditing(false);
                              pendingMonthAfterSaveRef.current = null;
                              return;
                            }

                            const transformed = {
                              date: selectedDate,
                              employeeAttendance,
                            };

                            console.log("💾 Saving attendance changes:", {
                              date: selectedDate,
                              month: selectedMonth + 1,
                              year: selectedYear,
                              changesCount: employeeAttendance.length,
                              employeeIds: [
                                ...new Set(
                                  employeeAttendance.map((a) => a.employeeId)
                                ),
                              ],
                              sample: employeeAttendance.slice(0, 5),
                              fullPayload: employeeAttendance,
                            });

                            try {
                              console.debug(
                                "Attendance.jsx - transformed payload",
                                transformed
                              );
                            } catch (e) {
                              console.debug(
                                "Attendance.jsx - debug log failed",
                                e
                              );
                            }

                            // OPTIMISTIC UPDATE: Apply changes immediately to UI
                            console.log(
                              "🚀 Applying optimistic update with",
                              employeeAttendance.length,
                              "records"
                            );
                            try {
                              const savedEmployeeIds = [
                                ...new Set(
                                  employeeAttendance.map((r) =>
                                    String(r.employeeId)
                                  )
                                ),
                              ];
                              const snapshot = {};
                              for (const id of savedEmployeeIds.slice(0, 10)) {
                                snapshot[id] =
                                  attendanceData?.[id] ||
                                  attendanceData?.[Number(id)];
                              }
                              console.info(
                                "Optimistic update - before applying",
                                {
                                  changesCount: employeeAttendance.length,
                                  savedEmployeeIdsCount:
                                    savedEmployeeIds.length,
                                  sampleSavedEmployeeIds:
                                    savedEmployeeIds.slice(0, 5),
                                  attendanceSnapshot: snapshot,
                                }
                              );
                            } catch (e) {
                              /* ignore logging errors */
                            }

                            setAttendanceData((prev) => {
                              const next = { ...(prev || {}) };
                              for (const record of employeeAttendance) {
                                try {
                                  const empKey = String(record.employeeId);
                                  const parsedDay = getDayOfMonthSafe(record.date);
                                  if (!parsedDay) continue;
                                  const dayKey = String(parsedDay);

                                  // CRITICAL: Create a NEW object reference so React.memo detects the change
                                  if (!next[empKey]) {
                                    next[empKey] = {};
                                  } else {
                                    next[empKey] = { ...next[empKey] }; // Clone the employee's data object
                                  }
                                  next[empKey][dayKey] = record.status;
                                } catch (e) {
                                  console.warn(
                                    "Failed to apply optimistic update",
                                    record,
                                    e
                                  );
                                }
                              }
                              return next;
                            });

                            // Removed setOriginalAttendance - it's never read and only creates redundant state updates
                            console.log(
                              "✅ Optimistic update applied - UI updated instantly!"
                            );

                            console.timeEnd("⏱️ SAVE_PREP_PAYLOAD");
                            console.time("⏱️ SAVE_API_CALL");
                            // Now save to backend (no await blocking UI)
                            const saveResponse =
                              await attendanceService.bulkMarkAttendance(
                                transformed
                              );
                            console.timeEnd("⏱️ SAVE_API_CALL");
                            console.time("⏱️ SAVE_STATE_UPDATES");

                            console.log("✅ Save response:", saveResponse);
                            console.log(
                              "📊 Saved records count:",
                              saveResponse?.data?.length || "N/A"
                            );

                            // Log what backend actually saved
                            if (
                              saveResponse?.data &&
                              saveResponse.data.length > 0
                            ) {
                              console.log(
                                "📊 Backend saved records:",
                                saveResponse.data
                              );
                              saveResponse.data.forEach((rec) => {
                                console.log(
                                  `   ✓ Saved: Employee ${rec.employeeId}, Date ${rec.date}, Status ${rec.status}`
                                );
                              });

                              // Merge server-returned records into attendanceData state
                              try {
                                if (Array.isArray(saveResponse.data)) {
                                  setAttendanceData((prev) => {
                                    const next = { ...(prev || {}) };
                                    for (const rec of saveResponse.data) {
                                      try {
                                        const empKey = String(rec.employeeId);
                                        const dayKey = String(
                                          new Date(rec.date).getDate()
                                        );
                                        if (!next[empKey]) next[empKey] = {};
                                        else next[empKey] = { ...next[empKey] };
                                        next[empKey][dayKey] = rec.status;
                                      } catch (e) {
                                        console.debug(
                                          "Failed to merge saved record into attendanceData",
                                          rec,
                                          e
                                        );
                                      }
                                    }
                                    return next;
                                  });

                                  // Also update the in-memory attendance cache so subsequent
                                  // fetches that consult the cache see the saved records.
                                  try {
                                    const attCacheKey = `${selectedMonth}|${selectedYear}|${debouncedSearchTerm}|${JSON.stringify(
                                      appliedFilters
                                    )}`;
                                    const existing =
                                      attendanceCacheRef.current.get(
                                        attCacheKey
                                      ) || null;
                                    const existingArr = Array.isArray(
                                      existing?.data
                                    )
                                      ? existing.data.slice()
                                      : [];

                                    // Build map by employeeId_date to upsert
                                    const m = new Map();
                                    for (const r of existingArr) {
                                      m.set(`${r.employeeId}_${r.date}`, r);
                                    }
                                    for (const r of saveResponse.data) {
                                      m.set(`${r.employeeId}_${r.date}`, r);
                                    }
                                    const merged = Array.from(m.values());
                                    const newCacheEntry = existing
                                      ? { ...existing, data: merged }
                                      : { success: true, data: merged };
                                    attendanceCacheRef.current.set(
                                      attCacheKey,
                                      newCacheEntry
                                    );
                                  } catch (e) {
                                    console.debug(
                                      "Failed to update attendance cache",
                                      e
                                    );
                                  }
                                }
                              } catch (e) {
                                console.debug("Merge after save failed", e);
                              }
                            }

                            // Clear overrideMap keys for saved records (if any)
                            try {
                              if (
                                saveResponse?.data &&
                                saveResponse.data.length
                              ) {
                                const savedKeys = saveResponse.data.map(
                                  (r) =>
                                    `${r.employeeId}_${new Date(
                                      r.date
                                    ).getDate()}`
                                );
                                setOverrideMap((prev) => {
                                  const next = { ...(prev || {}) };
                                  for (const k of savedKeys) delete next[k];
                                  return next;
                                });
                              }
                            } catch (e) {
                              console.debug(
                                "Failed to clear overrideMap after save",
                                e
                              );
                            }

                            // ✅ CLEAR CHANGED RECORDS for next save cycle
                            try {
                              changedRecordsRef.current.clear();
                              console.log(
                                "✅ Cleared changedRecordsRef after successful save"
                              );
                            } catch (e) {
                              console.debug(
                                "Failed to clear changedRecordsRef",
                                e
                              );
                            }

                            // Exit edit mode and show success
                            // Force month revisit to fetch fresh persisted state instead of stale cache.
                            try {
                              if (attendanceCacheRef?.current) attendanceCacheRef.current.clear();
                            } catch (e) {
                              console.debug("Failed to clear attendance cache after save", e);
                            }
                            setIsEditing(false);
                            setIsSaved(true);
                            setHasMarkAllUnsavedChanges(false);
                            showAlert(
                              `✅ ${employeeAttendance.length} records saved successfully!`
                            );
                            // If user chose "Save" in unsaved-changes modal, switch month after save
                            if (pendingMonthAfterSaveRef.current) {
                              const { nextYear: ny, nextMonth: nm } = pendingMonthAfterSaveRef.current;
                              pendingMonthAfterSaveRef.current = null;
                              applyMonthChange(ny, nm);
                            }

                            // DISABLED FOR TESTING: Comment out to allow manual refresh
                            // Attempt to refresh from server to ensure persisted data
                            // is returned by the attendance/history endpoint. This
                            // prevents situations where optimistic updates show
                            // locally but a subsequent full page reload does not
                            // reflect the saved changes due to cache/consistency.
                            /*
                            try {
                              console.log(
                                "↻ Refetching attendance from server to confirm saved changes"
                              );
                              // fetchData is the consolidated fetch routine defined
                              // earlier in this component and will update the
                              // attendance state and cache.
                              if (typeof fetchData === "function") {
                                await fetchData();
                                console.log("↻ Refetch complete");
                              } else {
                                console.debug(
                                  "fetchData not available to refetch after save"
                                );
                              }
                            } catch (e) {
                              console.debug("Refetch after save failed", e);
                            }
                            */
                            console.timeEnd("⏱️ SAVE_STATE_UPDATES");
                            console.timeEnd("⏱️ SAVE_TOTAL");
                          } catch (err) {
                            console.error("❌ Save failed:", err);
                            console.timeEnd("⏱️ SAVE_STATE_UPDATES");
                            console.timeEnd("⏱️ SAVE_TOTAL");
                            pendingMonthAfterSaveRef.current = null;
                            setError(
                              err.message || "Failed to save attendance data"
                            );
                            showAlert(
                              err.message || "Failed to save attendance data",
                              "error"
                            );
                          }
                        }}
                        className="text-sm rh-btn-primary hover:opacity-95 px-4 py-2 rounded-md"
                      >
                        Save
                      </button>
                    )}
                  </div>

                  {/* Show Options */}
                  <div className="flex items-center space-x-1.5">
                    <label
                      className={`text-xs font-medium ${
                        darkMode ? "text-[#E0E0E0]" : "text-gray-700"
                      }`}
                    >
                      Show:
                    </label>
                    <select
                      value={itemsPerPage}
                      onChange={(e) => {
                        setItemsPerPage(e.target.value);
                        setPage(1);
                      }}
                      className={`px-2 py-1.5 border rounded-md text-xs font-medium transition-all duration-200 focus:outline-none shadow-sm ${
                        darkMode
                          ? "bg-[#2C2C2C] border-[#3C3C3C] text-[#E0E0E0] focus:border-redhalo-red"
                          : "bg-white/95 border-[#f3d6bf] text-gray-900 focus:border-[#ef6f2e]"
                      }`}
                    >
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                      <option value="all">All</option>
                    </select>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleShowFilters();
                      }}
                      className={`flex items-center justify-center rh-btn-primary text-sm px-3 py-1.5 transition-all ${
                        hasActiveFilters
                          ? "ring-2 ring-redhalo-red ring-offset-1 dark:ring-offset-[#1C1C1C]"
                          : ""
                      }`}
                      style={
                        hasActiveFilters
                          ? { animation: "blink-border 1.5s ease-in-out infinite" }
                          : undefined
                      }
                      title="Filters"
                      aria-label="Filters"
                    >
                      <Filter className="w-4 h-4" aria-hidden />
                    </button>
                  </div>
                </div>
              </div>

              {itemsPerPage === "all" && paginatedEmployees.length >= 1000 && (
                <div
                  className={`mt-2 text-sm font-medium px-3 py-1.5 rounded-md ${
                    darkMode
                      ? "bg-amber-950 text-amber-100 border border-amber-700"
                      : "border border-amber-300 bg-amber-100 text-amber-900"
                  }`}
                >
                  ⚠️ Showing {paginatedEmployees.length} employees (capped at 1000 for performance) — Click
                  &quot;Calculate and Show Summary&quot; to refresh aggregate totals in the bar below
                </div>
              )}
            </div>
            <div
              className={`mt-0.5 flex min-h-0 flex-1 flex-col overflow-hidden rounded-b-2xl border ${
                darkMode ? "border-[#3C3C3C]" : "border-[#165b5d]/30"
              }`}
            >
            <div className="attendance-list-container relative flex flex-1 min-h-0 flex-col overflow-hidden">
              {employeesLoading ? (
                <div className="flex justify-center items-center py-12">
                  <div className="flex flex-col items-center gap-4">
                    {/* Spinner */}
                    <div className="flex items-center justify-center">
                      <div
                        className={`w-10 h-10 border-4 rounded-full animate-spin ${
                          darkMode
                            ? "border-gray-600 border-t-redhalo-red"
                            : "border-gray-300 border-t-redhalo-red"
                        }`}
                      ></div>
                    </div>
                    {/* Loading text */}
                    <div
                      className={`text-sm font-medium ${
                        darkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Loading employees...
                    </div>
                  </div>
                </div>
              ) : filteredEmployees.length === 0 ? (
                <div className="flex justify-center items-center py-8">
                  <div
                    className={`text-sm ${
                      darkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    No employees found. Please check if employees are loaded
                    properly.
                  </div>
                </div>
              ) : (
                <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                  <div className="relative flex min-h-0 flex-1 flex-col overflow-y-auto overflow-x-hidden">
                      <AttendanceTable
                        employees={paginatedEmployees}
                        darkMode={darkMode}
                        selectedMonth={selectedMonth}
                        selectedYear={selectedYear}
                        attendanceData={attendanceData}
                        setAttendanceData={setAttendanceData}
                        attendanceLOPReversalMap={attendanceLOPReversalMap}
                        isEditing={isEditing && canModifyAttendance}
                        isLocked={isLocked}
                        totals={totals}
                        selectedDate={selectedDate}
                        setSelectedDate={setSelectedDate}
                        weeklyOffPolicies={weeklyOffPolicies}
                        employeeWeeklyOffMappings={employeeWeeklyOffMappings}
                        holidaysData={holidaysData}
                        holidayPolicies={holidayPolicies}
                        employeeHolidayMappings={employeeHolidayMappings}
                        onApplyLeave={canManageLeaveOps ? handleApplyLeaveClick : null}
                        onLOPReversal={canAttendanceApprove ? handleLOPReversalClick : null}
                        onLeaveAdjustment={canModifyAttendance ? handleLeaveAdjustmentClick : null}
                        onLeaveBalance={canModifyAttendance ? handleLeaveBalanceClick : null}
                        onViewAttendance={handleViewAttendanceClick}
                        page={page}
                        itemsPerPage={itemsPerPage}
                        serverSidePagination={serverSidePagination}
                        onLoadMore={triggerLoadMore}
                        leaveTypes={leaveTypes}
                        attendanceStatusesState={attendanceStatusesState}
                        statusDefinitionsState={statusDefinitionsState}
                        statusColorMapState={statusColorMapState}
                        employeesLoading={employeesLoading}
                        infiniteLoading={infiniteLoading}
                        hasMoreEmployees={hasMoreEmployees}
                        overrideMap={overrideMap}
                        setOverrideMap={setOverrideMap}
                        changedRecordsRef={changedRecordsRef}
                        lopReversalDaysMap={lopReversalDaysMap}
                        hasActiveFilters={hasActiveFilters}
                        departmentsList={departmentsList}
                        designationsList={designationsList}
                        branchesList={branchesList}
                      />
                      {/* infinite scroll sentinel moved into AttendanceTable (per-chunk prefetch) */}
                    </div>
                </div>
              )}
              {/* Totals bar: sibling of ternary inside list container — full width matches grid card (same px-3 parent) */}
              {!employeesLoading && filteredEmployees.length > 0 && (
                <div
                  className={`-mx-px w-[calc(100%+2px)] shrink-0 border border-t-0 px-3 py-2 ${
                    darkMode
                      ? "border-[#3C3C4C] bg-[#2C2C2C]"
                      : "border-[#165b5d]/30 bg-gray-50"
                  }`}
                >
                  <div className="flex w-full min-w-0 flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
                    <div className="flex min-w-0 shrink-0 flex-wrap items-center gap-2">
                      <span
                        className={`inline-flex max-w-full flex-wrap items-center gap-x-1.5 gap-y-0.5 rounded-md border px-2 py-1 text-xs font-semibold tabular-nums tracking-tight ${
                          darkMode
                            ? "border-[#4C4C4C] bg-[#252525] text-[#E0E0E0]"
                            : "border-[#d7dee8] bg-white text-gray-800 shadow-sm"
                        }`}
                        title={
                          Number(totalEmployees) > 0
                            ? `Total employees (reported): ${totalEmployees}. ${paginatedEmployees.length} row(s) visible in the grid above.`
                            : `Employees in scope for this month: ${filteredEmployees.length}. ${paginatedEmployees.length} row(s) visible in the grid.`
                        }
                      >
                        <span className="whitespace-nowrap">Listed Employees:</span>{" "}
                        <span className="font-bold">
                          {Number(totalEmployees) > 0
                            ? totalEmployees
                            : filteredEmployees.length}
                        </span>
                        {paginatedEmployees.length <
                          (Number(totalEmployees) > 0
                            ? Number(totalEmployees)
                            : filteredEmployees.length) && (
                          <span
                            className={`whitespace-nowrap text-[10px] font-normal opacity-80 ${
                              darkMode ? "text-[#A6A9C8]" : "text-gray-500"
                            }`}
                          >
                            ({paginatedEmployees.length} in grid)
                          </span>
                        )}
                      </span>
                    </div>
                    <div className="flex min-w-0 flex-1 items-center justify-end">
                      <div className="flex min-w-0 items-center justify-end space-x-2 overflow-x-auto whitespace-nowrap scrollbar-hide">
                        <>
                          {hasActiveFilters && (
                            <span
                              className={`px-2 py-1 rounded-md text-xs font-semibold ${
                                darkMode
                                  ? "bg-red-600 text-white"
                                  : "bg-red-500 text-white"
                              }`}
                            >
                              Filtered
                            </span>
                          )}
                          <span
                            className={`text-xs font-medium ${
                              darkMode ? "text-gray-400" : "text-gray-600"
                            }`}
                          >
                            Totals:
                          </span>
                          <div className="flex items-center space-x-3 text-xs whitespace-nowrap">
                            {(() => {
                              const labelFor = (code) =>
                                (attendanceMapping &&
                                  attendanceMapping[
                                    String(code || "").toUpperCase()
                                  ]) ||
                                code;

                              return (
                                <>
                                  <span
                                    className={`px-2 py-1 rounded-md ${
                                      darkMode
                                        ? "rh-btn-primary"
                                        : "bg-emerald-50 text-emerald-900"
                                    }`}
                                  >
                                    {labelFor("PR")}: {totals.PR ?? 0}
                                  </span>

                                  <span
                                    className={`px-2 py-1 rounded-md ${
                                      darkMode
                                        ? "bg-amber-600 text-white"
                                        : "bg-amber-100 text-amber-900"
                                    }`}
                                  >
                                    {labelFor("PH")}: {totals.PH ?? 0}
                                  </span>

                                  <span
                                    className={`px-2 py-1 rounded-md ${
                                      darkMode
                                        ? "rh-btn-primary"
                                        : "rh-accent-soft"
                                    }`}
                                  >
                                    {labelFor("WO")}: {totals.WO ?? 0}
                                  </span>

                                  <span
                                    className={`px-2 py-1 rounded-md ${
                                      darkMode
                                        ? "bg-redhalo-navy text-white"
                                        : "bg-redhalo-mint/90 text-redhalo-navy"
                                    }`}
                                  >
                                    {labelFor("CL")}: {totals.CL ?? 0}
                                  </span>

                                  <span
                                    className={`px-2 py-1 rounded-md ${
                                      darkMode
                                        ? "bg-redhalo-navy text-white"
                                        : "rh-accent-soft"
                                    }`}
                                  >
                                    {labelFor("EL")}: {totals.EL ?? 0}
                                  </span>

                                  <span
                                    className={`px-2 py-1 rounded-md ${
                                      darkMode
                                        ? "bg-orange-600 text-white"
                                        : "bg-orange-100 text-orange-800"
                                    }`}
                                  >
                                    {labelFor("CO")}: {totals.CO ?? 0}
                                  </span>

                                  <span
                                    className={`px-2 py-1 rounded-md ${
                                      darkMode
                                        ? "border border-redhalo-teal/50 bg-redhalo-navy text-redhalo-mint"
                                        : "border border-redhalo-teal/80 bg-redhalo-teal/40 text-redhalo-navy"
                                    }`}
                                  >
                                    {labelFor("CW")}: {totals.CW ?? 0}
                                  </span>

                                  {Object.keys(statusDefinitions || {})
                                    .filter(
                                      (k) =>
                                        ![
                                          "PR",
                                          "PH",
                                          "WO",
                                          "CL",
                                          "EL",
                                          "CO",
                                          "CW",
                                          "LOP",
                                        ].includes(k)
                                    )
                                    .map((code) => {
                                      const textColor = statusColorMap && statusColorMap[code];
                                      return (
                                        <span
                                          key={`dyn-${code}`}
                                          className={`px-2 py-1 rounded-md ${
                                            darkMode
                                              ? "bg-gray-700"
                                              : "bg-gray-100"
                                          }`}
                                          style={textColor ? { color: textColor } : undefined}
                                          title={statusDefinitions[code]}
                                        >
                                          {labelFor(code)}:{" "}
                                          {totals[code.toUpperCase()] ?? 0}
                                        </span>
                                      );
                                    })}

                                  <span
                                    className={`px-2 py-1 rounded-md ${
                                      darkMode
                                        ? "bg-red-600 text-white"
                                        : "bg-red-100 text-red-800"
                                    }`}
                                  >
                                    {labelFor("LOP")}: {totals.LOP ?? 0}
                                  </span>

                                  <span
                                    className={`px-2 py-1 rounded-md ${
                                      darkMode
                                        ? "rh-btn-primary"
                                        : "rh-accent-soft"
                                    }`}
                                  >
                                    Pay Days: {totals.payDays ?? 0}
                                  </span>
                                  <span
                                    className={`px-2 py-1 rounded-md ${
                                      darkMode
                                        ? "bg-redhalo-navy text-white"
                                        : "bg-redhalo-mint/90 text-redhalo-navy"
                                    }`}
                                    title="LOP Reversal days for selected month"
                                  >
                                    LOP Rev: {totals.lopReversalDays ?? 0}
                                  </span>
                                </>
                              );
                            })()}
                          </div>
                        </>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            </div>
            </div>
            )}
          </div>
        )}

        {activeTab === "leaves" && (
          <div
            className={`p-3 ${
              darkMode ? "border-[#3C3C4C] bg-gray-800" : "border-[#165b5d]/30 bg-white"
            } rounded-lg border shadow-lg`}
          >
            <div className="mb-3">
              <h3
                className={`text-lg font-bold mb-1 ${
                  darkMode ? "text-white" : "text-gray-800"
                }`}
              >
                Leave Management
              </h3>
              <p
                className={`text-xs ${
                  darkMode ? "text-gray-300" : "text-gray-600"
                }`}
              >
                Use the action button next to each employee or the Mark All
                functionality to apply leaves. Leave details can also be
                uploaded via Excel template.
              </p>
            </div>
          </div>
        )}

        {/* Reports Tab */}
        {activeTab === "reports" && (
          <div className="min-h-0 flex-1 overflow-hidden">
            <AttendanceReports
              key={`attendance-reports-${activeReportsSubTab}`}
              darkMode={darkMode}
              setCurrentPage={setCurrentPage}
              toggleDarkMode={toggleDarkMode}
              forcedSubTab={activeReportsSubTab}
              hideSidebar
            />
          </div>
        )}

        {showFilters && (
          <Filters
            darkMode={darkMode}
            onClose={() => setShowFilters(false)}
            onClear={() => {
              setAppliedFilters({});
              setAppliedFiltersForm({});
              setPage(1);
            }}
            onApply={(_employees, filters, params) => {
              // Use params (API-formatted keys) instead of filters (form keys)
              setAppliedFilters(params || {});
              // Persist last-applied form state so it "stays" until Clear + Apply
              setAppliedFiltersForm(filters || {});
              setShowFilters(false);
            }}
            initialFilters={appliedFiltersForm}
          />
        )}

        {showMarkAllPopup && canModifyAttendance && (
          <MarkAllPopup
            darkMode={darkMode}
            onClose={() => setShowMarkAllPopup(false)}
            onMarkAll={handleMarkAll}
            daysInMonth={daysInMonth}
            allEmployees={markAllEmployeesList.length > 0 ? markAllEmployeesList : allEmployees}
          />
        )}

        {showUploadPopup && canAttendanceImport && (
          <UploadPopup
            darkMode={darkMode}
            onClose={() => setShowUploadPopup(false)}
            onUpload={handleImportFile}
            onDownloadTemplate={handleDownloadTemplate}
            selectedMonth={selectedMonth}
            selectedYear={selectedYear}
          />
        )}
        {showImportPreview && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div
              className={`w-[90vw] max-w-3xl p-6 rounded-lg shadow-xl ${
                darkMode ? "bg-gray-800 text-white" : "bg-white"
              }`}
            >
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold">Import Preview</h2>
                <button
                  onClick={() => {
                    setShowImportPreview(false);
                    setImportReport(null);
                  }}
                  className="text-gray-400"
                >
                  Close
                </button>
              </div>
              <div className="mb-4">
                <div className="text-sm">
                  Total rows parsed: <strong>{importReport?.total ?? 0}</strong>
                </div>
                <div className="text-sm text-emerald-700 dark:text-emerald-400">
                  Valid: <strong>{importReport?.validCount ?? 0}</strong>
                </div>
                <div className="text-sm text-red-700">
                  Invalid: <strong>{importReport?.invalidCount ?? 0}</strong>
                </div>
              </div>
              <div className="mb-4 max-h-64 overflow-auto border rounded p-2">
                {importPreviewInvalid && importPreviewInvalid.length > 0 ? (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left">
                        <th className="p-1">Employee Code</th>
                        <th className="p-1">Employee Name</th>
                        <th className="p-1">Date</th>
                        <th className="p-1">Status</th>
                        <th className="p-1">Errors</th>
                      </tr>
                    </thead>
                    <tbody>
                      {importPreviewInvalid.map((row, idx) => (
                        <tr key={idx} className="border-t">
                          <td className="p-1">{row.employeeCode}</td>
                          <td className="p-1">{row.employeeName || "-"}</td>
                          <td className="p-1">{row.date}</td>
                          <td className="p-1">{row.status}</td>
                          <td className="p-1">
                            {(row.errors || []).join(", ")}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="text-sm text-gray-600">No invalid rows.</div>
                )}
              </div>
              <div className="flex justify-end space-x-2">
                <button
                  onClick={() => {
                    setShowImportPreview(false);
                    setImportReport(null);
                    setImportPreviewInvalid([]);
                    setImportPreviewValid([]);
                  }}
                  className="px-4 py-2 rounded bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    // Proceed with valid rows
                    setShowImportPreview(false);
                    setIsImporting(true);
                    const payload = {
                      date: importReport
                        ? `${importReport.year}-${String(
                            importReport.month + 1
                          ).padStart(2, "0")}-01`
                        : `${exportYear}-${String(exportMonth + 1).padStart(
                            2,
                            "0"
                          )}-01`,
                      employeeAttendance: (importPreviewValid || []).map(
                        (it) => ({
                          ...(Number.isFinite(Number(it._resolvedEmployeeId))
                            ? { employeeId: Number(it._resolvedEmployeeId) }
                            : {}),
                          employeeCode:
                            it._resolvedEmployeeCode || it.employeeCode,
                          date: it.date,
                          status:
                            it._canonicalStatus ||
                            (importReverseMapping &&
                              importReverseMapping[
                                String(it.status || "").toUpperCase()
                              ]) ||
                            (reverseMapping &&
                              reverseMapping[
                                String(it.status || "").toUpperCase()
                              ]) ||
                            it.status,
                          remarks: it.remarks || "",
                        })
                      ),
                    };
                    try {
                      if (
                        !payload.employeeAttendance ||
                        payload.employeeAttendance.length === 0
                      ) {
                        console.warn(
                          "Proceed import: employeeAttendance is empty — skipping POST to /attendance/bulk-mark",
                          payload
                        );
                        showAlert("No valid rows to import.", "info");
                      } else {
                        const resp = await attendanceService.bulkMarkAttendance(
                          payload
                        );
                        if (resp && resp.success) {
                          const importYear = importReport?.year || exportYear;
                          const importMonth = importReport?.month !== undefined ? importReport.month : exportMonth;
                          
                          toast.success(
                            `Imported ${payload.employeeAttendance.length} rows successfully.`
                          );
                          // Invalidate ALL frontend caches so searches/refreshes hit fresh DB data
                          try {
                            attendanceCacheRef.current.clear();
                            employeesCacheRef.current.clear();
                            if (statusCountsCache?.current) statusCountsCache.current.clear();
                          } catch (e) {
                            /* ignore cache clear errors */
                          }

                          // Re-fetch via the main fetchData pipeline for proper chunking & merging
                          try {
                            attendanceFetchInFlightRef.current = false;
                            attendanceFetchKeyRef.current = "";
                            await fetchData();
                          } catch (refreshErr) {
                            console.warn("Post-import fetchData refresh failed:", refreshErr);
                          }
                        } else {
                          const errorMessage = resp?.message || "Import failed";
                          toast.error(errorMessage);
                        }
                      }
                    } catch (err) {
                      console.error(err);
                      showAlert(err.message || "Import failed", "error");
                    } finally {
                      setIsImporting(false);
                      setImportReport(null);
                      setImportPreviewValid([]);
                      setImportPreviewInvalid([]);
                    }
                  }}
                  className="rh-btn-primary rounded px-4 py-2 hover:opacity-95"
                >
                  Proceed with valid rows
                </button>
              </div>
            </div>
          </div>
        )}

        {showUploadLeavePopup && canManageLeaveOps && (
          <UploadLeavePopup
            darkMode={darkMode}
            onClose={() => setShowUploadLeavePopup(false)}
            onUploadLeave={handleUploadLeaveDetails}
            onDownloadLeaveTemplate={handleDownloadLeaveTemplate}
            showAlert={showAlert}
          />
        )}

        {/* Passkey Modal for Unlock */}
        {showPasskeyModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div
              className={`rounded-lg shadow-xl w-96 p-6 ${
                darkMode ? "bg-gray-800 text-white" : "bg-white text-gray-800"
              }`}
            >
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-bold">Unlock Attendance</h2>
                <button
                  onClick={() => {
                    setShowPasskeyModal(false);
                    setUnlockPasskey("");
                    setShowPasskey(false);
                    setPasskeyError("");
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="mb-4">
                <p className={`text-sm mb-2 ${
                  darkMode ? "text-gray-300" : "text-gray-600"
                }`}>
                  Enter the passkey to unlock attendance for{" "}
                  {new Date(selectedYear, selectedMonth, 1).toLocaleDateString(
                    "en-US",
                    { month: "long", year: "numeric" }
                  )}
                </p>
                <div className="relative">
                  <input
                    key={unlockPasskeyInputKey}
                    type="text"
                    name="attendance_unlock_code_entry"
                    id="attendance-unlock-passkey-input"
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck={false}
                    inputMode="text"
                    data-lpignore="true"
                    data-1p-ignore="true"
                    data-form-type="other"
                    value={unlockPasskey}
                    onChange={(e) => {
                      setUnlockPasskey(e.target.value);
                      setPasskeyError("");
                    }}
                    placeholder="Enter passkey"
                    style={{
                      WebkitTextSecurity: showPasskey ? "none" : "disc",
                    }}
                    className={`w-full px-3 py-2 pr-10 border rounded ${
                      passkeyError
                        ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                        : darkMode
                        ? "bg-gray-700 border-gray-600 text-white focus:border-redhalo-red"
                        : "bg-white border-gray-300 text-gray-800 focus:border-redhalo-red"
                    }`}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && unlockPasskey.trim()) {
                        handleUnlockWithPasskey();
                      }
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasskey(!showPasskey)}
                    className={`absolute right-2 top-1/2 transform -translate-y-1/2 ${
                      darkMode ? "text-gray-400 hover:text-gray-300" : "text-gray-500 hover:text-gray-700"
                    }`}
                    tabIndex={-1}
                  >
                    {showPasskey ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {passkeyError && (
                  <p className="text-red-500 text-xs mt-1">{passkeyError}</p>
                )}
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowPasskeyModal(false);
                    setUnlockPasskey("");
                    setShowPasskey(false);
                    setPasskeyError("");
                  }}
                  className={`px-4 py-2 rounded ${
                    darkMode
                      ? "bg-gray-600 text-white hover:bg-gray-700"
                      : "bg-gray-300 text-gray-700 hover:bg-gray-400"
                  }`}
                >
                  Cancel
                </button>
                <button
                  onClick={handleUnlockWithPasskey}
                  disabled={!unlockPasskey.trim() || unlockingBusy}
                  className={`px-4 py-2 rounded ${
                    darkMode
                      ? "bg-red-600 text-white hover:bg-red-700 disabled:bg-gray-600"
                      : "bg-red-500 text-white hover:bg-red-600 disabled:bg-gray-400"
                  }`}
                >
                  {unlockingBusy ? "Unlocking..." : "Unlock"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Confirmation Modal for Lock */}
        <ConfirmationModal
          isOpen={confirmationModal.isOpen}
          onClose={closeConfirmation}
          onConfirm={confirmationModal.onConfirm}
          onCancel={confirmationModal.onCancel}
          title={confirmationModal.title}
          message={confirmationModal.message}
          confirmText={confirmationModal.confirmText}
          cancelText={confirmationModal.cancelText}
          darkMode={darkMode}
        />

        {/* Unsaved changes when changing month: Save / Don't Save / Cancel */}
        {unsavedChangesModal.isOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div
              className="absolute inset-0 bg-black bg-opacity-50"
              onClick={() => {
                setMonthPickerValue(monthRangeValue);
                setUnsavedChangesModal({ isOpen: false, nextYear: null, nextMonth: null });
              }}
            />
            <div
              className={`relative z-10 w-full max-w-md mx-4 rounded-lg shadow-xl ${
                darkMode ? "bg-[#2C2C2C] border border-[#3C3C3C]" : "bg-white border border-gray-200"
              }`}
            >
              <div className={`px-6 py-4 border-b ${darkMode ? "border-[#3C3C3C]" : "border-gray-200"}`}>
                <h3 className={`text-lg font-semibold ${darkMode ? "text-[#E0E0E0]" : "text-gray-900"}`}>
                  Unsaved changes
                </h3>
              </div>
              <div className="px-6 py-4">
                <p className={`text-sm ${darkMode ? "text-gray-400" : "text-gray-600"}`}>
                  Do you want to save your changes before switching month?
                </p>
              </div>
              <div className={`px-6 py-4 border-t flex justify-end gap-3 ${darkMode ? "border-[#3C3C3C]" : "border-gray-200"}`}>
                <button
                  onClick={() => {
                    setMonthPickerValue(monthRangeValue);
                    setUnsavedChangesModal({ isOpen: false, nextYear: null, nextMonth: null });
                  }}
                  className={`px-4 py-2 rounded text-sm font-medium border ${
                    darkMode ? "bg-[#2C2C2C] text-[#E0E0E0] border-[#3C3C3C] hover:bg-[#3C3C3C]" : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
                  }`}
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    applyMonthChange(unsavedChangesModal.nextYear, unsavedChangesModal.nextMonth);
                    setUnsavedChangesModal({ isOpen: false, nextYear: null, nextMonth: null });
                  }}
                  className={`px-4 py-2 rounded text-sm font-medium ${
                    darkMode ? "bg-[#3C3C3C] text-[#E0E0E0] hover:bg-[#4C4C4C]" : "bg-gray-200 text-gray-800 hover:bg-gray-300"
                  }`}
                >
                  Don&apos;t Save
                </button>
                <button
                  onClick={() => {
                    const { nextYear, nextMonth } = unsavedChangesModal;
                    setUnsavedChangesModal({ isOpen: false, nextYear: null, nextMonth: null });
                    pendingMonthAfterSaveRef.current = { nextYear, nextMonth };
                    saveButtonRef.current?.click();
                  }}
                  className="px-4 py-2 rounded text-sm font-medium rh-btn-primary hover:opacity-95"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        )}

        {showExportModal && canAttendanceExport && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div
              className={`rounded-lg shadow-xl w-96 p-6 ${
                {
                  true: darkMode
                    ? "bg-gray-800 text-white"
                    : "bg-white text-gray-800",
                }[true]
              }`}
            >
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-bold">Export Attendance</h2>
                <button
                  onClick={() => setShowExportModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-2">
                  Choose month and year to export
                </p>
                <DatePicker
                  monthYearOnly
                  showMonthName
                  value={getMonthRangeValue(exportYear, exportMonth)}
                  onChange={handleExportMonthChange}
                />
              </div>

              <div className="flex justify-end space-x-2">
                <button
                  onClick={() => setShowExportModal(false)}
                  className={`px-4 py-2 rounded ${
                    darkMode
                      ? "bg-gray-700 text-white"
                      : "bg-gray-200 text-gray-800"
                  }`}
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmExport}
                  disabled={isExporting}
                  className={`px-4 py-2 rounded rh-btn-primary ${
                    isExporting ? "opacity-60 cursor-not-allowed" : ""
                  }`}
                >
                  {isExporting ? "Exporting..." : "Export"}
                </button>
              </div>
            </div>
          </div>
        )}

        {leaveInsightReview?.row && (
          <LeaveRequestReviewModal
            darkMode={darkMode}
            row={leaveInsightReview.row}
            onClose={() => setLeaveInsightReview(null)}
            onApprove={(row) => handleAttendanceInsightRowAction("approve", row)}
            onReject={(row) => handleAttendanceInsightRowAction("reject", row)}
            onCancel={(row) => handleAttendanceInsightRowAction("cancel", row)}
          />
        )}

        {showApplyLeavePopup && (
          <ApplyLeavePopup
            darkMode={darkMode}
            isOpen={showApplyLeavePopup}
            onClose={() => {
              setShowApplyLeavePopup(false);
              setSingleEmployeeForLeave(null);
            }}
            onApplyLeave={handleApplyLeave}
            allEmployees={allEmployees}
            departmentsList={departmentsList}
            branchesList={branchesList}
            singleEmployee={singleEmployeeForLeave}
            leaveTypes={leaveTypes}
          />
        )}

        {showEmployeeAttendanceCalendar && (
          <EmployeeAttendanceCalendarPopup
            darkMode={darkMode}
            isOpen={showEmployeeAttendanceCalendar}
            onClose={() => {
              setShowEmployeeAttendanceCalendar(false);
              setEmployeeAttendanceCalendarTarget(null);
            }}
            employee={employeeAttendanceCalendarTarget}
            selectedMonth={selectedMonth}
            selectedYear={selectedYear}
            attendanceData={attendanceData}
            attendanceDetailData={attendanceDetailData}
            leaveTypes={leaveTypes}
            isLocked={isLocked}
            onApplyLeave={canManageLeaveOps ? handleCalendarApplyLeaveClick : null}
            onSaveRegularizationTimes={
              canModifyAttendance ? handleCalendarRegularizationTimesSave : null
            }
            onEditAttendanceStatus={
              canModifyAttendance ? handleCalendarAttendanceStatusUpdate : null
            }
          />
        )}

        {showLOPReversalPopup && (
          <LOPReversalPopup
            darkMode={darkMode}
            isOpen={showLOPReversalPopup}
            onClose={() => {
              setShowLOPReversalPopup(false);
              setLOPReversalEmployee(null);
            }}
            employee={lopReversalEmployee}
            attendanceLocks={attendanceLocks}
            attendanceService={attendanceService}
            leaveTypes={leaveTypes}
            selectedYear={selectedYear}
            selectedMonth={selectedMonth}
            weeklyOffPolicies={weeklyOffPolicies}
            employeeWeeklyOffMappings={employeeWeeklyOffMappings}
            holidaysData={holidaysData}
            holidayPolicies={holidayPolicies}
            employeeHolidayMappings={employeeHolidayMappings}
            onSuccess={() => {
              if (attendanceCacheRef?.current) attendanceCacheRef.current.clear();
              // Refetch LOP reversal summary for current payroll month
              if (attendanceService && typeof attendanceService.getLOPReversalSummary === "function") {
                attendanceService.getLOPReversalSummary(selectedMonth + 1, selectedYear).then((res) => {
                  const data = res?.data ?? res;
                  if (data && typeof data === "object" && !Array.isArray(data)) setLOPReversalDaysMap(data);
                }).catch(() => {});
              }
            }}
          />
        )}

        {showLeaveAdjustmentPopup && (
          <LeaveAdjustmentPopup
            darkMode={darkMode}
            isOpen={showLeaveAdjustmentPopup}
            onClose={() => {
              setShowLeaveAdjustmentPopup(false);
              setLeaveAdjustmentEmployee(null);
            }}
            employee={leaveAdjustmentEmployee}
            leaveTypes={leaveTypes}
            attendanceStructureService={attendanceStructureService}
            onSuccess={() => {
              setLeaveBalanceRefreshKey((k) => k + 1);
            }}
          />
        )}

        {showLeaveBalancePopup && (
          <LeaveBalancePopup
            darkMode={darkMode}
            isOpen={showLeaveBalancePopup}
            onClose={() => {
              setShowLeaveBalancePopup(false);
              setLeaveBalanceEmployee(null);
            }}
            employee={leaveBalanceEmployee}
            leaveTypes={leaveTypes}
            attendanceStructureService={attendanceStructureService}
            refreshKey={leaveBalanceRefreshKey}
          />
        )}

        {alert && (
          <Alert
            message={alert.message}
            type={alert.type}
            onClose={() => setAlert(null)}
          />
        )}

      {/* Structures Modal — portal + high z-index so navbar/sidebars never cover it */}
      {showStructuresModal &&
        canManageAttendanceSettings &&
        createPortal(
          <div
            className="fixed inset-0 z-[10030] flex items-center justify-center bg-black/50 p-4"
            role="presentation"
            onClick={() => setShowStructuresModal(false)}
          >
            <div
              className={`flex h-[min(70vh,calc(100dvh-6rem))] w-[min(70vw,calc(100vw-2rem))] max-h-[calc(100dvh-4rem)] max-w-[calc(100vw-2rem)] flex-col rounded-lg shadow-2xl ${
                darkMode ? "bg-gray-800 text-white" : "bg-white text-gray-800"
              }`}
              role="dialog"
              aria-modal="true"
              aria-labelledby="attendance-settings-title"
              onClick={(e) => e.stopPropagation()}
            >
            {/* Fixed Header */}
            <div
              className={`flex shrink-0 items-center justify-between border-b p-6 ${
                darkMode ? "border-gray-600" : "border-gray-300"
              }`}
            >
              <h2 id="attendance-settings-title" className="text-xl font-bold">
                Attendance Settings
              </h2>
              <button
                type="button"
                onClick={() => setShowStructuresModal(false)}
                className={`rounded-md p-1 transition ${
                  darkMode
                    ? "text-gray-400 hover:bg-gray-700 hover:text-gray-100"
                    : "text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                }`}
                aria-label="Close attendance settings"
              >
                <X size={24} />
              </button>
            </div>

            {/* Fixed Sub-tabs */}
            <div
              className={`flex shrink-0 space-x-1 border-b px-6 py-3 ${
                darkMode ? "border-gray-600" : "border-gray-300"
              }`}
            >
              {[
                { key: "generalSettings", label: "General Settings" },
                { key: "weeklyOff", label: "Weekly Off" },
                { key: "holiday", label: "Holiday" },
                { key: "leaveManagement", label: "Leave Management" },
                { key: "shift", label: "Shift" },
                { key: "attendancePolicy", label: "Attendance Policy" },
                { key: "codesAndDevices", label: "Codes and devices" },
              ].map(({ key, label }) => (
                  <button
                    key={key}
                    type="button"
                    className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                      activeStructureTab === key
                        ? "rh-btn-primary"
                        : darkMode
                        ? "text-gray-300 hover:text-white"
                        : "text-gray-600 hover:text-gray-800"
                    }`}
                    onClick={() => setActiveStructureTab(key)}
                  >
                    {label}
                  </button>
                ))}
            </div>

            {/* Scrollable Content Area */}
            <div className="flex-1 overflow-hidden">
              <AttendanceStructuresContent
                activeStructureTab={activeStructureTab}
                darkMode={darkMode}
                employeeWeeklyOffMappings={employeeWeeklyOffMappings}
                setEmployeeWeeklyOffMappings={setEmployeeWeeklyOffMappings}
                parentServiceCommencesOnDate={minDate}
                // Pass pre-fetched structure data so modal shows immediately
                parentWeeklyOffPolicies={weeklyOffPolicies}
                parentHolidayPolicies={holidayPolicies}
                parentHolidays={holidaysData}
                parentLeaveTypes={leaveTypes}
                parentLeaveGroups={[]}
                parentHolidayBank={[]}
                parentBranches={branchesList}
                parentDepartments={departmentsList}
                parentDesignations={[]}
                parentGrades={[]}
                parentSubDepartments={[]}
                parentCategories={[]}
                parentEmploymentTypes={[]}
              />
            </div>
          </div>
          </div>,
          document.body
        )}
          </div>
        </div>
      </div>
    </div>
  );
}

